import { useState, useEffect, useMemo, useRef } from "react";
import {
  Home, Shield, FileText, Wallet as WalletIcon, Settings, Bell, Plus, Search, ChevronLeft,
  ChevronRight, ChevronDown, ChevronUp, X, Check, ArrowRight, ArrowUpRight, MapPin, Eye, EyeOff,
  Upload, Download, Share2, User, Building2, Phone, MessageCircle, Smartphone, Clock,
  ShieldCheck, Star, BadgeCheck, Lock, AlertTriangle, AlertCircle, Info, Sparkles,
  HelpCircle, LogOut, FileCheck2, Zap, Briefcase, Menu, Users, CreditCard, Activity,
  Calendar, Code, Copy, RefreshCw, MoreHorizontal, TrendingUp, Layers, Database, Filter,
} from "lucide-react";

// ═══════════════════════════════════════════════════════════════════════════════════
// DESIGN TOKENS — Casava Bloom (April 2026)
// Warm paper neutrals · Bloom Pink brand · Brown anchor · Plus Jakarta Sans + Fraunces
// ═══════════════════════════════════════════════════════════════════════════════════
const T = {
  bg: "#F7F3EF", surface: "#FFFFFF", surfaceTint: "#F2EEEA",
  text: "#1D1D1F", textSec: "#6C6C6C", textTer: "#7F7F7F",
  border: "#E5E5E5", borderStrong: "#828282",
  brand: "#FF147D", brandDeep: "#7C0C3F", brandLight: "#F5D8E1", brandBg: "#FFE8F2",
  brown: "#36291A", brownDeep: "#1F120E",
  success: "#5CBD75", successBg: "#E8F6EC", successBorder: "#B8E0C0", successText: "#1A6F3D",   // text-on-tint variant
  danger: "#E04C41", dangerBg: "#FBEAE9", dangerBorder: "#F4C2BD", dangerText: "#A1322B",       // text-on-tint variant
  amber: "#FFCD28", amberBg: "#FFF7D6", amberBorder: "#F5DB89", amberText: "#92400E",           // text-on-tint variant
  info: "#1D1D1F", infoBg: "#F2EEEA", infoBorder: "#E5E5E5",
  // Customer + decorative avatar colors (per design system avatar spec)
  green: "#018336", pinkCoral: "#E73C6A",
  // Extended palette — illustrations only, never primary UI
  pinkBright: "#FF75E5", salmon: "#FF6E9B", yellowSoft: "#EDE353",
  purple: "#6F52ED", cyan: "#49DCFC",
};
const FONT = "'Plus Jakarta Sans', -apple-system, BlinkMacSystemFont, sans-serif";
const FONT_DISPLAY = "'Fraunces', 'Plus Jakarta Sans', Georgia, serif";

// Inject Google Fonts + global styles
const useFonts = () => {
  useEffect(() => {
    const id = "casava-fonts";
    if (document.getElementById(id)) return;
    const link = document.createElement("link");
    link.id = id;
    link.rel = "stylesheet";
    link.href = "https://fonts.googleapis.com/css2?family=Plus+Jakarta+Sans:wght@400;500;600;700;800&family=Fraunces:opsz,wght@9..144,300;9..144,400;9..144,500;9..144,600&display=swap";
    document.head.appendChild(link);
    const style = document.createElement("style");
    style.id = "casava-globals";
    style.textContent = `
      @keyframes fadeIn { from { opacity: 0 } to { opacity: 1 } }
      @keyframes slideUp { from { opacity: 0; transform: translateY(8px) } to { opacity: 1; transform: translateY(0) } }
      @keyframes loginSlideIn { from { opacity: 0; transform: translateY(16px) } to { opacity: 1; transform: translateY(0) } }
      @keyframes pulse { 0%, 100% { opacity: 1 } 50% { opacity: 0.55 } }
      @keyframes shimmer { 0% { background-position: -400px 0 } 100% { background-position: 400px 0 } }
      @keyframes casaDot { 0%, 60%, 100% { opacity: 0.3 } 30% { opacity: 1 } }
      @keyframes casaIdle {
        0%, 100% { transform: scale(1) translateY(0); }
        25% { transform: scale(1.018) translateY(-1.2px); }
        50% { transform: scale(1.025) translateY(-2px); }
        75% { transform: scale(1.018) translateY(-1.2px); }
      }
      @keyframes casaPop {
        0% { transform: scale(1); }
        35% { transform: scale(0.92); }
        70% { transform: scale(1.10); }
        100% { transform: scale(1); }
      }
      @keyframes casaWave {
        0%, 100% { transform: rotate(0deg); }
        15% { transform: rotate(-12deg); }
        30% { transform: rotate(8deg); }
        45% { transform: rotate(-6deg); }
        60% { transform: rotate(2deg); }
      }
      @keyframes casaSparklePulse {
        0%, 100% { opacity: 1; transform: scale(1); }
        50% { opacity: 0.6; transform: scale(1.2); }
      }
      @keyframes casaSheetIn {
        from { transform: translateX(100%); opacity: 0; }
        to { transform: translateX(0); opacity: 1; }
      }
      /* Responsive helper classes — collapse 2-col layouts on mobile and tablet */
      .grid-2col-1col { display: grid; grid-template-columns: minmax(0, 2fr) minmax(0, 1fr); gap: 16px; }
      .grid-1col-1col { display: grid; grid-template-columns: 1fr 1fr; gap: 16px; }
      .grid-2col-fields { display: grid; grid-template-columns: 1fr 1fr; gap: 14px 24px; }
      .topbar-pad { padding: 16px 32px; }
      .page-pad { padding: 32px; }
      .topbar-search { padding: 7px 14px 7px 12px; }
      .user-chip-text { display: block; }
      @media (max-width: 900px) {
        .grid-2col-1col { grid-template-columns: 1fr; gap: 14px; }
      }
      @media (max-width: 720px) {
        .topbar-search-label, .topbar-search-kbd { display: none; }
        .topbar-search { padding: 8px; border-radius: 50%; width: 36px; height: 36px; justify-content: center; }
        .user-chip-text { display: none; }
        .user-chip { padding: 0 !important; border: none !important; background: transparent !important; }
      }
      @media (max-width: 600px) {
        .grid-1col-1col { grid-template-columns: 1fr; gap: 12px; }
        .grid-2col-fields { grid-template-columns: 1fr; gap: 12px 0; }
        .topbar-pad { padding: 12px 16px; }
        .page-pad { padding: 16px; }
        .filter-bar-right { width: 100%; margin-left: 0 !important; }
        .filter-bar-right input { width: 100%; }
        .ai-rec { padding: 16px 14px; }
        .ai-rec-meta { flex-direction: column; align-items: flex-start !important; gap: 8px !important; }
        .ai-rec-actions button { flex: 1; }
        .sell-step2-grid { grid-template-columns: 1fr !important; }
        .product-modal-stats { grid-template-columns: repeat(2, 1fr) !important; }
        .product-modal-claims { grid-template-columns: repeat(2, 1fr) !important; }
      }
      /* Login: stack on tablet/phone, hide decorative photo pane */
      @media (max-width: 900px) {
        .login-grid { grid-template-columns: 1fr !important; }
        .login-hero { display: none !important; }
        .login-form-pane { max-width: 100% !important; padding: 32px 24px !important; }
      }
      .casa-dot-1 { animation: casaDot 1.4s infinite ease-in-out; animation-delay: 0s }
      .casa-dot-2 { animation: casaDot 1.4s infinite ease-in-out; animation-delay: 0.2s }
      .casa-dot-3 { animation: casaDot 1.4s infinite ease-in-out; animation-delay: 0.4s }
      .casa-idle { animation: casaIdle 4s ease-in-out infinite; }
      .casa-pop { animation: casaPop 0.4s ease-out; }
      .casa-wave { animation: casaWave 1.4s ease-in-out; transform-origin: 50% 100%; }
      .casa-sparkle { animation: casaSparklePulse 2s ease-in-out infinite; transform-origin: 78px 22px; }
      .casa-sparkle-2 { animation: casaSparklePulse 2s ease-in-out infinite; animation-delay: 0.4s; transform-origin: 14px 26px; }
      .casa-sparkle-3 { animation: casaSparklePulse 2s ease-in-out infinite; animation-delay: 0.8s; transform-origin: 22px 14px; }
      .stagger-fadeIn { animation: slideUp 0.5s ease-out backwards }
      .skeleton { background: linear-gradient(90deg, #F2EEEA 0%, #FAF7F4 50%, #F2EEEA 100%); background-size: 800px 100%; animation: shimmer 1.5s infinite linear }
      *::-webkit-scrollbar { width: 10px; height: 10px }
      *::-webkit-scrollbar-track { background: transparent }
      *::-webkit-scrollbar-thumb { background: #E5E5E5; border-radius: 999px; border: 2px solid #F7F3EF }
      *::-webkit-scrollbar-thumb:hover { background: #828282 }
    `;
    document.head.appendChild(style);
  }, []);
};

// ═══════════════════════════════════════════════════════════════════════════════════
// IDENTITY — vector-traced from official Casava brand assets (April 2026 system v3)
// ═══════════════════════════════════════════════════════════════════════════════════
const WORDMARK_VIEWBOX = "0 0 2400.000000 535.000000";
const WORDMARK_TRANSFORM = "translate(0.000000,535.000000) scale(0.100000,-0.100000)";
const WORDMARK_PATHS = `<path d="M2815 5340 c-16 -4 -57 -13 -90 -20 -137 -29 -338 -81 -380 -99 -11 -5 -40 -17 -65 -26 -68 -27 -110 -45 -163 -71 -27 -13 -53 -24 -59 -24 -5 0 -22 -8 -36 -18 -15 -11 -56 -34 -92 -53 -128 -67 -327 -198 -445 -293 -186 -151 -402 -359 -515 -496 -25 -30 -59 -70 -76 -90 -78 -89 -255 -347 -371 -540 -99 -165 -263 -511 -328 -692 -81 -225 -121 -380 -175 -668 -19 -105 -21 -144 -18 -435 3 -322 5 -346 47 -525 16 -66 61 -212 75 -243 9 -18 25 -57 36 -87 29 -74 105 -213 152 -277 21 -29 38 -55 38 -59 0 -10 106 -130 177 -200 104 -102 245 -201 374 -262 124 -59 146 -67 239 -96 180 -55 215 -59 545 -63 189 -3 324 0 345 6 19 5 71 15 115 21 76 11 101 17 235 53 54 15 176 58 215 76 240 112 349 178 505 305 98 80 237 218 331 331 71 85 231 301 274 372 142 231 255 441 335 623 18 41 41 94 51 117 71 160 154 444 183 623 8 47 17 96 20 110 13 55 19 54 -390 58 -207 2 -387 1 -400 -2 -12 -3 -25 -16 -29 -29 -3 -12 -10 -31 -15 -42 -5 -11 -16 -42 -26 -70 -46 -129 -163 -312 -267 -417 -57 -57 -207 -178 -221 -178 -4 0 -23 -10 -44 -23 -120 -74 -344 -127 -542 -127 -236 0 -432 81 -592 247 -191 197 -299 566 -268 916 17 193 37 282 100 437 192 474 525 816 911 936 223 70 480 74 651 11 21 -7 41 -16 44 -20 4 -4 14 -7 21 -7 8 0 38 -18 67 -39 197 -146 295 -383 301 -733 2 -80 6 -151 9 -157 10 -16 809 -14 829 2 41 34 62 199 62 502 0 234 -8 315 -44 470 -15 66 -51 185 -61 203 -5 9 -25 51 -44 93 -50 107 -143 244 -222 329 -127 134 -378 274 -554 309 -25 5 -71 16 -103 25 -47 13 -111 16 -340 15 -155 0 -295 -4 -312 -9z"/> <path d="M10410 4424 c-277 -25 -534 -117 -702 -251 -153 -122 -273 -325 -309 -523 -9 -49 -13 -141 -12 -265 1 -182 3 -196 36 -326 53 -205 136 -395 289 -660 69 -120 125 -258 134 -330 13 -109 -38 -209 -125 -245 -55 -23 -183 -15 -243 15 -160 82 -240 284 -255 651 -3 80 -10 151 -16 158 -7 9 -63 12 -218 12 -207 0 -259 -7 -259 -34 0 -8 -6 -27 -14 -43 -8 -15 -19 -39 -24 -53 -40 -101 -52 -134 -72 -200 -108 -357 -154 -446 -262 -501 -72 -37 -163 -18 -208 43 -23 31 -24 41 -23 143 1 96 5 125 36 225 42 134 81 249 101 294 8 17 51 126 96 241 45 116 86 219 91 230 4 11 38 97 74 190 77 198 114 292 126 320 5 11 18 45 29 75 20 53 25 66 48 120 6 14 22 54 36 90 13 36 56 147 95 248 39 101 71 189 71 196 0 8 -18 16 -47 19 -66 9 -1171 9 -1222 1 -47 -8 -49 -14 -21 -80 11 -25 28 -89 37 -140 15 -86 15 -101 0 -192 -17 -107 -60 -205 -72 -167 -4 11 -11 29 -15 40 -5 11 -13 36 -20 55 -28 90 -121 212 -225 297 -52 42 -182 113 -207 113 -8 0 -18 4 -23 8 -12 11 -104 36 -210 58 -117 25 -450 25 -560 1 -132 -29 -167 -38 -229 -59 -277 -91 -537 -259 -787 -508 -100 -100 -257 -285 -307 -362 -9 -14 -37 -54 -60 -89 -167 -237 -343 -585 -436 -863 -187 -552 -192 -1028 -17 -1419 70 -155 165 -273 332 -410 39 -33 142 -86 214 -112 28 -9 59 -21 70 -26 83 -34 384 -43 540 -16 459 82 789 328 987 735 23 48 43 89 45 91 7 9 15 -10 28 -66 31 -142 49 -193 108 -308 59 -117 110 -186 192 -263 156 -145 322 -206 565 -206 172 0 317 31 458 95 35 16 65 29 68 29 15 0 193 137 258 199 73 70 216 253 276 350 13 23 29 41 35 41 5 0 19 -19 30 -42 63 -133 189 -304 265 -361 35 -25 59 -44 108 -83 35 -27 157 -96 202 -113 19 -8 49 -19 65 -26 206 -82 481 -117 762 -97 162 12 188 17 333 59 421 122 710 459 770 898 15 106 7 310 -16 424 -9 42 -20 94 -24 116 -12 60 -65 205 -101 277 -79 158 -178 286 -381 494 -76 78 -151 156 -167 175 -16 19 -41 48 -56 64 -41 47 -100 153 -130 235 -24 65 -28 90 -28 185 0 97 3 117 27 175 42 100 103 154 210 184 56 17 63 17 133 0 96 -22 154 -71 196 -164 26 -57 29 -76 29 -156 0 -61 -6 -111 -20 -155 -29 -93 -60 -168 -83 -200 -42 -60 -51 -58 309 -58 180 -1 333 3 340 7 25 16 115 172 142 247 67 179 87 305 70 438 -6 51 -15 101 -20 112 -5 11 -11 35 -14 54 -9 51 -71 176 -124 251 -135 190 -335 313 -607 371 -88 20 -383 33 -495 23z m-3377 -983 c147 -67 261 -227 278 -389 9 -93 2 -230 -16 -292 -7 -25 -21 -72 -31 -105 -22 -77 -139 -305 -188 -370 -20 -27 -49 -66 -64 -85 -34 -45 -132 -140 -202 -194 -234 -182 -535 -241 -780 -152 -94 34 -218 152 -258 246 -67 158 -69 346 -6 538 19 59 85 204 101 223 7 8 13 19 13 23 0 18 138 198 206 269 62 64 211 187 228 187 2 0 33 16 68 36 77 44 117 57 293 98 11 3 81 4 155 2 127 -3 139 -5 203 -35z"/> <path d="M13570 4271 c-108 -9 -279 -43 -360 -73 -30 -11 -69 -24 -85 -29 -31 -9 -179 -82 -270 -134 -187 -105 -412 -302 -598 -524 -203 -240 -420 -596 -547 -897 -24 -54 -46 -103 -50 -109 -4 -5 -10 -21 -13 -35 -3 -14 -25 -83 -50 -155 -57 -165 -79 -250 -113 -445 -23 -131 -23 -514 0 -615 24 -105 55 -210 76 -260 85 -195 188 -328 345 -443 109 -81 232 -132 385 -162 87 -17 352 -15 465 4 111 18 246 53 259 66 6 6 17 10 26 10 22 0 178 76 217 106 17 13 34 24 38 24 3 0 11 4 18 10 188 152 277 248 360 390 38 64 107 205 107 217 0 7 4 13 9 13 5 0 14 -30 21 -67 39 -227 155 -430 331 -580 50 -43 96 -80 103 -82 6 -2 44 -19 83 -38 40 -18 92 -38 115 -43 262 -65 436 -63 623 7 247 93 477 315 607 584 31 63 61 131 68 152 l12 38 19 -38 c10 -20 19 -42 19 -47 0 -5 20 -50 44 -100 75 -154 190 -294 341 -414 99 -78 207 -137 309 -168 28 -9 60 -19 71 -24 70 -29 129 -35 320 -34 192 1 230 5 415 43 25 6 95 26 155 46 303 100 592 328 835 660 24 33 47 62 52 63 4 2 8 7 8 11 0 4 42 70 93 148 50 78 106 165 122 195 144 256 249 454 406 763 56 110 107 211 114 225 8 14 26 52 42 85 16 33 59 119 95 190 117 233 408 823 655 1332 24 51 43 101 41 110 -3 17 -34 18 -508 18 l-505 0 -18 -35 c-9 -19 -38 -82 -63 -140 -25 -58 -52 -118 -59 -135 -7 -16 -25 -56 -39 -87 -14 -31 -26 -59 -26 -62 0 -4 -20 -48 -44 -99 -24 -50 -60 -130 -81 -177 -20 -47 -52 -116 -70 -155 -18 -38 -52 -113 -76 -165 -23 -52 -64 -140 -90 -195 -26 -55 -59 -127 -74 -160 -46 -103 -170 -346 -224 -439 -28 -49 -51 -90 -51 -92 0 -15 -157 -228 -215 -291 -62 -67 -191 -173 -211 -173 -3 0 -26 -10 -52 -22 -39 -19 -65 -22 -152 -23 -89 0 -113 4 -157 24 -94 42 -143 120 -160 252 -5 45 7 166 29 279 23 119 113 396 178 548 6 12 14 32 19 45 5 12 19 44 30 71 12 27 21 52 21 57 0 5 13 37 28 71 16 35 43 101 61 148 18 47 44 109 57 138 13 29 24 56 24 60 0 4 11 34 24 67 13 33 36 92 51 130 15 39 33 84 40 100 7 17 40 104 74 195 34 91 69 180 77 198 18 42 18 58 -2 66 -23 9 -1267 7 -1279 -2 -13 -9 -45 -79 -45 -97 0 -9 -4 -23 -9 -33 -16 -29 -36 -80 -101 -252 -34 -91 -85 -223 -112 -295 -28 -71 -64 -166 -81 -210 -16 -44 -33 -89 -38 -100 -33 -81 -118 -305 -124 -325 -9 -32 -113 -289 -127 -314 -11 -20 -48 -117 -74 -196 -9 -25 -22 -59 -29 -76 -8 -17 -21 -55 -29 -85 -8 -30 -24 -83 -35 -119 -12 -36 -21 -77 -21 -91 0 -41 -48 -170 -73 -197 -54 -58 -154 -78 -238 -49 -49 17 -69 35 -90 82 -38 83 -10 268 71 470 4 11 16 40 25 65 10 25 23 59 30 75 12 27 39 97 111 283 13 34 35 90 48 125 61 154 76 196 76 206 0 6 4 19 10 29 13 24 71 170 166 422 26 69 57 152 70 185 12 33 38 103 57 155 19 52 50 136 70 185 19 50 38 104 42 121 5 29 3 32 -22 37 -41 8 -1159 9 -1227 1 -56 -7 -57 -8 -51 -33 4 -14 13 -39 20 -56 32 -71 39 -115 39 -230 0 -120 -4 -144 -48 -247 -18 -43 -36 -50 -36 -14 0 35 -53 160 -93 221 -41 61 -131 153 -190 194 -192 131 -514 198 -837 172z m445 -796 c61 -8 178 -66 234 -118 48 -43 112 -144 128 -202 40 -139 42 -223 9 -380 -83 -392 -370 -759 -697 -893 -334 -137 -641 -74 -794 163 -81 125 -107 323 -65 491 38 153 112 324 166 385 8 8 14 19 14 23 0 5 23 38 52 75 154 198 323 328 533 411 42 17 153 41 225 49 73 9 93 8 195 -4z"/> <path d="M21200 4270 c-99 -8 -247 -38 -315 -63 -22 -8 -60 -20 -85 -28 -25 -7 -56 -18 -70 -25 -14 -7 -52 -25 -85 -40 -186 -85 -448 -280 -616 -459 -20 -22 -49 -53 -64 -69 -136 -147 -347 -448 -453 -648 -80 -149 -172 -340 -172 -354 0 -2 -9 -23 -19 -47 -36 -78 -89 -234 -130 -382 -128 -467 -111 -912 47 -1235 92 -188 284 -378 457 -454 22 -10 48 -22 57 -27 10 -5 34 -13 55 -18 21 -5 65 -17 98 -27 80 -23 374 -23 490 -1 178 35 265 62 380 117 261 127 462 328 601 600 26 52 49 97 51 99 7 9 14 -10 44 -110 17 -57 37 -115 44 -129 7 -14 19 -40 26 -59 13 -36 124 -198 168 -245 31 -34 140 -121 199 -159 72 -47 209 -96 327 -118 129 -23 453 -11 530 20 11 5 47 16 80 26 131 37 298 131 427 239 67 55 205 209 243 270 10 17 22 33 25 36 12 12 64 97 102 168 22 42 43 82 47 87 8 10 73 168 110 265 98 262 185 666 198 931 5 94 4 96 -19 102 -37 10 -504 8 -531 -2 -15 -6 -27 -21 -31 -38 -40 -180 -155 -452 -237 -566 -71 -98 -177 -139 -264 -103 -125 53 -95 273 93 696 30 69 77 184 123 305 22 61 45 119 50 130 5 11 20 49 33 85 13 36 35 95 50 133 29 75 67 177 96 252 10 28 30 82 45 120 15 39 39 104 55 145 15 41 51 136 80 210 28 74 69 184 91 243 l39 108 -26 10 c-35 13 -1251 11 -1275 -2 -17 -9 -17 -11 1 -55 51 -118 65 -296 32 -409 -17 -60 -40 -105 -54 -105 -4 0 -10 10 -14 23 -30 98 -119 241 -196 313 -57 54 -142 114 -161 114 -8 0 -20 7 -27 15 -7 8 -31 20 -54 27 -22 6 -45 15 -51 19 -15 11 -79 27 -185 48 -112 23 -326 32 -460 21z m564 -827 c64 -33 148 -94 176 -130 47 -60 91 -170 105 -267 12 -82 13 -110 1 -193 -50 -352 -301 -737 -593 -910 -155 -91 -320 -137 -473 -131 -167 7 -271 49 -370 148 -108 108 -157 239 -153 414 1 91 20 214 35 231 4 5 8 18 8 29 0 11 27 74 60 140 101 206 237 376 396 497 49 38 93 69 97 69 4 0 28 13 54 29 179 109 514 146 657 74z"/>`;
const CMARK_VIEWBOX = "0 0 1339.000000 1600.000000";
const CMARK_TRANSFORM = "translate(0.000000,1600.000000) scale(0.100000,-0.100000)";
const CMARK_PATHS = `<path d="M8930 15909 c-80 -4 -208 -15 -285 -24 -77 -9 -171 -21 -210 -25 -110 -13 -214 -31 -415 -70 -211 -41 -604 -139 -642 -160 -10 -6 -24 -10 -33 -10 -19 0 -459 -148 -555 -187 -14 -6 -52 -20 -85 -32 -33 -12 -82 -33 -110 -45 -27 -13 -81 -37 -120 -54 -184 -79 -256 -114 -470 -222 -210 -107 -308 -160 -390 -211 -16 -10 -37 -22 -45 -26 -14 -6 -192 -113 -264 -158 -164 -102 -628 -421 -646 -444 -4 -6 -39 -32 -125 -93 -5 -4 -28 -22 -50 -40 -51 -42 -124 -101 -200 -162 -33 -27 -78 -65 -100 -85 -22 -20 -53 -47 -70 -60 -16 -12 -46 -37 -66 -55 -19 -19 -75 -68 -123 -110 -238 -207 -625 -601 -896 -911 -52 -59 -103 -118 -114 -129 -38 -42 -232 -280 -265 -325 -19 -25 -40 -50 -46 -56 -6 -5 -79 -100 -161 -210 -82 -110 -152 -201 -156 -203 -5 -2 -8 -8 -8 -13 0 -5 -44 -72 -97 -149 -88 -125 -145 -209 -258 -377 -19 -29 -35 -55 -35 -58 0 -4 -11 -21 -24 -38 -14 -18 -40 -59 -59 -92 -19 -33 -79 -134 -134 -225 -55 -91 -116 -194 -135 -230 -20 -36 -59 -108 -89 -160 -29 -52 -65 -117 -80 -145 -36 -66 -301 -596 -329 -660 -13 -27 -38 -84 -57 -125 -20 -41 -43 -96 -53 -121 -22 -55 -58 -137 -71 -162 -5 -9 -17 -39 -25 -65 -9 -26 -27 -69 -40 -96 -13 -28 -24 -58 -24 -68 0 -10 -4 -18 -10 -18 -5 0 -10 -7 -10 -15 0 -8 -7 -31 -16 -52 -23 -51 -28 -66 -59 -153 -14 -41 -30 -83 -36 -92 -5 -10 -9 -23 -9 -29 0 -6 -6 -25 -14 -42 -7 -18 -40 -115 -74 -217 -56 -173 -71 -219 -136 -441 -14 -48 -26 -96 -26 -107 0 -12 -4 -23 -9 -26 -4 -3 -11 -22 -14 -43 -4 -21 -14 -63 -23 -93 -9 -30 -20 -73 -24 -95 -5 -22 -16 -69 -24 -105 -9 -36 -25 -110 -36 -165 -11 -55 -24 -113 -29 -130 -5 -16 -14 -64 -21 -105 -6 -41 -18 -104 -25 -140 -12 -60 -28 -163 -39 -250 -2 -19 -8 -62 -14 -95 -5 -33 -15 -105 -21 -160 -5 -55 -15 -143 -20 -195 -6 -52 -18 -210 -27 -350 -19 -294 -14 -709 11 -975 9 -88 20 -205 24 -260 10 -104 25 -224 41 -310 5 -27 14 -81 20 -120 6 -38 17 -97 25 -130 8 -33 21 -94 29 -135 14 -73 77 -325 96 -385 14 -44 77 -242 96 -300 8 -27 19 -59 24 -70 4 -11 25 -63 46 -115 20 -52 48 -119 61 -148 12 -29 23 -55 23 -57 0 -6 95 -210 122 -260 11 -22 41 -80 66 -129 25 -49 80 -144 122 -210 42 -67 79 -126 81 -131 16 -38 219 -317 324 -445 469 -573 1110 -1044 1780 -1306 50 -20 99 -40 110 -44 41 -17 162 -59 215 -75 30 -9 66 -21 80 -26 33 -12 164 -46 265 -69 44 -10 111 -25 148 -34 37 -9 98 -21 135 -26 37 -5 110 -17 162 -25 104 -18 219 -30 410 -45 156 -11 784 -11 945 1 66 5 179 13 250 18 72 5 182 17 245 26 63 9 147 20 185 25 39 5 108 16 155 25 47 9 110 20 140 25 71 10 295 61 460 104 154 40 192 51 255 76 11 4 47 15 80 24 33 10 69 21 80 26 11 5 65 25 120 45 261 95 583 242 784 357 46 26 86 48 88 48 2 0 42 24 89 52 47 29 93 58 102 63 41 24 98 60 107 68 6 4 69 47 140 95 72 48 171 119 219 157 92 73 333 271 352 289 6 6 49 45 95 86 123 109 375 364 553 560 68 74 296 347 326 390 13 19 30 41 38 49 63 64 354 467 513 711 142 216 235 362 259 406 12 21 39 66 61 99 21 33 43 70 49 83 6 13 31 58 56 100 26 42 58 96 72 121 14 25 34 62 45 81 121 210 373 710 486 960 87 195 124 281 156 362 20 51 48 117 61 146 13 29 24 56 24 59 0 8 67 187 100 268 7 17 21 57 31 90 11 33 23 69 28 80 15 32 100 300 141 440 43 148 41 143 69 250 12 44 25 96 31 115 12 43 53 223 69 305 7 33 19 89 26 125 8 36 19 99 25 140 6 41 16 102 22 135 18 102 26 249 16 263 -8 9 -258 12 -1188 12 l-1178 0 -15 -47 c-8 -27 -20 -68 -27 -93 -13 -49 -37 -115 -51 -142 -5 -10 -9 -23 -9 -30 -1 -7 -9 -31 -20 -53 -11 -22 -19 -44 -20 -49 -1 -23 -179 -397 -246 -516 -86 -152 -249 -387 -370 -530 -221 -263 -477 -486 -771 -671 -108 -68 -376 -209 -397 -209 -3 0 -25 -8 -49 -19 -23 -10 -58 -24 -77 -31 -19 -7 -48 -18 -65 -25 -50 -20 -186 -62 -245 -75 -30 -7 -72 -19 -93 -26 -20 -8 -51 -14 -67 -14 -16 0 -39 -4 -52 -9 -104 -40 -647 -90 -842 -77 -225 15 -431 39 -476 55 -14 5 -52 15 -85 22 -122 25 -273 80 -435 156 -465 219 -854 619 -1112 1143 -66 134 -108 229 -108 244 0 7 -3 16 -7 20 -8 7 -26 57 -50 131 -8 28 -19 59 -23 70 -5 11 -15 45 -24 75 -52 179 -94 368 -118 530 -39 271 -47 398 -44 715 2 176 9 365 15 420 16 133 60 422 71 460 4 17 21 84 36 150 15 66 38 154 51 195 12 41 28 93 34 115 12 44 25 78 84 230 15 39 33 84 40 100 8 17 22 50 33 75 60 138 165 354 231 475 15 28 44 81 64 118 102 188 313 505 446 671 31 39 63 80 71 92 25 36 228 262 310 344 247 248 556 486 840 647 101 57 353 183 366 183 3 0 21 7 40 15 19 8 49 21 67 29 43 19 254 88 347 114 316 89 572 121 945 121 216 0 297 -5 455 -29 92 -13 310 -67 365 -90 17 -7 39 -16 50 -19 51 -18 296 -139 305 -151 3 -4 35 -27 71 -51 91 -60 160 -118 251 -211 78 -80 199 -228 225 -276 8 -15 24 -43 35 -62 65 -108 144 -267 163 -330 4 -14 11 -29 15 -35 4 -5 15 -37 25 -70 10 -33 21 -64 26 -70 5 -5 9 -19 9 -32 0 -12 4 -32 9 -45 12 -30 37 -128 51 -198 7 -30 18 -82 25 -115 7 -33 18 -105 25 -160 6 -55 15 -136 21 -180 17 -140 23 -490 9 -620 -6 -66 -14 -143 -17 -172 l-5 -52 33 -6 c60 -10 2447 -1 2458 10 10 10 27 68 62 210 31 127 36 154 53 265 9 58 20 132 26 165 11 75 26 232 41 445 31 419 -4 1037 -82 1455 -6 33 -15 83 -20 110 -12 68 -48 225 -71 310 -11 39 -28 102 -38 140 -26 93 -133 398 -167 475 -86 195 -173 367 -259 509 -285 474 -731 915 -1171 1157 -29 16 -74 41 -100 56 -27 15 -58 32 -71 38 -13 5 -51 23 -85 39 -140 65 -178 81 -191 81 -7 0 -16 3 -20 7 -11 11 -262 95 -371 124 -22 6 -58 17 -80 24 -22 7 -67 18 -100 25 -33 6 -80 16 -105 22 -364 81 -934 115 -1470 87z"/>`;

function Wordmark({ color = T.brand, height = 22 }) {
  const width = height * (2400 / 535);
  const inner = `<g transform="${WORDMARK_TRANSFORM}" fill="currentColor">${WORDMARK_PATHS}</g>`;
  return (
    <span style={{ display: "inline-flex", alignItems: "center", height, lineHeight: 0, color }} role="img" aria-label="Casava">
      <svg viewBox={WORDMARK_VIEWBOX} height={height} width={width} preserveAspectRatio="xMidYMid meet" style={{ display: "block" }} dangerouslySetInnerHTML={{ __html: inner }}/>
    </span>
  );
}

function CMark({ color = T.brand, size = 32 }) {
  const width = size * (1339 / 1600);
  const inner = `<g transform="${CMARK_TRANSFORM}" fill="currentColor">${CMARK_PATHS}</g>`;
  return (
    <span style={{ display: "inline-flex", alignItems: "center", justifyContent: "center", height: size, lineHeight: 0, color }} role="img" aria-label="Casava">
      <svg viewBox={CMARK_VIEWBOX} height={size} width={width} preserveAspectRatio="xMidYMid meet" style={{ display: "block" }} dangerouslySetInnerHTML={{ __html: inner }}/>
    </span>
  );
}

function CMarkBadge({ surface = "bloom", size = 56 }) {
  const surfaces = {
    bloom: { bg: T.brand, mark: "#FFFFFF" },
    paper: { bg: T.bg, mark: T.brand },
    brown: { bg: T.brown, mark: T.brand },
  }[surface];
  return (
    <div style={{ width: size, height: size, borderRadius: "50%", background: surfaces.bg, display: "inline-flex", alignItems: "center", justifyContent: "center", flexShrink: 0 }}>
      <CMark size={size * 0.62} color={surfaces.mark}/>
    </div>
  );
}

// PartnerLogo — monogram tile or uploaded logo image for the embedded partner
function PartnerLogo({ name = "Partner", color = "#1A4D2E", size = 20, logoUrl }) {
  if (logoUrl) {
    return (
      <img src={logoUrl} alt={name} style={{
        width: size, height: size, borderRadius: 4, objectFit: "contain",
        background: "#fff", flexShrink: 0,
      }}/>
    );
  }
  const initial = name[0].toUpperCase();
  return (
    <div style={{
      width: size, height: size, borderRadius: 4, background: color, color: "#FFFFFF",
      display: "inline-flex", alignItems: "center", justifyContent: "center",
      fontSize: size * 0.55, fontWeight: 700, fontFamily: FONT, flexShrink: 0,
    }}>{initial}</div>
  );
}

// Active embedded partner. Default state — App promotes this to mutable state for logo upload.
const DEFAULT_PARTNER = {
  name: "Moniepoint",
  legalName: "Moniepoint Microfinance Bank Ltd.",
  color: "#0066B2",
  logoUrl: null,
  rcNumber: "RC-1043029",
  tin: "20098721-0001",
  partnerType: "Microfinance Bank",
  industry: "Digital banking & SME lending",
  established: "2015",
  primaryContact: "Tobi Aladejana",
  primaryEmail: "tobi.a@moniepoint.com",
  primaryPhone: "+234 700 666 4357",
  registeredAddress: "21 Mojidi Street, Off Toyin Street, Ikeja, Lagos",
  productsEnabled: ["Credit Life", "Funds Protection", "Device Insurance"],
  monthlyVolumeCap: 5000,
  commissionRate: 12,
  contractStart: "01 Jul 2024",
  contractRenewal: "30 Jun 2027",
};
const ACTIVE_PARTNER = DEFAULT_PARTNER;

// ═══════════════════════════════════════════════════════════════════════════════════
// CASA — animated AI character. Cursor tracking, auto-blink, hover wink, breathing.
// ═══════════════════════════════════════════════════════════════════════════════════
function useCasaAnimation({ trackCursor = false, autoBlink = true } = {}) {
  const [eyeOffset, setEyeOffset] = useState({ dx: 0, dy: 0 });
  const [blinking, setBlinking] = useState(false);
  const ref = useRef(null);

  useEffect(() => {
    if (!trackCursor) return;
    let raf = null;
    const handler = (e) => {
      if (raf) cancelAnimationFrame(raf);
      raf = requestAnimationFrame(() => {
        const el = ref.current;
        if (!el) return;
        const rect = el.getBoundingClientRect();
        const cx = rect.left + rect.width / 2;
        const cy = rect.top + rect.height / 2;
        const dx = e.clientX - cx;
        const dy = e.clientY - cy;
        const dist = Math.sqrt(dx * dx + dy * dy);
        if (dist < 1) { setEyeOffset({ dx: 0, dy: 0 }); return; }
        const maxDist = 400;
        const maxOffset = 2.4;
        const factor = Math.min(dist, maxDist) / maxDist;
        setEyeOffset({
          dx: (dx / dist) * maxOffset * factor,
          dy: (dy / dist) * maxOffset * factor * 0.7, // dampen vertical
        });
      });
    };
    window.addEventListener("mousemove", handler, { passive: true });
    return () => {
      window.removeEventListener("mousemove", handler);
      if (raf) cancelAnimationFrame(raf);
    };
  }, [trackCursor]);

  useEffect(() => {
    if (!autoBlink) return;
    let timer;
    const scheduleBlink = () => {
      timer = setTimeout(() => {
        setBlinking(true);
        setTimeout(() => {
          setBlinking(false);
          // Sometimes do a double-blink
          if (Math.random() < 0.18) {
            setTimeout(() => {
              setBlinking(true);
              setTimeout(() => { setBlinking(false); scheduleBlink(); }, 130);
            }, 160);
          } else {
            scheduleBlink();
          }
        }, 130);
      }, 2200 + Math.random() * 4500);
    };
    scheduleBlink();
    return () => clearTimeout(timer);
  }, [autoBlink]);

  return { eyeOffset, blinking, ref };
}

function CasaAvatar({ size = 40, state = "idle", color = T.brand, eyeOffset = { dx: 0, dy: 0 }, blinking = false, innerRef }) {
  const eye = "#FFFFFF", pup = T.brownDeep;
  const ox = blinking ? 0 : eyeOffset.dx;
  const oy = blinking ? 0 : eyeOffset.dy;

  // States with open eyes that blink and track cursor
  const hasOpenEyes = state === "idle" || state === "talking" || state === "concerned" || state === "winking";

  let geo;
  if (blinking && hasOpenEyes) {
    // Closed-eye geometry: gentle downward arcs (eyelashes)
    const mouth = state === "talking" ? <ellipse cx="50" cy="65" rx="6" ry="4" fill={pup}/>
      : state === "winking" ? <path d="M 36 60 Q 50 74 64 62" stroke={pup} strokeWidth="3" strokeLinecap="round" fill="none"/>
      : <path d="M 38 62 Q 50 70 62 62" stroke={pup} strokeWidth="3" strokeLinecap="round" fill="none"/>;
    geo = (
      <>
        <path d="M 32 44 Q 38 47 44 44" stroke={pup} strokeWidth="2.6" strokeLinecap="round" fill="none"/>
        <path d="M 56 44 Q 62 47 68 44" stroke={pup} strokeWidth="2.6" strokeLinecap="round" fill="none"/>
        {mouth}
      </>
    );
  } else {
    geo = {
      idle: <>
        <ellipse cx="38" cy="44" rx="6" ry="5" fill={eye}/><ellipse cx="62" cy="44" rx="6" ry="5" fill={eye}/>
        <circle cx={38 + ox} cy={45 + oy} r="1.8" fill={pup}/>
        <circle cx={62 + ox} cy={45 + oy} r="1.8" fill={pup}/>
        <path d="M 38 62 Q 50 70 62 62" stroke={pup} strokeWidth="3" strokeLinecap="round" fill="none"/>
      </>,
      happy: <>
        <path d="M 32 42 Q 38 36 44 42" stroke={pup} strokeWidth="3" strokeLinecap="round" fill="none"/>
        <path d="M 56 42 Q 62 36 68 42" stroke={pup} strokeWidth="3" strokeLinecap="round" fill="none"/>
        <path d="M 34 60 Q 50 76 66 60" stroke={pup} strokeWidth="3" strokeLinecap="round" fill="none"/>
      </>,
      thinking: <>
        <line x1="32" y1="44" x2="44" y2="44" stroke={pup} strokeWidth="3" strokeLinecap="round"/>
        <line x1="56" y1="44" x2="68" y2="44" stroke={pup} strokeWidth="3" strokeLinecap="round"/>
        <line x1="40" y1="64" x2="60" y2="64" stroke={pup} strokeWidth="3" strokeLinecap="round"/>
      </>,
      talking: <>
        <ellipse cx="38" cy="44" rx="6" ry="5" fill={eye}/><ellipse cx="62" cy="44" rx="6" ry="5" fill={eye}/>
        <circle cx={38 + ox} cy={45 + oy} r="1.8" fill={pup}/>
        <circle cx={62 + ox} cy={45 + oy} r="1.8" fill={pup}/>
        <ellipse cx="50" cy="65" rx="6" ry="4" fill={pup}/>
      </>,
      winking: <>
        <ellipse cx="38" cy="44" rx="6" ry="5" fill={eye}/>
        <circle cx={38 + ox} cy={45 + oy} r="1.8" fill={pup}/>
        <path d="M 56 44 Q 62 38 68 44" stroke={pup} strokeWidth="3" strokeLinecap="round" fill="none"/>
        <path d="M 36 60 Q 50 74 64 62" stroke={pup} strokeWidth="3" strokeLinecap="round" fill="none"/>
      </>,
      celebrating: <>
        <path d="M 32 42 Q 38 36 44 42" stroke={pup} strokeWidth="3" strokeLinecap="round" fill="none"/>
        <path d="M 56 42 Q 62 36 68 42" stroke={pup} strokeWidth="3" strokeLinecap="round" fill="none"/>
        <path d="M 32 58 Q 50 80 68 58" stroke={pup} strokeWidth="3.2" strokeLinecap="round" fill="none"/>
      </>,
    }[state] || null;
  }

  return (
    <span ref={innerRef} style={{ display: "inline-block", lineHeight: 0 }}>
      <svg viewBox="0 0 100 100" width={size} height={size}>
        {state === "thinking" && (
          <g>
            <circle cx="74" cy="22" r="3" fill={color} className="casa-dot-1"/>
            <circle cx="84" cy="18" r="3.5" fill={color} className="casa-dot-2"/>
            <circle cx="92" cy="13" r="4" fill={color} className="casa-dot-3"/>
          </g>
        )}
        {(state === "happy" || state === "celebrating" || state === "winking") && (
          <path d="M 78 22 L 80 17 L 85 19 L 80 21 L 79 26 L 77 21 L 72 19 L 77 21 Z" fill={color} className="casa-sparkle"/>
        )}
        {state === "celebrating" && (
          <>
            <path d="M 14 26 L 16 21 L 21 23 L 16 25 L 15 30 L 13 25 L 8 23 L 13 25 Z" fill={color} className="casa-sparkle-2"/>
            <circle cx="22" cy="14" r="2" fill={color} className="casa-sparkle-3"/>
          </>
        )}
        <g transform="rotate(-4, 50, 55)">
          <ellipse cx="50" cy="55" rx="34" ry="36" fill={color}/>
          <path d="M 28 22 Q 35 14 46 18" stroke={color} strokeWidth="6" strokeLinecap="round" fill="none"/>
          {geo}
        </g>
      </svg>
    </span>
  );
}

// ═══════════════════════════════════════════════════════════════════════════════════
// PRIMITIVES
// ═══════════════════════════════════════════════════════════════════════════════════
const Eyebrow = ({ children, color = T.textSec, mb = 8 }) => (
  <div style={{ fontSize: 11, fontWeight: 700, letterSpacing: "0.12em", textTransform: "uppercase", color, marginBottom: mb }}>{children}</div>
);

const Display = ({ children, size = "lg", color = T.text, italic = false, style }) => {
  const sizes = { sm: 22, md: 28, lg: 36, xl: 48, xxl: 64 };
  return (
    <div style={{ fontFamily: FONT_DISPLAY, fontSize: sizes[size], fontWeight: 400, fontStyle: italic ? "italic" : "normal", letterSpacing: "-0.03em", lineHeight: 1.05, color, ...style }}>{children}</div>
  );
};

const Pill = ({ children, color = "gray", solid = false, size = "md" }) => {
  const colors = {
    gray: { bg: T.surfaceTint, text: T.textSec },
    green: { bg: T.successBg, text: T.success },
    amber: { bg: T.amberBg, text: T.amber },
    red: { bg: T.dangerBg, text: T.danger },
    brand: { bg: T.brandBg, text: T.brand },
    dark: { bg: T.text, text: "#fff" },
  }[color];
  const isInverted = color === "dark";
  const bg = isInverted ? colors.bg : (solid ? colors.text : colors.bg);
  const fg = isInverted ? colors.text : (solid ? "#fff" : colors.text);
  const padY = size === "sm" ? 2 : 4;
  const padX = size === "sm" ? 8 : 10;
  const fs = size === "sm" ? 10 : 11;
  return (
    <span style={{ display: "inline-flex", alignItems: "center", gap: 4, padding: `${padY}px ${padX}px`, borderRadius: 999, fontSize: fs, fontWeight: 600, letterSpacing: "-0.01em", background: bg, color: fg }}>{children}</span>
  );
};

const Btn = ({ children, variant = "secondary", size = "md", onClick, disabled, icon: Icon, iconRight: IconRight, full, style }) => {
  const sizes = { sm: { padding: "8px 14px", fontSize: 12 }, md: { padding: "11px 20px", fontSize: 14 }, lg: { padding: "15px 24px", fontSize: 15 } };
  const variants = {
    primary: { background: T.text, color: "#fff", border: "none" },
    brand: { background: T.brand, color: "#fff", border: "none" },
    secondary: { background: T.surfaceTint, color: T.text, border: "none" },
    ghost: { background: "transparent", color: T.text, border: "none" },
    outline: { background: "transparent", color: T.text, border: `1.5px solid ${T.border}` },
    danger: { background: T.dangerBg, color: T.dangerText, border: "none" },
  };
  return (
    <button onClick={onClick} disabled={disabled} style={{
      ...sizes[size], ...variants[variant], borderRadius: 999, fontWeight: 600, fontFamily: FONT,
      cursor: disabled ? "not-allowed" : "pointer", opacity: disabled ? 0.4 : 1,
      display: "inline-flex", alignItems: "center", justifyContent: "center", gap: 7,
      transition: "transform 0.15s, opacity 0.15s", width: full ? "100%" : "auto", whiteSpace: "nowrap",
      ...style,
    }}
      onMouseDown={e => !disabled && (e.currentTarget.style.transform = "scale(0.97)")}
      onMouseUp={e => e.currentTarget.style.transform = "scale(1)"}
      onMouseLeave={e => e.currentTarget.style.transform = "scale(1)"}>
      {Icon && <Icon size={size === "sm" ? 14 : 16} strokeWidth={2.3}/>}
      {children}
      {IconRight && <IconRight size={size === "sm" ? 14 : 16} strokeWidth={2.3}/>}
    </button>
  );
};

// WhatsAppButton — tap-to-WhatsApp. Lagos context: WhatsApp is the assumed channel
// for customer-facing communication (renewal nudges, claim updates, support). Three variants:
// primary (full WhatsApp green), outline (transparent + green border), subtle (tinted bg).
const WA_GREEN = "#25D366";
const WA_GREEN_DEEP = "#128C5E";
const WA_GREEN_TINT = "#E8F8EE";
function WhatsAppButton({ phoneNumber = "", message, label = "Chat on WhatsApp", variant = "primary", size = "md", full, onClick }) {
  const href = phoneNumber ? `https://wa.me/${phoneNumber.replace(/\D/g, "")}${message ? `?text=${encodeURIComponent(message)}` : ""}` : null;
  const variants = {
    primary: { bg: WA_GREEN, color: "#fff", border: "none" },
    outline: { bg: "transparent", color: WA_GREEN, border: `1.5px solid ${WA_GREEN}` },
    subtle:  { bg: WA_GREEN_TINT, color: WA_GREEN_DEEP, border: "none" },
  };
  const v = variants[variant];
  const sizes = { sm: { padding: "8px 14px", fontSize: 12 }, md: { padding: "11px 20px", fontSize: 14 }, lg: { padding: "15px 24px", fontSize: 15 } };
  const sharedStyle = {
    ...sizes[size], ...v, borderRadius: 999, fontWeight: 600, fontFamily: FONT,
    cursor: "pointer", display: "inline-flex", alignItems: "center", justifyContent: "center", gap: 7,
    width: full ? "100%" : "auto", whiteSpace: "nowrap", textDecoration: "none",
  };
  // Inline WhatsApp logo glyph — a stylised speech-bubble silhouette
  const WhatsAppGlyph = (
    <svg width={size === "sm" ? 14 : 16} height={size === "sm" ? 14 : 16} viewBox="0 0 24 24" fill="currentColor" aria-hidden="true">
      <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51l-.57-.01c-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.626.712.226 1.36.194 1.872.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 0 1-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 0 1-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 0 1 2.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0 0 12.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 0 0 5.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 0 0-3.48-8.413"/>
    </svg>
  );
  if (href) {
    return (
      <a href={href} target="_blank" rel="noopener noreferrer" onClick={onClick} style={sharedStyle}>
        {WhatsAppGlyph}{label}
      </a>
    );
  }
  return (
    <button onClick={onClick} style={sharedStyle}>
      {WhatsAppGlyph}{label}
    </button>
  );
}

// NetworkBanner — sticky offline/slow/syncing indicator. Lagos networks aren't reliable;
// surfacing this is a credibility moment, not a polish detail.
function NetworkBanner({ status = "offline", onRetry }) {
  const config = {
    offline: { icon: "📡", color: T.dangerText, bg: T.dangerBg, title: "You're offline", message: "We'll resume when you're back online. Nothing's lost." },
    slow:    { icon: "🐢", color: T.amberText,  bg: T.amberBg,  title: "Slow connection", message: "This might take a moment. Hang tight." },
    syncing: { icon: "🔄", color: T.text,       bg: T.surfaceTint, title: "Syncing changes", message: "Just catching up on what you did offline." },
  }[status];
  return (
    <div style={{
      display: "flex", alignItems: "center", gap: 12,
      padding: "10px 16px", background: config.bg,
      borderBottom: `1px solid ${T.border}`, fontSize: 13,
    }}>
      <div style={{ fontSize: 18 }}>{config.icon}</div>
      <div style={{ flex: 1, minWidth: 0 }}>
        <div style={{ fontWeight: 700, color: config.color, fontSize: 13 }}>{config.title}</div>
        {config.message && <div style={{ color: T.textSec, fontSize: 12, marginTop: 1 }}>{config.message}</div>}
      </div>
      {status === "offline" && onRetry && (
        <button onClick={onRetry} style={{
          background: "transparent", border: "none", color: config.color,
          fontSize: 12, fontWeight: 700, cursor: "pointer", fontFamily: FONT,
        }}>Retry</button>
      )}
    </div>
  );
}

// ApplicationProgress — segmented progress bar with step counter. Used at the top of every
// ApplicationStep. Goal: orient without dominating.
function ApplicationProgress({ current = 1, total = 8 }) {
  return (
    <div style={{ display: "flex", alignItems: "center", gap: 14 }}>
      <div style={{ display: "flex", gap: 4, flex: 1 }}>
        {Array.from({ length: total }).map((_, i) => (
          <div key={i} style={{
            flex: 1, height: 4, borderRadius: 2,
            background: i < current ? T.brand : T.border,
            transition: "background-color 0.3s",
          }}/>
        ))}
      </div>
      <div style={{ fontSize: 11, fontWeight: 600, color: T.textSec, letterSpacing: "0.04em", whiteSpace: "nowrap" }}>
        Step {current} of {total}
      </div>
    </div>
  );
}

// ApplicationStep — Lemonade-style one-question-per-screen application step.
// Progress + Casa avatar + casaMessage chat-bubble + question + hint + content + sticky footer.
// `bare` strips the card chrome for use inside a modal body (where the modal is the surface).
function ApplicationStep({
  stepNumber, totalSteps, question, hint, casaMessage, children,
  primaryAction, secondaryAction, casaState = "idle", showAvatar = true,
  bare = false,
}) {
  const { eyeOffset, blinking, ref } = useCasaAnimation({ trackCursor: true, autoBlink: true });
  const wrapperStyle = bare
    ? { padding: 0, background: "transparent", border: "none", borderRadius: 0 }
    : { background: T.surface, border: `1px solid ${T.border}`, borderRadius: 18, padding: 32 };
  return (
    <div style={{ ...wrapperStyle, position: "relative" }}>
      {(stepNumber != null && totalSteps != null) && (
        <div style={{ marginBottom: 20 }}>
          <ApplicationProgress current={stepNumber} total={totalSteps}/>
        </div>
      )}

      <div style={{ display: "flex", alignItems: "flex-start", gap: 16, marginBottom: 24 }}>
        {showAvatar && <CasaAvatar size={52} state={casaState} eyeOffset={eyeOffset} blinking={blinking} innerRef={ref}/>}
        <div style={{ flex: 1, paddingTop: showAvatar ? 4 : 0, minWidth: 0 }}>
          {casaMessage && (
            <div style={{
              display: "inline-block", padding: "8px 14px",
              background: T.brandBg, borderRadius: 14, borderTopLeftRadius: 4,
              fontSize: 13, color: T.brandDeep, fontWeight: 500, lineHeight: 1.5,
              marginBottom: 12, maxWidth: "100%",
            }}>{casaMessage}</div>
          )}
          <div style={{ fontFamily: FONT_DISPLAY, fontSize: 28, fontWeight: 400, letterSpacing: "-0.02em", lineHeight: 1.2, color: T.text }}>
            {question}
          </div>
          {hint && (
            <div style={{ fontSize: 14, color: T.textSec, marginTop: 8, lineHeight: 1.55 }}>{hint}</div>
          )}
        </div>
      </div>

      <div style={{ marginBottom: 24 }}>
        {children}
      </div>

      <div style={{ display: "flex", gap: 8, justifyContent: "space-between", paddingTop: 18, borderTop: `1px solid ${T.border}` }}>
        {secondaryAction || <div/>}
        {primaryAction}
      </div>
    </div>
  );
}

// ChoiceTileGrid — big tappable tiles for choice questions. Single-select by default;
// pass `multiSelect` for multi. Tile clicks call `onChange(value)`. The selected tile
// becomes the answer — no Continue tax in single-select mode.
function ChoiceTileGrid({ options = [], value, onChange, columns = 3, multiSelect = false, size = "md" }) {
  const sizes = {
    sm: { padding: 16, iconSize: 22, iconBox: 44, labelFs: 13, descFs: 11, gap: 10 },
    md: { padding: 22, iconSize: 28, iconBox: 56, labelFs: 14, descFs: 12, gap: 12 },
    lg: { padding: 28, iconSize: 36, iconBox: 72, labelFs: 16, descFs: 13, gap: 14 },
  };
  const s = sizes[size] || sizes.md;
  const isSelected = (val) => multiSelect ? Array.isArray(value) && value.includes(val) : value === val;
  const handleClick = (val) => {
    if (!onChange) return;
    if (multiSelect) {
      const arr = Array.isArray(value) ? value : [];
      onChange(arr.includes(val) ? arr.filter(v => v !== val) : [...arr, val]);
    } else {
      onChange(val);
    }
  };
  return (
    <div style={{ display: "grid", gridTemplateColumns: `repeat(${columns}, 1fr)`, gap: s.gap }}>
      {options.map(opt => {
        const selected = isSelected(opt.value);
        const Icon = opt.icon;
        return (
          <button
            key={opt.value}
            onClick={() => handleClick(opt.value)}
            aria-pressed={selected}
            style={{
              padding: s.padding,
              background: selected ? T.brandBg : T.surface,
              border: `2px solid ${selected ? T.brand : T.border}`,
              borderRadius: 14,
              cursor: "pointer", textAlign: "left", fontFamily: FONT,
              display: "flex", flexDirection: "column", alignItems: "flex-start", gap: 10,
              transition: "transform 0.12s, border-color 0.12s, background 0.12s",
              position: "relative",
            }}
            onMouseEnter={e => !selected && (e.currentTarget.style.borderColor = T.borderStrong)}
            onMouseLeave={e => !selected && (e.currentTarget.style.borderColor = T.border)}
          >
            {opt.badge && (
              <div style={{ position: "absolute", top: 10, right: 10 }}>
                <Pill color="brand" size="sm">{opt.badge}</Pill>
              </div>
            )}
            <div style={{
              width: s.iconBox, height: s.iconBox, borderRadius: 14,
              background: selected ? T.brand : T.brandBg, color: selected ? "#fff" : T.brand,
              display: "inline-flex", alignItems: "center", justifyContent: "center",
              transition: "background 0.12s, color 0.12s",
            }}>
              {Icon ? <Icon size={s.iconSize} strokeWidth={2.2}/> : opt.emoji ? <span style={{ fontSize: s.iconSize }}>{opt.emoji}</span> : null}
            </div>
            <div style={{ width: "100%" }}>
              <div style={{ fontSize: s.labelFs, fontWeight: 700, color: T.text }}>{opt.label}</div>
              {opt.description && <div style={{ fontSize: s.descFs, color: T.textSec, marginTop: 4, lineHeight: 1.5 }}>{opt.description}</div>}
            </div>
            {selected && (
              <div style={{
                position: "absolute", top: 10, right: opt.badge ? "auto" : 10,
                ...(opt.badge ? { left: 10 } : {}),
                width: 22, height: 22, borderRadius: 999, background: T.brand,
                display: "inline-flex", alignItems: "center", justifyContent: "center", color: "#fff",
              }}>
                <Check size={14} strokeWidth={3}/>
              </div>
            )}
          </button>
        );
      })}
    </div>
  );
}

// LiveQuoteRail — sticky companion that builds up a quote as the user answers an
// ApplicationStep. Different from a static end-of-flow summary: this updates in flight,
// shows pending line items as greyed-out, and surfaces the running total. Use as the right
// rail of a desktop application flow.
function LiveQuoteRail({
  productName, productIcon, lineItems = [], total, totalFrequency, hint,
  primaryAction, sticky = true,
}) {
  const formatVal = (v) => {
    if (v == null) return "—";
    if (typeof v === "number") return `₦${v.toLocaleString()}`;
    return v;
  };
  const stickyStyle = sticky ? { position: "sticky", top: 20, alignSelf: "flex-start" } : {};
  return (
    <div style={{
      background: T.surface, border: `1px solid ${T.border}`, borderRadius: 18,
      padding: 18, display: "flex", flexDirection: "column", gap: 14,
      ...stickyStyle,
    }}>
      {/* Header: product context */}
      <div style={{ display: "flex", alignItems: "center", gap: 10, paddingBottom: 12, borderBottom: `1px solid ${T.border}` }}>
        {productIcon && (
          <div style={{
            width: 36, height: 36, borderRadius: 10, background: T.brandBg,
            display: "inline-flex", alignItems: "center", justifyContent: "center", fontSize: 18, flexShrink: 0,
          }}>{productIcon}</div>
        )}
        <div style={{ minWidth: 0 }}>
          <div style={{ fontSize: 10, fontWeight: 700, color: T.textSec, letterSpacing: "0.08em", textTransform: "uppercase" }}>Live quote</div>
          <div style={{ fontSize: 13, fontWeight: 700, color: T.text, whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>{productName}</div>
        </div>
      </div>

      {/* Line items */}
      {lineItems.length === 0 ? (
        <div style={{ padding: "20px 4px", textAlign: "center", color: T.textTer, fontSize: 12, lineHeight: 1.6 }}>
          Casa fills this in as you answer.
          <div style={{ fontSize: 11, marginTop: 4 }}>Pick a customer to start.</div>
        </div>
      ) : (
        <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
          {lineItems.map((item) => (
            <div key={item.key} className="stagger-fadeIn" style={{
              display: "flex", justifyContent: "space-between", alignItems: "baseline", gap: 10,
              opacity: item.pending ? 0.45 : 1,
              transition: "opacity 0.3s",
            }}>
              <div style={{ fontSize: 12, color: T.textSec, lineHeight: 1.4, minWidth: 0, flex: 1 }}>{item.label}</div>
              <div style={{ fontSize: 12, fontWeight: 600, color: T.text, fontFamily: typeof item.value === "number" ? FONT_DISPLAY : FONT, fontVariantNumeric: "tabular-nums", whiteSpace: "nowrap" }}>
                {formatVal(item.value)}{item.frequency && <span style={{ color: T.textTer, fontWeight: 500, marginLeft: 2 }}>/{item.frequency}</span>}
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Total */}
      {total != null && (
        <div style={{ paddingTop: 12, borderTop: `1px solid ${T.border}`, display: "flex", justifyContent: "space-between", alignItems: "baseline" }}>
          <div style={{ fontSize: 11, fontWeight: 700, color: T.textSec, letterSpacing: "0.08em", textTransform: "uppercase" }}>Total</div>
          <div style={{ fontFamily: FONT_DISPLAY, fontSize: 22, fontWeight: 400, color: T.text, fontVariantNumeric: "tabular-nums" }}>
            ₦{total.toLocaleString()}{totalFrequency && <span style={{ fontSize: 12, color: T.textSec, fontWeight: 500, marginLeft: 4 }}>/{totalFrequency}</span>}
          </div>
        </div>
      )}

      {hint && <div style={{ fontSize: 11, color: T.textTer, lineHeight: 1.55 }}>{hint}</div>}

      {primaryAction && <div>{primaryAction}</div>}
    </div>
  );
}

// PartnerOnboardingChecklist — staged setup tracker. Partners need to complete N steps
// before they can go live. Each step is done / current / locked.
// `collapsible` adds a chevron header that lets the partner tuck it away on the dashboard
// once they're past onboarding; auto-collapses to a compact pill if all steps are done.
function PartnerOnboardingChecklist({ steps = [], collapsible = false, defaultOpen = true, onDismiss }) {
  const allDone = steps.length > 0 && steps.every(s => s.status === "done");
  const [open, setOpen] = useState(allDone ? false : defaultOpen);
  const doneCount = steps.filter(s => s.status === "done").length;

  // Compact "all complete" pill state
  if (collapsible && allDone && !open) {
    return (
      <div style={{
        display: "flex", alignItems: "center", justifyContent: "space-between",
        padding: "10px 16px", background: T.successBg, border: `1px solid ${T.successBorder}`,
        borderRadius: 999, gap: 12,
      }}>
        <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
          <Check size={14} color={T.successText} strokeWidth={3}/>
          <span style={{ fontSize: 13, fontWeight: 700, color: T.successText }}>Setup complete</span>
        </div>
        <button onClick={() => setOpen(true)} style={{ background: "transparent", border: "none", color: T.successText, fontSize: 12, fontWeight: 600, cursor: "pointer", fontFamily: FONT }}>
          Show steps
        </button>
      </div>
    );
  }

  return (
    <div style={{ background: T.surface, border: `1px solid ${T.border}`, borderRadius: 14, padding: 20 }}>
      <div style={{ display: "flex", alignItems: "baseline", justifyContent: "space-between", marginBottom: open ? 16 : 0, gap: 12 }}>
        <div style={{ minWidth: 0 }}>
          <div style={{ fontSize: 11, fontWeight: 700, color: T.brand, letterSpacing: "0.08em", textTransform: "uppercase", marginBottom: 4 }}>Onboarding</div>
          <div style={{ fontFamily: FONT_DISPLAY, fontSize: 22, fontWeight: 400, letterSpacing: "-0.02em", color: T.text }}>
            {doneCount} of {steps.length} complete
          </div>
        </div>
        <div style={{ display: "flex", alignItems: "center", gap: 4, flexShrink: 0 }}>
          {collapsible && (
            <button onClick={() => setOpen(o => !o)} aria-label={open ? "Collapse" : "Expand"} style={{
              background: T.surfaceTint, border: "none", borderRadius: 999,
              width: 32, height: 32, display: "inline-flex", alignItems: "center", justifyContent: "center",
              cursor: "pointer", color: T.textSec,
            }}>{open ? <ChevronUp size={16}/> : <ChevronDown size={16}/>}</button>
          )}
          {onDismiss && (
            <button onClick={onDismiss} aria-label="Dismiss" style={{
              background: T.surfaceTint, border: "none", borderRadius: 999,
              width: 32, height: 32, display: "inline-flex", alignItems: "center", justifyContent: "center",
              cursor: "pointer", color: T.textSec,
            }}><X size={14}/></button>
          )}
        </div>
      </div>
      {open && (
        <div style={{ display: "flex", flexDirection: "column", gap: 4 }}>
          {steps.map((step, i) => {
            const done    = step.status === "done";
            const current = step.status === "current";
            const locked  = step.status === "locked";
            return (
              <div key={i} onClick={() => step.onClick && !locked && step.onClick()} style={{
                display: "flex", alignItems: "center", gap: 12, padding: "10px 12px",
                borderRadius: 10,
                background: current ? T.brandBg : "transparent",
                cursor: locked ? "not-allowed" : (step.onClick ? "pointer" : "default"),
                opacity: locked ? 0.5 : 1,
              }}>
                <div style={{
                  width: 22, height: 22, borderRadius: 999,
                  background: done ? T.brand : "transparent",
                  border: done ? "none" : `2px solid ${current ? T.brand : T.border}`,
                  display: "inline-flex", alignItems: "center", justifyContent: "center", flexShrink: 0,
                }}>
                  {done && <Check size={12} color="#fff" strokeWidth={3}/>}
                  {current && <div style={{ width: 8, height: 8, borderRadius: 999, background: T.brand }}/>}
                </div>
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div style={{ fontSize: 13, fontWeight: 700, color: locked ? T.textTer : T.text, textDecoration: done ? "line-through" : "none", textDecorationColor: T.textTer }}>{step.label}</div>
                  {step.detail && <div style={{ fontSize: 11, color: T.textSec, marginTop: 2, lineHeight: 1.5 }}>{step.detail}</div>}
                </div>
                {step.cta && current && <div style={{ flexShrink: 0 }}>{step.cta}</div>}
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}

// ═══════════════════════════════════════════════════════════════════════════════════
// CHARTS — pure SVG, no library dependency. Sized to live inside Card surfaces in the
// product UI (they're quieter than the marketing-tier infographics). T-token colored.
// ═══════════════════════════════════════════════════════════════════════════════════

// BarChart — vertical bars. `data` is [{ label, value, color? }].
function BarChart({
  data = [], height = 240,
  barColor = T.brand, valueFormat = (v) => v.toLocaleString(),
  showValues = true, yAxisLabel,
}) {
  if (!data.length) return null;
  const max = Math.max(...data.map(d => d.value)) || 1;
  const padding = { top: 32, right: 16, bottom: 40, left: 48 };
  const w = 800;
  const innerW = w - padding.left - padding.right;
  const innerH = height - padding.top - padding.bottom;
  const barWidth = (innerW / data.length) * 0.6;
  const barGap   = (innerW / data.length) * 0.4;
  return (
    <div style={{ width: "100%", overflow: "hidden" }}>
      <svg viewBox={`0 0 ${w} ${height}`} preserveAspectRatio="xMidYMid meet" style={{ width: "100%", height: "auto", display: "block" }}>
        {yAxisLabel && (
          <text x={padding.left} y={18} fontSize="11" fontWeight="700" fill={T.textSec}
                fontFamily={FONT} letterSpacing="0.08em">{yAxisLabel.toUpperCase()}</text>
        )}
        {[0, 0.25, 0.5, 0.75, 1].map((t, i) => {
          const y = padding.top + innerH * (1 - t);
          return (
            <g key={i}>
              <line x1={padding.left} y1={y} x2={padding.left + innerW} y2={y}
                    stroke={T.border} strokeWidth="1" strokeDasharray={t === 0 ? "0" : "2,4"}/>
              <text x={padding.left - 8} y={y + 4} fontSize="10" fill={T.textSec} textAnchor="end" fontFamily={FONT}>
                {valueFormat(Math.round(max * t))}
              </text>
            </g>
          );
        })}
        {data.map((d, i) => {
          const barH = (d.value / max) * innerH;
          const x = padding.left + (i * (barWidth + barGap)) + barGap / 2;
          const y = padding.top + innerH - barH;
          return (
            <g key={i}>
              <rect x={x} y={y} width={barWidth} height={barH} fill={d.color || barColor} rx="4"/>
              {showValues && (
                <text x={x + barWidth / 2} y={y - 8} fontSize="11" fontWeight="700"
                      fill={T.text} textAnchor="middle" fontFamily={FONT_DISPLAY}>
                  {valueFormat(d.value)}
                </text>
              )}
              <text x={x + barWidth / 2} y={padding.top + innerH + 20} fontSize="11"
                    fill={T.textSec} textAnchor="middle" fontFamily={FONT} fontWeight="600">{d.label}</text>
            </g>
          );
        })}
      </svg>
    </div>
  );
}

// LineChart — area-filled line. `series` is [{ label, points: [{x, y}], color? }].
function LineChart({
  series = [], height = 240,
  fillOpacity = 0.12, yAxisLabel,
  valueFormat = (v) => v.toLocaleString(),
}) {
  if (!series.length || !series[0].points.length) return null;
  const allPoints = series.flatMap(s => s.points);
  const max = Math.max(...allPoints.map(p => p.y));
  const min = Math.min(0, Math.min(...allPoints.map(p => p.y)));
  const labels = series[0].points.map(p => p.x);
  const padding = { top: 24, right: 16, bottom: 36, left: 56 };
  const w = 800;
  const innerW = w - padding.left - padding.right;
  const innerH = height - padding.top - padding.bottom;
  const xStep = innerW / Math.max(1, labels.length - 1);
  const lineColors = [T.brand, T.brown, T.amber, T.success];
  return (
    <div style={{ width: "100%", overflow: "hidden" }}>
      <svg viewBox={`0 0 ${w} ${height}`} preserveAspectRatio="xMidYMid meet" style={{ width: "100%", height: "auto", display: "block" }}>
        {yAxisLabel && (
          <text x={padding.left} y={14} fontSize="11" fontWeight="700" fill={T.textSec} fontFamily={FONT} letterSpacing="0.08em">{yAxisLabel.toUpperCase()}</text>
        )}
        {[0, 0.25, 0.5, 0.75, 1].map((t, i) => {
          const y = padding.top + innerH * (1 - t);
          return (
            <g key={i}>
              <line x1={padding.left} y1={y} x2={padding.left + innerW} y2={y}
                    stroke={T.border} strokeWidth="1" strokeDasharray={t === 0 ? "0" : "2,4"}/>
              <text x={padding.left - 8} y={y + 4} fontSize="10" fill={T.textSec} textAnchor="end" fontFamily={FONT}>
                {valueFormat(Math.round(min + (max - min) * t))}
              </text>
            </g>
          );
        })}
        {labels.map((label, i) => {
          const x = padding.left + i * xStep;
          return (
            <text key={i} x={x} y={padding.top + innerH + 20} fontSize="11"
                  fill={T.textSec} textAnchor="middle" fontFamily={FONT} fontWeight="600">{label}</text>
          );
        })}
        {series.map((s, si) => {
          const c = s.color || lineColors[si % lineColors.length];
          const pts = s.points.map((p, i) => {
            const x = padding.left + i * xStep;
            const y = padding.top + innerH * (1 - (p.y - min) / (max - min || 1));
            return { x, y };
          });
          const linePath = pts.map((p, i) => `${i === 0 ? "M" : "L"} ${p.x.toFixed(1)} ${p.y.toFixed(1)}`).join(" ");
          const areaPath = `${linePath} L ${pts[pts.length - 1].x.toFixed(1)} ${(padding.top + innerH).toFixed(1)} L ${pts[0].x.toFixed(1)} ${(padding.top + innerH).toFixed(1)} Z`;
          return (
            <g key={si}>
              <path d={areaPath} fill={c} opacity={fillOpacity}/>
              <path d={linePath} stroke={c} strokeWidth="2.5" fill="none" strokeLinecap="round" strokeLinejoin="round"/>
              {pts.map((p, i) => (
                <circle key={i} cx={p.x} cy={p.y} r="3.5" fill="#fff" stroke={c} strokeWidth="2"/>
              ))}
            </g>
          );
        })}
      </svg>
      {series.length > 1 && (
        <div style={{ display: "flex", gap: 18, justifyContent: "center", marginTop: 14, flexWrap: "wrap" }}>
          {series.map((s, si) => (
            <div key={si} style={{ display: "flex", alignItems: "center", gap: 6, fontSize: 12, color: T.textSec }}>
              <span style={{ width: 10, height: 10, borderRadius: 2, background: s.color || lineColors[si % lineColors.length] }}/>
              <span style={{ fontWeight: 600 }}>{s.label}</span>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

// DonutChart — share-of-whole. `data` is [{ label, value, color? }].
function DonutChart({
  data = [], size = 200, thickness = 32,
  centerLabel, centerValue, colors,
}) {
  const palette = colors || [T.brand, T.brown, T.brandDeep, T.amber, T.success, T.brandLight];
  const total = data.reduce((sum, d) => sum + d.value, 0) || 1;
  const radius = (size - thickness) / 2;
  const center = size / 2;
  const circumference = 2 * Math.PI * radius;
  let cumulative = 0;
  const slices = data.map((d, i) => {
    const fraction = d.value / total;
    const dashLength = fraction * circumference;
    const offset = -cumulative * circumference;
    cumulative += fraction;
    return {
      ...d,
      color: d.color || palette[i % palette.length],
      dashArray: `${dashLength} ${circumference - dashLength}`,
      dashOffset: offset,
      pct: Math.round(fraction * 100),
    };
  });
  return (
    <div style={{ display: "flex", alignItems: "center", gap: 28, flexWrap: "wrap" }}>
      <svg width={size} height={size} viewBox={`0 0 ${size} ${size}`} style={{ flexShrink: 0 }}>
        <circle cx={center} cy={center} r={radius} fill="none" stroke={T.surfaceTint} strokeWidth={thickness}/>
        {slices.map((slice, i) => (
          <circle
            key={i}
            cx={center} cy={center} r={radius}
            fill="none"
            stroke={slice.color}
            strokeWidth={thickness}
            strokeDasharray={slice.dashArray}
            strokeDashoffset={slice.dashOffset}
            transform={`rotate(-90 ${center} ${center})`}
            strokeLinecap="butt"
          />
        ))}
        {(centerLabel || centerValue) && (
          <g>
            {centerValue && (
              <text x={center} y={center - 4} textAnchor="middle"
                    fontFamily={FONT_DISPLAY} fontSize="24" fontWeight="400"
                    letterSpacing="-0.02em" fill={T.text}>{centerValue}</text>
            )}
            {centerLabel && (
              <text x={center} y={center + 16} textAnchor="middle"
                    fontFamily={FONT} fontSize="10" fontWeight="700"
                    letterSpacing="0.08em" fill={T.textSec}>{centerLabel.toUpperCase()}</text>
            )}
          </g>
        )}
      </svg>
      <div style={{ display: "flex", flexDirection: "column", gap: 8, flex: 1, minWidth: 180 }}>
        {slices.map((slice, i) => (
          <div key={i} style={{ display: "flex", alignItems: "center", gap: 10 }}>
            <span style={{ width: 10, height: 10, borderRadius: 2, background: slice.color, flexShrink: 0 }}/>
            <span style={{ fontSize: 12, fontWeight: 600, color: T.text, flex: 1, minWidth: 0, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{slice.label}</span>
            <span style={{ fontSize: 12, fontWeight: 700, color: T.text, fontVariantNumeric: "tabular-nums" }}>{slice.pct}%</span>
          </div>
        ))}
      </div>
    </div>
  );
}

// Avatar — circular initials per design system (pink users, brown staff, green customers, pinkCoral variety).
// `kind="business"` switches to a rounded-square tile with a Building2 icon for business entities.
const Avatar = ({ name, size = 40, color, gradient = "pink", kind = "individual" }) => {
  const initials = (name || "??").split(" ").map(w => w[0]).slice(0, 2).join("").toUpperCase();
  // Design system palette — solid colors, no gradients
  const solids = {
    pink: { bg: T.brand, fg: "#fff" },          // default users
    brown: { bg: T.brown, fg: "#fff" },         // staff, agents, advisors
    green: { bg: T.green, fg: "#fff" },           // customers (deeper green for white-text contrast)
    pinkCoral: { bg: T.pinkCoral, fg: "#fff" },   // variety / decorative
    // Legacy aliases
    pinkBright: { bg: T.pinkCoral, fg: "#fff" },
    yellow: { bg: T.yellowSoft, fg: T.text },
    cyan: { bg: T.cyan, fg: T.text },
    purple: { bg: T.purple, fg: "#fff" },
  };
  const key = color || gradient;
  const g = solids[key] || solids.pink;
  // Business variant: rounded square + building icon if no initials forced
  if (kind === "business") {
    return (
      <div style={{
        width: size, height: size, borderRadius: size * 0.22, background: g.bg, color: g.fg,
        display: "inline-flex", alignItems: "center", justifyContent: "center",
        fontSize: size * 0.32, fontWeight: 700, fontFamily: FONT, flexShrink: 0,
      }}>{initials}</div>
    );
  }
  return (
    <div style={{
      width: size, height: size, borderRadius: "50%", background: g.bg, color: g.fg,
      display: "inline-flex", alignItems: "center", justifyContent: "center",
      fontSize: size * 0.36, fontWeight: 700, fontFamily: FONT, flexShrink: 0,
    }}>{initials}</div>
  );
};

// CustomerAvatar — looks up the customer in CUSTOMERS and renders a type-aware avatar.
// Use anywhere a customer is shown in a list. Falls back to "individual" green if not found.
const CustomerAvatar = ({ name, customerId, size = 32 }) => {
  const c = customerId
    ? CUSTOMERS.find(x => x.id === customerId)
    : CUSTOMERS.find(x => x.name === name);
  const isBusiness = c?.type === "business";
  return (
    <Avatar
      name={name}
      size={size}
      color={isBusiness ? "pinkCoral" : "green"}
      kind={isBusiness ? "business" : "individual"}
    />
  );
};

const Tag = ({ children, color = "gray", icon: Icon }) => {
  const colors = {
    gray: { bg: T.surfaceTint, text: T.textSec },
    brand: { bg: T.brandBg, text: T.brand },
    green: { bg: T.successBg, text: T.success },
    amber: { bg: T.amberBg, text: T.amber },
    red: { bg: T.dangerBg, text: T.danger },
  }[color];
  return (
    <span style={{ display: "inline-flex", alignItems: "center", gap: 5, padding: "4px 9px", borderRadius: 6, background: colors.bg, color: colors.text, fontSize: 11, fontWeight: 600 }}>
      {Icon && <Icon size={11} strokeWidth={2.4}/>}
      {children}
    </span>
  );
};

const KeyValueGrid = ({ items, columns = 2 }) => {
  // Use minmax with reasonable floor so columns collapse gracefully on narrow viewports
  const minCol = columns === 1 ? "100%" : "200px";
  return (
    <div style={{ display: "grid", gridTemplateColumns: `repeat(auto-fit, minmax(${minCol}, 1fr))`, gap: "16px 24px" }}>
      {items.map((it, i) => (
        <div key={i}>
          <div style={{ fontSize: 11, fontWeight: 600, color: T.textSec, letterSpacing: "0.06em", textTransform: "uppercase", marginBottom: 4 }}>{it.label}</div>
          <div style={{ fontSize: 14, fontWeight: 500, color: T.text, fontFamily: it.mono ? "ui-monospace, monospace" : FONT, wordBreak: "break-word" }}>{it.value}</div>
        </div>
      ))}
    </div>
  );
};

const DisclosureRow = ({ label, value, hint, ruled = true, accent }) => (
  <div style={{ display: "flex", justifyContent: "space-between", alignItems: "baseline", padding: "10px 0", borderBottom: ruled ? `1px solid ${T.border}` : "none", gap: 16 }}>
    <div>
      <div style={{ fontSize: 13, color: T.textSec }}>{label}</div>
      {hint && <div style={{ fontSize: 11, color: T.textTer, marginTop: 2 }}>{hint}</div>}
    </div>
    <div style={{ fontSize: 14, fontWeight: 600, color: accent || T.text, fontVariantNumeric: "tabular-nums", textAlign: "right" }}>{value}</div>
  </div>
);

const Banner = ({ variant = "info", title, description, icon: Icon, action }) => {
  const v = {
    info: { bg: T.infoBg, border: T.infoBorder, color: T.text },
    success: { bg: T.successBg, border: T.successBorder, color: T.success },
    amber: { bg: T.amberBg, border: T.amberBorder, color: T.amber },
    danger: { bg: T.dangerBg, border: T.dangerBorder, color: T.danger },
  }[variant];
  const I = Icon || { info: Info, success: Check, amber: AlertTriangle, danger: AlertCircle }[variant];
  return (
    <div style={{ padding: 14, background: v.bg, border: `1px solid ${v.border}`, borderRadius: 14, display: "flex", gap: 12, alignItems: "flex-start" }}>
      <I size={18} strokeWidth={2.2} color={v.color} style={{ flexShrink: 0, marginTop: 1 }}/>
      <div style={{ flex: 1 }}>
        {title && <div style={{ fontSize: 13, fontWeight: 700, color: T.text, marginBottom: 2 }}>{title}</div>}
        {description && <div style={{ fontSize: 13, color: T.textSec, lineHeight: 1.5 }}>{description}</div>}
        {action && <div style={{ marginTop: 10 }}>{action}</div>}
      </div>
    </div>
  );
};

const ProgressBar = ({ value, max = 100, color = T.brand }) => (
  <div style={{ height: 6, background: T.surfaceTint, borderRadius: 999, overflow: "hidden" }}>
    <div style={{ width: `${Math.min(100, (value / max) * 100)}%`, height: "100%", background: color, borderRadius: 999, transition: "width 0.5s" }}/>
  </div>
);

const SearchInput = ({ value, onChange, placeholder = "Search..." }) => (
  <div style={{ position: "relative", display: "inline-flex", alignItems: "center", flex: 1, minWidth: 200 }}>
    <Search size={15} strokeWidth={2.3} color={T.textSec} style={{ position: "absolute", left: 12 }}/>
    <input
      type="search"
      autoComplete="off"
      value={value || ""}
      onChange={e => onChange?.(e.target.value)}
      placeholder={placeholder}
      style={{
        width: "100%", padding: "9px 14px 9px 36px", borderRadius: 10,
        border: `1px solid ${T.border}`, background: T.surface,
        fontSize: 13, fontFamily: FONT, color: T.text, outline: "none",
      }}
    />
  </div>
);

// ═══════════════════════════════════════════════════════════════════════════════════
// AUTHORITY (regulatory moat — NAICOM seal prominently)
// ═══════════════════════════════════════════════════════════════════════════════════
function AuthoritySeal({ type, size = 40 }) {
  const seals = {
    naicom: (
      <svg viewBox="0 0 40 40" width={size} height={size}>
        <rect x="2" y="2" width="36" height="36" rx="8" fill="#1A4D2E"/>
        <path d="M20 8 L 28 14 L 28 26 Q 28 32 20 34 Q 12 32 12 26 L 12 14 Z" fill="#FFF"/>
        <path d="M16 20 L 19 23 L 24 17" stroke="#1A4D2E" strokeWidth="2.2" fill="none" strokeLinecap="round" strokeLinejoin="round"/>
      </svg>
    ),
    cac: (
      <svg viewBox="0 0 40 40" width={size} height={size}>
        <rect x="2" y="2" width="36" height="36" rx="8" fill="#7B2D26"/>
        <rect x="10" y="12" width="20" height="22" fill="#FFF" rx="1"/>
        <rect x="13" y="16" width="14" height="1.5" fill="#7B2D26"/>
        <rect x="13" y="19" width="14" height="1.5" fill="#7B2D26"/>
        <rect x="13" y="22" width="10" height="1.5" fill="#7B2D26"/>
        <circle cx="24" cy="28" r="3" fill="#D4AF37"/>
      </svg>
    ),
    ndpr: (
      <svg viewBox="0 0 40 40" width={size} height={size}>
        <rect x="2" y="2" width="36" height="36" rx="8" fill="#0F5132"/>
        <path d="M20 8 L 30 14 L 30 24 Q 30 32 20 34 Q 10 32 10 24 L 10 14 Z" fill="#FFF" opacity="0.95"/>
        <rect x="16" y="18" width="8" height="10" rx="1" fill="#0F5132"/>
        <path d="M17 18 V 15 Q 17 12 20 12 Q 23 12 23 15 V 18" stroke="#0F5132" strokeWidth="1.5" fill="none"/>
      </svg>
    ),
  };
  return <div style={{ display: "inline-flex", flexShrink: 0 }}>{seals[type]}</div>;
}

const AuthorityLockup = ({ authorities, label, variant = "default" }) => {
  if (variant === "badge") {
    return (
      <div style={{ display: "inline-flex", alignItems: "center", gap: 8, padding: "6px 12px", background: T.surfaceTint, borderRadius: 999, border: `1px solid ${T.border}` }}>
        {authorities.map(a => <AuthoritySeal key={a} type={a} size={18}/>)}
        {label && <span style={{ fontSize: 11, fontWeight: 600, color: T.textSec }}>{label}</span>}
      </div>
    );
  }
  return (
    <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
      <div style={{ display: "flex", gap: 8 }}>
        {authorities.map(a => <AuthoritySeal key={a} type={a} size={28}/>)}
      </div>
      {label && <div style={{ fontSize: 11, color: T.textSec, fontWeight: 500 }}>{label}</div>}
    </div>
  );
};

// ═══════════════════════════════════════════════════════════════════════════════════
// HERO + STAT
// ═══════════════════════════════════════════════════════════════════════════════════
function HeroGradientCard({ title, subtitle, tag, action, surface = "brown", stat }) {
  const surfaces = { brown: T.brown, brand: T.brand, government: "#1E3A8A" };
  const bg = surfaces[surface];
  return (
    <div style={{ background: bg, color: "#fff", borderRadius: 20, padding: 32, position: "relative", overflow: "hidden" }}>
      <div style={{ position: "absolute", bottom: -32, right: -28, opacity: 0.08, pointerEvents: "none" }}>
        <CMark color="#fff" size={240}/>
      </div>
      <div style={{ position: "relative", maxWidth: stat ? "65%" : "100%" }}>
        {tag && <div style={{ marginBottom: 14 }}>{tag}</div>}
        <Display size="md" color="#fff" style={{ marginBottom: 10 }}>{title}</Display>
        {subtitle && <div style={{ fontSize: 14, opacity: 0.85, marginBottom: action ? 22 : 0, lineHeight: 1.5, maxWidth: 460 }}>{subtitle}</div>}
        {action}
      </div>
      {stat && (
        <div style={{ position: "absolute", bottom: 28, right: 32, textAlign: "right" }}>
          <div style={{ fontFamily: FONT_DISPLAY, fontSize: 42, fontWeight: 400, letterSpacing: "-0.02em", lineHeight: 1 }}>{stat.value}</div>
          <div style={{ fontSize: 11, fontWeight: 600, opacity: 0.65, marginTop: 6, letterSpacing: "0.06em", textTransform: "uppercase" }}>{stat.label}</div>
        </div>
      )}
    </div>
  );
}

function StatBlock({ label, value, trend, accent = T.text, prefix, suffix, delay = 0 }) {
  const [display, setDisplay] = useState(0);
  useEffect(() => {
    const target = typeof value === "number" ? value : parseFloat(String(value).replace(/[^\d.-]/g, "")) || 0;
    const duration = 800; const start = performance.now();
    const tick = (now) => {
      const t = Math.min((now - start) / duration, 1);
      const eased = 1 - Math.pow(1 - t, 3);
      setDisplay(target * eased);
      if (t < 1) requestAnimationFrame(tick);
    };
    const timer = setTimeout(() => requestAnimationFrame(tick), delay);
    return () => clearTimeout(timer);
  }, [value, delay]);
  const isNum = typeof value === "number";
  const formatted = isNum ? Math.round(display).toLocaleString() : value;
  return (
    <div className="stagger-fadeIn" style={{ animationDelay: `${delay}ms`, padding: 20, background: T.surface, borderRadius: 14, border: `1px solid ${T.border}` }}>
      <div style={{ fontSize: 12, fontWeight: 600, color: T.textSec, marginBottom: 10 }}>{label}</div>
      <div style={{ display: "flex", alignItems: "baseline", gap: 4 }}>
        {prefix && <span style={{ fontFamily: FONT_DISPLAY, fontSize: 22, fontWeight: 400, color: T.textSec }}>{prefix}</span>}
        <span style={{ fontFamily: FONT_DISPLAY, fontSize: 32, fontWeight: 400, letterSpacing: "-0.02em", color: accent, lineHeight: 1 }}>{formatted}</span>
        {suffix && <span style={{ fontSize: 14, fontWeight: 500, color: T.textSec, marginLeft: 4 }}>{suffix}</span>}
      </div>
      {trend && (
        <div style={{ marginTop: 10, fontSize: 12, color: trend.up ? T.success : T.danger, display: "inline-flex", alignItems: "center", gap: 4, fontWeight: 600 }}>
          <TrendingUp size={12} strokeWidth={2.4} style={{ transform: trend.up ? "none" : "rotate(180deg)" }}/>
          {trend.label}
        </div>
      )}
    </div>
  );
}

// ═══════════════════════════════════════════════════════════════════════════════════
// MASKED FIELD (audit fix P0-02 — BVN/NIN reveal with audit log)
// ═══════════════════════════════════════════════════════════════════════════════════
function MaskedField({ label, value, type = "BVN" }) {
  const [revealed, setRevealed] = useState(false);
  const [confirming, setConfirming] = useState(false);
  const masked = "•••• •••• " + String(value).slice(-4);

  const reveal = () => {
    setRevealed(true);
    setConfirming(false);
    // In production: POST audit log { actor, field, customerId, timestamp }
    setTimeout(() => setRevealed(false), 30_000); // auto-hide after 30s
  };

  return (
    <div>
      <div style={{ fontSize: 11, fontWeight: 600, color: T.textSec, letterSpacing: "0.06em", textTransform: "uppercase", marginBottom: 4 }}>{label}</div>
      <div style={{ display: "inline-flex", alignItems: "center", gap: 8 }}>
        <span style={{ fontSize: 14, fontWeight: 500, fontFamily: "ui-monospace, monospace", color: T.text }}>
          {revealed ? value : masked}
        </span>
        <button onClick={() => revealed ? setRevealed(false) : setConfirming(true)} style={{
          background: "transparent", border: "none", padding: 4, cursor: "pointer", display: "inline-flex",
          color: T.textSec, borderRadius: 6,
        }} title={revealed ? "Hide" : "Reveal (audit-logged)"}>
          {revealed ? <EyeOff size={14}/> : <Eye size={14}/>}
        </button>
      </div>
      {confirming && (
        <Modal title={`Reveal ${type}?`} onClose={() => setConfirming(false)} maxWidth={420}>
          <div style={{ fontSize: 13, color: T.textSec, lineHeight: 1.6, marginBottom: 18 }}>
            Revealing this {type} is logged to the audit trail under your name. The number will auto-hide after 30 seconds.
          </div>
          <div style={{ display: "flex", gap: 8, justifyContent: "flex-end" }}>
            <Btn variant="ghost" onClick={() => setConfirming(false)}>Cancel</Btn>
            <Btn variant="primary" onClick={reveal} icon={Eye}>Reveal {type}</Btn>
          </div>
        </Modal>
      )}
    </div>
  );
}

// ═══════════════════════════════════════════════════════════════════════════════════
// MODAL / SHEET
// ═══════════════════════════════════════════════════════════════════════════════════
function Modal({ children, title, onClose, maxWidth = 560, footer }) {
  return (
    <div style={{
      position: "fixed", inset: 0, background: "rgba(28, 18, 14, 0.55)",
      display: "flex", alignItems: "center", justifyContent: "center", zIndex: 1000,
      animation: "fadeIn 0.2s ease-out", padding: 16,
    }} onClick={onClose}>
      <div onClick={e => e.stopPropagation()} style={{
        width: "100%", maxWidth, maxHeight: "90vh", background: T.surface, borderRadius: 18,
        boxShadow: "0 24px 60px rgba(0,0,0,0.18)", display: "flex", flexDirection: "column",
        animation: "slideUp 0.25s ease-out",
      }}>
        {title && (
          <div style={{ padding: "20px 24px", borderBottom: `1px solid ${T.border}`, display: "flex", alignItems: "center", justifyContent: "space-between" }}>
            <Display size="sm">{title}</Display>
            <button onClick={onClose} style={{ background: T.surfaceTint, border: "none", borderRadius: "50%", width: 32, height: 32, display: "inline-flex", alignItems: "center", justifyContent: "center", cursor: "pointer", color: T.textSec }}><X size={16}/></button>
          </div>
        )}
        <div style={{ padding: 24, overflow: "auto", flex: 1 }}>{children}</div>
        {footer && <div style={{ padding: "16px 24px", borderTop: `1px solid ${T.border}`, display: "flex", gap: 8, justifyContent: "flex-end" }}>{footer}</div>}
      </div>
    </div>
  );
}

// ═══════════════════════════════════════════════════════════════════════════════════
// DATA TABLE — responsive (collapses to cards <768px) (audit fix P1-14)
// ═══════════════════════════════════════════════════════════════════════════════════
function DataTable({ columns, rows, onRowClick, density = "default", emptyMessage = "No records yet" }) {
  const [w, setW] = useState(typeof window !== "undefined" ? window.innerWidth : 1200);
  const [sortKey, setSortKey] = useState(null);
  const [sortDir, setSortDir] = useState("asc");
  useEffect(() => {
    const r = () => setW(window.innerWidth);
    window.addEventListener("resize", r);
    return () => window.removeEventListener("resize", r);
  }, []);
  const padY = density === "compact" ? 8 : 13;
  const padX = 16;
  const isMobile = w < 768;

  // Sort rows when sortKey is set. Numeric vs string-aware.
  const sortedRows = useMemo(() => {
    if (!sortKey) return rows;
    const col = columns.find(c => c.key === sortKey);
    const numeric = col?.numeric;
    const sorted = [...rows].sort((a, b) => {
      const av = a[sortKey], bv = b[sortKey];
      if (av == null) return 1;
      if (bv == null) return -1;
      if (numeric) return Number(av) - Number(bv);
      return String(av).localeCompare(String(bv), undefined, { numeric: true });
    });
    return sortDir === "desc" ? sorted.reverse() : sorted;
  }, [rows, columns, sortKey, sortDir]);

  const toggleSort = (key) => {
    if (sortKey === key) {
      if (sortDir === "asc") setSortDir("desc");
      else { setSortKey(null); setSortDir("asc"); } // third click clears
    } else {
      setSortKey(key); setSortDir("asc");
    }
  };

  if (rows.length === 0) {
    return (
      <div style={{ padding: 48, background: T.surface, border: `1px solid ${T.border}`, borderRadius: 14, textAlign: "center" }}>
        <div style={{ fontSize: 36, marginBottom: 8, opacity: 0.4 }}>○</div>
        <div style={{ fontSize: 14, color: T.textSec, fontWeight: 500 }}>{emptyMessage}</div>
      </div>
    );
  }

  if (isMobile) {
    return (
      <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
        {sortedRows.map((row, i) => (
          <div key={row.id || i} onClick={() => onRowClick?.(row)} style={{
            background: T.surface, border: `1px solid ${T.border}`, borderRadius: 14, padding: 16,
            cursor: onRowClick ? "pointer" : "default", display: "flex", flexDirection: "column", gap: 10,
          }}>
            {columns.filter(c => !c.hideMobile).map((col, j) => (
              <div key={col.key || j} style={{ display: "flex", justifyContent: "space-between", gap: 12, alignItems: "center" }}>
                <div style={{ fontSize: 11, fontWeight: 600, color: T.textSec, letterSpacing: "0.04em", textTransform: "uppercase" }}>{col.label}</div>
                <div style={{ fontSize: 13, color: T.text, textAlign: "right", flex: 1, fontVariantNumeric: col.numeric ? "tabular-nums" : "normal" }}>
                  {col.render ? col.render(row[col.key], row) : row[col.key]}
                </div>
              </div>
            ))}
          </div>
        ))}
      </div>
    );
  }

  return (
    <div style={{ background: T.surface, border: `1px solid ${T.border}`, borderRadius: 14, overflow: "hidden" }}>
      <div style={{ overflowX: "auto" }}>
        <table style={{ width: "100%", borderCollapse: "collapse", fontFamily: FONT }}>
          <thead>
            <tr style={{ background: T.surfaceTint, borderBottom: `1px solid ${T.border}` }}>
              {columns.map((col, i) => {
                const sortable = col.sortable !== false && col.key && !col.render?.toString().includes("custom"); // sortable unless opted out
                const isActive = sortKey === col.key;
                return (
                  <th key={col.key || i} onClick={() => sortable && col.key && toggleSort(col.key)} style={{
                    padding: `${padY}px ${padX}px`, textAlign: col.align || "left",
                    fontSize: 11, fontWeight: 700, color: isActive ? T.text : T.textSec, letterSpacing: "0.06em", textTransform: "uppercase",
                    whiteSpace: "nowrap", cursor: sortable && col.key ? "pointer" : "default", userSelect: "none",
                  }}>
                    <span style={{ display: "inline-flex", alignItems: "center", gap: 4 }}>
                      {col.label}
                      {sortable && col.key && (
                        <span style={{ display: "inline-flex", flexDirection: "column", lineHeight: 0.6, marginLeft: 2, opacity: isActive ? 1 : 0.3 }}>
                          {!isActive ? (
                            <span style={{ fontSize: 8 }}>↕</span>
                          ) : sortDir === "asc" ? (
                            <ChevronUp size={12} strokeWidth={2.6}/>
                          ) : (
                            <ChevronDown size={12} strokeWidth={2.6}/>
                          )}
                        </span>
                      )}
                    </span>
                  </th>
                );
              })}
            </tr>
          </thead>
          <tbody>
            {sortedRows.map((row, i) => (
              <tr key={row.id || i} onClick={() => onRowClick?.(row)} style={{
                borderBottom: i < sortedRows.length - 1 ? `1px solid ${T.border}` : "none",
                cursor: onRowClick ? "pointer" : "default",
                transition: "background 0.12s",
              }}
                onMouseEnter={e => onRowClick && (e.currentTarget.style.background = T.surfaceTint)}
                onMouseLeave={e => e.currentTarget.style.background = "transparent"}>
                {columns.map((col, j) => (
                  <td key={col.key || j} style={{
                    padding: `${padY}px ${padX}px`, textAlign: col.align || "left",
                    fontSize: 13, color: T.text,
                    fontVariantNumeric: col.numeric ? "tabular-nums" : "normal",
                  }}>
                    {col.render ? col.render(row[col.key], row) : row[col.key]}
                  </td>
                ))}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

// ═══════════════════════════════════════════════════════════════════════════════════
// FILTER BAR
// ═══════════════════════════════════════════════════════════════════════════════════
function FilterBar({ children, rightSlot }) {
  return (
    <div className="filter-bar" style={{
      display: "flex", alignItems: "center", gap: 10, flexWrap: "wrap",
      padding: "12px 14px", background: T.surface, border: `1px solid ${T.border}`, borderRadius: 14,
    }}>
      <Filter size={14} color={T.textSec} strokeWidth={2.3} style={{ flexShrink: 0 }}/>
      {children}
      {rightSlot && <div className="filter-bar-right" style={{ marginLeft: "auto", display: "flex", gap: 8, alignItems: "center", flex: "0 1 auto" }}>{rightSlot}</div>}
    </div>
  );
}

const FilterChip = ({ label, value, active, onClick }) => (
  <button onClick={onClick} style={{
    display: "inline-flex", alignItems: "center", gap: 6,
    padding: "6px 11px", borderRadius: 8,
    background: active ? T.brandBg : T.surfaceTint,
    color: active ? T.brand : T.text,
    border: active ? `1px solid ${T.brand}` : `1px solid transparent`,
    fontSize: 12, fontWeight: 600, fontFamily: FONT, cursor: "pointer",
  }}>
    {label}
    {value && <span style={{ color: active ? T.brand : T.textSec, fontWeight: 500 }}>· {value}</span>}
    <ChevronDown size={12} strokeWidth={2.4}/>
  </button>
);

// ChipMenu — interactive filter chip with a popover for selecting one option from a list.
// Active when the selected option is anything other than the first (assumed to be "All").
function ChipMenu({ label, value, options, selected, onSelect }) {
  const [open, setOpen] = useState(false);
  const ref = useRef(null);
  useEffect(() => {
    if (!open) return;
    const onDoc = (e) => { if (ref.current && !ref.current.contains(e.target)) setOpen(false); };
    document.addEventListener("mousedown", onDoc);
    return () => document.removeEventListener("mousedown", onDoc);
  }, [open]);
  const isActive = options.length > 0 && selected !== options[0].id;
  return (
    <div ref={ref} style={{ position: "relative", display: "inline-block" }}>
      <button onClick={() => setOpen(o => !o)} style={{
        display: "inline-flex", alignItems: "center", gap: 6,
        padding: "6px 11px", borderRadius: 8,
        background: isActive ? T.brandBg : T.surfaceTint,
        color: isActive ? T.brand : T.text,
        border: isActive ? `1px solid ${T.brand}` : `1px solid transparent`,
        fontSize: 12, fontWeight: 600, fontFamily: FONT, cursor: "pointer",
      }}>
        {label}
        {value && <span style={{ color: isActive ? T.brand : T.textSec, fontWeight: 500 }}>· {value}</span>}
        <ChevronDown size={12} strokeWidth={2.4} style={{ transform: open ? "rotate(180deg)" : "none", transition: "transform 0.15s" }}/>
      </button>
      {open && (
        <div style={{
          position: "absolute", top: "calc(100% + 4px)", left: 0, zIndex: 50,
          minWidth: 180, background: T.surface, border: `1px solid ${T.border}`, borderRadius: 10,
          boxShadow: "0 10px 24px rgba(0,0,0,0.10)", padding: 4,
        }}>
          {options.map(opt => (
            <button key={opt.id} onClick={() => { onSelect(opt.id); setOpen(false); }} style={{
              width: "100%", padding: "8px 12px", borderRadius: 7, border: "none",
              background: selected === opt.id ? T.brandBg : "transparent",
              color: selected === opt.id ? T.brand : T.text,
              fontFamily: FONT, fontSize: 13, fontWeight: 500, cursor: "pointer",
              display: "flex", alignItems: "center", justifyContent: "space-between", gap: 8,
              textAlign: "left",
            }}
              onMouseEnter={e => selected !== opt.id && (e.currentTarget.style.background = T.surfaceTint)}
              onMouseLeave={e => selected !== opt.id && (e.currentTarget.style.background = "transparent")}>
              <span>{opt.label}</span>
              {selected === opt.id && <Check size={13} strokeWidth={2.6}/>}
            </button>
          ))}
        </div>
      )}
    </div>
  );
}

// ComingSoonModal — reusable stub for production-pending actions. Casa-fronted to soften the "not built yet" message.
function ComingSoonModal({ title, description, onClose }) {
  return (
    <Modal title={title} onClose={onClose} maxWidth={460}
      footer={<Btn variant="primary" onClick={onClose}>Got it</Btn>}>
      <div style={{ display: "flex", gap: 16, alignItems: "flex-start" }}>
        <CasaAvatar size={48} state="thinking"/>
        <div style={{ flex: 1 }}>
          <div style={{ fontSize: 13, color: T.textSec, lineHeight: 1.6, marginBottom: 10 }}>
            {description}
          </div>
          <Banner variant="info" title="Demo mode" description="This action isn't wired up in the demo. The flow is fully designed and lives in the v2 backlog."/>
        </div>
      </div>
    </Modal>
  );
}

// ═══════════════════════════════════════════════════════════════════════════════════
// MONEY DISPLAY
// ═══════════════════════════════════════════════════════════════════════════════════
const Money = ({ amount, size = "md", showNaira = true, accent }) => {
  const sizes = { sm: 14, md: 17, lg: 22, xl: 30, xxl: 42 };
  const fs = sizes[size];
  return (
    <span style={{ fontFamily: FONT_DISPLAY, fontSize: fs, fontWeight: 400, letterSpacing: "-0.02em", color: accent || T.text, fontVariantNumeric: "tabular-nums", display: "inline-flex", alignItems: "baseline", gap: 2 }}>
      {showNaira && <span style={{ fontSize: fs * 0.7, opacity: 0.7 }}>₦</span>}
      {Number(amount).toLocaleString()}
    </span>
  );
};

// ═══════════════════════════════════════════════════════════════════════════════════
// POLICY CARD
// ═══════════════════════════════════════════════════════════════════════════════════
function PolicyCard({ icon = "🛡️", productName, policyId, status = "active", coverage, premium, premiumFrequency = "month", validUntil, daysRemaining, onClick, compact }) {
  const statusConfig = {
    active: { color: "green", label: "Active" },
    pending: { color: "amber", label: "Pending" },
    inactive: { color: "gray", label: "Inactive" },
    expired: { color: "red", label: "Expired" },
  }[status];
  return (
    <div onClick={onClick} style={{
      padding: compact ? 16 : 22, background: T.surface, border: `1px solid ${T.border}`, borderRadius: 14,
      cursor: onClick ? "pointer" : "default", transition: "transform 0.15s, box-shadow 0.15s",
    }}
      onMouseEnter={e => onClick && (e.currentTarget.style.transform = "translateY(-2px)", e.currentTarget.style.boxShadow = "0 8px 24px rgba(0,0,0,0.06)")}
      onMouseLeave={e => (e.currentTarget.style.transform = "none", e.currentTarget.style.boxShadow = "none")}>
      <div style={{ display: "flex", alignItems: "flex-start", justifyContent: "space-between", gap: 12, marginBottom: 14 }}>
        <div style={{ display: "flex", gap: 12, alignItems: "center" }}>
          <div style={{ fontSize: 28, lineHeight: 1 }}>{icon}</div>
          <div>
            <div style={{ fontSize: 14, fontWeight: 700, color: T.text }}>{productName}</div>
            <div style={{ fontSize: 11, color: T.textSec, fontFamily: "ui-monospace, monospace", marginTop: 2 }}>{policyId}</div>
          </div>
        </div>
        <Pill color={statusConfig.color}>{statusConfig.label}</Pill>
      </div>
      <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 12, paddingTop: 14, borderTop: `1px solid ${T.border}` }}>
        <div>
          <div style={{ fontSize: 10, fontWeight: 700, color: T.textSec, letterSpacing: "0.06em", textTransform: "uppercase", marginBottom: 4 }}>Coverage</div>
          <Money amount={coverage} size="md"/>
        </div>
        <div>
          <div style={{ fontSize: 10, fontWeight: 700, color: T.textSec, letterSpacing: "0.06em", textTransform: "uppercase", marginBottom: 4 }}>Premium</div>
          <div style={{ display: "flex", alignItems: "baseline", gap: 4 }}>
            <Money amount={premium} size="sm"/>
            <span style={{ fontSize: 11, color: T.textSec }}>/{premiumFrequency}</span>
          </div>
        </div>
      </div>
      {validUntil && (
        <div style={{ marginTop: 14, paddingTop: 12, borderTop: `1px solid ${T.border}`, display: "flex", justifyContent: "space-between", alignItems: "center" }}>
          <span style={{ fontSize: 11, color: T.textSec }}>Valid until {validUntil}</span>
          {daysRemaining != null && <Pill color={daysRemaining < 30 ? "amber" : "gray"} size="sm">{daysRemaining} days</Pill>}
        </div>
      )}
    </div>
  );
}

// ═══════════════════════════════════════════════════════════════════════════════════
// CLAIM TIMELINE
// ═══════════════════════════════════════════════════════════════════════════════════
function ClaimTimeline({ stages }) {
  return (
    <div>
      {stages.map((s, i) => {
        const done = s.status === "complete";
        const current = s.status === "current";
        return (
          <div key={i} style={{ display: "flex", gap: 14, alignItems: "flex-start", paddingBottom: i < stages.length - 1 ? 18 : 0, position: "relative" }}>
            {i < stages.length - 1 && (
              <div style={{ position: "absolute", left: 13, top: 28, bottom: 0, width: 2, background: done ? T.success : T.border }}/>
            )}
            <div style={{
              width: 28, height: 28, borderRadius: "50%", flexShrink: 0,
              background: done ? T.success : current ? T.brand : T.surfaceTint,
              border: done || current ? "none" : `2px dashed ${T.border}`,
              display: "inline-flex", alignItems: "center", justifyContent: "center", color: "#fff",
              boxShadow: current ? `0 0 0 6px ${T.brandBg}` : "none",
            }}>
              {done && <Check size={14} strokeWidth={3}/>}
              {current && <div style={{ width: 8, height: 8, borderRadius: "50%", background: "#fff" }}/>}
            </div>
            <div style={{ flex: 1, paddingTop: 3 }}>
              <div style={{ fontSize: 13, fontWeight: 700, color: done || current ? T.text : T.textSec }}>{s.label}</div>
              {s.date && <div style={{ fontSize: 11, color: T.textSec, marginTop: 2 }}>{s.date}</div>}
              {s.note && <div style={{ fontSize: 12, color: T.textSec, marginTop: 4, lineHeight: 1.5 }}>{s.note}</div>}
            </div>
          </div>
        );
      })}
    </div>
  );
}

// ═══════════════════════════════════════════════════════════════════════════════════
// AI RECOMMENDATION
// ═══════════════════════════════════════════════════════════════════════════════════
function AIRecommendation({ icon = "🛡️", productName, headline, reasoning, premium, confidence = 4, onAccept, onDismiss }) {
  const { eyeOffset, blinking, ref } = useCasaAnimation({ trackCursor: true, autoBlink: true });
  return (
    <div className="ai-rec" style={{
      padding: 18, background: T.surface, border: `2px solid ${T.brand}`, borderRadius: 14, position: "relative",
    }}>
      <div style={{ position: "absolute", top: -10, left: 18, background: T.brand, color: "#fff", padding: "3px 10px", borderRadius: 999, fontSize: 10, fontWeight: 700, letterSpacing: "0.04em", textTransform: "uppercase", display: "inline-flex", alignItems: "center", gap: 5 }}>
        <Sparkles size={10} strokeWidth={2.4}/>
        Casa recommends
      </div>
      <div className="ai-rec-body" style={{ display: "flex", gap: 14, marginTop: 4 }}>
        <CasaAvatar size={44} state="happy" eyeOffset={eyeOffset} blinking={blinking} innerRef={ref}/>
        <div style={{ flex: 1, minWidth: 0 }}>
          <div style={{ fontSize: 11, fontWeight: 600, color: T.textSec, marginBottom: 2 }}>{productName}</div>
          <div style={{ fontSize: 15, fontWeight: 700, color: T.text, lineHeight: 1.35, marginBottom: 6 }}>{headline}</div>
          <div style={{ fontSize: 13, color: T.textSec, lineHeight: 1.55, marginBottom: 12 }}>{reasoning}</div>
          <div className="ai-rec-meta" style={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 12, flexWrap: "wrap" }}>
            <div>
              <Money amount={premium} size="md"/>
              <span style={{ fontSize: 12, color: T.textSec, marginLeft: 4 }}>/month</span>
            </div>
            <div style={{ display: "flex", alignItems: "center", gap: 4 }}>
              <span style={{ fontSize: 11, color: T.textSec, marginRight: 4 }}>Confidence</span>
              {[1,2,3,4,5].map(d => (
                <div key={d} style={{ width: 6, height: 6, borderRadius: "50%", background: d <= confidence ? T.brand : T.border }}/>
              ))}
            </div>
          </div>
          <div className="ai-rec-actions" style={{ display: "flex", gap: 8, marginTop: 14, flexWrap: "wrap" }}>
            <Btn variant="brand" size="sm" onClick={onAccept} iconRight={ArrowRight}>Accept</Btn>
            <Btn variant="ghost" size="sm" onClick={onDismiss}>Not now</Btn>
          </div>
        </div>
      </div>
    </div>
  );
}

// AIRecommendationCardColored — hero/dashboard variant. Full-bleed colored surface with Casa anchored.
// Use for "campaign" suggestions where the recommendation is a marketable product moment, not a quiet
// sidebar tip. Five surfaces: brand (default Bloom Pink), brown, deepPink, pale, paper.
function AIRecommendationCardColored({
  productName, headline, reasoning, premium, premiumFrequency = "month", coverage,
  confidence = 4, onAccept, onDismiss, surface = "brand", casaState = "happy",
}) {
  const surfaces = {
    brand:    { bg: T.brand,       text: "#fff",   subText: "rgba(255,255,255,0.85)", panel: "rgba(255,255,255,0.14)", panelText: "#fff",   casaColor: "#fff",     confDot: "#fff",   confDim: "rgba(255,255,255,0.3)",  acceptBg: "#fff",   acceptFg: T.brandDeep },
    brown:    { bg: T.brown,       text: "#fff",   subText: "rgba(255,255,255,0.85)", panel: "rgba(255,255,255,0.10)", panelText: "#fff",   casaColor: T.brand,    confDot: T.brand,  confDim: "rgba(255,255,255,0.3)",  acceptBg: T.brand,  acceptFg: "#fff" },
    deepPink: { bg: T.brandDeep,   text: "#fff",   subText: "rgba(255,255,255,0.85)", panel: "rgba(255,255,255,0.14)", panelText: "#fff",   casaColor: "#fff",     confDot: "#fff",   confDim: "rgba(255,255,255,0.3)",  acceptBg: "#fff",   acceptFg: T.brandDeep },
    pale:     { bg: T.brandLight,  text: T.text,   subText: T.textSec,                panel: "rgba(255,255,255,0.6)",  panelText: T.text,   casaColor: T.brand,    confDot: T.brand,  confDim: "rgba(124,12,63,0.18)",   acceptBg: T.brand,  acceptFg: "#fff" },
    paper:    { bg: T.surfaceTint, text: T.text,   subText: T.textSec,                panel: T.surface,                panelText: T.text,   casaColor: T.brand,    confDot: T.brand,  confDim: T.border,                  acceptBg: T.text,   acceptFg: "#fff" },
  };
  const s = surfaces[surface] || surfaces.brand;
  const { eyeOffset, blinking, ref } = useCasaAnimation({ trackCursor: true, autoBlink: true });

  return (
    <div className="ai-rec" style={{
      padding: 24, background: s.bg, color: s.text, borderRadius: 18, position: "relative", overflow: "hidden",
    }}>
      <div style={{ display: "flex", alignItems: "flex-start", gap: 16, marginBottom: 18 }}>
        <CasaAvatar size={64} state={casaState} color={s.casaColor} eyeOffset={eyeOffset} blinking={blinking} innerRef={ref}/>
        <div style={{ flex: 1, minWidth: 0 }}>
          <div style={{ fontSize: 11, fontWeight: 700, color: s.subText, letterSpacing: "0.08em", textTransform: "uppercase", marginBottom: 4 }}>
            Casa recommends
          </div>
          <div style={{ fontSize: 16, fontWeight: 700, color: s.text }}>{productName}</div>
        </div>
        <div style={{ display: "flex", flexDirection: "column", alignItems: "flex-end", gap: 4, flexShrink: 0 }}>
          <div style={{ display: "flex", gap: 3 }}>
            {[1, 2, 3, 4, 5].map(n => (
              <div key={n} style={{
                width: 5, height: 5, borderRadius: 999,
                background: n <= confidence ? s.confDot : s.confDim,
              }}/>
            ))}
          </div>
          <span style={{ fontSize: 10, fontWeight: 600, color: s.subText, letterSpacing: "0.04em", textTransform: "uppercase" }}>Confidence</span>
        </div>
      </div>

      <div style={{ fontFamily: FONT_DISPLAY, fontSize: 24, fontWeight: 400, lineHeight: 1.2, letterSpacing: "-0.01em", marginBottom: 8 }}>{headline}</div>
      <div style={{ fontSize: 13, color: s.subText, lineHeight: 1.55, marginBottom: 16 }}>{reasoning}</div>

      {(premium != null || coverage != null) && (
        <div className="ai-rec-meta" style={{
          display: "flex", gap: 16, padding: "12px 14px", background: s.panel, borderRadius: 14,
          color: s.panelText, marginBottom: 16, flexWrap: "wrap",
        }}>
          {premium != null && (
            <div>
              <div style={{ fontSize: 10, fontWeight: 600, opacity: 0.78, letterSpacing: "0.04em", textTransform: "uppercase" }}>Premium</div>
              <div style={{ fontFamily: FONT_DISPLAY, fontSize: 20, fontWeight: 400, marginTop: 2 }}>₦{premium.toLocaleString()}<span style={{ fontSize: 12, opacity: 0.78, marginLeft: 4 }}>/{premiumFrequency}</span></div>
            </div>
          )}
          {coverage != null && (
            <div>
              <div style={{ fontSize: 10, fontWeight: 600, opacity: 0.78, letterSpacing: "0.04em", textTransform: "uppercase" }}>Cover</div>
              <div style={{ fontFamily: FONT_DISPLAY, fontSize: 20, fontWeight: 400, marginTop: 2 }}>₦{coverage >= 1_000_000 ? `${(coverage/1_000_000).toFixed(coverage % 1_000_000 === 0 ? 0 : 1)}M` : coverage.toLocaleString()}</div>
            </div>
          )}
        </div>
      )}

      <div className="ai-rec-actions" style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
        <button onClick={onAccept} style={{
          padding: "11px 20px", background: s.acceptBg, color: s.acceptFg, border: "none", borderRadius: 999,
          fontFamily: FONT, fontSize: 14, fontWeight: 600, cursor: "pointer",
          display: "inline-flex", alignItems: "center", gap: 7,
        }}>
          Accept <ArrowRight size={16} strokeWidth={2.3}/>
        </button>
        <button onClick={onDismiss} style={{
          padding: "11px 20px", background: "transparent", color: s.text, border: "none", borderRadius: 999,
          fontFamily: FONT, fontSize: 14, fontWeight: 600, cursor: "pointer", opacity: 0.78,
        }}>
          Not now
        </button>
      </div>
    </div>
  );
}

// ═══════════════════════════════════════════════════════════════════════════════════
// SETTLEMENT SUMMARY (partner-facing — Moniepoint / M-KOPA pitch)
// ═══════════════════════════════════════════════════════════════════════════════════
function SettlementSummary({ period, grossVolume, partnerFee, casavaFee, taxes, netToPartner, status = "pending", paidDate }) {
  const statusConfig = {
    pending: { color: "amber", label: "Pending" },
    paid: { color: "green", label: "Paid" },
  }[status];
  return (
    <div style={{ padding: 24, background: T.surface, border: `1px solid ${T.border}`, borderRadius: 14 }}>
      <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 18, flexWrap: "wrap", gap: 8 }}>
        <div>
          <Eyebrow>Settlement period</Eyebrow>
          <div style={{ fontSize: 17, fontWeight: 700 }}>{period}</div>
        </div>
        <Pill color={statusConfig.color}>{statusConfig.label}{paidDate && status === "paid" ? ` · ${paidDate}` : ""}</Pill>
      </div>
      <div style={{ marginBottom: 14 }}>
        <Eyebrow>Net to partner</Eyebrow>
        <Money amount={netToPartner} size="xxl"/>
      </div>
      <div style={{ paddingTop: 14, borderTop: `1px solid ${T.border}` }}>
        <DisclosureRow label="Gross volume" value={`₦${grossVolume.toLocaleString()}`}/>
        <DisclosureRow label="Partner commission" value={`+ ₦${partnerFee.toLocaleString()}`} accent={T.success}/>
        <DisclosureRow label="Casava platform fee" value={`− ₦${casavaFee.toLocaleString()}`}/>
        <DisclosureRow label="Taxes & withholding" value={`− ₦${taxes.toLocaleString()}`} ruled={false}/>
      </div>
    </div>
  );
}

// ═══════════════════════════════════════════════════════════════════════════════════
// API STATUS CARD
// ═══════════════════════════════════════════════════════════════════════════════════
function APIStatusCard({ name, endpoint, status = "healthy", lastSync, latencyMs, errors24h = 0, requests24h }) {
  const config = {
    healthy: { color: T.success, label: "Healthy" },
    degraded: { color: T.amber, label: "Degraded" },
    down: { color: T.danger, label: "Down" },
  }[status];
  return (
    <div style={{ padding: 18, background: T.surface, border: `1px solid ${T.border}`, borderRadius: 14 }}>
      <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 14, gap: 8 }}>
        <div style={{ minWidth: 0 }}>
          <div style={{ fontSize: 14, fontWeight: 700, color: T.text }}>{name}</div>
          {endpoint && <div style={{ fontSize: 11, color: T.textTer, fontFamily: "ui-monospace, monospace", marginTop: 2, overflow: "hidden", textOverflow: "ellipsis" }}>{endpoint}</div>}
        </div>
        <div style={{ display: "inline-flex", alignItems: "center", gap: 6, padding: "5px 10px", borderRadius: 999, background: T.surfaceTint, flexShrink: 0 }}>
          <div style={{ width: 7, height: 7, borderRadius: "50%", background: config.color, animation: status === "healthy" ? "pulse 2s infinite" : "none" }}/>
          <span style={{ fontSize: 11, fontWeight: 700, color: config.color }}>{config.label}</span>
        </div>
      </div>
      <div style={{ display: "grid", gridTemplateColumns: "repeat(3, 1fr)", gap: 10, paddingTop: 12, borderTop: `1px solid ${T.border}` }}>
        <div>
          <Eyebrow mb={4}>Last sync</Eyebrow>
          <div style={{ fontSize: 13, fontWeight: 600 }}>{lastSync}</div>
        </div>
        <div>
          <Eyebrow mb={4}>p50 latency</Eyebrow>
          <div style={{ fontSize: 13, fontWeight: 600, fontFamily: "ui-monospace, monospace" }}>{latencyMs}ms</div>
        </div>
        <div>
          <Eyebrow mb={4}>Errors 24h</Eyebrow>
          <div style={{ fontSize: 13, fontWeight: 600, fontFamily: "ui-monospace, monospace", color: errors24h > 0 ? T.danger : T.text }}>
            {errors24h}{requests24h ? ` / ${(requests24h/1000).toFixed(1)}k` : ""}
          </div>
        </div>
      </div>
    </div>
  );
}

// ═══════════════════════════════════════════════════════════════════════════════════
// PARTNER B2B EXTENSIONS (added in design system v3)
// ═══════════════════════════════════════════════════════════════════════════════════
function SparklinePath({ points = [], color = T.brand }) {
  if (points.length < 2) return null;
  const min = Math.min(...points), max = Math.max(...points);
  const range = max - min || 1;
  const w = 100, h = 32;
  const stepX = w / (points.length - 1);
  const path = points.map((p, i) => `${i === 0 ? "M" : "L"} ${(i * stepX).toFixed(1)} ${(h - ((p - min) / range) * h).toFixed(1)}`).join(" ");
  return (
    <svg viewBox={`0 0 ${w} ${h}`} preserveAspectRatio="none" style={{ width: "100%", height: "100%", display: "block" }}>
      <path d={path} stroke={color} strokeWidth="1.5" fill="none" strokeLinecap="round" strokeLinejoin="round"/>
    </svg>
  );
}

function PartnerKPICard({ label, value, delta, deltaDirection = "up", sparkline, accent = T.text, helpText }) {
  const deltaColor = deltaDirection === "up" ? T.success : deltaDirection === "down" ? T.danger : T.textSec;
  const deltaSymbol = deltaDirection === "up" ? "↑" : deltaDirection === "down" ? "↓" : "·";
  return (
    <div style={{ padding: 18, background: T.surface, border: `1px solid ${T.border}`, borderRadius: 14 }}>
      <div style={{ fontSize: 11, fontWeight: 700, color: T.textSec, letterSpacing: "0.08em", textTransform: "uppercase", marginBottom: 8 }}>{label}</div>
      <div style={{ fontFamily: FONT_DISPLAY, fontSize: 32, fontWeight: 400, letterSpacing: "-0.02em", color: accent, lineHeight: 1, marginBottom: 8, fontVariantNumeric: "tabular-nums" }}>{value}</div>
      <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
        {delta != null && <div style={{ fontSize: 12, fontWeight: 700, color: deltaColor }}>{deltaSymbol} {delta}</div>}
        {helpText && <div style={{ fontSize: 11, color: T.textSec }}>{helpText}</div>}
      </div>
      {sparkline && <div style={{ marginTop: 10, height: 32 }}><SparklinePath points={sparkline} color={accent}/></div>}
    </div>
  );
}

function APIKeyCard({ name, keyValue, masked = true, lastUsed, environment = "production", onCopy, onReveal, onRevoke, status = "active" }) {
  const envColors = {
    production: { bg: T.brandLight, color: T.brandDeep },
    staging: { bg: T.amberBg, color: T.amberText },
    test: { bg: T.surfaceTint, color: T.textSec },
    sandbox: { bg: T.surfaceTint, color: T.textSec },
    live: { bg: T.brandLight, color: T.brandDeep },
  };
  const e = envColors[environment] || envColors.production;
  const display = masked ? `${keyValue.slice(0, 6)}${"•".repeat(20)}${keyValue.slice(-4)}` : keyValue;
  const statusColors = { active: T.success, revoked: T.danger, expiring: T.amber };
  return (
    <div style={{ padding: 18, background: T.surface, border: `1px solid ${T.border}`, borderRadius: 14 }}>
      <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 12, flexWrap: "wrap", gap: 8 }}>
        <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
          <div style={{ fontSize: 14, fontWeight: 700, color: T.text }}>{name}</div>
          <div style={{ display: "inline-flex", padding: "2px 8px", borderRadius: 4, fontSize: 10, fontWeight: 700, letterSpacing: "0.06em", textTransform: "uppercase", background: e.bg, color: e.color }}>{environment}</div>
        </div>
        <div style={{ display: "flex", alignItems: "center", gap: 6 }}>
          <div style={{ width: 6, height: 6, borderRadius: "50%", background: statusColors[status] }}/>
          <div style={{ fontSize: 11, fontWeight: 600, color: T.textSec, textTransform: "capitalize" }}>{status}</div>
        </div>
      </div>
      <div style={{ padding: "10px 12px", background: T.surfaceTint, borderRadius: 8, fontFamily: "ui-monospace, SFMono-Regular, monospace", fontSize: 12, color: T.text, letterSpacing: "0.02em", marginBottom: 12, wordBreak: "break-all" }}>
        {display}
      </div>
      <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", flexWrap: "wrap", gap: 8 }}>
        {lastUsed && <div style={{ fontSize: 11, color: T.textSec }}>Last used <span style={{ color: T.text, fontWeight: 600 }}>{lastUsed}</span></div>}
        <div style={{ display: "flex", gap: 6 }}>
          <button onClick={onReveal} style={{ padding: "6px 12px", borderRadius: 6, border: `1px solid ${T.border}`, background: T.surface, fontSize: 11, fontWeight: 600, color: T.text, cursor: "pointer", fontFamily: FONT }}>{masked ? "Reveal" : "Hide"}</button>
          <button onClick={onCopy} style={{ padding: "6px 12px", borderRadius: 6, border: `1px solid ${T.border}`, background: T.surface, fontSize: 11, fontWeight: 600, color: T.text, cursor: "pointer", fontFamily: FONT }}>Copy</button>
          <button onClick={onRevoke} style={{ padding: "6px 12px", borderRadius: 6, border: "none", background: T.dangerBg, fontSize: 11, fontWeight: 600, color: T.dangerText, cursor: "pointer", fontFamily: FONT }}>Revoke</button>
        </div>
      </div>
    </div>
  );
}

function WebhookEventLog({ events = [], onRetry }) {
  const statusColors = {
    delivered: { bg: T.successBg, color: T.successText, border: T.successBorder },
    failed: { bg: T.dangerBg, color: T.dangerText, border: T.dangerBorder },
    pending: { bg: T.amberBg, color: T.amberText, border: T.amberBorder },
    retrying: { bg: T.surfaceTint, color: T.textSec, border: T.border },
  };
  return (
    <div style={{ background: T.surface, border: `1px solid ${T.border}`, borderRadius: 14, overflow: "hidden" }}>
      <div style={{ display: "flex", padding: "10px 16px", background: T.surfaceTint, fontSize: 10, fontWeight: 700, color: T.textSec, letterSpacing: "0.08em", textTransform: "uppercase", borderBottom: `1px solid ${T.border}` }}>
        <div style={{ flex: "0 0 110px" }}>Time</div>
        <div style={{ flex: 1 }}>Event</div>
        <div style={{ flex: "0 0 80px", textAlign: "center" }}>Status</div>
        <div style={{ flex: "0 0 60px", textAlign: "right" }}>Code</div>
        <div style={{ flex: "0 0 70px", textAlign: "right" }}>Attempts</div>
        <div style={{ flex: "0 0 80px" }}/>
      </div>
      {events.map((ev, i) => {
        const sc = statusColors[ev.status] || statusColors.pending;
        return (
          <div key={i} style={{ display: "flex", alignItems: "center", padding: "10px 16px", borderBottom: i === events.length - 1 ? "none" : `1px solid ${T.border}`, fontSize: 12 }}>
            <div style={{ flex: "0 0 110px", color: T.textSec, fontFamily: "ui-monospace, monospace", fontSize: 11 }}>{ev.timestamp}</div>
            <div style={{ flex: 1, minWidth: 0 }}>
              <div style={{ fontWeight: 700, color: T.text, whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>{ev.event}</div>
              <div style={{ color: T.textSec, fontFamily: "ui-monospace, monospace", fontSize: 10, whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>{ev.url}</div>
            </div>
            <div style={{ flex: "0 0 80px", display: "flex", justifyContent: "center" }}>
              <div style={{ display: "inline-flex", padding: "2px 8px", borderRadius: 4, background: sc.bg, color: sc.color, border: `1px solid ${sc.border}`, fontSize: 10, fontWeight: 700, letterSpacing: "0.04em", textTransform: "uppercase" }}>{ev.status}</div>
            </div>
            <div style={{ flex: "0 0 60px", textAlign: "right", fontFamily: "ui-monospace, monospace", color: ev.code >= 400 ? T.danger : T.text, fontWeight: 700 }}>{ev.code || "—"}</div>
            <div style={{ flex: "0 0 70px", textAlign: "right", color: T.textSec, fontVariantNumeric: "tabular-nums" }}>{ev.attempts || 1}{ev.maxAttempts ? `/${ev.maxAttempts}` : ""}</div>
            <div style={{ flex: "0 0 80px", textAlign: "right" }}>
              {ev.status === "failed" && (
                <button onClick={() => onRetry?.(ev)} style={{ padding: "4px 10px", borderRadius: 4, border: `1px solid ${T.border}`, background: T.surface, fontSize: 10, fontWeight: 700, color: T.text, cursor: "pointer", fontFamily: FONT }}>Retry</button>
              )}
            </div>
          </div>
        );
      })}
    </div>
  );
}

function PartnerHealthMatrix({ partners = [], integrations = [], status = {} }) {
  const statusColors = { healthy: T.success, degraded: T.amber, down: T.danger, paused: T.textTer, missing: T.surfaceTint };
  return (
    <div style={{ background: T.surface, border: `1px solid ${T.border}`, borderRadius: 14, overflow: "hidden" }}>
      <div style={{ overflowX: "auto" }}>
        <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 12 }}>
          <thead>
            <tr style={{ background: T.surfaceTint }}>
              <th style={{ padding: "10px 14px", textAlign: "left", fontSize: 10, fontWeight: 700, color: T.textSec, letterSpacing: "0.08em", textTransform: "uppercase" }}>Integration</th>
              {partners.map(p => (
                <th key={p} style={{ padding: "10px 8px", fontSize: 10, fontWeight: 700, color: T.textSec, letterSpacing: "0.06em", textTransform: "uppercase", textAlign: "center" }}>{p}</th>
              ))}
            </tr>
          </thead>
          <tbody>
            {integrations.map((integ, i) => (
              <tr key={integ} style={{ borderTop: i === 0 ? "none" : `1px solid ${T.border}` }}>
                <td style={{ padding: "12px 14px", fontWeight: 600, color: T.text }}>{integ}</td>
                {partners.map(p => {
                  const st = status[`${p}:${integ}`] || "missing";
                  return (
                    <td key={p} style={{ padding: "12px 8px", textAlign: "center" }}>
                      <span title={`${p} · ${integ}: ${st}`} style={{ display: "inline-block", width: 10, height: 10, borderRadius: "50%", background: statusColors[st], border: st === "missing" ? `1px solid ${T.border}` : "none" }}/>
                    </td>
                  );
                })}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      <div style={{ display: "flex", gap: 14, padding: "10px 14px", borderTop: `1px solid ${T.border}`, fontSize: 11, color: T.textSec, background: T.surfaceTint, flexWrap: "wrap" }}>
        {[["healthy","Healthy"], ["degraded","Degraded"], ["down","Down"], ["paused","Paused"], ["missing","Not configured"]].map(([k, label]) => (
          <div key={k} style={{ display: "flex", alignItems: "center", gap: 6 }}>
            <span style={{ width: 8, height: 8, borderRadius: "50%", background: statusColors[k], border: k === "missing" ? `1px solid ${T.border}` : "none" }}/>
            {label}
          </div>
        ))}
      </div>
    </div>
  );
}

function RemittanceSchedule({ schedule = [] }) {
  return (
    <div style={{ background: T.surface, border: `1px solid ${T.border}`, borderRadius: 14, overflow: "hidden" }}>
      <div style={{ padding: "14px 18px", borderBottom: `1px solid ${T.border}` }}>
        <div style={{ fontSize: 10, fontWeight: 700, color: T.textSec, letterSpacing: "0.08em", textTransform: "uppercase", marginBottom: 4 }}>Upcoming remittances</div>
        <div style={{ fontSize: 13, color: T.textSec }}>Scheduled payouts to your nominated account</div>
      </div>
      {schedule.map((row, i) => (
        <div key={i} style={{ display: "flex", alignItems: "center", padding: "14px 18px", borderBottom: i === schedule.length - 1 ? "none" : `1px solid ${T.border}`, gap: 12 }}>
          <div style={{ width: 64, flexShrink: 0, textAlign: "center" }}>
            <div style={{ fontSize: 10, fontWeight: 700, color: T.brand, letterSpacing: "0.08em", textTransform: "uppercase" }}>{row.month}</div>
            <div style={{ fontFamily: FONT_DISPLAY, fontSize: 26, fontWeight: 400, letterSpacing: "-0.02em", color: T.text, lineHeight: 1, marginTop: 2 }}>{row.day}</div>
          </div>
          <div style={{ flex: 1, minWidth: 0 }}>
            <div style={{ fontSize: 13, fontWeight: 700, color: T.text, marginBottom: 2 }}>{row.label}</div>
            <div style={{ fontSize: 11, color: T.textSec }}>{row.policies} policies · gross ₦{row.gross.toLocaleString()}</div>
          </div>
          <div style={{ textAlign: "right", flexShrink: 0 }}>
            <div style={{ fontFamily: FONT_DISPLAY, fontSize: 22, fontWeight: 400, letterSpacing: "-0.02em", color: T.text, lineHeight: 1, fontVariantNumeric: "tabular-nums" }}>₦{row.net.toLocaleString()}</div>
            <div style={{ fontSize: 10, fontWeight: 700, color: T.textSec, letterSpacing: "0.06em", textTransform: "uppercase", marginTop: 4 }}>Net to you</div>
          </div>
        </div>
      ))}
    </div>
  );
}

function AskCasa({ onClick, casaState = "idle" }) {
  const { eyeOffset, blinking, ref } = useCasaAnimation({ trackCursor: true, autoBlink: true });
  const [hoverState, setHoverState] = useState(null);
  const [popping, setPopping] = useState(false);
  const [waving, setWaving] = useState(false);

  // Periodic wave to draw attention every ~22s
  useEffect(() => {
    const t = setInterval(() => {
      setWaving(true);
      setTimeout(() => setWaving(false), 1400);
    }, 22000);
    return () => clearInterval(t);
  }, []);

  const effectiveState = hoverState || casaState;
  const handleClick = (e) => {
    setPopping(true);
    setHoverState("celebrating");
    setTimeout(() => { setPopping(false); setHoverState(null); }, 500);
    onClick?.(e);
  };

  return (
    <button
      onClick={handleClick}
      aria-label="Ask Casa"
      onMouseEnter={() => setHoverState("winking")}
      onMouseLeave={() => setHoverState(null)}
      className={`${popping ? "casa-pop" : "casa-idle"}`}
      style={{
        position: "fixed", bottom: 24, right: 24, zIndex: 100,
        width: 64, height: 64, borderRadius: "50%", background: "#fff",
        border: "none", cursor: "pointer", padding: 0,
        boxShadow: hoverState ? "0 16px 36px rgba(255,20,125,0.45), 0 4px 8px rgba(0,0,0,0.10)" : "0 12px 28px rgba(255,20,125,0.32), 0 4px 8px rgba(0,0,0,0.08)",
        display: "flex", alignItems: "center", justifyContent: "center",
        transition: "box-shadow 0.25s",
      }}>
      <span className={waving ? "casa-wave" : ""} style={{ display: "inline-flex" }}>
        <CasaAvatar size={56} state={effectiveState} eyeOffset={eyeOffset} blinking={blinking} innerRef={ref}/>
      </span>
    </button>
  );
}

// CasaChatSheet — slide-in chat panel triggered by the AskCasa floating button.
// Mock conversational AI for the demo: keyword-matched replies, suggested prompts,
// realistic typing delay. Production replaces simulateCasaResponse with the LLM streaming endpoint.
function CasaChatSheet({ open, onClose }) {
  const [messages, setMessages] = useState([
    { from: "casa", text: "Hi Bunmi 👋 I'm Casa. Ask me about your portfolio, partners, or claims. Or pick a prompt below to get started.", state: "happy" },
  ]);
  const [input, setInput] = useState("");
  const [typing, setTyping] = useState(false);
  const scrollRef = useRef(null);
  const { eyeOffset, blinking, ref: avatarRef } = useCasaAnimation({ trackCursor: true, autoBlink: true });

  const suggestedPrompts = [
    "How's my portfolio doing this week?",
    "Which partner has the highest claims ratio?",
    "Show me underperforming products",
    "What's my exposure on Credit Life?",
  ];

  useEffect(() => {
    if (scrollRef.current) scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
  }, [messages, typing]);

  const send = (text) => {
    if (!text.trim()) return;
    setMessages(m => [...m, { from: "user", text }]);
    setInput("");
    setTyping(true);
    setTimeout(() => {
      setTyping(false);
      setMessages(m => [...m, { from: "casa", text: simulateCasaResponse(text), state: "happy" }]);
    }, 900 + Math.random() * 600);
  };

  if (!open) return null;
  return (
    <>
      <div onClick={onClose} style={{
        position: "fixed", inset: 0, background: "rgba(28,18,14,0.32)", zIndex: 1050,
        animation: "fadeIn 0.18s ease-out",
      }}/>
      <aside style={{
        position: "fixed", top: 0, right: 0, bottom: 0, width: "min(440px, 100vw)", zIndex: 1051,
        background: T.surface, boxShadow: "-12px 0 32px rgba(0,0,0,0.18)",
        display: "flex", flexDirection: "column",
        animation: "casaSheetIn 0.28s cubic-bezier(0.2, 0.8, 0.2, 1)",
      }}>
        {/* Header */}
        <div style={{ padding: "16px 20px", borderBottom: `1px solid ${T.border}`, display: "flex", alignItems: "center", gap: 12 }}>
          <CasaAvatar size={40} state="happy" eyeOffset={eyeOffset} blinking={blinking} innerRef={avatarRef}/>
          <div style={{ flex: 1, minWidth: 0 }}>
            <div style={{ fontSize: 14, fontWeight: 700, color: T.text }}>Casa</div>
            <div style={{ fontSize: 11, color: T.successText, display: "flex", alignItems: "center", gap: 5 }}>
              <span style={{ width: 6, height: 6, borderRadius: "50%", background: T.success, animation: "pulse 2s infinite" }}/>
              Online · powered by Casava AI
            </div>
          </div>
          <button onClick={onClose} style={{ background: T.surfaceTint, border: "none", borderRadius: "50%", width: 32, height: 32, display: "inline-flex", alignItems: "center", justifyContent: "center", cursor: "pointer", color: T.textSec }}><X size={16}/></button>
        </div>

        {/* Messages */}
        <div ref={scrollRef} style={{ flex: 1, overflowY: "auto", padding: 20, display: "flex", flexDirection: "column", gap: 14 }}>
          {messages.map((m, i) => m.from === "user" ? (
            <div key={i} style={{ alignSelf: "flex-end", maxWidth: "82%" }} className="stagger-fadeIn">
              <div style={{ background: T.brand, color: "#fff", padding: "10px 14px", borderRadius: "16px 16px 4px 16px", fontSize: 13.5, lineHeight: 1.5, fontFamily: FONT }}>{m.text}</div>
            </div>
          ) : (
            <div key={i} style={{ display: "flex", gap: 10, alignSelf: "flex-start", maxWidth: "92%" }} className="stagger-fadeIn">
              <div style={{ flexShrink: 0, marginTop: 2 }}>
                <CasaAvatar size={28} state={m.state || "idle"}/>
              </div>
              <div style={{ background: T.surfaceTint, padding: "10px 14px", borderRadius: "16px 16px 16px 4px", fontSize: 13.5, lineHeight: 1.55, fontFamily: FONT, color: T.text, whiteSpace: "pre-wrap" }}>{m.text}</div>
            </div>
          ))}
          {typing && (
            <div style={{ display: "flex", gap: 10, alignSelf: "flex-start" }}>
              <div style={{ flexShrink: 0, marginTop: 2 }}><CasaAvatar size={28} state="thinking"/></div>
              <div style={{ background: T.surfaceTint, padding: "12px 16px", borderRadius: "16px 16px 16px 4px", display: "flex", gap: 4 }}>
                <span style={{ width: 5, height: 5, borderRadius: "50%", background: T.textSec }} className="casa-dot-1"/>
                <span style={{ width: 5, height: 5, borderRadius: "50%", background: T.textSec }} className="casa-dot-2"/>
                <span style={{ width: 5, height: 5, borderRadius: "50%", background: T.textSec }} className="casa-dot-3"/>
              </div>
            </div>
          )}
        </div>

        {/* Suggested prompts (only when conversation is fresh) */}
        {messages.length <= 1 && (
          <div style={{ padding: "0 20px 12px", display: "flex", flexDirection: "column", gap: 6 }}>
            <div style={{ fontSize: 10, fontWeight: 700, color: T.textSec, letterSpacing: "0.06em", textTransform: "uppercase", marginBottom: 4 }}>Try asking</div>
            {suggestedPrompts.map(p => (
              <button key={p} onClick={() => send(p)} style={{
                padding: "9px 12px", background: T.surfaceTint, border: `1px solid transparent`,
                borderRadius: 10, cursor: "pointer", textAlign: "left", fontFamily: FONT,
                fontSize: 12.5, fontWeight: 500, color: T.text,
              }}
                onMouseEnter={e => (e.currentTarget.style.background = T.surface, e.currentTarget.style.borderColor = T.border)}
                onMouseLeave={e => (e.currentTarget.style.background = T.surfaceTint, e.currentTarget.style.borderColor = "transparent")}>
                {p}
              </button>
            ))}
          </div>
        )}

        {/* Input */}
        <div style={{ padding: 16, borderTop: `1px solid ${T.border}`, display: "flex", gap: 8 }}>
          <input
            type="text"
            autoComplete="off"
            value={input}
            onChange={e => setInput(e.target.value)}
            onKeyDown={e => e.key === "Enter" && send(input)}
            placeholder="Ask Casa anything..."
            style={{
              flex: 1, padding: "10px 14px", borderRadius: 999,
              border: `1px solid ${T.border}`, background: T.surface,
              fontSize: 13, fontFamily: FONT, color: T.text, outline: "none",
            }}
          />
          <button onClick={() => send(input)} disabled={!input.trim()} style={{
            background: input.trim() ? T.brand : T.surfaceTint, color: input.trim() ? "#fff" : T.textSec,
            border: "none", borderRadius: 999, width: 40, height: 40,
            display: "inline-flex", alignItems: "center", justifyContent: "center",
            cursor: input.trim() ? "pointer" : "not-allowed", flexShrink: 0,
          }}>
            <ArrowUpRight size={18} strokeWidth={2.5}/>
          </button>
        </div>
      </aside>
    </>
  );
}

function simulateCasaResponse(text) {
  const t = text.toLowerCase();
  if (t.includes("portfolio") || t.includes("week") || t.includes("doing")) {
    return "Your portfolio crossed ₦42M in active cover this month — up 22% on March. The headline movements:\n\n• Credit Life is still 67% of premium volume\n• Device Insurance grew fastest (+38% MoM) thanks to M-KOPA upgrades\n• Claims ratio dropped to 14%, down from 17% last month\n\nWant me to break out by partner channel?";
  }
  if (t.includes("partner") || t.includes("claims ratio") || t.includes("ratio")) {
    return "By claims ratio, your partners rank:\n\n1. Moniepoint — 11% (best, embedded fraud filters help)\n2. M-KOPA — 13% (within target band)\n3. Kuda — 16%\n4. Samsung — 18% (slightly elevated, mostly device theft claims)\n5. PalmPay — 24% (flagged for review)\n\nPalmPay is sitting above the 20% threshold. Want me to draft a check-in note to their integrations lead?";
  }
  if (t.includes("underperform") || t.includes("product")) {
    return "Two products are below conversion targets:\n\n• Group Health — sits at 0.4% offer-to-bind, target is 2%. The funnel breaks at the medical declarations step.\n• Keyman Insurance — only 12 policies sold all quarter, mostly because we're not surfacing it on partner checkout flows.\n\nFunds Protection and Building Insurance are both hitting their numbers.";
  }
  if (t.includes("credit life") || t.includes("exposure")) {
    return "Credit Life exposure right now:\n\n• Active policies: 847\n• Total sum assured: ₦2.4B\n• Average sum per policy: ₦2.83M\n• Largest single exposure: ₦30M (Olatunde Akinola, Moniepoint SME loan)\n• Concentration: 58% of policies are SME loans, 42% are working-capital\n\nReinsurance treaty covers anything above ₦5M per life. Want the reinsurance utilization breakdown?";
  }
  if (t.includes("hi") || t.includes("hello") || t.includes("hey")) {
    return "Hey! What can I help you dig into today?";
  }
  return "Got it. In production I'd answer that with live portfolio data — for the demo I'm only wired up to a few sample prompts. Try one of the suggestions, or ask about portfolio, partners, products, or Credit Life exposure.";
}

// NotificationsDropdown — bell icon panel showing recent platform events.
function NotificationsDropdown({ open, onClose, onNavigate }) {
  const ref = useRef(null);
  useEffect(() => {
    if (!open) return;
    const onDoc = (e) => { if (ref.current && !ref.current.contains(e.target)) onClose(); };
    document.addEventListener("mousedown", onDoc);
    return () => document.removeEventListener("mousedown", onDoc);
  }, [open, onClose]);
  if (!open) return null;
  const notifications = [
    { id: 1, kind: "claim", title: "Claim approved", body: "CLM-2026-0014 · Tunde Salami · ₦300,000 disbursed", time: "12 min ago", action: () => onNavigate("claim-detail", { id: "cl1" }) },
    { id: 2, kind: "alert", title: "Webhook delivery failed", body: "Samsung integration · 3 retries · 504 timeout", time: "34 min ago", action: () => onNavigate("developers", { section: "webhooks" }) },
    { id: 3, kind: "policy", title: "New policy purchased", body: "CL-2026-0094 · Hauwa Mohammed · ₦3,100/mo", time: "1 hour ago", action: () => onNavigate("policy-detail", { id: "p7" }) },
    { id: 4, kind: "claim", title: "New claim submitted", body: "CLM-2026-0023 · Adaeze Nnamdi · awaiting review", time: "3 hours ago", action: () => onNavigate("claim-detail", { id: "cl3" }) },
    { id: 5, kind: "settlement", title: "April settlement ready", body: "Net ₦1,793,163 to your nominated account on May 02", time: "Yesterday", action: () => onNavigate("settlements") },
  ];
  const kindIcon = { claim: FileCheck2, alert: AlertTriangle, policy: Shield, settlement: WalletIcon };
  const kindColor = { claim: T.success, alert: T.danger, policy: T.brand, settlement: T.brown };
  return (
    <div ref={ref} style={{
      position: "absolute", top: 48, right: 0, zIndex: 60,
      width: 380, maxWidth: "calc(100vw - 32px)", background: T.surface,
      border: `1px solid ${T.border}`, borderRadius: 14,
      boxShadow: "0 20px 40px rgba(0,0,0,0.14)", overflow: "hidden",
      animation: "slideUp 0.18s ease-out",
    }}>
      <div style={{ padding: "14px 18px", borderBottom: `1px solid ${T.border}`, display: "flex", alignItems: "center", justifyContent: "space-between" }}>
        <div style={{ fontSize: 14, fontWeight: 700 }}>Notifications</div>
        <button style={{ background: "transparent", border: "none", color: T.brand, fontSize: 11, fontWeight: 600, cursor: "pointer", fontFamily: FONT }}>Mark all as read</button>
      </div>
      <div style={{ maxHeight: 420, overflowY: "auto" }}>
        {notifications.map(n => {
          const Icon = kindIcon[n.kind];
          return (
            <button key={n.id} onClick={() => { n.action?.(); onClose(); }} style={{
              width: "100%", padding: "12px 18px", border: "none", background: "transparent",
              display: "flex", gap: 12, alignItems: "flex-start", cursor: "pointer", fontFamily: FONT,
              textAlign: "left", borderBottom: `1px solid ${T.border}`,
            }}
              onMouseEnter={e => e.currentTarget.style.background = T.surfaceTint}
              onMouseLeave={e => e.currentTarget.style.background = "transparent"}>
              <div style={{ width: 32, height: 32, borderRadius: "50%", background: T.surfaceTint, display: "inline-flex", alignItems: "center", justifyContent: "center", flexShrink: 0 }}>
                <Icon size={14} color={kindColor[n.kind]} strokeWidth={2.4}/>
              </div>
              <div style={{ flex: 1, minWidth: 0 }}>
                <div style={{ fontSize: 13, fontWeight: 600, color: T.text }}>{n.title}</div>
                <div style={{ fontSize: 12, color: T.textSec, marginTop: 2, lineHeight: 1.5 }}>{n.body}</div>
                <div style={{ fontSize: 11, color: T.textTer, marginTop: 4 }}>{n.time}</div>
              </div>
            </button>
          );
        })}
      </div>
      <div style={{ padding: "10px 18px", borderTop: `1px solid ${T.border}`, background: T.surfaceTint, textAlign: "center" }}>
        <button style={{ background: "transparent", border: "none", color: T.text, fontSize: 12, fontWeight: 600, cursor: "pointer", fontFamily: FONT }}>View all activity</button>
      </div>
    </div>
  );
}

// ═══════════════════════════════════════════════════════════════════════════════════
// PRODUCT ICONS — hand-crafted SVG illustrations, one per product.
// Each tells the insurance story in a single visual: what's protected, not the
// generic asset (credit card ≠ credit life insurance).
// Usage: <ProductIcon productId="credit-life" size={48}/> or <ProductIcon product={PRODUCTS[0]} chip size={56}/>
// `chip` wraps the icon in the colored rounded-square chip used on cards/tables.
// ═══════════════════════════════════════════════════════════════════════════════════
const PRODUCT_ICON_PATHS = {
  // ─── Credit Life ─── shield containing a heart. Life is what's protected.
  // Anchor: heart (life). Protection: shield around it.
  "credit-life": (color = "currentColor") => (
    <svg viewBox="0 0 48 48" fill="none" xmlns="http://www.w3.org/2000/svg">
      {/* Shield outline + fill */}
      <path d="M24 4 L40 9 L40 24 C40 33 33 40 24 44 C15 40 8 33 8 24 L8 9 Z"
        fill={color} opacity="0.95"/>
      {/* Heart inside the shield */}
      <path d="M24 32 C24 32 14 26 14 19 C14 16 16 14 19 14 C21 14 23 15 24 17 C25 15 27 14 29 14 C32 14 34 16 34 19 C34 26 24 32 24 32 Z"
        fill={color === "#fff" ? "rgba(255,255,255,0)" : "#fff"}
        stroke={color === "#fff" ? "rgba(255,255,255,0)" : "none"}
        opacity={color === "#fff" ? 0 : 1}/>
      {/* Heart cutout — when on chip (white), we want the heart to "knock out" of the shield */}
      {color === "#fff" && (
        <path d="M24 32 C24 32 14 26 14 19 C14 16 16 14 19 14 C21 14 23 15 24 17 C25 15 27 14 29 14 C32 14 34 16 34 19 C34 26 24 32 24 32 Z"
          fill="rgba(0,0,0,0.18)"/>
      )}
    </svg>
  ),

  // ─── Device Insurance ─── phone with shield on corner.
  // Anchor: phone. Protection: shield wedge.
  "device": (color = "currentColor") => (
    <svg viewBox="0 0 48 48" fill="none" xmlns="http://www.w3.org/2000/svg">
      {/* Phone body */}
      <rect x="11" y="6" width="22" height="36" rx="4" fill={color} opacity="0.95"/>
      {/* Screen — knock-out so it reads as a phone screen */}
      <rect x="14" y="11" width="16" height="22" rx="1.5"
        fill={color === "#fff" ? "rgba(0,0,0,0.18)" : "#fff"} opacity={color === "#fff" ? 1 : 0.95}/>
      {/* Home button */}
      <circle cx="22" cy="38" r="1.6"
        fill={color === "#fff" ? "rgba(0,0,0,0.18)" : "#fff"} opacity={color === "#fff" ? 1 : 0.85}/>
      {/* Shield — top-right corner, slightly overlapping the phone */}
      <path d="M37 7 L43 9 L43 17 C43 21 40 24 37 25 C34 24 31 21 31 17 L31 9 Z"
        fill={color} opacity="0.95"/>
      {/* Tick inside the shield */}
      <path d="M34 16 L36 18 L40 14"
        stroke={color === "#fff" ? "rgba(0,0,0,0.4)" : "#fff"}
        strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" fill="none"/>
    </svg>
  ),

  // ─── Funds Protection ─── padlock above a banknote with ₦.
  // Anchor: banknote (money). Protection: padlock.
  "funds-protection": (color = "currentColor") => (
    <svg viewBox="0 0 48 48" fill="none" xmlns="http://www.w3.org/2000/svg">
      {/* Banknote — wide rectangle at the bottom */}
      <rect x="6" y="26" width="36" height="18" rx="2.5" fill={color} opacity="0.95"/>
      {/* Inner border on the note */}
      <rect x="9" y="29" width="30" height="12" rx="1" fill="none"
        stroke={color === "#fff" ? "rgba(0,0,0,0.18)" : "#fff"} strokeWidth="1" opacity="0.7"/>
      {/* Naira symbol — centered on the note */}
      <text x="24" y="40" textAnchor="middle" fontFamily="serif" fontSize="11" fontWeight="800"
        fill={color === "#fff" ? "rgba(0,0,0,0.4)" : "#fff"}>₦</text>
      {/* Padlock body — sits above the note, partially overlapping its top edge */}
      <rect x="17" y="18" width="14" height="13" rx="2" fill={color} opacity="0.95"/>
      {/* Padlock shackle */}
      <path d="M19 18 L19 13 C19 10.2 21 8 24 8 C27 8 29 10.2 29 13 L29 18"
        stroke={color} strokeWidth="2.4" fill="none" strokeLinecap="round"/>
      {/* Keyhole on the lock */}
      <circle cx="24" cy="23.5" r="1.6"
        fill={color === "#fff" ? "rgba(0,0,0,0.4)" : "#fff"}/>
      <rect x="23.3" y="24.2" width="1.4" height="3" rx="0.4"
        fill={color === "#fff" ? "rgba(0,0,0,0.4)" : "#fff"}/>
    </svg>
  ),

  // ─── Building Insurance ─── building with shield-shaped roof crown.
  // Anchor: building. Protection: shield as the roof.
  "building": (color = "currentColor") => (
    <svg viewBox="0 0 48 48" fill="none" xmlns="http://www.w3.org/2000/svg">
      {/* Building body */}
      <rect x="9" y="20" width="30" height="24" rx="2" fill={color} opacity="0.95"/>
      {/* Windows — 4 in a 2×2 grid */}
      <rect x="14" y="25" width="6" height="6" rx="1"
        fill={color === "#fff" ? "rgba(0,0,0,0.18)" : "#fff"} opacity="0.85"/>
      <rect x="28" y="25" width="6" height="6" rx="1"
        fill={color === "#fff" ? "rgba(0,0,0,0.18)" : "#fff"} opacity="0.85"/>
      {/* Door */}
      <rect x="20" y="34" width="8" height="10" rx="1.5"
        fill={color === "#fff" ? "rgba(0,0,0,0.18)" : "#fff"} opacity="0.85"/>
      {/* Roof — shield shape sitting on top, the protection metaphor doubling as the roof */}
      <path d="M24 4 L40 8 L40 14 C40 17 36 20 24 21 C12 20 8 17 8 14 L8 8 Z"
        fill={color} opacity="0.95"/>
      {/* Tick on the roof shield */}
      <path d="M20 12 L23 15 L29 9"
        stroke={color === "#fff" ? "rgba(0,0,0,0.4)" : "#fff"}
        strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round" fill="none"/>
    </svg>
  ),

  // ─── Group Health ─── heart shape with EKG pulse line through it.
  // Anchor: heart (health). Protection: the EKG signal — alive, monitored, covered.
  "group-health": (color = "currentColor") => (
    <svg viewBox="0 0 48 48" fill="none" xmlns="http://www.w3.org/2000/svg">
      {/* Heart shape — the dominant element */}
      <path d="M24 42 C24 42 6 30 6 17 C6 11 11 6 17 6 C20 6 23 8 24 11 C25 8 28 6 31 6 C37 6 42 11 42 17 C42 30 24 42 24 42 Z"
        fill={color} opacity="0.95"/>
      {/* EKG pulse line — clean horizontal line with one peak, knocked out from the heart */}
      <path d="M8 24 L17 24 L20 18 L23 30 L26 21 L29 24 L40 24"
        stroke={color === "#fff" ? "rgba(0,0,0,0.4)" : "#fff"}
        strokeWidth="2.6" strokeLinecap="round" strokeLinejoin="round" fill="none"/>
    </svg>
  ),

  // ─── Keyman ─── person silhouette with crown above. The "key person".
  // Anchor: person. Protection: crown signaling business-critical role.
  "keyman": (color = "currentColor") => (
    <svg viewBox="0 0 48 48" fill="none" xmlns="http://www.w3.org/2000/svg">
      {/* Crown — sits above the head */}
      <path d="M14 12 L14 6 L18 9 L24 4 L30 9 L34 6 L34 12 Z" fill={color} opacity="0.95"/>
      {/* Three crown jewels */}
      <circle cx="18" cy="9" r="1.4" fill={color === "#fff" ? "rgba(0,0,0,0.4)" : "#fff"}/>
      <circle cx="24" cy="6" r="1.6" fill={color === "#fff" ? "rgba(0,0,0,0.4)" : "#fff"}/>
      <circle cx="30" cy="9" r="1.4" fill={color === "#fff" ? "rgba(0,0,0,0.4)" : "#fff"}/>
      {/* Head */}
      <circle cx="24" cy="22" r="6" fill={color} opacity="0.95"/>
      {/* Body — shoulders/torso curve */}
      <path d="M10 44 C10 36 16 30 24 30 C32 30 38 36 38 44 Z" fill={color} opacity="0.95"/>
    </svg>
  ),
};

// ProductIcon — renders the named SVG icon for a given product.
// `chip` wraps it in the colored rounded-square tile used on cards, tables, and modals.
function ProductIcon({ productId, product, size = 48, chip = false }) {
  const id = productId || product?.id;
  const color = chip ? "#fff" : (product?.color || T.brand);
  const chipColor = product?.color || T.brand;
  const iconFn = PRODUCT_ICON_PATHS[id];
  if (!iconFn) {
    // Fallback for unknown products — generic shield
    return chip ? (
      <div style={{ width: size, height: size, borderRadius: Math.round(size * 0.29), background: chipColor, display: "inline-flex", alignItems: "center", justifyContent: "center", flexShrink: 0 }}>
        <Shield size={size * 0.5} color="#fff" strokeWidth={2}/>
      </div>
    ) : <Shield size={size} color={T.brand} strokeWidth={2}/>;
  }
  const svg = <div style={{ width: size, height: size, flexShrink: 0 }}>{iconFn(color)}</div>;
  if (!chip) return svg;
  return (
    <div style={{
      width: size, height: size,
      borderRadius: Math.round(size * 0.29),
      background: chipColor,
      display: "inline-flex", alignItems: "center", justifyContent: "center", flexShrink: 0,
    }}>
      <div style={{ width: size * 0.72, height: size * 0.72 }}>{iconFn("#fff")}</div>
    </div>
  );
}

// ═══════════════════════════════════════════════════════════════════════════════════
// MOCK DATA
// ═══════════════════════════════════════════════════════════════════════════════════
const CUSTOMERS = [
  // Individuals — standard BVN+NIN KYC, partner-furnished credit data for credit life
  { id: "c1", type: "individual", name: "Olatunde Akinola", email: "olatunde.a@moniepoint.com", phone: "+234 801 234 5678", policies: 3, status: "ACTIVE", joined: "12 Mar 2026", bvn: "22145678901", nin: "70234567890", dob: "14 Aug 1988", gender: "Male", occupation: "SME Owner", address: "12 Adeola Odeku St, Victoria Island", lga: "Eti-Osa, Lagos",
    partnerFurnished: { partnerCustomerId: "MP-CUST-481923", channelOfOrigin: "Moniepoint SME loan", loanAmount: 5000000, loanTenor: "12 months", riskScore: "B+", sector: "Retail" } },
  { id: "c2", type: "individual", name: "Chinwe Okonkwo", email: "chinwe@m-kopa.ng", phone: "+234 802 345 6789", policies: 2, status: "ACTIVE", joined: "03 Feb 2026", bvn: "22198765432", nin: "70398765432", dob: "22 May 1992", gender: "Female", occupation: "Trader", address: "45 Allen Avenue, Ikeja", lga: "Ikeja, Lagos",
    partnerFurnished: { partnerCustomerId: "MK-DV-22841", channelOfOrigin: "M-KOPA device upgrade", deviceModel: "Samsung Galaxy A54", devicePrice: 285000 } },
  { id: "c3", type: "individual", name: "Ibrahim Yusuf", email: "i.yusuf@samsung.partner.ng", phone: "+234 803 456 7890", policies: 1, status: "ACTIVE", joined: "28 Jan 2026", bvn: "22156789012", nin: "70256789012", dob: "07 Nov 1985", gender: "Male", occupation: "Logistics Manager", address: "8 Bourdillon Road, Ikoyi", lga: "Eti-Osa, Lagos",
    partnerFurnished: { partnerCustomerId: "SAM-PRT-09812", channelOfOrigin: "Samsung corporate" } },
  // Businesses — RC number, TIN, sector, key person KYC, partner-furnished business data
  { id: "c4", type: "business", name: "Adaeze Foods Ltd", legalName: "Adaeze Foods Ltd", tradingName: "Adaeze Foods", rcNumber: "RC-3892145", tin: "29481593-0001", sector: "Food & Beverage", industry: "Restaurant", incorporated: "08 Mar 2018", employees: 24, email: "ops@adaezefoods.ng", phone: "+234 804 567 8901", policies: 4, status: "ACTIVE", joined: "15 Dec 2025", address: "22 Awolowo Road, Ikoyi", lga: "Eti-Osa, Lagos",
    keyPerson: { name: "Adaeze Foods Ltd", role: "Founder & CEO", bvn: "22134567890", nin: "70134567890", dob: "30 Jan 1990", phone: "+234 804 567 8901", email: "adaeze@adaezefoods.ng" },
    partnerFurnished: { partnerCustomerId: "KUDA-BIZ-01284", channelOfOrigin: "Kuda Business banking", monthlyTurnover: 8400000, accountVintage: "3 years 2 months" } },
  { id: "c5", type: "individual", name: "Femi Bakare", email: "femi.b@palmpay.partner", phone: "+234 805 678 9012", policies: 1, status: "CREATED", joined: "27 Apr 2026", bvn: "22123456789", nin: "70323456789", dob: "11 Sep 1995", gender: "Male", occupation: "Ride-hail Driver", address: "5 Opebi Road, Ikeja", lga: "Ikeja, Lagos",
    partnerFurnished: { partnerCustomerId: "PP-DRV-7184", channelOfOrigin: "PalmPay driver protection" } },
  { id: "c6", type: "business", name: "Hauwa Trading Co", legalName: "Hauwa Trading Company Limited", tradingName: "Hauwa Trading", rcNumber: "RC-4127893", tin: "31092847-0001", sector: "Wholesale & Distribution", industry: "FMCG distribution", incorporated: "14 Jun 2019", employees: 42, email: "hello@hauwatrading.ng", phone: "+234 806 789 0123", policies: 2, status: "ACTIVE", joined: "08 Apr 2026", address: "78 Lekki Phase 1", lga: "Eti-Osa, Lagos",
    keyPerson: { name: "Hauwa Trading Co", role: "Managing Director", bvn: "22112345678", nin: "70412345678", dob: "19 Jul 1987", phone: "+234 806 789 0123", email: "hauwa@hauwatrading.ng" },
    partnerFurnished: { partnerCustomerId: "OPAY-MERCH-3819", channelOfOrigin: "OPay merchant", monthlyTurnover: 12300000, accountVintage: "1 year 8 months" } },
  { id: "c7", type: "individual", name: "Tunde Salami", email: "tunde@bolt.driver.ng", phone: "+234 807 890 1234", policies: 1, status: "INACTIVE", joined: "20 Oct 2025", bvn: "22167890123", nin: "70567890123", dob: "04 Mar 1980", gender: "Male", occupation: "Driver", address: "33 Surulere Road", lga: "Surulere, Lagos",
    partnerFurnished: { partnerCustomerId: "BOLT-DRV-22841", channelOfOrigin: "Bolt driver protection" } },
];

const POLICIES = [
  { id: "p1", policyNumber: "CL-2026-0042", customer: "Olatunde Akinola", customerId: "c1", product: "Credit Life", plan: "SME Loans up to ₦30M", status: "ACTIVE", premium: 4500, coverage: 5000000, start: "12 Mar 2026", end: "11 Mar 2027", insurer: "Casava Microinsurance", icon: "💳",
    loanRef: "MP-LN-2026-04211", loanProduct: "SME working capital", loanAmount: 5000000, disbursedAt: "12 Mar 2026", tenureMonths: 12, branch: "Lagos · VI", lenderRef: "MP/SME/04211" },
  { id: "p2", policyNumber: "DV-2026-0118", customer: "Chinwe Okonkwo", customerId: "c2", product: "Device Insurance", plan: "Smartphone protection", status: "ACTIVE", premium: 1850, coverage: 250000, start: "03 Feb 2026", end: "02 Feb 2027", insurer: "Casava Microinsurance", icon: "📱" },
  { id: "p3", policyNumber: "CL-2026-0073", customer: "Ibrahim Yusuf", customerId: "c3", product: "Credit Life", plan: "Working capital loans", status: "ACTIVE", premium: 2200, coverage: 1500000, start: "28 Jan 2026", end: "27 Jan 2027", insurer: "Casava Microinsurance", icon: "💳",
    loanRef: "MP-LN-2026-02890", loanProduct: "SME working capital", loanAmount: 1500000, disbursedAt: "28 Jan 2026", tenureMonths: 6, branch: "Abuja · Wuse", lenderRef: "MP/SME/02890" },
  { id: "p4", policyNumber: "FFP-2026-0021", customer: "Adaeze Foods Ltd", customerId: "c4", product: "Funds Protection", plan: "Embedded — fraud cover", status: "ACTIVE", premium: 350, coverage: 500000, start: "15 Dec 2025", end: "14 Dec 2026", insurer: "Casava Microinsurance", icon: "🛡️" },
  { id: "p5", policyNumber: "BLD-2026-0009", customer: "Adaeze Foods Ltd", customerId: "c4", product: "Building Insurance", plan: "Lagos State mandate", status: "ACTIVE", premium: 18500, coverage: 25000000, start: "01 Apr 2026", end: "31 Mar 2027", insurer: "Casava Microinsurance", icon: "🏢" },
  { id: "p6", policyNumber: "DV-2026-0184", customer: "Femi Bakare", customerId: "c5", product: "Device Insurance", plan: "Smartphone protection", status: "PENDING", premium: 1850, coverage: 200000, start: "27 Apr 2026", end: "26 Apr 2027", insurer: "Casava Microinsurance", icon: "📱" },
  { id: "p7", policyNumber: "CL-2026-0094", customer: "Hauwa Trading Co", customerId: "c6", product: "Credit Life", plan: "SME Loans up to ₦15M", status: "ACTIVE", premium: 3100, coverage: 2500000, start: "08 Apr 2026", end: "07 Apr 2027", insurer: "Casava Microinsurance", icon: "💳",
    loanRef: "MP-LN-2026-08412", loanProduct: "Asset finance", loanAmount: 2500000, disbursedAt: "08 Apr 2026", tenureMonths: 18, branch: "Kano", lenderRef: "MP/AF/08412" },
  { id: "p8", policyNumber: "CL-2026-0011", customer: "Tunde Salami", customerId: "c7", product: "Credit Life", plan: "Microloan cover", status: "INACTIVE", premium: 850, coverage: 300000, start: "20 Oct 2025", end: "19 Apr 2026", insurer: "Casava Microinsurance", icon: "💳",
    loanRef: "MP-LN-2025-19842", loanProduct: "Personal loan", loanAmount: 300000, disbursedAt: "20 Oct 2025", tenureMonths: 6, branch: "Lagos · Ikeja", lenderRef: "MP/PL/19842" },
];

// LOANS — disbursed loans from the partner LMS (Moniepoint). Each loan either has a
// credit-life policy attached or is an attachment opportunity. The Loan book page is
// the daily working surface for banking partners: see what disbursed, action the gap.
// Note: not every loan needs credit life (very small / very short tenures may opt out).
const LOANS = [
  // Today (28 Apr 2026) — recent disbursements, mixed attachment
  { id: "ln1",  loanRef: "MP-LN-2026-09823", customerId: "c1", customer: "Olatunde Akinola",   loanProduct: "SME working capital", loanAmount: 8500000,  disbursedAt: "28 Apr 2026", tenureMonths: 12, branch: "Lagos · VI",      attachmentStatus: "unattached", policyId: null, premium: null,  lenderRef: "MP/SME/09823" },
  { id: "ln2",  loanRef: "MP-LN-2026-09822", customerId: "c3", customer: "Ibrahim Yusuf",      loanProduct: "SME working capital", loanAmount: 2200000,  disbursedAt: "28 Apr 2026", tenureMonths: 6,  branch: "Abuja · Wuse",    attachmentStatus: "attached",   policyId: "p3", premium: 2200,  lenderRef: "MP/SME/09822" },
  { id: "ln3",  loanRef: "MP-LN-2026-09821", customerId: "c4", customer: "Adaeze Foods Ltd",   loanProduct: "Asset finance",       loanAmount: 12000000, disbursedAt: "28 Apr 2026", tenureMonths: 24, branch: "Lagos · Lekki",   attachmentStatus: "unattached", policyId: null, premium: null,  lenderRef: "MP/AF/09821" },
  { id: "ln4",  loanRef: "MP-LN-2026-09820", customerId: null, customer: "Ngozi Okeke",        loanProduct: "SME working capital", loanAmount: 2500000,  disbursedAt: "28 Apr 2026", tenureMonths: 12, branch: "Port Harcourt",   attachmentStatus: "unattached", policyId: null, premium: null,  lenderRef: "MP/SME/09820" },
  { id: "ln5",  loanRef: "MP-LN-2026-09819", customerId: "c5", customer: "Femi Bakare",        loanProduct: "Personal loan",       loanAmount: 350000,   disbursedAt: "28 Apr 2026", tenureMonths: 3,  branch: "Lagos · VI",      attachmentStatus: "attached",   policyId: null, premium: 850,   lenderRef: "MP/PL/09819" },
  { id: "ln6",  loanRef: "MP-LN-2026-09818", customerId: "c6", customer: "Hauwa Trading Co",   loanProduct: "Asset finance",       loanAmount: 18000000, disbursedAt: "28 Apr 2026", tenureMonths: 36, branch: "Kano",            attachmentStatus: "unattached", policyId: null, premium: null,  lenderRef: "MP/AF/09818" },
  { id: "ln7",  loanRef: "MP-LN-2026-09817", customerId: null, customer: "Tunde Salami",        loanProduct: "Personal loan",       loanAmount: 200000,   disbursedAt: "28 Apr 2026", tenureMonths: 3,  branch: "Lagos · Ikeja",   attachmentStatus: "attached",   policyId: null, premium: 580,   lenderRef: "MP/PL/09817" },
  // 27 Apr — yesterday
  { id: "ln8",  loanRef: "MP-LN-2026-09780", customerId: null, customer: "Yemi Adesina",        loanProduct: "SME working capital", loanAmount: 4200000,  disbursedAt: "27 Apr 2026", tenureMonths: 12, branch: "Lagos · VI",      attachmentStatus: "attached",   policyId: null, premium: 3780,  lenderRef: "MP/SME/09780" },
  { id: "ln9",  loanRef: "MP-LN-2026-09779", customerId: null, customer: "Aliyu Mohammed",      loanProduct: "SME working capital", loanAmount: 1800000,  disbursedAt: "27 Apr 2026", tenureMonths: 6,  branch: "Kano",            attachmentStatus: "unattached", policyId: null, premium: null,  lenderRef: "MP/SME/09779" },
  { id: "ln10", loanRef: "MP-LN-2026-09778", customerId: null, customer: "Blessing Eze",        loanProduct: "Personal loan",       loanAmount: 500000,   disbursedAt: "27 Apr 2026", tenureMonths: 6,  branch: "Lagos · Lekki",   attachmentStatus: "attached",   policyId: null, premium: 1100,  lenderRef: "MP/PL/09778" },
  { id: "ln11", loanRef: "MP-LN-2026-09777", customerId: null, customer: "Kelechi Nwosu",       loanProduct: "Asset finance",       loanAmount: 6500000,  disbursedAt: "27 Apr 2026", tenureMonths: 18, branch: "Abuja · Wuse",    attachmentStatus: "attached",   policyId: null, premium: 5200,  lenderRef: "MP/AF/09777" },
  { id: "ln12", loanRef: "MP-LN-2026-09776", customerId: null, customer: "Sade Adekunle",       loanProduct: "Personal loan",       loanAmount: 250000,   disbursedAt: "27 Apr 2026", tenureMonths: 3,  branch: "Lagos · Ikeja",   attachmentStatus: "pending",    policyId: null, premium: null,  lenderRef: "MP/PL/09776" },
  // 26 Apr
  { id: "ln13", loanRef: "MP-LN-2026-09712", customerId: null, customer: "Chukwuma Okeke",      loanProduct: "SME working capital", loanAmount: 3500000,  disbursedAt: "26 Apr 2026", tenureMonths: 12, branch: "Port Harcourt",   attachmentStatus: "attached",   policyId: null, premium: 3150,  lenderRef: "MP/SME/09712" },
  { id: "ln14", loanRef: "MP-LN-2026-09711", customerId: null, customer: "Folake Bankole",      loanProduct: "Personal loan",       loanAmount: 800000,   disbursedAt: "26 Apr 2026", tenureMonths: 6,  branch: "Lagos · VI",      attachmentStatus: "unattached", policyId: null, premium: null,  lenderRef: "MP/PL/09711" },
  { id: "ln15", loanRef: "MP-LN-2026-09710", customerId: null, customer: "Bisi Akande",         loanProduct: "Asset finance",       loanAmount: 9500000,  disbursedAt: "26 Apr 2026", tenureMonths: 24, branch: "Lagos · Lekki",   attachmentStatus: "attached",   policyId: null, premium: 7600,  lenderRef: "MP/AF/09710" },
  { id: "ln16", loanRef: "MP-LN-2026-09709", customerId: null, customer: "Garba Usman",         loanProduct: "SME working capital", loanAmount: 2800000,  disbursedAt: "26 Apr 2026", tenureMonths: 12, branch: "Kano",            attachmentStatus: "attached",   policyId: null, premium: 2520,  lenderRef: "MP/SME/09709" },
  // Older — 25-22 Apr
  { id: "ln17", loanRef: "MP-LN-2026-09640", customerId: null, customer: "Ahmed Bello",         loanProduct: "Personal loan",       loanAmount: 400000,   disbursedAt: "25 Apr 2026", tenureMonths: 6,  branch: "Abuja · Wuse",    attachmentStatus: "attached",   policyId: null, premium: 920,   lenderRef: "MP/PL/09640" },
  { id: "ln18", loanRef: "MP-LN-2026-09639", customerId: null, customer: "Tope Olawale",        loanProduct: "SME working capital", loanAmount: 5500000,  disbursedAt: "25 Apr 2026", tenureMonths: 12, branch: "Lagos · VI",      attachmentStatus: "attached",   policyId: null, premium: 4950,  lenderRef: "MP/SME/09639" },
  { id: "ln19", loanRef: "MP-LN-2026-09638", customerId: null, customer: "Esther Okonjo",       loanProduct: "Asset finance",       loanAmount: 7800000,  disbursedAt: "24 Apr 2026", tenureMonths: 24, branch: "Lagos · Lekki",   attachmentStatus: "attached",   policyId: null, premium: 6240,  lenderRef: "MP/AF/09638" },
  { id: "ln20", loanRef: "MP-LN-2026-09637", customerId: null, customer: "Idris Lawal",         loanProduct: "Personal loan",       loanAmount: 600000,   disbursedAt: "23 Apr 2026", tenureMonths: 6,  branch: "Kano",            attachmentStatus: "unattached", policyId: null, premium: null,  lenderRef: "MP/PL/09637" },
];

const CLAIMS = [
  { id: "cl1", claimRef: "CLM-2026-0014", customer: "Tunde Salami", customerId: "c7", policyId: "p8", product: "Credit Life", peril: "Death", status: "Approved", amount: 300000, lastUpdated: "22 Apr 2026", icon: "💳" },
  { id: "cl2", claimRef: "CLM-2026-0019", customer: "Chinwe Okonkwo", customerId: "c2", policyId: "p2", product: "Device Insurance", peril: "Theft", status: "Processing", amount: 180000, lastUpdated: "26 Apr 2026", icon: "📱" },
  { id: "cl3", claimRef: "CLM-2026-0023", customer: "Adaeze Foods Ltd", customerId: "c4", policyId: "p4", product: "Funds Protection", peril: "Unauthorized transaction", status: "Submitted", amount: 95000, lastUpdated: "28 Apr 2026", icon: "🛡️" },
];

const TRANSACTIONS = [
  { id: "t1", date: "28 Apr 2026", description: "Credit Life policy purchase · CL-2026-0094", type: "Credit", amount: 3100, source: "MONIEPOINT_API", status: "Completed" },
  { id: "t2", date: "28 Apr 2026", description: "Device Insurance policy purchase · DV-2026-0118", type: "Credit", amount: 1850, source: "M-KOPA_PARTNER", status: "Completed" },
  { id: "t3", date: "27 Apr 2026", description: "Funds Protection policy purchase · FFP-2026-0021", type: "Credit", amount: 350, source: "KUDA_EMBEDDED", status: "Completed" },
  { id: "t4", date: "27 Apr 2026", description: "Claim payout · CLM-2026-0014", type: "Debit", amount: 300000, source: "PAYSTACK_DISBURSE", status: "Completed" },
  { id: "t5", date: "26 Apr 2026", description: "Building Insurance policy · BLD-2026-0009", type: "Credit", amount: 18500, source: "LASBCA_MANDATE", status: "Completed" },
  { id: "t6", date: "25 Apr 2026", description: "Wallet top-up", type: "Credit", amount: 500000, source: "BANK_TRANSFER", status: "Completed" },
  { id: "t7", date: "25 Apr 2026", description: "Credit Life policy purchase · CL-2026-0073", type: "Credit", amount: 2200, source: "SAMSUNG_PARTNER", status: "Completed" },
  { id: "t8", date: "24 Apr 2026", description: "Settlement disbursement to partner", type: "Debit", amount: 145000, source: "MONTHLY_SETTLEMENT", status: "Completed" },
];

const PRODUCTS = [
  {
    id: "credit-life", name: "Credit Life", category: "CREDIT", icon: "💳", color: T.brand,
    description: "Loan protection for borrowers — covers outstanding balance on death or disability.",
    premium: "from ₦850/month",
    photo: "https://images.unsplash.com/photo-1556742502-ec7c0e9f34b1?w=900&auto=format&fit=crop&q=80",
    photoAlt: "SME owner in a Lagos market",
    tagline: "The bundle that turns 70% of your loans into recurring premium income.",
    casaTake: "For banks, this is the foundational bundle. Auto-attach via API at disbursement to capture 78%+ of qualifying loans without operations effort. Borrowers see a single combined premium folded into their EMI — not a separate purchase decision.",
    partnerEconomics: { commissionRate: "12%", avgPremium: "₦2,800", expectedAttach: "78%", timeToFirstSale: "Same day via API" },
    coverage: {
      perils: ["Death", "Permanent total disability", "Terminal illness diagnosis"],
      sumAssured: "Up to ₦30M (matches loan principal)",
      term: "Matches loan tenure (3 – 60 months)",
      payoutChannel: "Direct to lender — clears the outstanding loan balance",
    },
    eligibility: { ageRange: "18 – 65", loanRange: "₦50K – ₦30M", tenureRange: "3 – 60 months", exclusions: ["Pre-existing terminal conditions", "Self-inflicted injury within 12 months"] },
    claims: { approvalRate: "94%", avgSettleTime: "5 business days", lastQuarter: "₦184M paid out across 312 approved claims" },
    regulatory: { license: "NAICOM-approved class III microinsurance", actCompliance: "NIIRA 2025 fully compliant", standardTerms: "Casava master policy form pre-approved by NAICOM" },
    partnerActions: { integrations: ["API embed at disbursement", "CSV bulk upload", "Manual via portal"], setupTime: "1 – 2 days", requirements: ["Loan disbursement webhook", "Borrower KYC pass-through"] },
    socialProof: { partners: ["Moniepoint", "M-KOPA", "Renmoney"], book: "₦4.2B in covered loans", customers: "12,000+ policyholders" },
  },
  {
    id: "device", name: "Device Insurance", category: "DEVICE", icon: "📱", color: T.purple,
    description: "Theft, damage and screen cover for smartphones and laptops.",
    premium: "from ₦1,200/month",
    photo: "https://images.unsplash.com/photo-1512941937669-90a1b58e7e9c?w=900&auto=format&fit=crop&q=80",
    photoAlt: "Hands holding a smartphone",
    tagline: "Cross-sell that converts 4.2× when offered within 7 days of device purchase.",
    casaTake: "Strong cross-sell for banks with active card or salary customers. Highest attach when triggered by a card transaction at a phone retailer or on M-KOPA-style device finance. Premium feels like an after-thought to the customer, but it's a high-margin add-on for the partner.",
    partnerEconomics: { commissionRate: "15%", avgPremium: "₦1,850", expectedAttach: "12% (cross-sell)", timeToFirstSale: "Same day" },
    coverage: {
      perils: ["Accidental damage", "Theft (with police report)", "Screen damage", "Liquid damage", "Mechanical breakdown after warranty"],
      sumAssured: "Up to device replacement value",
      term: "12-month renewable",
      payoutChannel: "Repair voucher with our certified network or cash equivalent",
    },
    eligibility: { ageRange: "18+", deviceAge: "Under 24 months from purchase", deviceTypes: ["Smartphones", "Laptops", "Tablets"], exclusions: ["Cosmetic-only damage", "Loss without police report"] },
    claims: { approvalRate: "87%", avgSettleTime: "3 business days", lastQuarter: "₦52M paid out across 1,842 approved claims" },
    regulatory: { license: "NAICOM-approved general insurance", actCompliance: "NIIRA 2025", standardTerms: "Single product master policy" },
    partnerActions: { integrations: ["API embed at point of sale", "WhatsApp campaign templates", "In-app banner kits"], setupTime: "1 day", requirements: ["Customer transaction data feed (optional but improves attach)"] },
    socialProof: { partners: ["Samsung Nigeria", "M-KOPA", "Myka.Insure"], book: "23,000+ devices covered", customers: "21,400 policyholders" },
  },
  {
    id: "funds-protection", name: "Funds Protection", category: "FINTECH", icon: "🛡️", color: T.cyan, badge: "NEW",
    description: "Reimburses users for unauthorized transactions, SIM swap and phishing fraud.",
    premium: "from ₦150/month",
    photo: "https://images.unsplash.com/photo-1563013544-824ae1b704d3?w=900&auto=format&fit=crop&q=80",
    photoAlt: "Mobile banking and POS",
    tagline: "The fastest-growing micro-cover in Nigeria — bundled with current accounts at zero friction.",
    casaTake: "If you offer current accounts or wallets, this is a no-brainer add-on. ₦150/month isn't a customer decision — it's a small line item that boosts confidence in your product. Banks bundle it as a 'safe banking' feature; we underwrite the actual fraud risk.",
    partnerEconomics: { commissionRate: "20%", avgPremium: "₦150", expectedAttach: "34%", timeToFirstSale: "Same day" },
    coverage: {
      perils: ["Unauthorized transactions", "SIM swap fraud", "Phishing-related theft", "Card cloning", "Mobile-money account takeover"],
      sumAssured: "Up to ₦500K per incident, ₦1M aggregate annual",
      term: "Monthly recurring",
      payoutChannel: "Direct refund to customer's bank account",
    },
    eligibility: { ageRange: "18+", accountTypes: ["Current account", "Wallet", "Card"], exclusions: ["Customer-shared OTP/PIN", "Authorized transactions later disputed"] },
    claims: { approvalRate: "91%", avgSettleTime: "2 business days", lastQuarter: "₦21M paid out across 580 approved claims" },
    regulatory: { license: "NAICOM-approved general insurance", actCompliance: "NIIRA 2025", standardTerms: "Master policy with addendum per partner" },
    partnerActions: { integrations: ["API embed at account opening", "Toggleable in customer app"], setupTime: "1 – 3 days", requirements: ["Account opening event webhook"] },
    socialProof: { partners: ["Moniepoint", "Kuda", "PalmPay"], book: "47,000 policies in force", customers: "47,000+ subscribers" },
  },
  {
    id: "building", name: "Building Insurance", category: "PROPERTY", icon: "🏢", color: T.brown, badge: "LBI",
    description: "Lagos State LASBCA-compliant cover for buildings under construction and occupancy.",
    premium: "from ₦8,500/year",
    photo: "https://images.unsplash.com/photo-1531973576160-7125cd663d86?w=900&auto=format&fit=crop&q=80",
    photoAlt: "Lagos high-rise property",
    tagline: "Lagos State mandates this from July 2026. Get your customers compliant before the ₦5M fines start.",
    casaTake: "Regulatory tailwind. Every building in Lagos must hold an active policy by July 2026 or face ₦5M fines that compound monthly. For banks with mortgage / property customers this is a captive cross-sell — they need it whether you sell it or someone else does. Underwritten under our LASBCA-compliant master form.",
    partnerEconomics: { commissionRate: "10%", avgPremium: "₦18,500/year", expectedAttach: "8% on customer base, 100% of LASBCA-actioned customers", timeToFirstSale: "Same day" },
    coverage: {
      perils: ["Fire and lightning", "Flood and storm", "Burglary and theft", "Public liability", "Earthquake (LASBCA mandate)"],
      sumAssured: "Up to ₦5B per building",
      term: "Annual",
      payoutChannel: "Direct to building owner via partner",
    },
    eligibility: { propertyTypes: ["Residential", "Commercial", "Mixed-use"], exclusions: ["Buildings without occupancy permit", "War / nuclear / terrorism (separate cover)"] },
    claims: { approvalRate: "89%", avgSettleTime: "10 business days", lastQuarter: "₦14M paid out (small claim base — recently launched)" },
    regulatory: { license: "NAICOM-approved + LASBCA-registered underwriter", actCompliance: "LASBCA 2026 + NIIRA 2025", standardTerms: "Lagos Building Insurance master form" },
    partnerActions: { integrations: ["API for property finance customers", "WhatsApp campaign for LASBCA awareness", "Bulk upload of property portfolio"], setupTime: "2 – 3 days", requirements: ["Property address data", "LASBCA reference (where available)"] },
    socialProof: { partners: ["Lagos State Government partners", "Property finance lenders"], book: "5,840 buildings covered", customers: "5,840 properties" },
  },
  {
    id: "group-health", name: "Group Health", category: "HEALTH", icon: "❤️", color: T.salmon,
    description: "Outpatient and inpatient cover for SME teams and cooperative members.",
    premium: "Contact us",
    photo: "https://images.unsplash.com/photo-1576091160399-112ba8d25d1d?w=900&auto=format&fit=crop&q=80",
    photoAlt: "Healthcare professional with patient",
    tagline: "Bundle health cover with SME loans for stronger underwriting and customer retention.",
    casaTake: "Cross-sell for SME-focused lenders. Banks see better repayment rates from borrowers with health cover (medical emergencies are a leading cause of micro-default). Higher commission rates than core credit life, but longer underwriting cycle. Recommend for SME-loan-heavy partners as a second-wave product.",
    partnerEconomics: { commissionRate: "18%", avgPremium: "₦12,000 – ₦40,000/year", expectedAttach: "Custom by SME size", timeToFirstSale: "5 – 7 days for first group" },
    coverage: {
      perils: ["Outpatient care", "Inpatient and surgery", "Maternity (with waiting period)", "Dental and optical (optional)", "Chronic disease management"],
      sumAssured: "₦500K – ₦5M per member annual limit",
      term: "Annual group plan",
      payoutChannel: "Cashless at network providers; reimbursement at non-network",
    },
    eligibility: { groupSize: "Minimum 5 members", ageRange: "18 – 65", exclusions: ["Pre-existing conditions in first 6 months", "Cosmetic procedures"] },
    claims: { approvalRate: "92%", avgSettleTime: "Same day at network providers", lastQuarter: "Active across 41 partner SMEs" },
    regulatory: { license: "NAICOM + NHIA dual approval", actCompliance: "NIIRA 2025 + National Health Insurance Authority Act 2022", standardTerms: "Group health master policy" },
    partnerActions: { integrations: ["API for group enrolment", "Manual via portal for first 5 groups"], setupTime: "5 – 7 days for first group", requirements: ["Member roster", "Group anchor entity KYB"] },
    socialProof: { partners: ["SME-focused lenders", "Cooperative societies"], book: "1,400+ members enrolled", customers: "41 active groups" },
  },
  {
    id: "keyman", name: "Keyman Insurance", category: "BUSINESS", icon: "🎯", color: T.yellowSoft,
    description: "Business continuity cover for the loss of a key employee or founder.",
    premium: "Contact us",
    photo: "https://images.unsplash.com/photo-1573496359142-b8d87734a5a2?w=900&auto=format&fit=crop&q=80",
    photoAlt: "Business professional in Lagos",
    tagline: "For banks with high-net-worth or business banking customers — protect the loan against founder risk.",
    casaTake: "Niche but high-value for business banking. Banks lending to founder-led businesses use this to underwrite the founder dependency. Commission per policy is much higher than commodity products, but volume is low. Position it as an upsell for relationship-managed business customers, not a mass-market product.",
    partnerEconomics: { commissionRate: "20%", avgPremium: "₦85,000 – ₦450,000/year", expectedAttach: "Custom — relationship-driven", timeToFirstSale: "7 – 14 days underwriting" },
    coverage: {
      perils: ["Death of key person", "Permanent disability", "Critical illness diagnosis"],
      sumAssured: "₦5M – ₦200M per key person",
      term: "Annual renewable",
      payoutChannel: "To business entity nominated as beneficiary",
    },
    eligibility: { entityType: "Limited company with audited financials", keyPersonRole: "Founder, CEO, CFO, or revenue-critical role", ageRange: "18 – 70", exclusions: ["Pre-existing critical conditions"] },
    claims: { approvalRate: "96%", avgSettleTime: "10 business days", lastQuarter: "Small claim base — niche product" },
    regulatory: { license: "NAICOM-approved life insurance", actCompliance: "NIIRA 2025", standardTerms: "Bespoke per case (template available)" },
    partnerActions: { integrations: ["Manual underwriting via portal", "Quote-on-request"], setupTime: "7 – 14 days per case", requirements: ["Key person medical and financial disclosure", "Business entity audited financials"] },
    socialProof: { partners: ["Business banking divisions"], book: "₦2.4B sum assured in force", customers: "47 active policies" },
  },
];

// Mock data for v3 B2B components
const REMITTANCES = [
  { month: "May", day: "02", label: "April settlement", policies: 412, gross: 2147500, net: 1793163 },
  { month: "May", day: "16", label: "First-half May settlement", policies: 187, gross: 894200, net: 745532 },
  { month: "Jun", day: "01", label: "Second-half May settlement", policies: 245, gross: 1156800, net: 964482 },
];

const WEBHOOK_EVENTS = [
  { timestamp: "13:42:18", event: "policy.created", url: "https://moniepoint.com/casava/webhooks", status: "delivered", code: 200, attempts: 1 },
  { timestamp: "13:41:55", event: "policy.activated", url: "https://moniepoint.com/casava/webhooks", status: "delivered", code: 200, attempts: 1 },
  { timestamp: "13:38:12", event: "claim.submitted", url: "https://m-kopa.ng/insurance/hooks", status: "delivered", code: 200, attempts: 1 },
  { timestamp: "13:34:07", event: "policy.created", url: "https://samsung.partner.ng/hooks/casava", status: "failed", code: 504, attempts: 3, maxAttempts: 5 },
  { timestamp: "13:30:44", event: "premium.paid", url: "https://kuda.business/api/webhooks", status: "delivered", code: 200, attempts: 1 },
  { timestamp: "13:27:11", event: "policy.created", url: "https://palmpay.partner/hooks", status: "retrying", code: 503, attempts: 2, maxAttempts: 5 },
  { timestamp: "13:22:38", event: "claim.approved", url: "https://moniepoint.com/casava/webhooks", status: "delivered", code: 200, attempts: 1 },
];

const PARTNER_HEALTH = {
  partners: ["Moniepoint", "M-KOPA", "Samsung", "Kuda", "PalmPay", "OPay"],
  integrations: ["Policy API", "Claims API", "Webhooks", "Settlement", "Schedule upload"],
  status: {
    "Moniepoint:Policy API": "healthy", "Moniepoint:Claims API": "healthy", "Moniepoint:Webhooks": "healthy", "Moniepoint:Settlement": "healthy", "Moniepoint:Schedule upload": "healthy",
    "M-KOPA:Policy API": "healthy", "M-KOPA:Claims API": "healthy", "M-KOPA:Webhooks": "healthy", "M-KOPA:Settlement": "healthy", "M-KOPA:Schedule upload": "healthy",
    "Samsung:Policy API": "healthy", "Samsung:Claims API": "healthy", "Samsung:Webhooks": "degraded", "Samsung:Settlement": "healthy",
    "Kuda:Policy API": "healthy", "Kuda:Claims API": "healthy", "Kuda:Webhooks": "healthy", "Kuda:Settlement": "healthy",
    "PalmPay:Policy API": "healthy", "PalmPay:Webhooks": "degraded", "PalmPay:Settlement": "paused",
    "OPay:Policy API": "healthy", "OPay:Claims API": "healthy",
  },
};

const SPARKLINES = {
  customers: [712, 728, 740, 765, 781, 803, 821, 847],
  policies: [1102, 1142, 1180, 1198, 1224, 1247, 1268, 1284],
  premium: [1.62, 1.74, 1.81, 1.93, 1.99, 2.05, 2.11, 2.15],
  claimsRatio: [22, 21, 19, 18, 17, 16, 15, 14],
};

const QUOTES = [
  { id: "q1", quoteRef: "QT-2026-0142", customer: "Adebayo Onafeko", customerEmail: "adebayo.o@moniepoint.com", product: "Credit Life", premium: 4500, coverage: 5000000, status: "Bound", created: "28 Apr 2026", expires: "12 May 2026", channel: "Moniepoint", icon: "💳" },
  { id: "q2", quoteRef: "QT-2026-0141", customer: "Funmi Adesanya", customerEmail: "funmi.a@m-kopa.ng", product: "Device Insurance", premium: 1850, coverage: 250000, status: "Paid", created: "28 Apr 2026", expires: "12 May 2026", channel: "M-KOPA", icon: "📱" },
  { id: "q3", quoteRef: "QT-2026-0140", customer: "Chuka Eze", customerEmail: "chuka@samsung.partner.ng", product: "Device Insurance", premium: 2100, coverage: 320000, status: "Viewed", created: "28 Apr 2026", expires: "12 May 2026", channel: "Samsung", icon: "📱" },
  { id: "q4", quoteRef: "QT-2026-0139", customer: "Aisha Bello", customerEmail: "aisha@kuda.business", product: "Funds Protection", premium: 350, coverage: 500000, status: "Sent", created: "27 Apr 2026", expires: "11 May 2026", channel: "Kuda", icon: "🛡️" },
  { id: "q5", quoteRef: "QT-2026-0138", customer: "Olayinka Akande", customerEmail: "olayinka@palmpay.partner", product: "Credit Life", premium: 1850, coverage: 1500000, status: "Sent", created: "27 Apr 2026", expires: "11 May 2026", channel: "PalmPay", icon: "💳" },
  { id: "q6", quoteRef: "QT-2026-0137", customer: "Kehinde Lawal", customerEmail: "kehinde@opay.merchant", product: "Building Insurance", premium: 18500, coverage: 25000000, status: "Viewed", created: "26 Apr 2026", expires: "10 May 2026", channel: "Direct", icon: "🏢" },
  { id: "q7", quoteRef: "QT-2026-0136", customer: "Bola Sanusi", customerEmail: "bola@bolt.driver.ng", product: "Credit Life", premium: 850, coverage: 300000, status: "Expired", created: "12 Apr 2026", expires: "26 Apr 2026", channel: "Bolt", icon: "💳" },
  { id: "q8", quoteRef: "QT-2026-0135", customer: "Ngozi Okeke", customerEmail: "ngozi@moniepoint.com", product: "Credit Life", premium: 3100, coverage: 2500000, status: "Bound", created: "26 Apr 2026", expires: "10 May 2026", channel: "Moniepoint", icon: "💳" },
  { id: "q9", quoteRef: "QT-2026-0134", customer: "Tunde Akintola", customerEmail: "tunde.a@m-kopa.ng", product: "Device Insurance", premium: 1850, coverage: 200000, status: "Paid", created: "25 Apr 2026", expires: "09 May 2026", channel: "M-KOPA", icon: "📱" },
  { id: "q10", quoteRef: "QT-2026-0133", customer: "Yetunde Ojo", customerEmail: "yetunde@kuda.business", product: "Group Health", premium: 12500, coverage: 5000000, status: "Sent", created: "25 Apr 2026", expires: "09 May 2026", channel: "Kuda", icon: "❤️" },
];

const TEAM_MEMBERS = [
  { id: "t1", name: "Bunmi Adeyemi", email: "bunmi@casava.com", role: "Admin", lastActive: "Active now", status: "Active", joined: "Founding member" },
  { id: "t2", name: "Tola Akinsanya", email: "tola@casava.com", role: "Underwriter", lastActive: "12 min ago", status: "Active", joined: "Jan 2026" },
  { id: "t3", name: "Damilola Egwu", email: "damilola@casava.com", role: "Claims handler", lastActive: "2 hours ago", status: "Active", joined: "Feb 2026" },
  { id: "t4", name: "Seyi Williams", email: "seyi@casava.com", role: "Finance", lastActive: "Yesterday", status: "Active", joined: "Nov 2025" },
  { id: "t5", name: "Kemi Bassey", email: "kemi@casava.com", role: "Underwriter", lastActive: "3 days ago", status: "Active", joined: "Mar 2026" },
  { id: "t6", name: "Pending invite", email: "tobi@casava.com", role: "Viewer", lastActive: "—", status: "Pending", joined: "Invited 27 Apr 2026" },
];

const STATEMENTS = [
  { id: "stmt-202604", month: "April", year: 2026, gross: 2147500, fees: 139587, net: 2007913, policies: 412, generated: "01 May 2026" },
  { id: "stmt-202603", month: "March", year: 2026, gross: 1842300, fees: 119750, net: 1722550, policies: 387, generated: "01 Apr 2026" },
  { id: "stmt-202602", month: "February", year: 2026, gross: 1683900, fees: 109454, net: 1574446, policies: 354, generated: "01 Mar 2026" },
  { id: "stmt-202601", month: "January", year: 2026, gross: 1487600, fees: 96694, net: 1390906, policies: 312, generated: "01 Feb 2026" },
  { id: "stmt-202512", month: "December", year: 2025, gross: 1378400, fees: 89596, net: 1288804, policies: 289, generated: "01 Jan 2026" },
  { id: "stmt-202511", month: "November", year: 2025, gross: 1212100, fees: 78787, net: 1133313, policies: 254, generated: "01 Dec 2025" },
  { id: "stmt-202510", month: "October", year: 2025, gross: 987400, fees: 64181, net: 923219, policies: 218, generated: "01 Nov 2025" },
  { id: "stmt-202509", month: "September", year: 2025, gross: 854200, fees: 55523, net: 798677, policies: 192, generated: "01 Oct 2025" },
];

const SESSIONS = [
  { id: "s1", device: "MacBook Pro · Chrome 124", location: "Lagos, Nigeria", ip: "102.89.32.14", lastActive: "Now", current: true },
  { id: "s2", device: "iPhone 15 · Safari", location: "Lagos, Nigeria", ip: "102.89.32.14", lastActive: "32 min ago", current: false },
  { id: "s3", device: "Windows 11 · Firefox 124", location: "Abuja, Nigeria", ip: "197.210.64.220", lastActive: "3 days ago", current: false },
];

const LOGIN_ACTIVITY = [
  { id: "l1", event: "Successful sign-in", device: "MacBook Pro · Chrome", location: "Lagos, NG", ip: "102.89.32.14", time: "29 Apr 2026 · 08:14", outcome: "success" },
  { id: "l2", event: "Successful sign-in", device: "iPhone 15 · Safari", location: "Lagos, NG", ip: "102.89.32.14", time: "29 Apr 2026 · 07:42", outcome: "success" },
  { id: "l3", event: "Failed sign-in attempt", device: "Unknown · Chrome", location: "Lagos, NG", ip: "102.89.31.22", time: "28 Apr 2026 · 22:18", outcome: "failed" },
  { id: "l4", event: "Successful sign-in", device: "MacBook Pro · Chrome", location: "Lagos, NG", ip: "102.89.32.14", time: "28 Apr 2026 · 09:02", outcome: "success" },
  { id: "l5", event: "Password changed", device: "MacBook Pro · Chrome", location: "Lagos, NG", ip: "102.89.32.14", time: "26 Apr 2026 · 11:40", outcome: "success" },
];

// ═══════════════════════════════════════════════════════════════════════════════════
// SIDEBAR + TOPBAR (LAYOUT)
// 6 top-level groups + Settings: Home, Customers, Sell, Manage, Money, Developers
// ═══════════════════════════════════════════════════════════════════════════════════
const NAV_GROUPS = [
  { type: "item", id: "dashboard", label: "Home", icon: Home },
  { type: "item", id: "customers", label: "Customers", icon: Users },
  {
    type: "group", id: "sell", label: "Sell", icon: TrendingUp,
    items: [
      { id: "loan-book", label: "Loan book" },
      { id: "get-insurance", label: "Get Insurance" },
      { id: "quotes", label: "Quotes" },
      { id: "bulk-uploads", label: "Bulk uploads" },
    ],
  },
  {
    type: "group", id: "manage", label: "Manage", icon: Shield,
    items: [
      { id: "policies", label: "Policies" },
      { id: "renewals", label: "Renewals" },
      { id: "claims", label: "Claims" },
    ],
  },
  {
    type: "group", id: "money", label: "Money", icon: WalletIcon,
    items: [
      { id: "wallet", label: "Wallet" },
      { id: "settlements", label: "Settlements" },
      { id: "statements", label: "Statements" },
    ],
  },
  {
    type: "group", id: "developers", label: "Developers", icon: Code,
    items: [
      { id: "developers", label: "Overview", section: null },
      { id: "developers", label: "API keys", section: "api-keys" },
      { id: "developers", label: "Webhooks", section: "webhooks" },
      { id: "developers", label: "Integration health", section: "health" },
      { id: "developers", label: "Documentation", section: "docs" },
    ],
  },
];
const NAV_BOTTOM = [
  {
    type: "group", id: "settings-group", label: "Settings", icon: Settings,
    items: [
      { id: "settings", label: "Account" },
      { id: "roles", label: "Team & access" },
      { id: "notifications", label: "Notifications" },
      { id: "security", label: "Security" },
      { id: "audit-log", label: "Audit log" },
    ],
  },
];

// Find which group (if any) contains a given page id
function findGroupForPage(pageId, sectionId) {
  for (const g of [...NAV_GROUPS, ...NAV_BOTTOM]) {
    if (g.type === "group" && g.items.some(i => i.id === pageId)) return g.id;
  }
  return null;
}

function Sidebar({ current, sectionParam, onNavigate, collapsed, onToggle, onSearchOpen, openGroups, onToggleGroup, onLogout, partner }) {
  const W = collapsed ? 72 : 248;
  return (
    <aside style={{
      width: W, flexShrink: 0, background: T.surface, borderRight: `1px solid ${T.border}`,
      display: "flex", flexDirection: "column", padding: 16, gap: 4, position: "sticky", top: 0, height: "100vh",
      transition: "width 0.2s ease", overflowY: "auto",
    }}>
      <div style={{ padding: "8px 8px 16px", marginBottom: 4, borderBottom: `1px solid ${T.border}` }}>
        <div style={{ display: "flex", alignItems: "center", justifyContent: collapsed ? "center" : "space-between" }}>
          {collapsed ? (
            <CMark size={28} color={T.brand}/>
          ) : (
            <Wordmark height={20}/>
          )}
          {!collapsed && (
            <button onClick={onToggle} style={{ background: "transparent", border: "none", padding: 4, cursor: "pointer", color: T.textSec, borderRadius: 6 }}>
              <ChevronLeft size={16}/>
            </button>
          )}
        </div>
        {!collapsed && partner && (
          <div style={{ marginTop: 10, paddingTop: 10, borderTop: `1px solid ${T.border}`, display: "flex", alignItems: "center", gap: 8 }}>
            <span style={{ fontSize: 10, fontWeight: 600, color: T.textTer, letterSpacing: "0.06em", textTransform: "uppercase" }}>For</span>
            <PartnerLogo name={partner.name} color={partner.color} logoUrl={partner.logoUrl} size={16}/>
            <span style={{ fontSize: 12, fontWeight: 700, color: T.text }}>{partner.name}</span>
          </div>
        )}
      </div>

      {/* Search trigger — ⌘K */}
      <button onClick={onSearchOpen} title="Search (⌘K)" style={{
        marginBottom: 12, padding: collapsed ? "10px" : "8px 12px",
        background: T.surfaceTint, border: "none", borderRadius: 10, cursor: "pointer",
        display: "flex", alignItems: "center", gap: 10, color: T.textSec, fontFamily: FONT,
        fontSize: 13, fontWeight: 500, justifyContent: collapsed ? "center" : "flex-start",
      }}>
        <Search size={16} strokeWidth={2.2}/>
        {!collapsed && (
          <>
            <span style={{ flex: 1, textAlign: "left" }}>Search</span>
            <kbd style={{
              fontSize: 10, fontWeight: 700, fontFamily: "ui-monospace, monospace",
              background: T.surface, color: T.textSec, padding: "2px 6px", borderRadius: 4,
              border: `1px solid ${T.border}`,
            }}>⌘K</kbd>
          </>
        )}
      </button>

      {collapsed && (
        <button onClick={onToggle} style={{ background: T.surfaceTint, border: "none", padding: 8, cursor: "pointer", color: T.textSec, borderRadius: 8, marginBottom: 8 }}>
          <ChevronRight size={14}/>
        </button>
      )}

      <NavGroups groups={NAV_GROUPS} current={current} sectionParam={sectionParam} onNavigate={onNavigate} collapsed={collapsed} openGroups={openGroups} onToggleGroup={onToggleGroup}/>
      <div style={{ flex: 1 }}/>
      <NavGroups groups={NAV_BOTTOM} current={current} sectionParam={sectionParam} onNavigate={onNavigate} collapsed={collapsed} openGroups={openGroups} onToggleGroup={onToggleGroup}/>

      <button onClick={onLogout} style={{
        marginTop: 8, padding: collapsed ? "10px" : "10px 12px", background: "transparent", border: "none",
        borderRadius: 10, cursor: "pointer", display: "flex", alignItems: "center", gap: 12, color: T.textSec,
        fontFamily: FONT, fontSize: 13, fontWeight: 500, borderTop: `1px solid ${T.border}`,
        borderRadius: 0, paddingTop: 14, marginTop: 8,
      }}>
        <LogOut size={16} strokeWidth={2.2}/>
        {!collapsed && "Log out"}
      </button>
    </aside>
  );
}

function NavGroups({ groups, current, sectionParam, onNavigate, collapsed, openGroups, onToggleGroup }) {
  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 2 }}>
      {groups.map(g => {
        if (g.type === "item") {
          const active = current === g.id;
          const Icon = g.icon;
          return (
            <button key={g.id} onClick={() => onNavigate(g.id)} style={{
              padding: collapsed ? "10px" : "10px 12px",
              background: active ? T.brandBg : "transparent", border: "none",
              borderRadius: 10, cursor: "pointer", display: "flex", alignItems: "center",
              gap: 12, color: active ? T.brand : T.text, fontFamily: FONT,
              fontSize: 13, fontWeight: active ? 600 : 500, transition: "background 0.12s",
              justifyContent: collapsed ? "center" : "flex-start",
            }}
              onMouseEnter={e => !active && (e.currentTarget.style.background = T.surfaceTint)}
              onMouseLeave={e => !active && (e.currentTarget.style.background = "transparent")}>
              <Icon size={16} strokeWidth={active ? 2.4 : 2}/>
              {!collapsed && g.label}
            </button>
          );
        }
        // Group
        const Icon = g.icon;
        const isOpen = openGroups.includes(g.id);
        const hasActiveChild = g.items.some(i => {
          if (i.id !== current) return false;
          if (i.section !== undefined && i.section !== sectionParam) return false;
          return true;
        });
        const childIsOnSamePage = g.items.some(i => i.id === current);
        // When collapsed, clicking a group navigates to its first child instead of toggling (since submenu would be hidden)
        const handleGroupClick = () => {
          if (collapsed) {
            const first = g.items[0];
            if (first) onNavigate(first.id, first.section ? { section: first.section } : {});
          } else {
            onToggleGroup(g.id);
          }
        };
        return (
          <div key={g.id}>
            <button onClick={handleGroupClick} title={collapsed ? g.label : undefined} style={{
              padding: collapsed ? "10px" : "10px 12px", width: "100%",
              background: collapsed && childIsOnSamePage ? T.brandBg : "transparent", border: "none",
              borderRadius: 10, cursor: "pointer", display: "flex", alignItems: "center",
              gap: 12, color: collapsed && childIsOnSamePage ? T.brand : T.text, fontFamily: FONT,
              fontSize: 13, fontWeight: childIsOnSamePage ? 600 : 500, transition: "background 0.12s",
              justifyContent: collapsed ? "center" : "flex-start", position: "relative",
            }}
              onMouseEnter={e => !(collapsed && childIsOnSamePage) && (e.currentTarget.style.background = T.surfaceTint)}
              onMouseLeave={e => !(collapsed && childIsOnSamePage) && (e.currentTarget.style.background = "transparent")}>
              <span style={{ position: "relative", display: "inline-flex" }}>
                <Icon size={16} strokeWidth={childIsOnSamePage ? 2.4 : 2}/>
                {childIsOnSamePage && !isOpen && !collapsed && (
                  <span style={{ position: "absolute", top: -2, right: -2, width: 6, height: 6, borderRadius: "50%", background: T.brand }}/>
                )}
              </span>
              {!collapsed && (
                <>
                  <span style={{ flex: 1, textAlign: "left" }}>{g.label}</span>
                  <ChevronDown size={13} strokeWidth={2.2} color={T.textSec} style={{ transform: isOpen ? "rotate(0deg)" : "rotate(-90deg)", transition: "transform 0.2s" }}/>
                </>
              )}
            </button>
            {isOpen && !collapsed && (
              <div style={{ display: "flex", flexDirection: "column", gap: 1, paddingLeft: 16, marginTop: 2, marginBottom: 4, borderLeft: `1px solid ${T.border}`, marginLeft: 18 }}>
                {g.items.map((item, i) => {
                  const isActive = item.id === current && (item.section === undefined || item.section === sectionParam || (item.section === null && !sectionParam));
                  return (
                    <button key={`${item.id}-${item.section ?? i}`} onClick={() => onNavigate(item.id, item.section ? { section: item.section } : {})} style={{
                      padding: "7px 12px", background: isActive ? T.brandBg : "transparent", border: "none",
                      borderRadius: 8, cursor: "pointer", textAlign: "left", fontFamily: FONT,
                      fontSize: 13, fontWeight: isActive ? 600 : 500,
                      color: isActive ? T.brand : T.textSec, transition: "background 0.12s",
                    }}
                      onMouseEnter={e => !isActive && (e.currentTarget.style.background = T.surfaceTint)}
                      onMouseLeave={e => !isActive && (e.currentTarget.style.background = "transparent")}>
                      {item.label}
                    </button>
                  );
                })}
              </div>
            )}
          </div>
        );
      })}
    </div>
  );
}

// ═══════════════════════════════════════════════════════════════════════════════════
// COMMAND PALETTE — ⌘K search across customers, policies, claims, pages
// ═══════════════════════════════════════════════════════════════════════════════════
function CommandPalette({ open, onClose, onNavigate }) {
  const [q, setQ] = useState("");
  const [highlighted, setHighlighted] = useState(0);

  // Build flat search corpus
  const corpus = useMemo(() => {
    const items = [];
    // Pages (from nav)
    for (const g of [...NAV_GROUPS, ...NAV_BOTTOM]) {
      if (g.type === "item") {
        items.push({ kind: "page", label: g.label, hint: "Page", id: g.id, icon: g.icon });
      } else {
        for (const it of g.items) {
          items.push({ kind: "page", label: it.label, hint: g.label, id: it.id, section: it.section, icon: g.icon });
        }
      }
    }
    // Customers
    for (const c of CUSTOMERS) {
      items.push({ kind: "customer", label: c.name, hint: c.email, id: c.id, route: "customer-detail" });
    }
    // Policies
    for (const p of POLICIES) {
      items.push({ kind: "policy", label: p.policyNumber, hint: `${p.product} · ${p.customer}`, id: p.id, route: "policy-detail" });
    }
    // Claims
    for (const cl of CLAIMS) {
      items.push({ kind: "claim", label: cl.claimRef, hint: `${cl.product} · ${cl.customer}`, id: cl.id, route: "claim-detail" });
    }
    // Quotes
    for (const qt of QUOTES) {
      items.push({ kind: "quote", label: qt.quoteRef, hint: `${qt.product} · ${qt.customer}`, id: qt.id, route: "quote-detail" });
    }
    return items;
  }, []);

  const results = useMemo(() => {
    if (!q.trim()) return corpus.filter(i => i.kind === "page").slice(0, 8);
    const needle = q.toLowerCase();
    return corpus.filter(i => i.label.toLowerCase().includes(needle) || (i.hint || "").toLowerCase().includes(needle)).slice(0, 12);
  }, [q, corpus]);

  useEffect(() => { setHighlighted(0); }, [q]);

  useEffect(() => {
    if (!open) return;
    const handler = (e) => {
      if (e.key === "Escape") { e.preventDefault(); onClose(); }
      else if (e.key === "ArrowDown") { e.preventDefault(); setHighlighted(h => Math.min(h + 1, results.length - 1)); }
      else if (e.key === "ArrowUp") { e.preventDefault(); setHighlighted(h => Math.max(h - 1, 0)); }
      else if (e.key === "Enter") { e.preventDefault(); selectResult(results[highlighted]); }
    };
    window.addEventListener("keydown", handler);
    return () => window.removeEventListener("keydown", handler);
  }, [open, results, highlighted]);

  const selectResult = (r) => {
    if (!r) return;
    if (r.kind === "page") {
      onNavigate(r.id, r.section ? { section: r.section } : {});
    } else if (r.kind === "customer") {
      onNavigate("customer-detail", { id: r.id });
    } else if (r.kind === "policy") {
      onNavigate("policy-detail", { id: r.id });
    } else if (r.kind === "claim") {
      onNavigate("claim-detail", { id: r.id });
    } else if (r.kind === "quote") {
      onNavigate("quote-detail", { id: r.id });
    }
    setQ("");
    onClose();
  };

  if (!open) return null;
  const kindIcon = { page: Home, customer: User, policy: Shield, claim: FileCheck2, quote: FileText };
  const kindLabel = { page: "Page", customer: "Customer", policy: "Policy", claim: "Claim", quote: "Quote" };

  return (
    <div style={{
      position: "fixed", inset: 0, background: "rgba(28, 18, 14, 0.55)",
      display: "flex", alignItems: "flex-start", justifyContent: "center", zIndex: 1100,
      animation: "fadeIn 0.15s ease-out", padding: "8vh 16px",
    }} onClick={onClose}>
      <div onClick={e => e.stopPropagation()} style={{
        width: "100%", maxWidth: 560, background: T.surface, borderRadius: 14,
        boxShadow: "0 24px 60px rgba(0,0,0,0.22)", overflow: "hidden",
        animation: "slideUp 0.2s ease-out",
      }}>
        <div style={{ padding: "14px 18px", borderBottom: `1px solid ${T.border}`, display: "flex", alignItems: "center", gap: 12 }}>
          <Search size={18} color={T.textSec} strokeWidth={2.2}/>
          <input
            type="text"
            autoFocus
            autoComplete="off"
            value={q}
            onChange={e => setQ(e.target.value)}
            placeholder="Search customers, policies, claims, pages..."
            style={{ flex: 1, border: "none", outline: "none", fontFamily: FONT, fontSize: 15, color: T.text, background: "transparent" }}
          />
          <kbd style={{ fontSize: 10, fontWeight: 700, fontFamily: "ui-monospace, monospace", background: T.surfaceTint, color: T.textSec, padding: "3px 7px", borderRadius: 4 }}>ESC</kbd>
        </div>
        <div style={{ maxHeight: 420, overflowY: "auto", padding: 8 }}>
          {results.length === 0 ? (
            <div style={{ padding: "32px 16px", textAlign: "center", fontSize: 13, color: T.textSec }}>
              No matches for <strong>{q}</strong>
            </div>
          ) : (
            results.map((r, i) => {
              const I = kindIcon[r.kind] || Home;
              const isH = i === highlighted;
              return (
                <button key={`${r.kind}-${r.id}-${i}`} onClick={() => selectResult(r)} onMouseEnter={() => setHighlighted(i)} style={{
                  width: "100%", padding: "10px 12px", borderRadius: 10, border: "none",
                  background: isH ? T.brandBg : "transparent", cursor: "pointer",
                  display: "flex", alignItems: "center", gap: 12, fontFamily: FONT, textAlign: "left",
                }}>
                  <I size={16} strokeWidth={2.2} color={isH ? T.brand : T.textSec}/>
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <div style={{ fontSize: 13, fontWeight: 600, color: isH ? T.brand : T.text, whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>{r.label}</div>
                    <div style={{ fontSize: 11, color: T.textSec, marginTop: 1, whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>{r.hint}</div>
                  </div>
                  <Pill color="gray" size="sm">{kindLabel[r.kind]}</Pill>
                  {isH && <ArrowRight size={13} color={T.brand}/>}
                </button>
              );
            })
          )}
        </div>
        <div style={{ padding: "10px 16px", borderTop: `1px solid ${T.border}`, background: T.surfaceTint, display: "flex", gap: 16, fontSize: 11, color: T.textSec }}>
          <span><kbd style={kbdStyle}>↑</kbd> <kbd style={kbdStyle}>↓</kbd> Navigate</span>
          <span><kbd style={kbdStyle}>↵</kbd> Select</span>
          <span><kbd style={kbdStyle}>esc</kbd> Close</span>
        </div>
      </div>
    </div>
  );
}
const kbdStyle = { fontSize: 10, fontWeight: 700, fontFamily: "ui-monospace, monospace", background: T.surface, color: T.textSec, padding: "1px 5px", borderRadius: 3, border: `1px solid ${T.border}` };

function TopBar({ user, breadcrumb, onNavigate, onSearchOpen }) {
  const [notifOpen, setNotifOpen] = useState(false);
  return (
    <div className="topbar-pad" style={{
      display: "flex", alignItems: "center", justifyContent: "space-between",
      background: T.bg, borderBottom: `1px solid ${T.border}`, gap: 16,
    }}>
      <div style={{ display: "flex", alignItems: "center", gap: 8, fontSize: 13, color: T.textSec, minWidth: 0, overflow: "hidden" }}>
        {breadcrumb?.map((b, i) => {
          const isLast = i === breadcrumb.length - 1;
          const clickable = !isLast && b.page;
          return (
            <span key={i} style={{ display: "inline-flex", alignItems: "center", gap: 8 }}>
              {i > 0 && <ChevronRight size={13}/>}
              {clickable ? (
                <button onClick={() => onNavigate?.(b.page)} style={{
                  background: "transparent", border: "none", padding: 0, fontFamily: FONT,
                  cursor: "pointer", color: T.textSec, fontWeight: 500, fontSize: 13,
                  whiteSpace: "nowrap",
                }}
                  onMouseEnter={e => e.currentTarget.style.color = T.text}
                  onMouseLeave={e => e.currentTarget.style.color = T.textSec}>
                  {b.label}
                </button>
              ) : (
                <span style={{ color: isLast ? T.text : T.textSec, fontWeight: isLast ? 600 : 500, whiteSpace: "nowrap" }}>{b.label}</span>
              )}
            </span>
          );
        })}
      </div>
      <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
        <button onClick={onSearchOpen} title="Search (⌘K)" className="topbar-search" style={{ background: T.surface, border: `1px solid ${T.border}`, borderRadius: 999, cursor: "pointer", display: "inline-flex", alignItems: "center", gap: 8, color: T.textSec, fontFamily: FONT, fontSize: 12, fontWeight: 500 }}>
          <Search size={14} strokeWidth={2.2}/>
          <span className="topbar-search-label">Search</span>
          <kbd className="topbar-search-kbd" style={{ fontSize: 10, fontWeight: 700, fontFamily: "ui-monospace, monospace", background: T.surfaceTint, color: T.textSec, padding: "1px 5px", borderRadius: 3 }}>⌘K</kbd>
        </button>
        <div style={{ position: "relative" }}>
          <button onClick={() => setNotifOpen(o => !o)} style={{ position: "relative", background: T.surface, border: `1px solid ${T.border}`, borderRadius: "50%", width: 36, height: 36, cursor: "pointer", display: "inline-flex", alignItems: "center", justifyContent: "center", color: T.textSec }}>
            <Bell size={15}/>
            <span style={{ position: "absolute", top: 7, right: 7, width: 7, height: 7, background: T.brand, borderRadius: "50%" }}/>
          </button>
          <NotificationsDropdown open={notifOpen} onClose={() => setNotifOpen(false)} onNavigate={onNavigate}/>
        </div>
        <div className="user-chip" style={{ display: "flex", alignItems: "center", gap: 10, padding: "4px 14px 4px 4px", background: T.surface, border: `1px solid ${T.border}`, borderRadius: 999 }}>
          <Avatar name={user.name} size={28} gradient="brown"/>
          <div className="user-chip-text" style={{ fontSize: 12, lineHeight: 1.3 }}>
            <div style={{ fontWeight: 600 }}>{user.name}</div>
            <div style={{ color: T.textSec, fontSize: 11 }}>{user.role}</div>
          </div>
        </div>
      </div>
    </div>
  );
}

// ═══════════════════════════════════════════════════════════════════════════════════
// PAGES
// ═══════════════════════════════════════════════════════════════════════════════════
function DashboardPage({ navigate, openSell }) {
  const [range, setRange] = useState("7d");
  const ranges = { "7d": "7 days", "30d": "30 days", "90d": "90 days", "ytd": "YTD" };
  const rangeData = {
    "7d":  { policies: 1284, premium: 2.15,  polDelta: "47 this week",       premDelta: "22% vs last week" },
    "30d": { policies: 1247, premium: 8.42,  polDelta: "287 this month",      premDelta: "31% vs last month" },
    "90d": { policies: 1142, premium: 24.18, polDelta: "672 this quarter",    premDelta: "44% QoQ" },
    "ytd": { policies: 1024, premium: 36.62, polDelta: "954 YTD",             premDelta: "62% YoY" },
  }[range];

  const todayLoans      = LOANS.filter(l => l.disbursedAt === "28 Apr 2026");
  const todayUnattached = todayLoans.filter(l => l.attachmentStatus === "unattached");
  const opportunity     = todayUnattached.reduce((s, l) => s + Math.round(l.loanAmount * 0.0009), 0);
  const claimsAwaiting  = CLAIMS.filter(c => c.status === "In Review");
  const totalLoans      = LOANS.length;
  const attachedLoans   = LOANS.filter(l => l.attachmentStatus === "attached").length;
  const coverage        = totalLoans ? (attachedLoans / totalLoans) * 100 : 0;
  const recentLoans     = LOANS.filter(l => {
    const m = { Jan:1,Feb:2,Mar:3,Apr:4,May:5,Jun:6,Jul:7,Aug:8,Sep:9,Oct:10,Nov:11,Dec:12 };
    const [d, mon, y] = l.disbursedAt.split(" ");
    return parseInt(y)*10000 + m[mon]*100 + parseInt(d) >= 20260422;
  });
  const recentAttached  = recentLoans.filter(l => l.attachmentStatus === "attached").length;
  const rate7d          = recentLoans.length ? (recentAttached / recentLoans.length)*100 : 0;

  const actionItems = [
    todayUnattached.length > 0 && {
      id:"unattached", Icon:Zap, color:T.dangerText, bg:T.dangerBg, border:T.dangerBorder,
      label:`${todayUnattached.length} loans disbursed today without credit life`,
      sub:`₦${opportunity.toLocaleString()} in premium opportunity`,
      cta:"Attach now", action:()=>navigate("loan-book"),
    },
    claimsAwaiting.length > 0 && {
      id:"claims", Icon:FileText, color:T.amberText, bg:T.amberBg, border:T.amberBorder,
      label:`${claimsAwaiting.length} claim${claimsAwaiting.length>1?"s":""} awaiting your review`,
      sub:"First decision due — oldest filed 2 days ago",
      cta:"Review claims", action:()=>navigate("claims"),
    },
    { id:"renewals", Icon:Calendar, color:T.brandDeep, bg:T.brandBg, border:T.brandLight,
      label:"6 policies renewing in the next 7 days",
      sub:"Auto-renewal is off for 2 of these — customer may lapse",
      cta:"View renewals", action:()=>navigate("renewals"),
    },
  ].filter(Boolean);

  return (
    <div style={{ display:"flex", flexDirection:"column", gap:24 }}>

      {/* Header + range picker */}
      <div className="stagger-fadeIn" style={{ display:"flex", alignItems:"flex-start", justifyContent:"space-between", flexWrap:"wrap", gap:12 }}>
        <div>
          <Eyebrow color={T.brand}>Welcome back</Eyebrow>
          <Display size="xl" italic>Bunmi, here's your portfolio.</Display>
        </div>
        <div style={{ display:"flex", gap:4, padding:4, background:T.surface, border:`1px solid ${T.border}`, borderRadius:999 }}>
          {Object.entries(ranges).map(([id,label]) => (
            <button key={id} onClick={()=>setRange(id)} style={{ padding:"6px 12px", borderRadius:999, border:"none", fontFamily:FONT, fontSize:12, fontWeight:600, cursor:"pointer", background:range===id?T.brand:"transparent", color:range===id?"#fff":T.textSec, transition:"all 0.15s" }}>{label}</button>
          ))}
        </div>
      </div>

      {/* Onboarding checklist */}
      <div className="stagger-fadeIn" style={{ animationDelay:"30ms" }}>
        <PartnerOnboardingChecklist collapsible steps={[
          { status:"done",    label:"Sign Master Services Agreement",  detail:"Signed Apr 12, 2026 by Moniepoint legal" },
          { status:"done",    label:"Provide KYB documents",           detail:"Verified — CAC certificate, directors\' IDs, tax registration" },
          { status:"done",    label:"Connect Sandbox API key",         detail:"Last test event received 2 days ago" },
          { status:"done",    label:"Sign technical scope document",   detail:"Embed mode + API access for Credit Life, Device, Funds Protection" },
          { status:"current", label:"Switch to production credentials",detail:"Generate live keys and test with a real ₦100 transaction", cta:<Btn variant="brand" size="sm" iconRight={ArrowRight} onClick={()=>navigate("developers",{section:"api-keys"})}>Generate</Btn> },
          { status:"locked",  label:"First live policy underwritten",  detail:"We'll celebrate this one with you" },
        ]}/>
      </div>

      {/* Today's briefing strip */}
      <div style={{ background:T.brown, color:"#fff", borderRadius:18, padding:"20px 24px", display:"flex", flexWrap:"wrap", gap:0 }}>
        <div style={{ fontSize:11, fontWeight:700, letterSpacing:"0.08em", textTransform:"uppercase", opacity:0.78, width:"100%", marginBottom:14 }}>Today · 28 Apr 2026</div>
        {[
          { label:"Loans disbursed", value:todayLoans.length, sub:`${todayUnattached.length} unattached`, urgent:todayUnattached.length>0 },
          { label:"Policies issued", value:2,                  sub:"active and in force" },
          { label:"Claims filed",    value:1,                  sub:"under triage" },
          { label:"Commission",      value:"₦8,400",       sub:"earned today" },
        ].map((stat,i) => (
          <div key={i} style={{ flex:"1 1 120px", padding:"0 20px", borderLeft:i>0?"1px solid rgba(255,255,255,0.15)":"none" }}>
            <div style={{ fontFamily:FONT_DISPLAY, fontSize:32, fontWeight:400, letterSpacing:"-0.02em", lineHeight:1 }}>{stat.value}</div>
            <div style={{ fontSize:12, opacity:0.85, marginTop:4 }}>{stat.label}</div>
            <div style={{ fontSize:11, marginTop:2, opacity:stat.urgent?1:0.6, color:stat.urgent?T.amber:undefined, fontWeight:stat.urgent?700:400 }}>{stat.sub}</div>
          </div>
        ))}
      </div>

      {/* Action queue */}
      {actionItems.length > 0 && (
        <div>
          <Eyebrow mb={10} color={T.dangerText}>{actionItems.length} thing{actionItems.length!==1?"s":""} need your attention</Eyebrow>
          <div style={{ display:"flex", flexDirection:"column", gap:8 }}>
            {actionItems.map(item => (
              <div key={item.id} style={{ display:"flex", alignItems:"center", gap:14, padding:"14px 18px", background:item.bg, border:`1px solid ${item.border}`, borderRadius:14 }}>
                <div style={{ width:36,height:36,borderRadius:10,background:item.color,color:"#fff",display:"inline-flex",alignItems:"center",justifyContent:"center",flexShrink:0 }}>
                  <item.Icon size={17} strokeWidth={2.3}/>
                </div>
                <div style={{ flex:1, minWidth:0 }}>
                  <div style={{ fontSize:13, fontWeight:700, color:item.color }}>{item.label}</div>
                  <div style={{ fontSize:11, color:T.textSec, marginTop:2 }}>{item.sub}</div>
                </div>
                <button onClick={item.action} style={{ padding:"7px 14px", borderRadius:999, border:`1.5px solid ${item.color}`, background:"transparent", color:item.color, fontFamily:FONT, fontSize:12, fontWeight:700, cursor:"pointer", whiteSpace:"nowrap", flexShrink:0 }}>{item.cta} →</button>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* KPI tiles */}
      <div key={range} style={{ display:"grid", gridTemplateColumns:"repeat(auto-fit, minmax(200px, 1fr))", gap:14 }}>
        <button onClick={()=>navigate("loan-book")} style={{ all:"unset", cursor:"pointer", display:"block" }}>
          <PartnerKPICard label="Loan book coverage" value={`${coverage.toFixed(1)}%`} delta={`${attachedLoans} of ${totalLoans}`} deltaDirection={coverage>=75?"up":"down"} sparkline={SPARKLINES.policies} accent={coverage>=75?T.success:coverage>=50?T.amber:T.danger}/>
        </button>
        <button onClick={()=>navigate("loan-book")} style={{ all:"unset", cursor:"pointer", display:"block" }}>
          <PartnerKPICard label="Attachment rate · 7d" value={`${rate7d.toFixed(1)}%`} delta={`${recentAttached} of ${recentLoans.length} loans`} deltaDirection={rate7d>=75?"up":"down"} sparkline={SPARKLINES.customers} accent={rate7d>=75?T.success:rate7d>=50?T.amber:T.danger}/>
        </button>
        <button onClick={()=>navigate("policies")} style={{ all:"unset", cursor:"pointer", display:"block" }}>
          <PartnerKPICard label="Active policies" value={rangeData.policies.toLocaleString()} delta={rangeData.polDelta} deltaDirection="up" sparkline={SPARKLINES.policies}/>
        </button>
        <button onClick={()=>navigate("wallet")} style={{ all:"unset", cursor:"pointer", display:"block" }}>
          <PartnerKPICard label={`Premium ${range==="ytd"?"YTD":"MTD"}`} value={`₦${rangeData.premium}M`} delta={rangeData.premDelta} deltaDirection="up" sparkline={SPARKLINES.premium}/>
        </button>
      </div>

      {/* Charts row */}
      <div className="grid-2col-1col" style={{ gap:14 }}>
        <Card title="Daily attachment rate" right={<><Pill color="gray" size="sm">Last 7 days</Pill><Btn variant="ghost" size="sm" iconRight={ArrowRight} onClick={()=>navigate("loan-book")} style={{ marginLeft:8 }}>Loan book</Btn></>}>
          {(()=>{
            const days=["22 Apr","23 Apr","24 Apr","25 Apr","26 Apr","27 Apr","28 Apr"];
            const series=days.map(d=>{
              const dl=LOANS.filter(l=>l.disbursedAt.startsWith(d));
              const da=dl.filter(l=>l.attachmentStatus==="attached").length;
              return {x:d.replace(" Apr",""),y:dl.length?Math.round((da/dl.length)*100):0};
            });
            return <LineChart height={200} yAxisLabel="Rate (%)" valueFormat={v=>`${v}%`} series={[{label:"Attachment rate",points:series}]}/>;
          })()}
        </Card>
        <Card title="Portfolio mix" right={<Pill color="gray" size="sm">By premium</Pill>}>
          <DonutChart size={150} thickness={24} centerValue={POLICIES.filter(p=>p.status==="ACTIVE").length} centerLabel="Active"
            data={[
              {label:"Credit Life",      value:POLICIES.filter(p=>p.product==="Credit Life"       &&p.status==="ACTIVE").reduce((s,p)=>s+p.premium,0),color:T.brand},
              {label:"Device",           value:POLICIES.filter(p=>p.product==="Device Insurance"   &&p.status==="ACTIVE").reduce((s,p)=>s+p.premium,0),color:T.purple},
              {label:"Funds Protection", value:POLICIES.filter(p=>p.product==="Funds Protection"  &&p.status==="ACTIVE").reduce((s,p)=>s+p.premium,0),color:T.cyan},
              {label:"Building",         value:POLICIES.filter(p=>p.product==="Building Insurance"&&p.status==="ACTIVE").reduce((s,p)=>s+p.premium,0),color:T.brown},
            ].filter(d=>d.value>0)}
          />
        </Card>
      </div>

      {/* Quick actions */}
      <div>
        <Eyebrow mb={10}>Quick actions</Eyebrow>
        <div style={{ display:"grid", gridTemplateColumns:"repeat(auto-fit, minmax(200px, 1fr))", gap:12 }}>
          {[
            {icon:Plus,       label:"Sell insurance", desc:"Quote a customer",        onClick:()=>navigate("get-insurance"), color:T.brand},
            {icon:FileCheck2, label:"File a claim",   desc:"On behalf of a customer", onClick:()=>navigate("new-claim"),     color:T.amber},
            {icon:Upload,     label:"Bulk upload",    desc:"Issue policies at scale",  onClick:()=>navigate("bulk-uploads"), color:T.success},
            {icon:Code,       label:"API & webhooks", desc:"Developer dashboard",      onClick:()=>navigate("developers"),   color:T.purple},
          ].map(qa=>(
            <button key={qa.label} onClick={qa.onClick} style={{ padding:16,background:T.surface,border:`1px solid ${T.border}`,borderRadius:14,cursor:"pointer",display:"flex",alignItems:"center",gap:12,fontFamily:FONT,textAlign:"left",transition:"transform 0.12s, border-color 0.12s" }}
              onMouseEnter={e=>(e.currentTarget.style.transform="translateY(-1px)",e.currentTarget.style.borderColor=T.brand)}
              onMouseLeave={e=>(e.currentTarget.style.transform="none",e.currentTarget.style.borderColor=T.border)}>
              <div style={{ width:36,height:36,borderRadius:10,background:qa.color,color:"#fff",display:"inline-flex",alignItems:"center",justifyContent:"center",flexShrink:0 }}>
                <qa.icon size={17} strokeWidth={2.4}/>
              </div>
              <div style={{ flex:1, minWidth:0 }}>
                <div style={{ fontSize:13,fontWeight:700,color:T.text }}>{qa.label}</div>
                <div style={{ fontSize:11,color:T.textSec,marginTop:2 }}>{qa.desc}</div>
              </div>
              <ArrowRight size={14} color={T.textSec} strokeWidth={2.2}/>
            </button>
          ))}
        </div>
      </div>

      {/* Recent policies + AI Rec + Embedded channels */}
      <div className="grid-2col-1col">
        <div style={{ background:T.surface,border:`1px solid ${T.border}`,borderRadius:14,padding:22 }}>
          <div style={{ display:"flex",alignItems:"center",justifyContent:"space-between",marginBottom:16 }}>
            <Display size="sm">Recent policies</Display>
            <Btn variant="ghost" size="sm" iconRight={ArrowRight} onClick={()=>navigate("policies")}>View all</Btn>
          </div>
          <DataTable density="compact" columns={[
            {key:"customer",label:"Customer",render:(v,r)=>(
              <div style={{ display:"flex",alignItems:"center",gap:10 }}>
                <CustomerAvatar name={v} customerId={r.customerId} size={32}/>
                <div>
                  <div style={{ fontWeight:600 }}>{v}</div>
                  <div style={{ fontSize:11,color:T.textSec,fontFamily:"ui-monospace, monospace" }}>{r.policyNumber}</div>
                </div>
              </div>
            )},
            {key:"product",label:"Product",hideMobile:true,render:(v,r)=>(
              <span style={{ display:"inline-flex",alignItems:"center",gap:8 }}>
                <ProductIcon productId={PRODUCTS.find(p=>p.name===v)?.id||""} product={PRODUCTS.find(p=>p.name===v)} size={18}/>{v}
              </span>
            )},
            {key:"premium",label:"Premium",numeric:true,align:"right",render:v=><Money amount={v} size="sm"/>},
            {key:"status", label:"Status", align:"right",render:v=><Pill color={v==="ACTIVE"?"green":v==="PENDING"?"amber":"gray"} size="sm">{v}</Pill>},
          ]} rows={POLICIES.slice(0,5)} onRowClick={row=>navigate("policy-detail",{id:row.id})}/>
        </div>
        <div style={{ display:"flex", flexDirection:"column", gap:14 }}>
          <AIRecommendationCardColored surface="brand" casaState="thinking"
            productName="Credit Life · today's gap"
            headline={`${todayUnattached.length} loans went out today without credit life.`}
            reasoning={`${ACTIVE_PARTNER.name} disbursed ${todayLoans.length} loans today; ${todayUnattached.length} have no credit-life policy attached. Estimated opportunity: ₦${opportunity.toLocaleString()} in premium.`}
            confidence={5} onAccept={()=>navigate("loan-book")} onDismiss={()=>{}}
          />
          <div style={{ background:T.surface,border:`1px solid ${T.border}`,borderRadius:14,padding:22 }}>
            <Eyebrow>Embedded channels</Eyebrow>
            <div style={{ display:"flex", flexDirection:"column", gap:12, marginTop:4 }}>
              {[
                { name:"Moniepoint",       policies:412, share:78 },
                { name:"M-KOPA",           policies:287, share:54 },
                { name:"Samsung partner",  policies:198, share:38 },
                { name:"Kuda embedded",    policies:124, share:24 },
              ].map(c => (
                <button key={c.name} onClick={()=>navigate("developers",{section:"health"})} style={{ all:"unset",cursor:"pointer",display:"block",padding:"4px 0" }}>
                  <div style={{ display:"flex",justifyContent:"space-between",fontSize:13,marginBottom:6 }}>
                    <span style={{ fontWeight:600 }}>{c.name}</span>
                    <span style={{ color:T.textSec,fontVariantNumeric:"tabular-nums" }}>{c.policies}</span>
                  </div>
                  <ProgressBar value={c.share}/>
                </button>
              ))}
            </div>
          </div>
        </div>
      </div>

      <Card title="Partner integration health" right={<Btn variant="ghost" size="sm" iconRight={ArrowRight} onClick={()=>navigate("developers",{section:"health"})}>Open developers</Btn>}>
        <PartnerHealthMatrix partners={PARTNER_HEALTH.partners} integrations={PARTNER_HEALTH.integrations} status={PARTNER_HEALTH.status}/>
      </Card>
    </div>
  );
}


function CustomersPage({ navigate }) {
  const [q, setQ] = useState("");
  const [status, setStatus] = useState("all");
  const [addOpen, setAddOpen] = useState(false);

  const filtered = CUSTOMERS.filter(c => {
    const matchQ = c.name.toLowerCase().includes(q.toLowerCase()) || c.email.toLowerCase().includes(q.toLowerCase());
    const matchStatus = status === "all" || c.status.toLowerCase() === status.toLowerCase();
    return matchQ && matchStatus;
  });

  const statusLabel = status === "all" ? "All" : status[0].toUpperCase() + status.slice(1).toLowerCase();

  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 20 }}>
      <div style={{ display: "flex", alignItems: "flex-start", justifyContent: "space-between", flexWrap: "wrap", gap: 12 }}>
        <div>
          <Display size="lg">Customers</Display>
          <div style={{ fontSize: 14, color: T.textSec, marginTop: 6 }}>Customers across all your embedded channels and direct policies.</div>
        </div>
        <div style={{ display: "flex", gap: 8 }}>
          <Btn variant="outline" icon={Upload} onClick={() => navigate("bulk-uploads")}>Import CSV</Btn>
          <Btn variant="primary" icon={Plus} onClick={() => setAddOpen(true)}>New customer</Btn>
        </div>
      </div>
      <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(200px, 1fr))", gap: 14 }}>
        <StatBlock label="Total customers" value={847} delay={0}/>
        <StatBlock label="Active" value={812} delay={60} accent={T.success}/>
        <StatBlock label="Pending KYC" value={28} delay={120} accent={T.amber}/>
      </div>
      <FilterBar rightSlot={<SearchInput value={q} onChange={setQ} placeholder="Search by name, email, BVN..."/>}>
        <ChipMenu label="Status" value={statusLabel} options={[
          { id: "all", label: "All" },
          { id: "active", label: "Active" },
          { id: "inactive", label: "Inactive" },
          { id: "created", label: "Pending KYC" },
        ]} selected={status} onSelect={setStatus}/>
        <FilterChip label="Channel" value="All"/>
        <FilterChip label="Joined"/>
        {(status !== "all" || q) && (
          <button onClick={() => { setStatus("all"); setQ(""); }} style={{
            background: "transparent", border: "none", color: T.textSec,
            fontSize: 12, fontWeight: 600, cursor: "pointer", fontFamily: FONT, marginLeft: 4,
          }}>Clear all</button>
        )}
      </FilterBar>
      <DataTable
        emptyMessage={q || status !== "all" ? "No customers match your filters." : "No customers yet."}
        columns={[
          { key: "name", label: "Customer", render: (v, r) => (
            <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
              <Avatar name={v} size={32} color={r.type === "business" ? "pinkCoral" : "green"} kind={r.type}/>
              <div>
                <div style={{ fontWeight: 600 }}>{v}</div>
                <div style={{ fontSize: 11, color: T.textSec }}>{r.email}</div>
              </div>
            </div>
          )},
          { key: "type", label: "Type", render: v => <Pill color={v === "business" ? "brand" : "dark"} size="sm">{v === "business" ? "Business" : "Individual"}</Pill> },
          { key: "phone", label: "Phone", hideMobile: true },
          { key: "policies", label: "Policies", numeric: true, align: "center" },
          { key: "status", label: "Status", render: v => <Pill color={v === "ACTIVE" ? "green" : v === "INACTIVE" ? "gray" : "amber"} size="sm">{v}</Pill> },
          { key: "joined", label: "Joined", hideMobile: true },
        ]}
        rows={filtered}
        onRowClick={r => navigate("customer-detail", { id: r.id })}
      />
      {addOpen && <CustomerAddModal onClose={() => setAddOpen(false)}/>}
    </div>
  );
}

function CustomerAddModal({ onClose }) {
  const [type, setType] = useState("individual");
  const [step, setStep] = useState(1);
  return (
    <Modal title="Add new customer" onClose={onClose} maxWidth={520}
      footer={
        <>
          <Btn variant="ghost" onClick={onClose}>Cancel</Btn>
          {step === 1 ? (
            <Btn variant="primary" iconRight={ArrowRight} onClick={() => setStep(2)}>Continue</Btn>
          ) : (
            <Btn variant="primary" icon={Check} onClick={onClose}>Save customer</Btn>
          )}
        </>
      }>
      {step === 1 && (
        <div>
          <Eyebrow mb={10}>Customer type</Eyebrow>
          <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 10 }}>
            <button onClick={() => setType("individual")} style={{
              padding: 16, borderRadius: 14, cursor: "pointer", textAlign: "left", fontFamily: FONT,
              background: type === "individual" ? T.brandBg : T.surface,
              border: `1.5px solid ${type === "individual" ? T.brand : T.border}`,
            }}>
              <User size={22} color={type === "individual" ? T.brand : T.textSec} strokeWidth={2.2}/>
              <div style={{ fontSize: 14, fontWeight: 700, marginTop: 8 }}>Individual</div>
              <div style={{ fontSize: 11, color: T.textSec, marginTop: 4, lineHeight: 1.5 }}>Person — BVN, NIN, DOB</div>
            </button>
            <button onClick={() => setType("business")} style={{
              padding: 16, borderRadius: 14, cursor: "pointer", textAlign: "left", fontFamily: FONT,
              background: type === "business" ? T.brandBg : T.surface,
              border: `1.5px solid ${type === "business" ? T.brand : T.border}`,
            }}>
              <Building2 size={22} color={type === "business" ? T.brand : T.textSec} strokeWidth={2.2}/>
              <div style={{ fontSize: 14, fontWeight: 700, marginTop: 8 }}>Business</div>
              <div style={{ fontSize: 11, color: T.textSec, marginTop: 4, lineHeight: 1.5 }}>Entity — RC, TIN, key person</div>
            </button>
          </div>
          <Banner variant="info" title="Casa runs identity checks automatically" description={type === "individual" ? "BVN is verified against NIBSS in real-time. NIN is verified against NIMC." : "RC number is verified against CAC. Key person BVN is verified against NIBSS."} action={null}/>
        </div>
      )}
      {step === 2 && (
        <div style={{ display: "flex", flexDirection: "column", gap: 14 }}>
          <Eyebrow>{type === "business" ? "Business details" : "Personal details"}</Eyebrow>
          {type === "individual" ? (
            <>
              <Field label="Full legal name"><input type="text" placeholder="As on government ID" style={inputStyle}/></Field>
              <div className="grid-2col-fields">
                <Field label="BVN"><input type="text" inputMode="numeric" placeholder="11 digits" style={inputStyle}/></Field>
                <Field label="NIN"><input type="text" inputMode="numeric" placeholder="11 digits" style={inputStyle}/></Field>
              </div>
              <div className="grid-2col-fields">
                <Field label="Phone"><input type="tel" inputMode="tel" placeholder="+234 ..." style={inputStyle}/></Field>
                <Field label="Email"><input type="email" placeholder="customer@example.com" style={inputStyle}/></Field>
              </div>
            </>
          ) : (
            <>
              <Field label="Legal business name"><input type="text" placeholder="As registered with CAC" style={inputStyle}/></Field>
              <div className="grid-2col-fields">
                <Field label="RC number"><input type="text" placeholder="RC-..." style={inputStyle}/></Field>
                <Field label="TIN"><input type="text" placeholder="..." style={inputStyle}/></Field>
              </div>
              <Field label="Sector"><input type="text" placeholder="e.g. Wholesale" style={inputStyle}/></Field>
              <Banner variant="info" title="Key person KYC required next" description="After saving, you'll add the director or signatory whose KYC anchors this account."/>
            </>
          )}
        </div>
      )}
    </Modal>
  );
}

function CustomerDetailPage({ navigate, params }) {
  const c = CUSTOMERS.find(x => x.id === params.id) || CUSTOMERS[0];
  const customerPolicies = POLICIES.filter(p => p.customerId === c.id);
  const [stubOpen, setStubOpen] = useState(null);
  const isBusiness = c.type === "business";
  const partnerData = c.partnerFurnished || {};

  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 20 }}>
      <Btn variant="ghost" size="sm" icon={ChevronLeft} onClick={() => navigate("customers")}>Back to customers</Btn>
      <div style={{ display: "flex", alignItems: "center", gap: 16, flexWrap: "wrap" }}>
        <Avatar name={c.name} size={64} color={isBusiness ? "pinkCoral" : "green"} kind={isBusiness ? "business" : "individual"}/>
        <div style={{ flex: 1, minWidth: 200 }}>
          <Eyebrow>{isBusiness ? "Business customer" : "Individual customer"}</Eyebrow>
          <Display size="md">{c.name}</Display>
          <div style={{ fontSize: 13, color: T.textSec, marginTop: 4 }}>
            {c.email} · {c.phone}
            {isBusiness && c.sector && ` · ${c.sector}`}
          </div>
        </div>
        <Pill color={c.status === "ACTIVE" ? "green" : "gray"}>{c.status}</Pill>
      </div>
      <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
        <Btn variant="brand" icon={Plus} onClick={() => navigate("get-insurance")}>Add policy</Btn>
        <WhatsAppButton variant="subtle" phoneNumber={c.phone} message={`Hello ${(isBusiness ? c.keyPerson?.name : c.name)?.split(" ")[0] || "there"}, this is ${ACTIVE_PARTNER.name} via Casava.`} label="WhatsApp"/>
        <Btn variant="outline" icon={User} onClick={() => setStubOpen("edit")}>Edit details</Btn>
        <Btn variant="ghost" icon={X} onClick={() => setStubOpen("deactivate")} style={{ color: T.dangerText, marginLeft: "auto" }}>Deactivate</Btn>
      </div>

      <div className="grid-2col-1col">
        <div style={{ display: "flex", flexDirection: "column", gap: 16 }}>
          {isBusiness ? (
            <>
              <Card title="Business details">
                <div className="grid-2col-fields">
                  <KVP label="Legal name" value={c.legalName}/>
                  <KVP label="Trading name" value={c.tradingName}/>
                  <KVP label="RC number" value={c.rcNumber} mono/>
                  <KVP label="TIN" value={c.tin} mono/>
                  <KVP label="Sector" value={c.sector}/>
                  <KVP label="Industry" value={c.industry}/>
                  <KVP label="Incorporated" value={c.incorporated}/>
                  <KVP label="Employees" value={c.employees}/>
                </div>
              </Card>
              <Card title="Key person KYC" right={<Pill color="brand" size="sm">Director</Pill>}>
                <div style={{ display: "flex", alignItems: "center", gap: 12, marginBottom: 14, paddingBottom: 14, borderBottom: `1px solid ${T.border}` }}>
                  <Avatar name={c.keyPerson.name} size={40} color="brown"/>
                  <div>
                    <div style={{ fontSize: 14, fontWeight: 700 }}>{c.keyPerson.name}</div>
                    <div style={{ fontSize: 12, color: T.textSec, marginTop: 2 }}>{c.keyPerson.role}</div>
                  </div>
                </div>
                <div className="grid-2col-fields">
                  <MaskedField label="BVN" value={c.keyPerson.bvn} type="BVN"/>
                  <MaskedField label="NIN" value={c.keyPerson.nin} type="NIN"/>
                  <KVP label="Date of birth" value={c.keyPerson.dob}/>
                  <KVP label="Phone" value={c.keyPerson.phone}/>
                </div>
                <div style={{ marginTop: 14, padding: 12, background: T.successBg, borderRadius: 10, fontSize: 12, color: T.text, display: "flex", alignItems: "center", gap: 8 }}>
                  <BadgeCheck size={14} color={T.success} strokeWidth={2.4}/>
                  <span><strong>NDPR-compliant</strong> · BVN/NIN reveal is logged to the audit trail</span>
                </div>
              </Card>
            </>
          ) : (
            <Card title="Identity & KYC">
              <div className="grid-2col-fields">
                <MaskedField label="BVN" value={c.bvn} type="BVN"/>
                <MaskedField label="NIN" value={c.nin} type="NIN"/>
                <KVP label="Date of birth" value={c.dob}/>
                <KVP label="Gender" value={c.gender}/>
                <KVP label="Occupation" value={c.occupation}/>
                <KVP label="LGA / State" value={c.lga}/>
              </div>
              <div style={{ marginTop: 14, padding: 12, background: T.successBg, borderRadius: 10, fontSize: 12, color: T.text, display: "flex", alignItems: "center", gap: 8 }}>
                <BadgeCheck size={14} color={T.success} strokeWidth={2.4}/>
                <span><strong>NDPR-compliant</strong> · BVN/NIN reveal is logged to the audit trail</span>
              </div>
            </Card>
          )}

          {/* Partner-furnished data — what the partner shared at customer onboarding */}
          {Object.keys(partnerData).length > 0 && (
            <Card title="Partner-furnished data" right={<Pill color="dark" size="sm">From {ACTIVE_PARTNER.name}</Pill>}>
              <div style={{ fontSize: 12, color: T.textSec, marginBottom: 12, lineHeight: 1.55 }}>Data the partner shared at onboarding. Used for underwriting and claim adjudication.</div>
              <div className="grid-2col-fields">
                {partnerData.partnerCustomerId && <KVP label="Partner customer ID" value={partnerData.partnerCustomerId} mono/>}
                {partnerData.channelOfOrigin && <KVP label="Channel of origin" value={partnerData.channelOfOrigin}/>}
                {partnerData.loanAmount && <KVP label="Loan amount" value={`₦${partnerData.loanAmount.toLocaleString()}`}/>}
                {partnerData.loanTenor && <KVP label="Loan tenor" value={partnerData.loanTenor}/>}
                {partnerData.riskScore && <KVP label="Partner risk score" value={partnerData.riskScore}/>}
                {partnerData.sector && !isBusiness && <KVP label="Loan sector" value={partnerData.sector}/>}
                {partnerData.deviceModel && <KVP label="Device model" value={partnerData.deviceModel}/>}
                {partnerData.devicePrice && <KVP label="Device price" value={`₦${partnerData.devicePrice.toLocaleString()}`}/>}
                {partnerData.monthlyTurnover && <KVP label="Monthly turnover" value={`₦${partnerData.monthlyTurnover.toLocaleString()}`}/>}
                {partnerData.accountVintage && <KVP label="Account vintage" value={partnerData.accountVintage}/>}
              </div>
            </Card>
          )}

          <Card title="Policies" right={<Pill size="sm" color="brand">{customerPolicies.length}</Pill>}>
            {customerPolicies.length === 0 ? (
              <EmptyState message="No policies yet"/>
            ) : (
              <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
                {customerPolicies.map(p => (
                  <PolicyCard key={p.id} compact icon={p.icon} productName={p.product} policyId={p.policyNumber} status={p.status.toLowerCase()} coverage={p.coverage} premium={p.premium} onClick={() => navigate("policy-detail", { id: p.id })}/>
                ))}
              </div>
            )}
          </Card>
        </div>
        <div style={{ display: "flex", flexDirection: "column", gap: 16 }}>
          <Card title={isBusiness ? "Business address" : "Address"}>
            <div style={{ fontSize: 13, color: T.text, lineHeight: 1.6 }}>{c.address}</div>
            <div style={{ fontSize: 12, color: T.textSec, marginTop: 4 }}>{c.lga}</div>
          </Card>
          <Card title="Account summary">
            <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
              <DisclosureRow label="Total policies" value={customerPolicies.length}/>
              <DisclosureRow label="Active cover" value={`₦${customerPolicies.reduce((s, p) => s + p.coverage, 0).toLocaleString()}`}/>
              <DisclosureRow label="Member since" value={c.joined} ruled={false}/>
            </div>
          </Card>
        </div>
      </div>
      {stubOpen && (
        <ComingSoonModal
          title={{ edit: `Edit ${isBusiness ? "business" : "customer"} details`, message: "Send message to customer", deactivate: "Deactivate customer" }[stubOpen]}
          description={{
            edit: isBusiness ? "Update business details, key person KYC, and partner-furnished data. Changes are audit-logged." : "Update name, contact info, and KYC fields. Changes are audit-logged.",
            message: `Send an in-app, WhatsApp, or SMS message to ${c.name}. Production wires this to the messaging service.`,
            deactivate: `Mark ${c.name} as inactive. Existing policies remain in force; no new policies can be issued. This action is reversible by an admin.`,
          }[stubOpen]}
          onClose={() => setStubOpen(null)}
        />
      )}
    </div>
  );
}

function PoliciesPage({ navigate }) {
  const [q, setQ] = useState("");
  const [status, setStatus] = useState("all");
  const [product, setProduct] = useState("all");

  const filtered = POLICIES.filter(p => {
    const matchQ = p.customer.toLowerCase().includes(q.toLowerCase()) || p.policyNumber.toLowerCase().includes(q.toLowerCase());
    const matchStatus = status === "all" || p.status.toLowerCase() === status.toLowerCase();
    const matchProduct = product === "all" || p.product === product;
    return matchQ && matchStatus && matchProduct;
  });

  const productOptions = ["all", ...new Set(POLICIES.map(p => p.product))];

  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 20 }}>
      <div style={{ display: "flex", alignItems: "flex-start", justifyContent: "space-between", flexWrap: "wrap", gap: 12 }}>
        <div>
          <Display size="lg">Policies</Display>
          <div style={{ fontSize: 14, color: T.textSec, marginTop: 6 }}>All policies across your channels and direct portfolio.</div>
        </div>
        <div style={{ display: "flex", gap: 8 }}>
          <Btn variant="outline" icon={Download} onClick={() => alert("Export queued — production downloads CSV")}>Export</Btn>
          <Btn variant="brand" icon={Plus} onClick={() => navigate("get-insurance")}>New policy</Btn>
        </div>
      </div>
      <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(200px, 1fr))", gap: 14 }}>
        <StatBlock label="Total policies" value={1284} delay={0}/>
        <StatBlock label="Active" value={1217} delay={60} accent={T.success}/>
        <StatBlock label="Pending" value={48} delay={120} accent={T.amber}/>
        <StatBlock label="Inactive" value={19} delay={180}/>
      </div>
      <FilterBar rightSlot={<SearchInput value={q} onChange={setQ} placeholder="Search policies..."/>}>
        <ChipMenu label="Status" value={status === "all" ? "All" : status[0].toUpperCase() + status.slice(1).toLowerCase()} options={[
          { id: "all", label: "All" },
          { id: "active", label: "Active" },
          { id: "pending", label: "Pending" },
          { id: "inactive", label: "Inactive" },
        ]} selected={status} onSelect={setStatus}/>
        <ChipMenu label="Product" value={product === "all" ? "All" : product} options={productOptions.map(p => ({ id: p, label: p === "all" ? "All products" : p }))} selected={product} onSelect={setProduct}/>
        <FilterChip label="Channel" value="All"/>
        {(status !== "all" || product !== "all" || q) && (
          <button onClick={() => { setStatus("all"); setProduct("all"); setQ(""); }} style={{
            background: "transparent", border: "none", color: T.textSec,
            fontSize: 12, fontWeight: 600, cursor: "pointer", fontFamily: FONT, marginLeft: 4,
          }}>Clear all</button>
        )}
      </FilterBar>
      <DataTable
        emptyMessage={q || status !== "all" || product !== "all" ? "No policies match your filters." : "No policies yet."}
        columns={[
          { key: "customer", label: "Customer", render: (v, r) => (
            <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
              <CustomerAvatar name={v} customerId={r.customerId} size={32}/>
              <div>
                <div style={{ fontWeight: 600 }}>{v}</div>
                <div style={{ fontSize: 11, color: T.textSec, fontFamily: "ui-monospace, monospace" }}>{r.policyNumber}</div>
              </div>
            </div>
          )},
          { key: "product", label: "Product / plan", hideMobile: true, render: (v, r) => (
            <div>
              <div style={{ display: "inline-flex", alignItems: "center", gap: 8 }}><ProductIcon productId={PRODUCTS.find(p => p.name === v)?.id || ""} product={PRODUCTS.find(p => p.name === v)} size={18}/>{v}</div>
              <div style={{ fontSize: 11, color: T.textSec }}>{r.plan}</div>
            </div>
          )},
          { key: "premium", label: "Premium", numeric: true, align: "right", render: v => <Money amount={v} size="sm"/> },
          { key: "coverage", label: "Coverage", numeric: true, align: "right", hideMobile: true, render: v => <Money amount={v} size="sm"/> },
          { key: "status", label: "Status", render: v => <Pill color={v === "ACTIVE" ? "green" : v === "PENDING" ? "amber" : "gray"} size="sm">{v}</Pill> },
        ]}
        rows={filtered}
        onRowClick={r => navigate("policy-detail", { id: r.id })}
      />
    </div>
  );
}

function PolicyDetailPage({ navigate, params }) {
  const p = POLICIES.find(x => x.id === params.id) || POLICIES[0];
  const c = CUSTOMERS.find(x => x.id === p.customerId);
  const claim = CLAIMS.find(x => x.policyId === p.id);
  const startDate = new Date(p.start), endDate = new Date(p.end);
  const total = endDate - startDate;
  const elapsed = Math.max(0, Math.min(total, Date.now() - startDate));
  const progress = total > 0 ? (elapsed / total) * 100 : 0;
  const [stubOpen, setStubOpen] = useState(null);

  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 20 }}>
      <Btn variant="ghost" size="sm" icon={ChevronLeft} onClick={() => navigate("policies")}>Back to policies</Btn>

      <div style={{ display: "flex", alignItems: "flex-start", gap: 20, flexWrap: "wrap" }}>
        <div style={{ fontSize: 48, lineHeight: 1 }}>{p.icon}</div>
        <div style={{ flex: 1, minWidth: 200 }}>
          <Eyebrow color={p.status === "ACTIVE" ? T.success : T.textSec}>{p.status} POLICY</Eyebrow>
          <Display size="lg">{p.product}</Display>
          <div style={{ fontSize: 13, color: T.textSec, marginTop: 4, fontFamily: "ui-monospace, monospace" }}>{p.policyNumber} · {p.plan}</div>
        </div>
      </div>

      <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
        <Btn variant="primary" icon={FileCheck2} onClick={() => navigate("new-claim", { policyId: p.id })}>File a claim</Btn>
        <Btn variant="outline" icon={Download} onClick={() => setStubOpen("certificate")}>Download certificate</Btn>
        <Btn variant="outline" icon={Share2} onClick={() => setStubOpen("share")}>Share with customer</Btn>
        <Btn variant="ghost" icon={X} onClick={() => setStubOpen("cancel")} style={{ color: T.dangerText, marginLeft: "auto" }}>Cancel policy</Btn>
      </div>

      <AuthorityLockup authorities={["naicom", "cac", "ndpr"]} label="Underwritten by Casava Microinsurance · NAICOM-licensed under NIIRA 2025"/>

      <div className="grid-2col-1col">
        <div style={{ display: "flex", flexDirection: "column", gap: 16 }}>
          <Card title="Coverage">
            <div style={{ display: "flex", alignItems: "baseline", gap: 16, marginBottom: 16, flexWrap: "wrap" }}>
              <div>
                <Eyebrow>Sum assured</Eyebrow>
                <Money amount={p.coverage} size="xxl"/>
              </div>
              <div>
                <Eyebrow>Premium</Eyebrow>
                <div style={{ display: "flex", alignItems: "baseline", gap: 4 }}>
                  <Money amount={p.premium} size="lg"/>
                  <span style={{ fontSize: 13, color: T.textSec }}>/month</span>
                </div>
              </div>
            </div>
            <Eyebrow>Coverage period</Eyebrow>
            <ProgressBar value={progress}/>
            <div style={{ display: "flex", justifyContent: "space-between", marginTop: 8, fontSize: 12, color: T.textSec }}>
              <span>{p.start}</span>
              <span style={{ fontWeight: 600, color: T.text }}>{Math.round(progress)}% elapsed</span>
              <span>{p.end}</span>
            </div>
          </Card>

          {p.loanRef && (
            <Card title="Loan details" right={<Pill color="brand" size="sm">Linked</Pill>}>
              <div style={{ fontSize: 12, color: T.textSec, marginBottom: 12, lineHeight: 1.55 }}>
                This credit-life policy is bundled with a loan disbursed by {ACTIVE_PARTNER.name}. The policy term and sum assured mirror the loan tenure and principal.
              </div>
              <KeyValueGrid items={[
                { label: "Loan reference", value: p.loanRef, mono: true },
                { label: "Loan product", value: p.loanProduct },
                { label: "Loan amount", value: `₦${p.loanAmount?.toLocaleString()}` },
                { label: "Tenure", value: `${p.tenureMonths} months` },
                { label: "Disbursed on", value: p.disbursedAt },
                { label: "Branch", value: p.branch },
                { label: "Lender ref", value: p.lenderRef, mono: true },
              ]}/>
            </Card>
          )}

          <Card title="Premium breakdown">
            <DisclosureRow label="Base premium" value={`₦${(p.premium * 0.85).toLocaleString(undefined, { maximumFractionDigits: 0 })}`}/>
            <DisclosureRow label="Stamp duty" value={`₦${(p.premium * 0.005).toLocaleString(undefined, { maximumFractionDigits: 0 })}`}/>
            <DisclosureRow label="Insurance levy" value={`₦${(p.premium * 0.01).toLocaleString(undefined, { maximumFractionDigits: 0 })}`}/>
            <DisclosureRow label="Service charge" value={`₦${(p.premium * 0.135).toLocaleString(undefined, { maximumFractionDigits: 0 })}`}/>
            <DisclosureRow label="Total premium" value={<strong>₦{p.premium.toLocaleString()}/mo</strong>} ruled={false}/>
          </Card>

          <Card title="Policyholder">
            <div style={{ display: "flex", alignItems: "center", gap: 14, marginBottom: 16, paddingBottom: 14, borderBottom: `1px solid ${T.border}` }}>
              <Avatar name={c.name} size={48} color="green" kind={c.type}/>
              <div style={{ flex: 1 }}>
                <div style={{ fontSize: 15, fontWeight: 700 }}>{c.name}</div>
                <div style={{ fontSize: 12, color: T.textSec }}>{c.email} · {c.phone}</div>
              </div>
              <Tag color="brand" icon={Shield}>Primary insured</Tag>
            </div>
            <div className="grid-2col-fields">
              <MaskedField label="BVN" value={c.bvn} type="BVN"/>
              <MaskedField label="NIN" value={c.nin} type="NIN"/>
            </div>
          </Card>
        </div>

        <div style={{ display: "flex", flexDirection: "column", gap: 16 }}>
          <Card title="What's covered">
            <ul style={{ margin: 0, padding: 0, listStyle: "none", display: "flex", flexDirection: "column", gap: 10 }}>
              {[
                "Outstanding loan balance on death",
                "Permanent disability lump sum",
                "Critical illness diagnosis",
                "Funeral expenses up to ₦300,000",
              ].map((b, i) => (
                <li key={i} style={{ display: "flex", gap: 10, alignItems: "flex-start", fontSize: 13, color: T.text, lineHeight: 1.5 }}>
                  <Check size={14} color={T.success} strokeWidth={2.6} style={{ flexShrink: 0, marginTop: 3 }}/>
                  {b}
                </li>
              ))}
            </ul>
          </Card>

          {claim && (
            <Card title="Active claim">
              <div style={{ marginBottom: 14 }}>
                <Eyebrow>Claim ref</Eyebrow>
                <div style={{ fontSize: 13, fontFamily: "ui-monospace, monospace", marginBottom: 10 }}>{claim.claimRef}</div>
                <Pill color={claim.status === "Approved" ? "green" : "amber"}>{claim.status}</Pill>
              </div>
              <Btn variant="outline" size="sm" iconRight={ArrowRight} onClick={() => navigate("claim-detail", { id: claim.id })} full>View claim</Btn>
            </Card>
          )}
        </div>
      </div>
      {stubOpen && (
        <ComingSoonModal
          title={{ certificate: "Download policy certificate", share: "Share with customer", cancel: "Cancel this policy" }[stubOpen]}
          description={{
            certificate: `Generate a NAICOM-compliant PDF certificate for ${p.policyNumber}. Production wires this to the document generation service.`,
            share: `Send the policy details and certificate to ${c.name} via email, WhatsApp, or SMS.`,
            cancel: `Cancel ${p.policyNumber} and process any pro-rated refund. This action requires reason capture and is audit-logged.`,
          }[stubOpen]}
          onClose={() => setStubOpen(null)}
        />
      )}
    </div>
  );
}

function ClaimsPage({ navigate }) {
  const [q, setQ] = useState("");
  const [status, setStatus] = useState("all");

  const filtered = CLAIMS.filter(c => {
    const matchQ = c.customer.toLowerCase().includes(q.toLowerCase()) || c.claimRef.toLowerCase().includes(q.toLowerCase());
    const matchStatus = status === "all" || c.status.toLowerCase() === status.toLowerCase();
    return matchQ && matchStatus;
  });

  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 20 }}>
      <div style={{ display: "flex", alignItems: "flex-start", justifyContent: "space-between", flexWrap: "wrap", gap: 12 }}>
        <div>
          <Display size="lg">Claims</Display>
          <div style={{ fontSize: 14, color: T.textSec, marginTop: 6 }}>Track and process claims across all policies.</div>
        </div>
        <div style={{ display: "flex", gap: 8 }}>
          <Btn variant="outline" icon={Download}>Export</Btn>
          <Btn variant="primary" icon={Plus} onClick={() => navigate("new-claim")}>New claim</Btn>
        </div>
      </div>
      <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(200px, 1fr))", gap: 14 }}>
        <StatBlock label="Total claims" value={47} delay={0}/>
        <StatBlock label="Pending review" value={8} delay={60} accent={T.amber}/>
        <StatBlock label="Approval rate" value={86} suffix="%" delay={120} accent={T.success}/>
        <StatBlock label="Avg time to settle" value={5.2} suffix="days" delay={180}/>
      </div>
      <FilterBar rightSlot={<SearchInput value={q} onChange={setQ} placeholder="Search claims..."/>}>
        <ChipMenu label="Status" value={status === "all" ? "All" : status} options={[
          { id: "all", label: "All" },
          { id: "Submitted", label: "Submitted" },
          { id: "Processing", label: "Processing" },
          { id: "Approved", label: "Approved" },
        ]} selected={status} onSelect={setStatus}/>
        <FilterChip label="Product" value="All"/>
      </FilterBar>
      <DataTable
        emptyMessage={q || status !== "all" ? "No claims match your filters." : "No claims yet."}
        columns={[
          { key: "customer", label: "Customer", render: (v, r) => (
            <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
              <CustomerAvatar name={v} customerId={r.customerId} size={32}/>
              <div>
                <div style={{ fontWeight: 600 }}>{v}</div>
                <div style={{ fontSize: 11, color: T.textSec, fontFamily: "ui-monospace, monospace" }}>{r.claimRef}</div>
              </div>
            </div>
          )},
          { key: "product", label: "Product", hideMobile: true, render: (v, r) => (
            <span style={{ display: "inline-flex", alignItems: "center", gap: 8 }}>
              <ProductIcon productId={PRODUCTS.find(p => p.name === v)?.id || ""} product={PRODUCTS.find(p => p.name === v)} size={18}/>
              {v}
            </span>
          )},
          { key: "peril", label: "Peril", hideMobile: true },
          { key: "amount", label: "Amount", numeric: true, align: "right", render: v => <Money amount={v} size="sm"/> },
          { key: "status", label: "Status", render: v => <Pill color={v === "Approved" ? "green" : v === "Processing" ? "amber" : "brand"} size="sm">{v}</Pill> },
          { key: "lastUpdated", label: "Updated", hideMobile: true },
        ]}
        rows={filtered}
        onRowClick={r => navigate("claim-detail", { id: r.id })}
      />
    </div>
  );
}

function NewClaimPage({ navigate, params }) {
  const [step, setStep] = useState(1);
  const [policy, setPolicy] = useState(POLICIES.find(p => p.id === params?.policyId) || null);
  const [peril, setPeril] = useState(null);
  const [details, setDetails] = useState({ description: "", date: "" });
  const [docs, setDocs] = useState([]);
  const [searchQ, setSearchQ] = useState("");
  const [submitted, setSubmitted] = useState(false);

  const filteredPolicies = POLICIES.filter(p => p.status === "ACTIVE" && (p.customer.toLowerCase().includes(searchQ.toLowerCase()) || p.policyNumber.toLowerCase().includes(searchQ.toLowerCase())));
  const perils = policy ? perilsForProduct(policy.product) : [];

  // Required documents per peril type
  const requiredDocs = peril ? requiredDocsForPeril(peril.id) : [];

  // Casa assistant messages adapt to current state
  const casaContext = {
    1: { state: "talking", title: "Find the policy", lines: [
      "I'll help you file this claim end-to-end.",
      policy ? `Found ${policy.policyNumber} for ${policy.customer}. Premium is paid through April — eligible to file.` : "Search by customer name or policy number above. Only ACTIVE policies are eligible.",
    ]},
    2: { state: policy ? "talking" : "thinking", title: "What happened?", lines: [
      "Pick the peril that best matches what happened.",
      peril ? `For "${peril.label}", I'll need: ${requiredDocs.map(d => d.label).join(", ")}.` : "Each peril triggers a different document checklist.",
    ]},
    3: { state: "thinking", title: "Add details", lines: [
      "Be specific about time, location, and circumstances. I look for triage signals while you type.",
      details.description.length > 30 ? "Detail level looks good. I'll auto-categorize and route this to the right adjudicator." : "Try to include date, time, location and what was lost or damaged.",
      docs.length > 0 ? `${docs.length} of ${requiredDocs.length} documents uploaded.` : null,
    ].filter(Boolean)},
    4: { state: "happy", title: "Casa pre-flight check", lines: [
      "Policy active ✓",
      "Premium paid through April ✓",
      "No fraud signals detected ✓",
      `${docs.length} of ${requiredDocs.length} documents attached`,
      "Estimated processing time: 3-5 business days.",
    ]},
  }[step];

  const submit = () => {
    setSubmitted(true);
    // Append to mock CLAIMS so it shows up
    const seq = String(CLAIMS.length + 1).padStart(4, "0");
    const newClaim = {
      id: `cl-new-${Date.now()}`, claimRef: `CLM-2026-0${seq}`, customer: policy.customer, customerId: policy.customerId,
      policyId: policy.id, product: policy.product, peril: peril.label, status: "Submitted",
      amount: Math.round(policy.coverage * 0.6), lastUpdated: formatDate(new Date()), icon: policy.icon,
    };
    CLAIMS.unshift(newClaim);
    setTimeout(() => navigate("claim-detail", { id: newClaim.id }), 1200);
  };

  return (
    <div className="grid-2col-1col" style={{ gap: 20 }}>
      {/* Left: stepped form using ApplicationStep */}
      <div style={{ display: "flex", flexDirection: "column", gap: 18 }}>
        <Btn variant="ghost" size="sm" icon={ChevronLeft} onClick={() => navigate("claims")}>Back to claims</Btn>

        {step === 1 && (
          <ApplicationStep
            stepNumber={1} totalSteps={4}
            casaState={policy ? "happy" : "talking"}
            casaMessage={policy
              ? `Found ${policy.policyNumber} for ${policy.customer}. Premium is paid through April — eligible to file.`
              : "I'll help you file this claim end-to-end. Search by name or policy number — only ACTIVE policies show up."}
            question="Find the policy"
            primaryAction={<Btn variant="primary" disabled={!policy} iconRight={ArrowRight} onClick={() => setStep(2)}>Continue</Btn>}
          >
            <SearchInput value={searchQ} onChange={setSearchQ} placeholder="Search by name or policy number..."/>
            <div style={{ marginTop: 14, display: "flex", flexDirection: "column", gap: 8, maxHeight: 360, overflowY: "auto" }}>
              {filteredPolicies.length === 0 ? <EmptyState message={searchQ ? "No policies match" : "Start typing to search"}/> :
                filteredPolicies.map(p => (
                  <button key={p.id} onClick={() => setPolicy(p)} style={{
                    display: "flex", alignItems: "center", gap: 12, padding: 14,
                    background: policy?.id === p.id ? T.brandBg : T.surface,
                    border: `1.5px solid ${policy?.id === p.id ? T.brand : T.border}`,
                    borderRadius: 14, cursor: "pointer", textAlign: "left", fontFamily: FONT, width: "100%",
                  }}>
                    <CustomerAvatar name={p.customer} customerId={p.customerId} size={36}/>
                    <div style={{ flex: 1, minWidth: 0 }}>
                      <div style={{ fontSize: 13, fontWeight: 700 }}>{p.customer}</div>
                      <div style={{ fontSize: 11, color: T.textSec, fontFamily: "ui-monospace, monospace" }}>{p.policyNumber} · {p.product}</div>
                    </div>
                    <Pill color="green" size="sm">ACTIVE</Pill>
                  </button>
                ))}
            </div>
          </ApplicationStep>
        )}

        {step === 2 && (
          <ApplicationStep
            stepNumber={2} totalSteps={4}
            casaState={peril ? "happy" : "thinking"}
            casaMessage={peril
              ? `For "${peril.label}", I'll need: ${requiredDocs.map(d => d.label).join(", ")}.`
              : "Each peril triggers a different document checklist. Pick what best matches what happened."}
            question="What happened?"
            primaryAction={<Btn variant="primary" disabled={!peril} iconRight={ArrowRight} onClick={() => setStep(3)}>Continue</Btn>}
            secondaryAction={<Btn variant="ghost" icon={ChevronLeft} onClick={() => setStep(1)}>Back</Btn>}
          >
            <ChoiceTileGrid
              columns={2}
              size="sm"
              value={peril?.id}
              onChange={(id) => setPeril(perils.find(p => p.id === id))}
              options={perils.map(p => ({
                value: p.id,
                emoji: p.icon,
                label: p.label,
                description: p.desc,
              }))}
            />
          </ApplicationStep>
        )}

        {step === 3 && (
          <ApplicationStep
            stepNumber={3} totalSteps={4}
            casaState="thinking"
            casaMessage={
              details.description.length > 30
                ? `Detail level looks good. ${docs.length} of ${requiredDocs.length} documents uploaded.`
                : "Be specific about time, location and circumstances — I look for triage signals while you type."
            }
            question="Add details & documents"
            primaryAction={<Btn variant="primary" disabled={!details.date || !details.description || docs.length < requiredDocs.length} iconRight={ArrowRight} onClick={() => setStep(4)}>Continue</Btn>}
            secondaryAction={<Btn variant="ghost" icon={ChevronLeft} onClick={() => setStep(2)}>Back</Btn>}
          >
            <div style={{ display: "flex", flexDirection: "column", gap: 16 }}>
              <Field label="When did it happen?">
                <input type="date" value={details.date} onChange={e => setDetails(d => ({ ...d, date: e.target.value }))} style={inputStyle}/>
              </Field>
              <Field label="Tell us what happened" hint="Casa uses this to triage. Be specific about location, time, and circumstances.">
                <textarea value={details.description} onChange={e => setDetails(d => ({ ...d, description: e.target.value }))} rows={5} style={{ ...inputStyle, fontFamily: FONT, resize: "vertical" }} placeholder="On 23 Apr at around 6pm, while I was at..."/>
              </Field>
              <Field label={`Documents (${docs.length}/${requiredDocs.length})`} hint="Drag files here or tap to browse. Casa scans uploads for completeness.">
                <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
                  {requiredDocs.map(rd => {
                    const uploaded = docs.find(d => d.kind === rd.id);
                    return (
                      <div key={rd.id} style={{
                        display: "flex", alignItems: "center", gap: 12, padding: "12px 14px",
                        background: uploaded ? T.successBg : T.surfaceTint,
                        border: `1px solid ${uploaded ? T.successBorder : T.border}`,
                        borderRadius: 10,
                      }}>
                        <div style={{ width: 32, height: 32, borderRadius: 8, background: uploaded ? T.success : T.surface, color: uploaded ? "#fff" : T.textSec, display: "inline-flex", alignItems: "center", justifyContent: "center", flexShrink: 0 }}>
                          {uploaded ? <Check size={16} strokeWidth={2.6}/> : <Upload size={14} strokeWidth={2.2}/>}
                        </div>
                        <div style={{ flex: 1, minWidth: 0 }}>
                          <div style={{ fontSize: 13, fontWeight: 600, color: T.text }}>{rd.label}</div>
                          <div style={{ fontSize: 11, color: T.textSec, marginTop: 1 }}>{uploaded ? uploaded.filename : rd.hint}</div>
                        </div>
                        {uploaded ? (
                          <button onClick={() => setDocs(docs.filter(d => d.kind !== rd.id))} style={{ background: "transparent", border: "none", padding: 4, cursor: "pointer", color: T.textSec }}><X size={14}/></button>
                        ) : (
                          <Btn variant="outline" size="sm" onClick={() => setDocs([...docs, { kind: rd.id, filename: `${rd.id}_${Date.now()}.pdf`, size: "1.2 MB" }])}>Upload</Btn>
                        )}
                      </div>
                    );
                  })}
                </div>
              </Field>
            </div>
          </ApplicationStep>
        )}

        {step === 4 && (
          <ApplicationStep
            stepNumber={4} totalSteps={4}
            casaState="happy"
            casaMessage="I've checked the basics. Policy active, premium paid, no fraud signals. Submit when you're ready."
            question="Review & submit"
            primaryAction={
              <Btn variant="primary" icon={submitted ? Check : ArrowRight} onClick={submit} disabled={submitted}>
                {submitted ? "Submitted ✓" : "Submit claim"}
              </Btn>
            }
            secondaryAction={<Btn variant="ghost" icon={ChevronLeft} onClick={() => setStep(3)} disabled={submitted}>Back</Btn>}
          >
            <KeyValueGrid items={[
              { label: "Policy", value: policy.policyNumber, mono: true },
              { label: "Product", value: policy.product },
              { label: "Customer", value: policy.customer },
              { label: "Peril", value: peril?.label },
              { label: "Incident date", value: details.date },
              { label: "Coverage", value: `₦${policy.coverage.toLocaleString()}` },
            ]}/>
            <div style={{ marginTop: 16, paddingTop: 16, borderTop: `1px solid ${T.border}` }}>
              <Eyebrow>Description</Eyebrow>
              <div style={{ fontSize: 13, color: T.text, lineHeight: 1.6 }}>{details.description}</div>
            </div>
            <div style={{ marginTop: 16, paddingTop: 16, borderTop: `1px solid ${T.border}` }}>
              <Eyebrow mb={8}>Documents ({docs.length})</Eyebrow>
              <div style={{ display: "flex", flexDirection: "column", gap: 6 }}>
                {docs.map(d => (
                  <div key={d.kind} style={{ display: "flex", alignItems: "center", gap: 8, fontSize: 12, color: T.text }}>
                    <FileText size={13} color={T.textSec}/>
                    <span style={{ fontFamily: "ui-monospace, monospace" }}>{d.filename}</span>
                    <span style={{ color: T.textSec }}>· {d.size}</span>
                  </div>
                ))}
              </div>
            </div>
          </ApplicationStep>
        )}
      </div>

      {/* Right: Casa assistant panel — LiveQuoteRail-style sticky companion */}
      <CasaAssistantPanel context={casaContext} step={step}/>
    </div>
  );
}

// CasaAssistantPanel — sticky live-triage rail (LiveQuoteRail equivalent for claims).
// Casa stays small and inline so it complements rather than competes with the conversational
// Casa inside the ApplicationStep on the left.
function CasaAssistantPanel({ context, step }) {
  const { eyeOffset, blinking, ref } = useCasaAnimation({ trackCursor: true, autoBlink: true });
  return (
    <div style={{
      background: T.brand, color: "#fff", borderRadius: 18, padding: 20,
      display: "flex", flexDirection: "column", gap: 14,
      position: "sticky", top: 20, alignSelf: "flex-start",
    }}>
      <div style={{ display: "flex", alignItems: "center", gap: 8, paddingBottom: 12, borderBottom: "1px solid rgba(255,255,255,0.18)" }}>
        <CasaAvatar size={24} state={context.state} eyeOffset={eyeOffset} blinking={blinking} innerRef={ref} color="#fff"/>
        <div style={{ fontSize: 10, fontWeight: 700, letterSpacing: "0.1em", textTransform: "uppercase", opacity: 0.92 }}>
          Casa · {context.title}
        </div>
      </div>
      <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
        {context.lines.map((line, i) => (
          <div key={i} className="stagger-fadeIn" style={{ animationDelay: `${i * 80}ms`, fontSize: 13, lineHeight: 1.55, opacity: i === 0 ? 1 : 0.92 }}>
            {line}
          </div>
        ))}
      </div>
      {step === 4 && (
        <div style={{ marginTop: 4, padding: 12, background: "rgba(255,255,255,0.16)", borderRadius: 10, fontSize: 11, lineHeight: 1.5 }}>
          <strong>Heads up:</strong> If approved, payout disburses to the customer's saved bank account. They'll get an SMS and WhatsApp confirmation.
        </div>
      )}
    </div>
  );
}

function requiredDocsForPeril(perilId) {
  const docs = {
    death: [
      { id: "death_cert", label: "Death certificate", hint: "Issued by hospital or coroner" },
      { id: "id_deceased", label: "Deceased's ID", hint: "NIN slip or driver's license" },
      { id: "next_of_kin", label: "Next of kin form", hint: "Signed and witnessed" },
    ],
    disability: [
      { id: "medical_report", label: "Medical report", hint: "From licensed physician" },
      { id: "id_holder", label: "Policyholder's ID", hint: "NIN or driver's license" },
    ],
    critical: [
      { id: "diagnosis", label: "Diagnosis report", hint: "From oncologist or cardiologist" },
      { id: "id_holder", label: "Policyholder's ID", hint: "NIN or driver's license" },
    ],
    theft: [
      { id: "police_report", label: "Police report", hint: "Filed within 48 hours" },
      { id: "device_proof", label: "Proof of purchase", hint: "Receipt or invoice" },
    ],
    damage: [
      { id: "device_photos", label: "Photos of damage", hint: "At least 3 angles" },
      { id: "device_proof", label: "Proof of purchase", hint: "Receipt or invoice" },
    ],
    loss: [
      { id: "police_report", label: "Police report", hint: "Loss declaration" },
      { id: "device_proof", label: "Proof of purchase", hint: "Receipt or invoice" },
    ],
    fraud: [
      { id: "bank_statement", label: "Bank statement", hint: "Showing fraudulent debit" },
      { id: "fraud_report", label: "Fraud report", hint: "Filed with bank or NCC" },
    ],
    sim: [
      { id: "sim_swap_evidence", label: "SIM swap evidence", hint: "Telco confirmation" },
      { id: "bank_statement", label: "Bank statement", hint: "Showing loss" },
    ],
    phish: [
      { id: "phish_evidence", label: "Phishing evidence", hint: "Screenshots, emails, SMS" },
      { id: "bank_statement", label: "Bank statement", hint: "Showing loss" },
    ],
  };
  return docs[perilId] || [{ id: "supporting_docs", label: "Supporting documentation", hint: "Anything that supports this claim" }];
}

function ClaimDetailPage({ navigate, params }) {
  const cl = CLAIMS.find(x => x.id === params.id) || CLAIMS[0];
  const stages = stagesForClaim(cl.status);
  const [stubOpen, setStubOpen] = useState(null);
  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 20 }}>
      <Btn variant="ghost" size="sm" icon={ChevronLeft} onClick={() => navigate("claims")}>Back to claims</Btn>
      <div style={{ display: "flex", alignItems: "flex-start", gap: 16, flexWrap: "wrap" }}>
        <div style={{ fontSize: 40, lineHeight: 1 }}>{cl.icon}</div>
        <div style={{ flex: 1, minWidth: 200 }}>
          <Eyebrow>Claim · {cl.product}</Eyebrow>
          <Display size="md">{cl.claimRef}</Display>
          <div style={{ fontSize: 13, color: T.textSec, marginTop: 4 }}>{cl.peril} · {cl.customer}</div>
        </div>
        <Pill color={cl.status === "Approved" ? "green" : cl.status === "Processing" ? "amber" : "brand"}>{cl.status}</Pill>
      </div>

      {cl.status !== "Approved" && (
        <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
          <Btn variant="primary" icon={Check} onClick={() => setStubOpen("approve")}>Approve claim</Btn>
          <Btn variant="outline" icon={MessageCircle} onClick={() => setStubOpen("note")}>Add note</Btn>
          {(() => {
            const customer = CUSTOMERS.find(c => c.id === cl.customerId);
            const phone = customer?.phone || customer?.keyPerson?.phone || "";
            return <WhatsAppButton variant="subtle" phoneNumber={phone} message={`Hi, regarding your claim ${cl.claimRef} we need a bit more information.`} label="Message claimant"/>;
          })()}
          <Btn variant="ghost" icon={X} onClick={() => setStubOpen("reject")} style={{ color: T.dangerText, marginLeft: "auto" }}>Reject</Btn>
        </div>
      )}

      <div className="grid-2col-1col">
        <div style={{ display: "flex", flexDirection: "column", gap: 16 }}>
          <Card title="Claim summary">
            <KeyValueGrid items={[
              { label: "Customer", value: cl.customer },
              { label: "Product", value: cl.product },
              { label: "Peril", value: cl.peril },
              { label: "Claim amount", value: `₦${cl.amount.toLocaleString()}` },
              { label: "Last updated", value: cl.lastUpdated },
              { label: "Reference", value: cl.claimRef, mono: true },
            ]}/>
          </Card>
          <Card title="Incident details">
            <div style={{ fontSize: 13, color: T.text, lineHeight: 1.6 }}>
              On {cl.lastUpdated} at approximately 6:30pm, the customer reported {cl.peril.toLowerCase()} affecting their insured {cl.product.toLowerCase()}. Casa pre-verified policy status and forwarded the claim to underwriting for human review.
            </div>
          </Card>
          <Card title="Documents">
            <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
              {["Police report.pdf", "Identity verification.pdf", "Bank account confirmation.pdf"].map(d => (
                <div key={d} style={{ display: "flex", alignItems: "center", gap: 12, padding: 12, background: T.surfaceTint, borderRadius: 10 }}>
                  <FileText size={16} color={T.textSec}/>
                  <div style={{ flex: 1, fontSize: 13, fontWeight: 500 }}>{d}</div>
                  <BadgeCheck size={15} color={T.success}/>
                  <Btn variant="ghost" size="sm" onClick={() => setStubOpen("document")}>View</Btn>
                </div>
              ))}
            </div>
          </Card>
        </div>
        <div>
          <Card title="Timeline">
            <ClaimTimeline stages={stages}/>
          </Card>
        </div>
      </div>
      {stubOpen && (
        <ComingSoonModal
          title={{ approve: "Approve this claim", reject: "Reject this claim", note: "Add internal note", request: "Request more information", document: "View document" }[stubOpen]}
          description={{
            approve: `Approve ${cl.claimRef} and queue ₦${cl.amount.toLocaleString()} for disbursement to ${cl.customer}'s nominated bank account.`,
            reject: `Reject ${cl.claimRef}. Production captures the rejection reason, notifies the customer, and provides an appeal path.`,
            note: `Add an internal note to this claim. Notes are visible to the underwriting team but not to ${cl.customer}.`,
            request: `Request additional documentation from ${cl.customer} (police report, medical certificate, photographs, etc.). They'll be notified via WhatsApp.`,
            document: "Open the document in a secure preview. Production renders PDFs inline with watermarking and download protection.",
          }[stubOpen]}
          onClose={() => setStubOpen(null)}
        />
      )}
    </div>
  );
}

// ═══════════════════════════════════════════════════════════════════════════════════
// LOAN BOOK — daily disbursement feed + attachment-rate working surface for banking
// partners. The 70%-of-our-book page. Lists loans with attached/unattached status,
// surfaces the gap, lets partners attach credit life with one tap (opens SellFlow with
// loan details prefilled).
// ═══════════════════════════════════════════════════════════════════════════════════
function LoanBookPage({ navigate, openSell }) {
  const [windowDays, setWindowDays] = useState("7");
  const [statusFilter, setStatusFilter] = useState("all");
  const [productFilter, setProductFilter] = useState("all");
  const [branchFilter, setBranchFilter] = useState("all");
  const [q, setQ] = useState("");

  // Window filter — relative to "today" (28 Apr 2026) which is the latest disbursedAt
  // in the mock data. Today=1, 7d=window of last 7 days, 30d=last 30.
  const today = "28 Apr 2026";
  const dateValue = (s) => {
    // Crude string compare since all our mock dates are 2025/2026 and same format
    const m = { "Jan": 1, "Feb": 2, "Mar": 3, "Apr": 4, "May": 5, "Jun": 6, "Jul": 7, "Aug": 8, "Sep": 9, "Oct": 10, "Nov": 11, "Dec": 12 };
    const [d, mon, y] = s.split(" ");
    return parseInt(y) * 10000 + m[mon] * 100 + parseInt(d);
  };
  const todayVal = dateValue(today);
  const windowDaysInt = parseInt(windowDays);
  const inWindow = (loan) => {
    const diff = todayVal - dateValue(loan.disbursedAt);
    // Approximate — fine for mock data
    return diff <= windowDaysInt;
  };

  const branches = Array.from(new Set(LOANS.map(l => l.branch))).sort();
  const products = Array.from(new Set(LOANS.map(l => l.loanProduct))).sort();

  const windowLoans = LOANS.filter(inWindow);
  const filtered = windowLoans.filter(l => {
    const matchSearch = !q || l.customer.toLowerCase().includes(q.toLowerCase()) || l.loanRef.toLowerCase().includes(q.toLowerCase());
    const matchStatus = statusFilter === "all" || l.attachmentStatus === statusFilter;
    const matchProduct = productFilter === "all" || l.loanProduct === productFilter;
    const matchBranch = branchFilter === "all" || l.branch === branchFilter;
    return matchSearch && matchStatus && matchProduct && matchBranch;
  });

  // KPIs from windowLoans (the unfiltered window — KPIs reflect the window, not the table filter)
  const totalDisbursed = windowLoans.reduce((s, l) => s + l.loanAmount, 0);
  const attached = windowLoans.filter(l => l.attachmentStatus === "attached");
  const unattached = windowLoans.filter(l => l.attachmentStatus === "unattached");
  const attachmentRate = windowLoans.length ? (attached.length / windowLoans.length) * 100 : 0;
  const premiumWritten = attached.reduce((s, l) => s + (l.premium || 0), 0);
  const premiumOpportunity = unattached.reduce((s, l) => s + Math.round(l.loanAmount * 0.0009), 0); // ~0.09% of loan, ballpark for SME credit life

  // Attach-policy CTA — open SellFlow with the loan prefilled
  const attachPolicy = (loan) => {
    const creditLifeProduct = PRODUCTS.find(p => p.id === "credit-life");
    if (!creditLifeProduct) return;
    let linkedCustomer = loan.customerId ? CUSTOMERS.find(c => c.id === loan.customerId) : null;
    if (!linkedCustomer) {
      // Synthesize a customer from loan data so SellFlow can prefill cleanly. In production
      // the borrower already exists in the bank's KYC; this would be a real lookup.
      linkedCustomer = {
        id: `loan-${loan.id}`,
        name: loan.customer,
        email: `${loan.customer.toLowerCase().replace(/\s+/g, ".")}@borrower.${ACTIVE_PARTNER.id}`,
        phone: "",
        type: "individual",
      };
    }
    openSell?.(creditLifeProduct, linkedCustomer, loan);
  };

  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 20 }}>
      <div style={{ display: "flex", alignItems: "flex-start", justifyContent: "space-between", flexWrap: "wrap", gap: 12 }}>
        <div>
          <Display size="lg">Loan book</Display>
          <div style={{ fontSize: 14, color: T.textSec, marginTop: 6 }}>Disbursed loans from {ACTIVE_PARTNER.name}'s LMS. Attach credit life to unattached loans to close the gap.</div>
        </div>
        <ChipMenu label="Window" value={windowDays === "1" ? "Today" : `Last ${windowDays} days`} options={[
          { id: "1",  label: "Today" },
          { id: "7",  label: "Last 7 days" },
          { id: "30", label: "Last 30 days" },
          { id: "90", label: "Last 90 days" },
        ]} selected={windowDays} onSelect={setWindowDays}/>
      </div>

      {/* KPI row — banking-shaped */}
      <div key={windowDays} style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(180px, 1fr))", gap: 14 }}>
        <PartnerKPICard label="Disbursed loans" value={windowLoans.length.toLocaleString()} helpText={windowDays === "1" ? "today" : `last ${windowDays} days`}/>
        <PartnerKPICard label="Total disbursed" value={`₦${(totalDisbursed / 1_000_000).toFixed(1)}M`} helpText="loan principal"/>
        <PartnerKPICard label="Attachment rate" value={`${attachmentRate.toFixed(1)}%`} delta={`${attached.length} of ${windowLoans.length}`} deltaDirection={attachmentRate >= 75 ? "up" : "down"} accent={attachmentRate >= 75 ? T.success : attachmentRate >= 50 ? T.amber : T.danger}/>
        <PartnerKPICard label="Premium written" value={`₦${(premiumWritten / 1000).toFixed(0)}K`} helpText="from attached"/>
        <PartnerKPICard label="Unattached gap" value={unattached.length.toLocaleString()} helpText={`₦${(premiumOpportunity / 1000).toFixed(0)}K opportunity`} accent={T.danger}/>
      </div>

      {unattached.length > 0 && (
        <Banner
          variant="amber"
          title={`${unattached.length} unattached loans · ₦${(premiumOpportunity / 1000).toFixed(0)}K in premium opportunity`}
          description="Casa drafted WhatsApp nudges for each borrower. Or auto-attach via API for next-day disbursements."
          action={<WhatsAppButton variant="subtle" size="sm" label="Send WhatsApp nudges" onClick={() => alert(`Stub — would draft ${unattached.length} WhatsApp messages for borrower follow-up.`)}/>}
        />
      )}

      {/* Charts row — attachment rate by branch + daily attachment rate trend */}
      <div className="grid-2col-1col" style={{ gap: 14 }}>
        <Card title="Attachment rate by branch" right={<Pill color="gray" size="sm">{windowDays === "1" ? "Today" : `Last ${windowDays}d`}</Pill>}>
          {(() => {
            const byBranch = branches.map(b => {
              const branchLoans = windowLoans.filter(l => l.branch === b);
              const branchAttached = branchLoans.filter(l => l.attachmentStatus === "attached").length;
              const rate = branchLoans.length ? (branchAttached / branchLoans.length) * 100 : 0;
              return {
                label: b.replace("Lagos · ", "L·").replace("Abuja · ", "A·").replace("Port Harcourt", "PH"),
                value: Math.round(rate),
                color: rate >= 75 ? T.success : rate >= 50 ? T.amber : T.danger,
              };
            }).filter(b => b.value > 0 || windowLoans.some(l => l.branch.includes(b.label.replace("L·", "Lagos · ").replace("A·", "Abuja · ").replace("PH", "Port Harcourt"))));
            return <BarChart height={220} data={byBranch} valueFormat={(v) => `${v}%`}/>;
          })()}
        </Card>
        <Card title="Daily attachment rate" right={<Pill color="gray" size="sm">Last 7 days</Pill>}>
          {(() => {
            // Build a 7-day series ending today
            const days = ["22 Apr", "23 Apr", "24 Apr", "25 Apr", "26 Apr", "27 Apr", "28 Apr"];
            const series = days.map(d => {
              const dayLoans = LOANS.filter(l => l.disbursedAt.startsWith(d));
              const dayAttached = dayLoans.filter(l => l.attachmentStatus === "attached").length;
              const rate = dayLoans.length ? Math.round((dayAttached / dayLoans.length) * 100) : 0;
              return { x: d.replace(" Apr", ""), y: rate };
            });
            return (
              <LineChart
                height={220}
                yAxisLabel="Rate (%)"
                valueFormat={(v) => `${v}%`}
                series={[{ label: "Attachment rate", points: series }]}
              />
            );
          })()}
        </Card>
      </div>

      {/* Filter bar */}
      <FilterBar rightSlot={<SearchInput value={q} onChange={setQ} placeholder="Search by name or loan ref..."/>}>
        <ChipMenu label="Status" value={statusFilter === "all" ? "All" : statusFilter} options={[
          { id: "all", label: "All" },
          { id: "unattached", label: "Unattached" },
          { id: "attached",   label: "Attached" },
          { id: "pending",    label: "Pending" },
        ]} selected={statusFilter} onSelect={setStatusFilter}/>
        <ChipMenu label="Loan product" value={productFilter === "all" ? "All" : productFilter} options={[
          { id: "all", label: "All" },
          ...products.map(p => ({ id: p, label: p })),
        ]} selected={productFilter} onSelect={setProductFilter}/>
        <ChipMenu label="Branch" value={branchFilter === "all" ? "All" : branchFilter} options={[
          { id: "all", label: "All" },
          ...branches.map(b => ({ id: b, label: b })),
        ]} selected={branchFilter} onSelect={setBranchFilter}/>
      </FilterBar>

      <DataTable
        emptyMessage={q || statusFilter !== "all" ? "No loans match your filters." : "No loans disbursed in this window."}
        columns={[
          { key: "customer", label: "Borrower", render: (v, r) => (
            <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
              <CustomerAvatar name={v} customerId={r.customerId} size={32}/>
              <div>
                <div style={{ fontWeight: 600 }}>{v}</div>
                <div style={{ fontSize: 11, color: T.textSec, fontFamily: "ui-monospace, monospace" }}>{r.loanRef}</div>
              </div>
            </div>
          )},
          { key: "loanProduct", label: "Loan product", hideMobile: true, render: (v, r) => (
            <div>
              <div style={{ fontSize: 13 }}>{v}</div>
              <div style={{ fontSize: 11, color: T.textSec }}>{r.tenureMonths}-month tenure</div>
            </div>
          )},
          { key: "loanAmount", label: "Loan amount", numeric: true, align: "right", render: v => <Money amount={v} size="sm"/> },
          { key: "branch", label: "Branch", hideMobile: true, render: v => <code style={{ fontSize: 11, color: T.textSec }}>{v}</code> },
          { key: "disbursedAt", label: "Disbursed", hideMobile: true },
          { key: "attachmentStatus", label: "Status", render: v => (
            <Pill color={v === "attached" ? "green" : v === "pending" ? "amber" : "red"} size="sm">
              {v === "attached" ? "Attached" : v === "pending" ? "Pending" : "Unattached"}
            </Pill>
          )},
          { key: "premium", label: "Premium", numeric: true, align: "right", render: (v, r) => (
            r.attachmentStatus === "attached" && v ? <Money amount={v} size="sm"/> : <span style={{ color: T.textTer }}>—</span>
          )},
          { key: "actions", label: "", sortable: false, align: "right", render: (_, r) => (
            r.attachmentStatus === "unattached"
              ? <Btn variant="primary" size="sm" icon={Plus} onClick={(e) => { e?.stopPropagation?.(); attachPolicy(r); }}>Attach</Btn>
              : r.policyId
                ? <Btn variant="ghost" size="sm" iconRight={ArrowRight} onClick={(e) => { e?.stopPropagation?.(); navigate("policy-detail", { id: r.policyId }); }}>View policy</Btn>
                : <span style={{ fontSize: 11, color: T.textTer }}>—</span>
          )},
        ]}
        rows={filtered}
      />
    </div>
  );
}

function GetInsurancePage({ navigate, openSell }) {
  const [category, setCategory] = useState("all");
  const [detailProduct, setDetailProduct] = useState(null); // selected product for the education modal
  const filtered = category === "all" ? PRODUCTS : PRODUCTS.filter(p => p.category.toLowerCase() === category);
  const counts = PRODUCTS.reduce((acc, p) => { acc[p.category.toLowerCase()] = (acc[p.category.toLowerCase()] || 0) + 1; return acc; }, {});

  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 20 }}>
      <div>
        <Display size="lg">Get insurance</Display>
        <div style={{ fontSize: 14, color: T.textSec, marginTop: 6 }}>Embedded products distributed through your channels. Tap a card to learn more before deciding to sell.</div>
      </div>

      <AIRecommendation
        productName="DEVICE INSURANCE"
        headline="Casa surfaced 3 customers ready for device cover."
        reasoning="Olatunde, Ibrahim, and Hauwa all upgraded phones via M-KOPA in the last 14 days. Embedded conversion rate at this window: 4.2× baseline."
        premium={1850}
        confidence={5}
        onAccept={() => openSell(PRODUCTS.find(p => p.id === "device"), CUSTOMERS.find(c => c.id === "c1"))}
        onDismiss={() => {}}
      />

      <FilterBar>
        {[
          { id: "all", label: "All products" },
          { id: "credit", label: "Credit" },
          { id: "device", label: "Device" },
          { id: "fintech", label: "Fintech" },
          { id: "property", label: "Property" },
          { id: "health", label: "Health" },
          { id: "business", label: "Business" },
        ].map(c => (
          <FilterChip key={c.id} label={c.label} active={category === c.id} onClick={() => setCategory(c.id)} value={category === c.id && c.id !== "all" ? `${counts[c.id] || 0}` : null}/>
        ))}
      </FilterBar>

      <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(300px, 1fr))", gap: 14 }}>
        {filtered.length === 0 ? (
          <div style={{ gridColumn: "1 / -1", padding: 40, textAlign: "center", color: T.textSec }}>No products in this category yet.</div>
        ) : filtered.map(p => (
          <ProductPhotoCard key={p.id} product={p} onClick={() => setDetailProduct(p)}/>
        ))}
      </div>

      {detailProduct && (
        <ProductDetailModal
          product={detailProduct}
          onClose={() => setDetailProduct(null)}
          onStartSelling={() => {
            const product = detailProduct;
            setDetailProduct(null);
            openSell(product);
          }}
        />
      )}
    </div>
  );
}

// ProductPhotoCard — photographic top + chip-icon overlap + partner economics inline.
// Replaces the old colored-tile-only card. Click opens ProductDetailModal for partner education.
function ProductPhotoCard({ product, onClick }) {
  const p = product;
  return (
    <div className="stagger-fadeIn" onClick={onClick} style={{
      background: T.surface, border: `1px solid ${T.border}`, borderRadius: 14, overflow: "hidden",
      transition: "transform 0.15s, box-shadow 0.15s", cursor: "pointer", position: "relative",
      display: "flex", flexDirection: "column",
    }}
      onMouseEnter={e => { e.currentTarget.style.transform = "translateY(-3px)"; e.currentTarget.style.boxShadow = "0 12px 28px rgba(0,0,0,0.08)"; }}
      onMouseLeave={e => { e.currentTarget.style.transform = "none"; e.currentTarget.style.boxShadow = "none"; }}
    >
      {/* Photo top */}
      <div style={{
        height: 160, position: "relative",
        backgroundColor: p.color,
        backgroundImage: p.photo ? `linear-gradient(180deg, rgba(0,0,0,0) 40%, rgba(0,0,0,0.18) 100%), url('${p.photo}')` : "none",
        backgroundSize: "cover", backgroundPosition: "center",
      }}>
        {/* Top-right badge */}
        {p.badge && (
          <div style={{ position: "absolute", top: 12, right: 12 }}>
            <Pill color={p.badge === "NEW" ? "brand" : "dark"} solid={p.badge === "NEW"} size="sm">{p.badge}</Pill>
          </div>
        )}
        {/* Bottom-left chip-icon — overlaps the photo bottom edge */}
        <div style={{
          position: "absolute", left: 16, bottom: -22,
          boxShadow: "0 4px 12px rgba(0,0,0,0.12)", border: `3px solid ${T.surface}`,
          borderRadius: 18, flexShrink: 0,
        }}>
          <ProductIcon productId={p.id} product={p} size={56} chip/>
        </div>
      </div>

      {/* Body */}
      <div style={{ padding: "32px 18px 18px", display: "flex", flexDirection: "column", flex: 1 }}>
        <Eyebrow>{p.category}</Eyebrow>
        <div style={{ fontSize: 18, fontWeight: 700, marginTop: 4, marginBottom: 6 }}>{p.name}</div>
        <div style={{ fontSize: 13, color: T.textSec, lineHeight: 1.5, marginBottom: 14 }}>{p.description}</div>

        {/* Partner economics — three small stats inline */}
        {p.partnerEconomics && (
          <div style={{ display: "flex", gap: 16, padding: "10px 12px", background: T.surfaceTint, borderRadius: 10, marginBottom: 14, flexWrap: "wrap" }}>
            <div style={{ minWidth: 0 }}>
              <div style={{ fontSize: 9, fontWeight: 700, color: T.textSec, letterSpacing: "0.06em", textTransform: "uppercase" }}>Commission</div>
              <div style={{ fontSize: 13, fontWeight: 700, color: T.brand, fontVariantNumeric: "tabular-nums" }}>{p.partnerEconomics.commissionRate}</div>
            </div>
            <div style={{ minWidth: 0 }}>
              <div style={{ fontSize: 9, fontWeight: 700, color: T.textSec, letterSpacing: "0.06em", textTransform: "uppercase" }}>Avg attach</div>
              <div style={{ fontSize: 13, fontWeight: 700, color: T.text, fontVariantNumeric: "tabular-nums" }}>{p.partnerEconomics.expectedAttach}</div>
            </div>
            {p.claims?.approvalRate && (
              <div style={{ minWidth: 0 }}>
                <div style={{ fontSize: 9, fontWeight: 700, color: T.textSec, letterSpacing: "0.06em", textTransform: "uppercase" }}>Claim rate</div>
                <div style={{ fontSize: 13, fontWeight: 700, color: T.successText, fontVariantNumeric: "tabular-nums" }}>{p.claims.approvalRate}</div>
              </div>
            )}
          </div>
        )}

        {/* Footer */}
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginTop: "auto", gap: 8 }}>
          <div style={{ fontSize: 12, color: T.textSec }}>{p.premium}</div>
          <Btn variant="ghost" size="sm" iconRight={ArrowRight}>Learn more</Btn>
        </div>
      </div>
    </div>
  );
}

// ═══════════════════════════════════════════════════════════════════════════════════
// PRODUCT DETAIL MODAL — partner-facing education layer. Sits between the catalog card
// and the SellFlow. Answers "should I enable this product?" not "let me bind a policy."
// ═══════════════════════════════════════════════════════════════════════════════════
function ProductDetailModal({ product, onClose, onStartSelling }) {
  if (!product) return null;
  const p = product;
  return (
    <div style={{
      position: "fixed", inset: 0, background: "rgba(28, 18, 14, 0.55)",
      display: "flex", alignItems: "center", justifyContent: "center", zIndex: 1000,
      animation: "fadeIn 0.2s ease-out", padding: 16,
    }} onClick={onClose}>
      <div onClick={e => e.stopPropagation()} style={{
        width: "100%", maxWidth: 760, maxHeight: "92vh", background: T.surface, borderRadius: 18,
        boxShadow: "0 24px 60px rgba(0,0,0,0.18)", display: "flex", flexDirection: "column",
        animation: "slideUp 0.25s ease-out", overflow: "hidden",
      }}>
        {/* Photographic hero with brown scrim, product name + tagline overlaid */}
        <div style={{
          position: "relative", height: 200,
          backgroundColor: p.color,
          backgroundImage: p.photo ? `linear-gradient(180deg, rgba(31,18,14,0.32) 0%, rgba(31,18,14,0.78) 100%), url('${p.photo}')` : "none",
          backgroundSize: "cover", backgroundPosition: "center",
          color: "#fff", padding: "24px 28px", display: "flex", flexDirection: "column", justifyContent: "space-between",
        }}>
          <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", gap: 10 }}>
            <div style={{ fontSize: 11, fontWeight: 700, letterSpacing: "0.08em", textTransform: "uppercase", opacity: 0.92 }}>
              {p.category} · Product spec
            </div>
            <button onClick={onClose} aria-label="Close" style={{ background: "rgba(255,255,255,0.18)", backdropFilter: "blur(8px)", border: "1px solid rgba(255,255,255,0.22)", borderRadius: 999, width: 32, height: 32, display: "inline-flex", alignItems: "center", justifyContent: "center", cursor: "pointer", color: "#fff", flexShrink: 0 }}>
              <X size={16}/>
            </button>
          </div>
          <div style={{ display: "flex", alignItems: "flex-end", gap: 14 }}>
            <div style={{ border: "3px solid #fff", borderRadius: 18, flexShrink: 0 }}>
              <ProductIcon productId={p.id} product={p} size={56} chip/>
            </div>
            <div style={{ flex: 1, minWidth: 0 }}>
              <div style={{ fontFamily: FONT_DISPLAY, fontSize: 28, fontWeight: 400, letterSpacing: "-0.02em", lineHeight: 1.15, textShadow: "0 2px 12px rgba(0,0,0,0.25)" }}>
                {p.name}
              </div>
              {p.tagline && (
                <div style={{ fontSize: 13, opacity: 0.92, marginTop: 4, maxWidth: 520 }}>{p.tagline}</div>
              )}
            </div>
          </div>
        </div>

        {/* Scrollable body */}
        <div style={{ padding: "20px 28px", overflow: "auto", flex: 1, display: "flex", flexDirection: "column", gap: 18 }}>
          {/* Casa's strategic take — the "should I sell this" callout */}
          {p.casaTake && (
            <div style={{ background: T.brandBg, borderRadius: 14, padding: "16px 18px", display: "flex", gap: 12, alignItems: "flex-start" }}>
              <CasaAvatar size={36} state="thinking" color={T.brand}/>
              <div style={{ flex: 1, minWidth: 0 }}>
                <div style={{ fontSize: 10, fontWeight: 700, color: T.brandDeep, letterSpacing: "0.08em", textTransform: "uppercase", marginBottom: 4 }}>Casa's take</div>
                <div style={{ fontSize: 13, color: T.brandDeep, lineHeight: 1.55 }}>{p.casaTake}</div>
              </div>
            </div>
          )}

          {/* Partner economics — three big stat boxes */}
          {p.partnerEconomics && (
            <div>
              <Eyebrow mb={10}>Partner economics</Eyebrow>
              <div className="product-modal-stats" style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(140px, 1fr))", gap: 10 }}>
                <div style={{ padding: 14, background: T.brand, borderRadius: 14, color: "#fff" }}>
                  <div style={{ fontSize: 10, fontWeight: 700, opacity: 0.85, letterSpacing: "0.08em", textTransform: "uppercase" }}>Commission</div>
                  <div style={{ fontFamily: FONT_DISPLAY, fontSize: 24, fontWeight: 400, letterSpacing: "-0.02em", marginTop: 2 }}>{p.partnerEconomics.commissionRate}</div>
                  <div style={{ fontSize: 11, opacity: 0.85, marginTop: 4 }}>of premium</div>
                </div>
                <div style={{ padding: 14, background: T.surfaceTint, borderRadius: 14 }}>
                  <div style={{ fontSize: 10, fontWeight: 700, color: T.textSec, letterSpacing: "0.08em", textTransform: "uppercase" }}>Avg premium</div>
                  <div style={{ fontFamily: FONT_DISPLAY, fontSize: 22, fontWeight: 400, letterSpacing: "-0.02em", marginTop: 2 }}>{p.partnerEconomics.avgPremium}</div>
                  <div style={{ fontSize: 11, color: T.textSec, marginTop: 4 }}>per policy</div>
                </div>
                <div style={{ padding: 14, background: T.surfaceTint, borderRadius: 14 }}>
                  <div style={{ fontSize: 10, fontWeight: 700, color: T.textSec, letterSpacing: "0.08em", textTransform: "uppercase" }}>Expected attach</div>
                  <div style={{ fontFamily: FONT_DISPLAY, fontSize: 22, fontWeight: 400, letterSpacing: "-0.02em", marginTop: 2 }}>{p.partnerEconomics.expectedAttach}</div>
                  <div style={{ fontSize: 11, color: T.textSec, marginTop: 4 }}>industry benchmark</div>
                </div>
                <div style={{ padding: 14, background: T.surfaceTint, borderRadius: 14 }}>
                  <div style={{ fontSize: 10, fontWeight: 700, color: T.textSec, letterSpacing: "0.08em", textTransform: "uppercase" }}>Time to first sale</div>
                  <div style={{ fontFamily: FONT_DISPLAY, fontSize: 16, fontWeight: 500, letterSpacing: "-0.01em", marginTop: 4, lineHeight: 1.2 }}>{p.partnerEconomics.timeToFirstSale}</div>
                </div>
              </div>
            </div>
          )}

          {/* Coverage */}
          {p.coverage && (
            <div>
              <Eyebrow mb={10}>What's covered</Eyebrow>
              <div style={{ background: T.surface, border: `1px solid ${T.border}`, borderRadius: 14, padding: 16 }}>
                <div style={{ fontSize: 12, fontWeight: 700, color: T.textSec, marginBottom: 8 }}>Perils</div>
                <div style={{ display: "flex", flexWrap: "wrap", gap: 6, marginBottom: 12 }}>
                  {p.coverage.perils.map(peril => <Tag key={peril} color="green" icon={Check}>{peril}</Tag>)}
                </div>
                <KeyValueGrid items={[
                  { label: "Sum assured", value: p.coverage.sumAssured },
                  { label: "Term", value: p.coverage.term },
                  { label: "Payout channel", value: p.coverage.payoutChannel },
                ]}/>
              </div>
            </div>
          )}

          {/* Eligibility */}
          {p.eligibility && (
            <div>
              <Eyebrow mb={10}>Who qualifies</Eyebrow>
              <div style={{ background: T.surface, border: `1px solid ${T.border}`, borderRadius: 14, padding: 16 }}>
                <KeyValueGrid items={Object.entries(p.eligibility)
                  .filter(([k]) => k !== "exclusions")
                  .map(([k, v]) => ({
                    label: k.replace(/([A-Z])/g, " $1").replace(/^./, c => c.toUpperCase()),
                    value: Array.isArray(v) ? v.join(", ") : v,
                  }))}/>
                {p.eligibility.exclusions && (
                  <div style={{ marginTop: 12, paddingTop: 12, borderTop: `1px solid ${T.border}` }}>
                    <div style={{ fontSize: 11, fontWeight: 700, color: T.dangerText, letterSpacing: "0.04em", textTransform: "uppercase", marginBottom: 6 }}>Key exclusions</div>
                    <div style={{ display: "flex", flexWrap: "wrap", gap: 6 }}>
                      {p.eligibility.exclusions.map(ex => <Tag key={ex} color="red">{ex}</Tag>)}
                    </div>
                  </div>
                )}
              </div>
            </div>
          )}

          {/* Claims performance */}
          {p.claims && (
            <div>
              <Eyebrow mb={10}>Claims performance</Eyebrow>
              <div className="product-modal-claims" style={{ background: T.surface, border: `1px solid ${T.border}`, borderRadius: 14, padding: 16, display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(140px, 1fr))", gap: 14 }}>
                <div>
                  <div style={{ fontSize: 11, fontWeight: 700, color: T.textSec, letterSpacing: "0.04em", textTransform: "uppercase" }}>Approval rate</div>
                  <div style={{ fontFamily: FONT_DISPLAY, fontSize: 26, fontWeight: 400, color: T.successText, marginTop: 4 }}>{p.claims.approvalRate}</div>
                </div>
                <div>
                  <div style={{ fontSize: 11, fontWeight: 700, color: T.textSec, letterSpacing: "0.04em", textTransform: "uppercase" }}>Avg settle time</div>
                  <div style={{ fontFamily: FONT_DISPLAY, fontSize: 16, fontWeight: 500, color: T.text, marginTop: 4, lineHeight: 1.3 }}>{p.claims.avgSettleTime}</div>
                </div>
                {p.claims.lastQuarter && (
                  <div style={{ gridColumn: "1 / -1", paddingTop: 10, borderTop: `1px solid ${T.border}` }}>
                    <div style={{ fontSize: 12, color: T.textSec, lineHeight: 1.55 }}>
                      <strong style={{ color: T.text }}>Last quarter:</strong> {p.claims.lastQuarter}
                    </div>
                  </div>
                )}
              </div>
            </div>
          )}

          {/* Partner integration */}
          {p.partnerActions && (
            <div>
              <Eyebrow mb={10}>How you sell it</Eyebrow>
              <div style={{ background: T.surface, border: `1px solid ${T.border}`, borderRadius: 14, padding: 16 }}>
                <div style={{ fontSize: 12, fontWeight: 700, color: T.textSec, marginBottom: 8 }}>Available integrations</div>
                <div style={{ display: "flex", flexWrap: "wrap", gap: 6, marginBottom: 12 }}>
                  {p.partnerActions.integrations.map(int => <Tag key={int} color="brand">{int}</Tag>)}
                </div>
                <KeyValueGrid items={[
                  { label: "Setup time", value: p.partnerActions.setupTime },
                  { label: "Requirements", value: p.partnerActions.requirements.join(" · ") },
                ]}/>
              </div>
            </div>
          )}

          {/* Compliance */}
          {p.regulatory && (
            <div>
              <Eyebrow mb={10}>Regulatory</Eyebrow>
              <div style={{ background: T.surfaceTint, borderRadius: 14, padding: 14, display: "flex", gap: 12, alignItems: "flex-start" }}>
                <AuthoritySeal type="naicom" size={40}/>
                <div style={{ flex: 1, minWidth: 0, fontSize: 12, color: T.text, lineHeight: 1.6 }}>
                  <div><strong>License:</strong> {p.regulatory.license}</div>
                  <div><strong>Act compliance:</strong> {p.regulatory.actCompliance}</div>
                  <div><strong>Standard terms:</strong> {p.regulatory.standardTerms}</div>
                </div>
              </div>
            </div>
          )}

          {/* Social proof */}
          {p.socialProof && (
            <div style={{ padding: 16, background: T.brown, color: "#fff", borderRadius: 14 }}>
              <div style={{ fontSize: 10, fontWeight: 700, letterSpacing: "0.08em", textTransform: "uppercase", opacity: 0.85, marginBottom: 8 }}>Already trusted by</div>
              <div style={{ fontSize: 14, fontWeight: 600, marginBottom: 10 }}>{p.socialProof.partners.join(" · ")}</div>
              <div style={{ display: "flex", gap: 24, flexWrap: "wrap", paddingTop: 10, borderTop: "1px solid rgba(255,255,255,0.18)" }}>
                <div>
                  <div style={{ fontFamily: FONT_DISPLAY, fontSize: 20, fontWeight: 400 }}>{p.socialProof.book}</div>
                  <div style={{ fontSize: 11, opacity: 0.78 }}>Book in force</div>
                </div>
                <div>
                  <div style={{ fontFamily: FONT_DISPLAY, fontSize: 20, fontWeight: 400 }}>{p.socialProof.customers}</div>
                  <div style={{ fontSize: 11, opacity: 0.78 }}>Active</div>
                </div>
              </div>
            </div>
          )}
        </div>

        {/* Footer CTAs */}
        <div style={{ padding: "14px 28px", borderTop: `1px solid ${T.border}`, display: "flex", gap: 8, justifyContent: "space-between", flexWrap: "wrap", background: T.surface }}>
          <Btn variant="ghost" onClick={onClose}>Not now</Btn>
          <div style={{ display: "flex", gap: 8 }}>
            <Btn variant="outline" icon={Download} onClick={() => alert(`Stub — would download the ${p.name} spec sheet PDF.`)}>Spec sheet</Btn>
            <Btn variant="brand" iconRight={ArrowRight} onClick={onStartSelling}>Start selling this</Btn>
          </div>
        </div>
      </div>
    </div>
  );
}

// ═══════════════════════════════════════════════════════════════════════════════════
// SELL FLOW — end-to-end quote-to-bind. Stores quote and (if bound) a new policy.
// ═══════════════════════════════════════════════════════════════════════════════════
function SellFlow({ open, product, prefilledCustomer, prefilledLoan, onClose, onComplete }) {
  const [step, setStep] = useState(1);
  const [customer, setCustomer] = useState(prefilledCustomer || null);
  const [customerSearch, setCustomerSearch] = useState("");
  const [coverage, setCoverage] = useState(0);
  const [premium, setPremium] = useState(0);
  const [bindResult, setBindResult] = useState(null); // { quoteRef, policyNumber }

  // Reset state on open and seed sensible defaults from the product / loan
  useEffect(() => {
    if (!open) return;
    setStep(prefilledCustomer ? 2 : 1);
    setCustomer(prefilledCustomer || null);
    setCustomerSearch("");
    setBindResult(null);
    if (product) {
      // If we're attaching to a loan, coverage = loanAmount. Otherwise sensible per-product defaults.
      let defaultCoverage;
      if (prefilledLoan) {
        defaultCoverage = prefilledLoan.loanAmount;
      } else {
        const defaults = {
          "credit-life": 1000000,
          device: 250000,
          "funds-protection": 500000,
          building: 25000000,
          "group-health": 2000000,
          keyman: 10000000,
        };
        defaultCoverage = defaults[product.id] || 1000000;
      }
      setCoverage(defaultCoverage);
      setPremium(calcPremium(product, defaultCoverage));
    }
  }, [open, product, prefilledCustomer, prefilledLoan]);

  if (!open || !product) return null;

  const customers = CUSTOMERS.filter(c => !customerSearch || c.name.toLowerCase().includes(customerSearch.toLowerCase()) || c.email.toLowerCase().includes(customerSearch.toLowerCase()));

  const handleCoverageChange = (newCov) => {
    setCoverage(newCov);
    setPremium(calcPremium(product, newCov));
  };

  const bind = () => {
    // Generate refs and append to mock data so the new policy/quote actually appears in lists
    const seq = String(QUOTES.length + POLICIES.length + 1).padStart(4, "0");
    const quoteRef = `QT-2026-0${seq}`;
    const policyNumber = `${productCodePrefix(product.id)}-2026-0${seq}`;
    const today = formatDate(new Date());
    const newQuote = {
      id: `q-new-${Date.now()}`, quoteRef, customer: customer.name, customerEmail: customer.email,
      product: product.name, premium, coverage, status: "Bound", created: today, expires: addDays(new Date(), 14),
      channel: ACTIVE_PARTNER.name, icon: product.icon,
    };
    const newPolicy = {
      id: `p-new-${Date.now()}`, policyNumber, customer: customer.name, customerId: customer.id,
      product: product.name, plan: product.description, status: "ACTIVE", premium, coverage,
      start: today, end: addDays(new Date(), 365), insurer: "Casava Microinsurance", icon: product.icon,
      // If attaching to a loan, copy loan metadata so PolicyDetailPage can show it
      ...(prefilledLoan ? {
        loanRef: prefilledLoan.loanRef,
        loanProduct: prefilledLoan.loanProduct,
        loanAmount: prefilledLoan.loanAmount,
        disbursedAt: prefilledLoan.disbursedAt,
        tenureMonths: prefilledLoan.tenureMonths,
        branch: prefilledLoan.branch,
        lenderRef: prefilledLoan.lenderRef,
      } : {}),
    };
    QUOTES.unshift(newQuote);
    POLICIES.unshift(newPolicy);
    // If this was a loan attach, flip the loan's status and link the new policy
    if (prefilledLoan) {
      const loanIdx = LOANS.findIndex(l => l.id === prefilledLoan.id);
      if (loanIdx >= 0) {
        LOANS[loanIdx] = {
          ...LOANS[loanIdx],
          attachmentStatus: "attached",
          policyId: newPolicy.id,
          premium,
        };
      }
    }
    setBindResult({ quoteRef, policyNumber, policyId: newPolicy.id });
    setStep(4);
    onComplete?.();
  };

  return (
    <div style={{
      position: "fixed", inset: 0, background: "rgba(28, 18, 14, 0.55)",
      display: "flex", alignItems: "center", justifyContent: "center", zIndex: 1000,
      animation: "fadeIn 0.2s ease-out", padding: 16,
    }} onClick={onClose}>
      <div onClick={e => e.stopPropagation()} style={{
        width: "100%", maxWidth: 720, maxHeight: "92vh", background: T.surface, borderRadius: 18,
        boxShadow: "0 24px 60px rgba(0,0,0,0.18)", display: "flex", flexDirection: "column",
        animation: "slideUp 0.25s ease-out",
      }}>
        {/* Modal header — product context + close. Stepper lives inside ApplicationStep below. */}
        <div style={{ padding: "18px 20px", borderBottom: `1px solid ${T.border}`, display: "flex", alignItems: "center", justifyContent: "space-between", gap: 12 }}>
          <div style={{ display: "flex", alignItems: "center", gap: 12, minWidth: 0 }}>
            <ProductIcon productId={product.id} product={product} size={40} chip/>
            <div style={{ minWidth: 0 }}>
              <div style={{ fontSize: 11, fontWeight: 700, color: T.textSec, letterSpacing: "0.06em", textTransform: "uppercase" }}>{prefilledLoan ? "Attach · Credit Life" : `Sell · ${product.category}`}</div>
              <div style={{ fontSize: 16, fontWeight: 700, whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>{product.name}</div>
            </div>
          </div>
          <button onClick={onClose} style={{ background: T.surfaceTint, border: "none", borderRadius: 999, width: 32, height: 32, display: "inline-flex", alignItems: "center", justifyContent: "center", cursor: "pointer", color: T.textSec, flexShrink: 0 }}><X size={16}/></button>
        </div>

        {/* Loan-attach context strip */}
        {prefilledLoan && (
          <div style={{ padding: "10px 20px", background: T.brandBg, borderBottom: `1px solid ${T.border}`, display: "flex", alignItems: "center", gap: 10, flexWrap: "wrap" }}>
            <div style={{ width: 6, height: 6, borderRadius: 999, background: T.brand, flexShrink: 0 }}/>
            <span style={{ fontSize: 12, fontWeight: 600, color: T.brandDeep }}>
              Attaching to loan
            </span>
            <code style={{ fontSize: 11, fontFamily: "ui-monospace, monospace", color: T.brandDeep, fontWeight: 600 }}>{prefilledLoan.loanRef}</code>
            <span style={{ fontSize: 11, color: T.brandDeep, opacity: 0.78 }}>
              · {prefilledLoan.loanProduct} · ₦{prefilledLoan.loanAmount.toLocaleString()} · {prefilledLoan.tenureMonths}-mo · {prefilledLoan.branch}
            </span>
          </div>
        )}

        {/* Body — ApplicationStep bare so it inherits the modal's surface */}
        <div style={{ padding: 20, overflow: "auto", flex: 1 }}>
          {step === 1 && (
            <ApplicationStep bare
              stepNumber={1} totalSteps={3}
              casaState="talking"
              casaMessage="Who's this cover for? Search by name or email."
              question="Pick a customer"
              primaryAction={<Btn variant="primary" iconRight={ArrowRight} disabled={!customer} onClick={() => setStep(2)}>Continue</Btn>}
              secondaryAction={<Btn variant="ghost" onClick={onClose}>Cancel</Btn>}
            >
              <SearchInput value={customerSearch} onChange={setCustomerSearch} placeholder="Search customers..."/>
              <div style={{ marginTop: 12, display: "flex", flexDirection: "column", gap: 6, maxHeight: 280, overflowY: "auto" }}>
                {customers.map(c => (
                  <button key={c.id} onClick={() => setCustomer(c)} style={{
                    display: "flex", alignItems: "center", gap: 12, padding: 12,
                    background: customer?.id === c.id ? T.brandBg : T.surface,
                    border: `1.5px solid ${customer?.id === c.id ? T.brand : T.border}`,
                    borderRadius: 10, cursor: "pointer", textAlign: "left", fontFamily: FONT, width: "100%",
                  }}>
                    <Avatar name={c.name} size={32} color={c.type === "business" ? "pinkCoral" : "green"} kind={c.type}/>
                    <div style={{ flex: 1, minWidth: 0 }}>
                      <div style={{ fontSize: 13, fontWeight: 700 }}>{c.name}</div>
                      <div style={{ fontSize: 11, color: T.textSec, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{c.email}</div>
                    </div>
                    {customer?.id === c.id && <Check size={16} color={T.brand} strokeWidth={2.6}/>}
                  </button>
                ))}
              </div>
              <button onClick={() => alert("New customer flow opens — production captures BVN, NIN, KYC")} style={{ marginTop: 12, width: "100%", padding: 12, background: "transparent", border: `1.5px dashed ${T.border}`, borderRadius: 10, cursor: "pointer", fontFamily: FONT, fontSize: 13, color: T.textSec, fontWeight: 600 }}>
                + Create a new customer
              </button>
            </ApplicationStep>
          )}

          {step === 2 && customer && (
            <div className="sell-step2-grid" style={{ display: "grid", gridTemplateColumns: "1fr 260px", gap: 18, alignItems: "flex-start" }}>
              <ApplicationStep bare
                stepNumber={2} totalSteps={3}
                casaState="thinking"
                casaMessage={`For ${customer.name.split(" ")[0]}, how much cover should I quote?`}
                question="Set the cover amount"
                hint="Pick a preset or type a custom amount."
                primaryAction={<Btn variant="primary" iconRight={ArrowRight} disabled={coverage <= 0} onClick={() => setStep(3)}>Generate quote</Btn>}
                secondaryAction={<Btn variant="ghost" icon={ChevronLeft} onClick={() => setStep(1)}>Back</Btn>}
              >
                <Eyebrow>Sum assured</Eyebrow>
                <div style={{ position: "relative", display: "flex", alignItems: "center", marginTop: 6 }}>
                  <span style={{ position: "absolute", left: 16, fontFamily: FONT_DISPLAY, fontSize: 22, color: T.textSec }}>₦</span>
                  <input type="number" inputMode="numeric" value={coverage || ""} onChange={e => handleCoverageChange(parseInt(e.target.value) || 0)} style={{
                    width: "100%", padding: "14px 16px 14px 40px", borderRadius: 10,
                    border: `1px solid ${T.border}`, background: T.surface, fontFamily: FONT_DISPLAY,
                    fontSize: 24, color: T.text, outline: "none", boxSizing: "border-box",
                  }}/>
                </div>
                <div style={{ display: "flex", gap: 8, marginTop: 12, flexWrap: "wrap" }}>
                  {coveragePresets(product.id).map(c => (
                    <button key={c} onClick={() => handleCoverageChange(c)} style={{
                      padding: "6px 12px", borderRadius: 999,
                      background: coverage === c ? T.brand : T.surfaceTint,
                      color: coverage === c ? "#fff" : T.text,
                      border: "none", fontSize: 12, fontWeight: 600, fontFamily: FONT, cursor: "pointer",
                    }}>{compactNaira(c)}</button>
                  ))}
                </div>
              </ApplicationStep>

              <LiveQuoteRail
                productName={product.name}
                productIcon={product.icon}
                sticky={false}
                lineItems={[
                  { key: "customer", label: "Customer", value: customer.name.split(" ")[0] },
                  { key: "channel",  label: "Channel",  value: ACTIVE_PARTNER.name },
                  { key: "kyc",      label: "KYC status", value: "Cleared" },
                  { key: "cover",    label: "Sum assured", value: coverage || 0 },
                  { key: "premium",  label: "Premium", value: premium || 0, frequency: "month" },
                ]}
                total={premium}
                totalFrequency="month"
                hint="Casa pre-checks KYC and fraud signals before bind."
              />
            </div>
          )}

          {step === 3 && customer && (
            <ApplicationStep bare
              stepNumber={3} totalSteps={3}
              casaState="happy"
              casaMessage="I've checked premium-paid status, KYC, and fraud signals. This looks clean."
              question="Ready to bind?"
              hint="Once you confirm, the policy issues immediately and a quote ref is generated."
              primaryAction={<Btn variant="brand" icon={Check} onClick={bind}>Bind policy</Btn>}
              secondaryAction={<Btn variant="ghost" icon={ChevronLeft} onClick={() => setStep(2)}>Back</Btn>}
            >
              <div style={{ background: T.surfaceTint, padding: 16, borderRadius: 14, marginBottom: 12 }}>
                <DisclosureRow label="Customer" value={customer.name}/>
                <DisclosureRow label="Product" value={product.name}/>
                <DisclosureRow label="Sum assured" value={`₦${coverage.toLocaleString()}`}/>
                <DisclosureRow label="Premium" value={`₦${premium.toLocaleString()}/mo`}/>
                <DisclosureRow label="Channel" value={ACTIVE_PARTNER.name} ruled={false}/>
              </div>
              <Banner variant="success" title="Casa pre-checked this sale" description="Customer is KYC-cleared. Premium will debit their saved Moniepoint card. Policy issues immediately on confirm."/>
            </ApplicationStep>
          )}

          {step === 4 && bindResult && customer && (
            <div style={{ textAlign: "center", padding: "32px 12px" }}>
              <CasaAvatar size={72} state="celebrating"/>
              <Display size="md" style={{ marginTop: 16 }}>Policy bound 🎉</Display>
              <div style={{ fontSize: 14, color: T.textSec, marginTop: 8, lineHeight: 1.55 }}>
                {customer.name} is now covered for <strong style={{ color: T.text }}>₦{coverage.toLocaleString()}</strong> on {product.name}.
              </div>
              <div style={{ marginTop: 18, padding: 14, background: T.surfaceTint, borderRadius: 10, fontFamily: "ui-monospace, monospace", fontSize: 12, display: "flex", justifyContent: "space-between", flexWrap: "wrap", gap: 8 }}>
                <span><span style={{ color: T.textSec }}>Quote:</span> {bindResult.quoteRef}</span>
                <span><span style={{ color: T.textSec }}>Policy:</span> {bindResult.policyNumber}</span>
              </div>
              <div style={{ display: "flex", gap: 8, justifyContent: "center", marginTop: 24 }}>
                <Btn variant="ghost" onClick={onClose}>Close</Btn>
                <Btn variant="primary" iconRight={ArrowRight} onClick={() => { onClose(); }}>View policy</Btn>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

// Helpers for SellFlow
function calcPremium(product, coverage) {
  // Mock pricing model: rate per ₦1k of cover, varies by product type
  const ratePerThousand = {
    "credit-life": 0.9,
    device: 7.4,
    "funds-protection": 0.7,
    building: 0.74,
    "group-health": 6.25,
    keyman: 1.5,
  }[product.id] || 1.0;
  const monthly = (coverage / 1000) * ratePerThousand / 12;
  return Math.max(150, Math.round(monthly / 50) * 50); // floor at ₦150, round to ₦50
}
function coveragePresets(productId) {
  return {
    "credit-life": [500000, 1000000, 2500000, 5000000, 10000000],
    device: [150000, 250000, 500000, 750000, 1000000],
    "funds-protection": [200000, 500000, 1000000, 2000000],
    building: [10000000, 25000000, 50000000, 100000000],
    "group-health": [1000000, 2000000, 5000000, 10000000],
    keyman: [5000000, 10000000, 25000000, 50000000],
  }[productId] || [500000, 1000000, 2500000];
}
function productCodePrefix(productId) {
  return { "credit-life": "CL", device: "DV", "funds-protection": "FFP", building: "BLD", "group-health": "GH", keyman: "KM" }[productId] || "POL";
}
function compactNaira(n) {
  if (n >= 1_000_000) return `₦${(n/1_000_000).toFixed(n % 1_000_000 === 0 ? 0 : 1)}M`;
  if (n >= 1_000) return `₦${(n/1_000).toFixed(0)}K`;
  return `₦${n}`;
}
function formatDate(d) {
  return d.toLocaleDateString("en-GB", { day: "2-digit", month: "short", year: "numeric" });
}
function addDays(d, days) {
  const r = new Date(d); r.setDate(r.getDate() + days);
  return formatDate(r);
}

function WalletPage({ navigate }) {
  const [topUpOpen, setTopUpOpen] = useState(false);
  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 20 }}>
      <div style={{ display: "flex", alignItems: "flex-start", justifyContent: "space-between", flexWrap: "wrap", gap: 12 }}>
        <div>
          <Display size="lg">Wallet</Display>
          <div style={{ fontSize: 14, color: T.textSec, marginTop: 6 }}>Float balance and transaction history. <button onClick={() => navigate("settlements")} style={{ background: "transparent", border: "none", padding: 0, color: T.brand, cursor: "pointer", fontFamily: FONT, fontSize: 14, fontWeight: 600 }}>View settlements →</button></div>
        </div>
        <div style={{ display: "flex", gap: 8 }}>
          <Btn variant="outline" icon={Download} onClick={() => navigate("statements")}>Statement</Btn>
          <Btn variant="brand" icon={Plus} onClick={() => setTopUpOpen(true)}>Top up</Btn>
        </div>
      </div>

      <HeroGradientCard
        surface="brand"
        tag={<Eyebrow color="rgba(255,255,255,0.8)">Wallet balance</Eyebrow>}
        title="₦4,287,540"
        subtitle="Last updated 2 minutes ago. Auto-funded via Moniepoint settlement."
        stat={{ value: "+₦2.15M", label: "Inflows MTD" }}
      />

      {/* Commission earnings — partner-side P&L. The reason embedded distribution works.  */}
      <Card title="Your commission earnings" right={<Pill color="green" size="sm">12% rate</Pill>}>
        <div className="grid-2col-1col" style={{ gap: 14 }}>
          <div>
            <div style={{ display: "flex", alignItems: "baseline", gap: 6, marginBottom: 4 }}>
              <Money amount={257700} size="xl" accent={T.success}/>
              <span style={{ fontSize: 13, color: T.textSec }}>this month</span>
            </div>
            <div style={{ fontSize: 12, color: T.textSec, lineHeight: 1.55 }}>
              Earned on <strong>{POLICIES.length + 412}</strong> policies sold this month at 12% commission. Settled on the 2nd of next month.
            </div>
          </div>
          <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 10 }}>
            <div style={{ padding: 12, background: T.surfaceTint, borderRadius: 10 }}>
              <Eyebrow mb={4}>Pending</Eyebrow>
              <div style={{ fontSize: 16, fontWeight: 700, fontVariantNumeric: "tabular-nums" }}>₦257,700</div>
              <div style={{ fontSize: 11, color: T.textSec, marginTop: 2 }}>Settles 02 May</div>
            </div>
            <div style={{ padding: 12, background: T.successBg, borderRadius: 10 }}>
              <Eyebrow mb={4} color={T.successText}>Paid YTD</Eyebrow>
              <div style={{ fontSize: 16, fontWeight: 700, fontVariantNumeric: "tabular-nums" }}>₦892,840</div>
              <div style={{ fontSize: 11, color: T.textSec, marginTop: 2 }}>4 settlements</div>
            </div>
          </div>
        </div>
        <div style={{ marginTop: 14, paddingTop: 14, borderTop: `1px solid ${T.border}`, display: "flex", justifyContent: "flex-end", alignItems: "center", flexWrap: "wrap", gap: 10 }}>
          <Btn variant="ghost" size="sm" iconRight={ArrowRight} onClick={() => navigate("settlements")}>See settlements</Btn>
        </div>
      </Card>

      {/* Charts row — premium volume trend + commission mix by product */}
      <div className="grid-2col-1col" style={{ gap: 14 }}>
        <Card title="Premium volume" right={<Pill color="gray" size="sm">Last 6 months</Pill>}>
          <LineChart
            height={220}
            yAxisLabel="Naira (millions)"
            valueFormat={(v) => `₦${(v / 1_000_000).toFixed(1)}M`}
            series={[{
              label: "Premium written",
              points: [
                { x: "Nov", y: 1_840_000 },
                { x: "Dec", y: 2_120_000 },
                { x: "Jan", y: 2_680_000 },
                { x: "Feb", y: 3_240_000 },
                { x: "Mar", y: 3_810_000 },
                { x: "Apr", y: 4_287_540 },
              ],
            }]}
          />
        </Card>
        <Card title="Commission by product" right={<Pill color="gray" size="sm">This month</Pill>}>
          <DonutChart
            size={160}
            thickness={26}
            centerValue="₦257K"
            centerLabel="Earned"
            data={[
              { label: "Credit Life",      value: 184_000 },
              { label: "Device Insurance", value: 52_000 },
              { label: "Funds Protection", value: 21_700 },
            ]}
          />
        </Card>
      </div>

      <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(180px, 1fr))", gap: 14 }}>
        <StatBlock label="This month in" value={2147500} prefix="₦" delay={0} accent={T.success}/>
        <StatBlock label="This month out" value={445000} prefix="₦" delay={60}/>
        <StatBlock label="Transactions" value={847} delay={120}/>
        <StatBlock label="Avg policy value" value={2536} prefix="₦" delay={180}/>
      </div>

      <FilterBar rightSlot={<Btn variant="outline" size="sm" icon={Download} onClick={() => alert("Export queued")}>Export</Btn>}>
        <FilterChip label="All" active/>
        <FilterChip label="Credits"/>
        <FilterChip label="Debits"/>
        <FilterChip label="Source" value="All"/>
      </FilterBar>

      <DataTable
        columns={[
          { key: "date", label: "Date" },
          { key: "description", label: "Description", render: v => <span style={{ fontWeight: 500 }}>{v}</span> },
          { key: "source", label: "Source", hideMobile: true, render: v => <code style={{ fontSize: 11, color: T.textSec }}>{v}</code> },
          { key: "type", label: "Type", hideMobile: true, render: v => <Pill color={v === "Credit" ? "green" : "gray"} size="sm">{v}</Pill> },
          { key: "amount", label: "Amount", numeric: true, align: "right", render: (v, r) => (
            <span style={{ color: r.type === "Credit" ? T.success : T.text, fontWeight: 600, fontVariantNumeric: "tabular-nums" }}>
              {r.type === "Credit" ? "+" : "−"} ₦{v.toLocaleString()}
            </span>
          )},
        ]}
        rows={TRANSACTIONS}
      />

      {topUpOpen && <TopUpModal onClose={() => setTopUpOpen(false)}/>}
    </div>
  );
}

function SettlementsPage({ navigate }) {
  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 20 }}>
      <div>
        <Display size="lg">Settlements</Display>
        <div style={{ fontSize: 14, color: T.textSec, marginTop: 6 }}>Period summaries, upcoming remittances, and partner fee breakdown.</div>
      </div>

      <SettlementSummary
        period="April 2026"
        grossVolume={2147500}
        partnerFee={214750}
        casavaFee={107375}
        taxes={32212}
        netToPartner={1793163}
        status="pending"
      />

      <RemittanceSchedule schedule={REMITTANCES}/>

      <Card title="Settlement history" right={<Btn variant="outline" size="sm" icon={Download} onClick={() => alert("Export queued")}>Export</Btn>}>
        <DataTable
          columns={[
            { key: "period", label: "Period" },
            { key: "policies", label: "Policies", numeric: true, align: "right" },
            { key: "gross", label: "Gross", numeric: true, align: "right", render: v => <Money amount={v} size="sm"/> },
            { key: "net", label: "Net to partner", numeric: true, align: "right", render: v => <Money amount={v} size="sm"/> },
            { key: "status", label: "Status", render: v => <Pill color={v === "Paid" ? "green" : "amber"} size="sm">{v}</Pill> },
            { key: "paidDate", label: "Paid", hideMobile: true },
          ]}
          rows={[
            { id: "s1", period: "March 2026", policies: 387, gross: 1842300, net: 1538122, status: "Paid", paidDate: "02 Apr 2026" },
            { id: "s2", period: "February 2026", policies: 354, gross: 1683900, net: 1405897, status: "Paid", paidDate: "02 Mar 2026" },
            { id: "s3", period: "January 2026", policies: 312, gross: 1487600, net: 1241738, status: "Paid", paidDate: "01 Feb 2026" },
            { id: "s4", period: "December 2025", policies: 289, gross: 1378400, net: 1150286, status: "Paid", paidDate: "02 Jan 2026" },
            { id: "s5", period: "November 2025", policies: 254, gross: 1212100, net: 1011703, status: "Paid", paidDate: "01 Dec 2025" },
          ]}
        />
      </Card>
    </div>
  );
}

function TopUpModal({ onClose }) {
  const [method, setMethod] = useState("card");
  const [amount, setAmount] = useState(0);
  const valid = amount >= 2000;
  return (
    <Modal title="Top up wallet" onClose={onClose}
      footer={
        <>
          <Btn variant="ghost" onClick={onClose}>Cancel</Btn>
          <Btn variant="brand" iconRight={ArrowRight} disabled={!valid} onClick={onClose}>Proceed to payment</Btn>
        </>
      }>
      <div style={{ fontSize: 13, color: T.textSec, marginBottom: 16, lineHeight: 1.5 }}>Choose a payment method to fund your wallet.</div>
      <div style={{ display: "flex", gap: 8, marginBottom: 16, padding: 4, background: T.surfaceTint, borderRadius: 10 }}>
        {[{ id: "card", label: "Card / Online" }, { id: "transfer", label: "Bank transfer" }].map(m => (
          <button key={m.id} onClick={() => setMethod(m.id)} style={{
            flex: 1, padding: "10px 14px", borderRadius: 8,
            background: method === m.id ? T.surface : "transparent",
            border: "none", fontFamily: FONT, fontSize: 13, fontWeight: 600, cursor: "pointer",
            color: method === m.id ? T.text : T.textSec,
            boxShadow: method === m.id ? "0 1px 2px rgba(0,0,0,0.04)" : "none",
          }}>{m.label}</button>
        ))}
      </div>
      <Field label="Amount" hint="Minimum top-up: ₦2,000">
        <div style={{ position: "relative", display: "flex", alignItems: "center" }}>
          <span style={{ position: "absolute", left: 14, fontFamily: FONT_DISPLAY, fontSize: 22, color: T.textSec }}>₦</span>
          <input type="number" inputMode="numeric" pattern="[0-9]*" value={amount || ""} onChange={e => setAmount(parseInt(e.target.value) || 0)} placeholder="0" style={{
            ...inputStyle, paddingLeft: 36, fontFamily: FONT_DISPLAY, fontSize: 24, fontWeight: 400, height: 56,
          }}/>
        </div>
      </Field>
      {amount > 0 && amount < 2000 && (
        <div style={{ marginTop: 10, fontSize: 12, color: T.dangerText }}>Minimum top-up is ₦2,000.</div>
      )}
    </Modal>
  );
}

function IntegrationsPage({ section }) {
  const [confirming, setConfirming] = useState(false);
  const [liveRevealed, setLiveRevealed] = useState(false);
  const [sandboxRevealed, setSandboxRevealed] = useState(false);
  const [docOpen, setDocOpen] = useState(null);
  const sandboxKey = "sk_test_4f8a92e1c5d9b3f6a7c0e8d2";
  const liveKey = "sk_live_91a3c8f5e2d4b6a8c0e7f9d2";

  // Scroll to section anchor on mount or when section changes
  useEffect(() => {
    if (!section) return;
    const t = setTimeout(() => {
      const el = document.getElementById(`dev-${section}`);
      if (el) el.scrollIntoView({ behavior: "smooth", block: "start" });
    }, 80);
    return () => clearTimeout(t);
  }, [section]);

  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 20 }}>
      <div>
        <Display size="lg">Developers</Display>
        <div style={{ fontSize: 14, color: T.textSec, marginTop: 6 }}>API credentials, integration health, webhooks, and documentation.</div>
      </div>

      <div id="dev-health" style={{ scrollMarginTop: 20 }}>
        <Eyebrow>Integration health</Eyebrow>
        <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(260px, 1fr))", gap: 14, marginTop: 8 }}>
          <APIStatusCard name="Policy API" endpoint="/api/v1/policies" status="healthy" lastSync="48s ago" latencyMs={87} errors24h={0} requests24h={12400}/>
          <APIStatusCard name="Claims API" endpoint="/api/v1/claims" status="healthy" lastSync="2m ago" latencyMs={142} errors24h={0} requests24h={847}/>
          <APIStatusCard name="Webhooks" endpoint="/webhooks/*" status="degraded" lastSync="6m ago" latencyMs={680} errors24h={2} requests24h={4100}/>
        </div>
      </div>

      <div id="dev-api-keys" style={{ scrollMarginTop: 20 }}>
        <Card title="API credentials">
        <div style={{ display: "flex", flexDirection: "column", gap: 12 }}>
          <APIKeyCard
            name="Sandbox key"
            keyValue={sandboxKey}
            masked={!sandboxRevealed}
            lastUsed="2 hours ago"
            environment="sandbox"
            status="active"
            onReveal={() => setSandboxRevealed(v => !v)}
            onCopy={() => {}}
            onRevoke={() => {}}
          />
          <APIKeyCard
            name="Live key"
            keyValue={liveKey}
            masked={!liveRevealed}
            lastUsed="14 minutes ago"
            environment="live"
            status="active"
            onReveal={() => liveRevealed ? setLiveRevealed(false) : setConfirming(true)}
            onCopy={() => {}}
            onRevoke={() => {}}
          />
        </div>
        <div style={{ marginTop: 14 }}>
          <Banner
            variant="info"
            title="Security guidelines"
            description="Never expose live API keys client-side. Use Casava webhooks to receive policy and claim events. Rotate keys on team-member offboarding."
          />
        </div>
      </Card>
      </div>

      <div id="dev-webhooks" style={{ scrollMarginTop: 20 }}>
        <Card title="Webhook deliveries" right={<Btn variant="ghost" size="sm" icon={RefreshCw} onClick={() => setDocOpen("refresh")}>Refresh</Btn>}>
          <WebhookEventLog events={WEBHOOK_EVENTS} onRetry={() => setDocOpen("retry")}/>
        </Card>
      </div>

      <div id="dev-docs" style={{ scrollMarginTop: 20 }}>
        <Card title="Documentation">
        <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(220px, 1fr))", gap: 12 }}>
          {[
            { name: "OpenAPI Spec", desc: "Machine-readable schema for the full Casava API.", icon: Code, badge: "Stable" },
            { name: "Quickstart", desc: "Issue your first policy via API in under 5 minutes.", icon: Zap, badge: "v3" },
            { name: "Webhooks guide", desc: "Subscribe to policy lifecycle and claim events.", icon: Activity, badge: "Stable" },
            { name: "Embedded SDK", desc: "Drop-in widget for partner checkout flows.", icon: Layers, badge: "Beta" },
          ].map(d => (
            <button key={d.name} onClick={() => setDocOpen(d)} style={{
              padding: 16, background: T.surfaceTint, borderRadius: 14, color: T.text, fontFamily: FONT,
              display: "flex", flexDirection: "column", gap: 8, position: "relative", border: `1px solid transparent`,
              cursor: "pointer", textAlign: "left",
            }}
              onMouseEnter={e => e.currentTarget.style.borderColor = T.border}
              onMouseLeave={e => e.currentTarget.style.borderColor = "transparent"}>
              <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                <d.icon size={18} color={T.brand} strokeWidth={2.2}/>
                <Pill color="gray" size="sm">{d.badge}</Pill>
              </div>
              <div style={{ fontSize: 14, fontWeight: 700 }}>{d.name}</div>
              <div style={{ fontSize: 12, color: T.textSec, lineHeight: 1.5 }}>{d.desc}</div>
              <div style={{ marginTop: "auto", display: "flex", alignItems: "center", gap: 4, fontSize: 12, color: T.brand, fontWeight: 600 }}>
                View docs <ArrowUpRight size={12}/>
              </div>
            </button>
          ))}
        </div>
      </Card>
      </div>

      {confirming && (
        <Modal title="Reveal Live API key?" onClose={() => setConfirming(false)} maxWidth={460}
          footer={
            <>
              <Btn variant="ghost" onClick={() => setConfirming(false)}>Cancel</Btn>
              <Btn variant="primary" icon={Eye} onClick={() => { setLiveRevealed(true); setConfirming(false); setTimeout(() => setLiveRevealed(false), 30_000); }}>Yes, reveal</Btn>
            </>
          }>
          <Banner
            variant="amber"
            title="This action is logged"
            description="Revealing the live API key writes an audit entry under your name. The key will auto-hide after 30 seconds. Live keys grant access to production policies, claims, and disbursement."
          />
        </Modal>
      )}
      {docOpen && (
        <ComingSoonModal
          title={docOpen.name}
          description={`${docOpen.desc} The full documentation lives at docs.casava.com. Production opens this in a new tab.`}
          onClose={() => setDocOpen(null)}
        />
      )}
    </div>
  );
}

function BulkUploadsPage() {
  const [uploadModal, setUploadModal] = useState(null); // null | "uploading" | "preview" | "confirming" | "done"
  const [dragActive, setDragActive] = useState(false);
  const [progress, setProgress] = useState(0);
  const [previewRows, setPreviewRows] = useState([]);
  const [filename, setFilename] = useState("");
  const [uploads, setUploads] = useState([
    { id: "u1", filename: "moniepoint-april-week4.csv", partner: "Moniepoint", rows: 287, processed: 287, errors: 0, status: "Completed", uploaded: "28 Apr 2026 · 14:22" },
    { id: "u2", filename: "m-kopa-device-batch-q2.csv", partner: "M-KOPA", rows: 154, processed: 152, errors: 2, status: "Completed with errors", uploaded: "27 Apr 2026 · 09:14" },
    { id: "u3", filename: "samsung-corporate-q2.csv", partner: "Samsung", rows: 89, processed: 89, errors: 0, status: "Completed", uploaded: "25 Apr 2026 · 16:48" },
    { id: "u4", filename: "kuda-business-extension.csv", partner: "Kuda", rows: 412, processed: 0, errors: 0, status: "Processing", uploaded: "29 Apr 2026 · 08:12" },
    { id: "u5", filename: "palmpay-march.csv", partner: "PalmPay", rows: 0, processed: 0, errors: 47, status: "Failed", uploaded: "24 Apr 2026 · 11:33" },
  ]);

  const startUpload = (file) => {
    const name = file?.name || `${ACTIVE_PARTNER.name.toLowerCase()}-batch-${Date.now()}.csv`;
    setFilename(name);
    setUploadModal("uploading");
    setProgress(0);
    // Simulate progress
    let pct = 0;
    const interval = setInterval(() => {
      pct += 8 + Math.random() * 12;
      if (pct >= 100) {
        pct = 100;
        clearInterval(interval);
        setProgress(100);
        setTimeout(() => {
          // Generate mock preview rows
          setPreviewRows(generateMockRows());
          setUploadModal("preview");
        }, 300);
      } else {
        setProgress(pct);
      }
    }, 180);
  };

  const onDrop = (e) => {
    e.preventDefault();
    setDragActive(false);
    const file = e.dataTransfer?.files?.[0];
    startUpload(file);
  };

  const confirmIssue = () => {
    setUploadModal("confirming");
    setTimeout(() => {
      const validRows = previewRows.filter(r => r.status === "valid").length;
      const errorRows = previewRows.filter(r => r.status !== "valid").length;
      const newUpload = {
        id: `u-new-${Date.now()}`, filename, partner: ACTIVE_PARTNER.name,
        rows: previewRows.length, processed: validRows, errors: errorRows,
        status: errorRows === 0 ? "Completed" : "Completed with errors",
        uploaded: `${formatDate(new Date())} · ${new Date().toLocaleTimeString("en-GB", { hour: "2-digit", minute: "2-digit" })}`,
      };
      setUploads([newUpload, ...uploads]);
      setUploadModal("done");
    }, 1100);
  };

  const closeModal = () => {
    setUploadModal(null);
    setProgress(0);
    setPreviewRows([]);
    setFilename("");
  };

  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 20 }}>
      <div style={{ display: "flex", alignItems: "flex-start", justifyContent: "space-between", flexWrap: "wrap", gap: 12 }}>
        <div>
          <Display size="lg">Bulk uploads</Display>
          <div style={{ fontSize: 14, color: T.textSec, marginTop: 6 }}>Upload partner schedules to issue policies in bulk.</div>
        </div>
        <Btn variant="outline" icon={Download} onClick={() => alert("CSV template downloaded (demo)")}>Download CSV template</Btn>
      </div>

      <div className="grid-2col-1col">
        <div
          onDragOver={e => { e.preventDefault(); setDragActive(true); }}
          onDragLeave={() => setDragActive(false)}
          onDrop={onDrop}
          onClick={() => startUpload(null)}
          style={{
            padding: "48px 32px", borderRadius: 14, cursor: "pointer",
            border: `2px dashed ${dragActive ? T.brand : T.borderStrong}`,
            background: dragActive ? T.brandBg : T.surface,
            display: "flex", flexDirection: "column", alignItems: "center", gap: 12,
            transition: "all 0.15s",
          }}>
          <div style={{ width: 56, height: 56, borderRadius: "50%", background: dragActive ? T.brand : T.surfaceTint, display: "inline-flex", alignItems: "center", justifyContent: "center", color: dragActive ? "#fff" : T.brand, transition: "all 0.15s" }}>
            <Upload size={22} strokeWidth={2.4}/>
          </div>
          <div style={{ textAlign: "center" }}>
            <div style={{ fontSize: 15, fontWeight: 700, marginBottom: 4 }}>Drop a CSV file or click to browse</div>
            <div style={{ fontSize: 12, color: T.textSec, lineHeight: 1.6, maxWidth: 380 }}>Up to 5,000 rows per upload. Casa validates every row and surfaces errors before issuing policies.</div>
          </div>
          <Btn variant="primary" icon={Upload}>Choose file</Btn>
        </div>
        <div style={{ background: T.surface, border: `1px solid ${T.border}`, borderRadius: 14, padding: 22 }}>
          <Eyebrow>Required columns</Eyebrow>
          <div style={{ display: "flex", flexDirection: "column", gap: 6, marginTop: 6 }}>
            {[
              { col: "customer_name", note: "Full legal name" },
              { col: "bvn", note: "11 digits" },
              { col: "phone", note: "+234 format" },
              { col: "product_code", note: "CL / DEV / FFP / BLD" },
              { col: "premium_naira", note: "Whole number" },
              { col: "coverage_naira", note: "Whole number" },
              { col: "start_date", note: "ISO YYYY-MM-DD" },
            ].map(c => (
              <div key={c.col} style={{ display: "flex", justifyContent: "space-between", alignItems: "baseline", padding: "6px 0", borderBottom: `1px solid ${T.border}`, fontSize: 12 }}>
                <code style={{ fontFamily: "ui-monospace, monospace", color: T.text, fontWeight: 600 }}>{c.col}</code>
                <span style={{ color: T.textSec, fontSize: 11 }}>{c.note}</span>
              </div>
            ))}
          </div>
        </div>
      </div>

      <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(180px, 1fr))", gap: 14 }}>
        <StatBlock label="Uploads this month" value={47} delay={0}/>
        <StatBlock label="Policies issued" value={942} delay={60} accent={T.success}/>
        <StatBlock label="Failed rows" value={49} delay={120} accent={T.danger}/>
        <StatBlock label="Avg processing time" value={2.4} suffix="min" delay={180}/>
      </div>

      <Card title="Recent uploads">
        <DataTable
          columns={[
            { key: "filename", label: "File", render: (v, r) => (
              <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
                <FileText size={16} color={T.textSec} strokeWidth={2.2}/>
                <div>
                  <div style={{ fontWeight: 600, fontFamily: "ui-monospace, monospace", fontSize: 12 }}>{v}</div>
                  <div style={{ fontSize: 11, color: T.textSec }}>{r.partner}</div>
                </div>
              </div>
            )},
            { key: "rows", label: "Rows", numeric: true, align: "right", hideMobile: true, render: (v, r) => (
              <span>{r.processed}/{v}</span>
            )},
            { key: "errors", label: "Errors", numeric: true, align: "right", render: v => (
              <span style={{ color: v > 0 ? T.danger : T.text, fontWeight: 600 }}>{v}</span>
            )},
            { key: "status", label: "Status", render: v => {
              const c = v === "Completed" ? "green" : v === "Processing" ? "amber" : v === "Failed" ? "red" : "amber";
              return <Pill color={c} size="sm">{v}</Pill>;
            }},
            { key: "uploaded", label: "Uploaded", hideMobile: true, render: v => <span style={{ color: T.textSec, fontSize: 11 }}>{v}</span> },
          ]}
          rows={uploads}
        />
      </Card>

      {/* Upload simulation modal */}
      {uploadModal && (
        <div style={{
          position: "fixed", inset: 0, background: "rgba(28,18,14,0.55)",
          display: "flex", alignItems: "center", justifyContent: "center", zIndex: 1000,
          animation: "fadeIn 0.2s ease-out", padding: 16,
        }} onClick={uploadModal === "done" ? closeModal : undefined}>
          <div onClick={e => e.stopPropagation()} style={{
            width: "100%", maxWidth: 720, maxHeight: "90vh", background: T.surface, borderRadius: 18,
            boxShadow: "0 24px 60px rgba(0,0,0,0.18)", display: "flex", flexDirection: "column",
            animation: "slideUp 0.25s ease-out",
          }}>
            <div style={{ padding: "18px 20px", borderBottom: `1px solid ${T.border}`, display: "flex", alignItems: "center", justifyContent: "space-between" }}>
              <Display size="sm">{
                uploadModal === "uploading" ? "Uploading..." :
                uploadModal === "preview" ? "Review rows" :
                uploadModal === "confirming" ? "Issuing policies..." :
                "Upload complete"
              }</Display>
              <button onClick={closeModal} style={{ background: T.surfaceTint, border: "none", borderRadius: "50%", width: 32, height: 32, display: "inline-flex", alignItems: "center", justifyContent: "center", cursor: "pointer", color: T.textSec }}><X size={16}/></button>
            </div>
            <div style={{ padding: 24, overflow: "auto", flex: 1 }}>
              {uploadModal === "uploading" && (
                <div>
                  <div style={{ display: "flex", alignItems: "center", gap: 12, marginBottom: 16 }}>
                    <FileText size={20} color={T.brand}/>
                    <div style={{ flex: 1, minWidth: 0 }}>
                      <div style={{ fontSize: 13, fontWeight: 700, fontFamily: "ui-monospace, monospace", overflow: "hidden", textOverflow: "ellipsis" }}>{filename}</div>
                      <div style={{ fontSize: 11, color: T.textSec, marginTop: 2 }}>{Math.round(progress)}% · validating rows</div>
                    </div>
                  </div>
                  <ProgressBar value={progress}/>
                  <div style={{ marginTop: 14, padding: 12, background: T.surfaceTint, borderRadius: 10, fontSize: 12, color: T.textSec, lineHeight: 1.5 }}>
                    Casa is checking BVN format, phone validity, product codes, premium calculations, and date formats.
                  </div>
                </div>
              )}

              {uploadModal === "preview" && (
                <>
                  <div style={{ display: "flex", gap: 8, marginBottom: 16, flexWrap: "wrap" }}>
                    <Pill color="green">{previewRows.filter(r => r.status === "valid").length} valid</Pill>
                    <Pill color="amber">{previewRows.filter(r => r.status === "warning").length} warnings</Pill>
                    <Pill color="red">{previewRows.filter(r => r.status === "error").length} errors</Pill>
                    <span style={{ marginLeft: "auto", fontSize: 12, color: T.textSec }}>{previewRows.length} rows</span>
                  </div>
                  <div style={{ border: `1px solid ${T.border}`, borderRadius: 10, overflow: "hidden", maxHeight: 400, overflowY: "auto" }}>
                    {previewRows.map((r, i) => (
                      <div key={i} style={{
                        display: "flex", alignItems: "center", gap: 12, padding: "10px 14px",
                        borderBottom: i < previewRows.length - 1 ? `1px solid ${T.border}` : "none",
                        background: r.status === "error" ? T.dangerBg : r.status === "warning" ? T.amberBg : T.surface,
                      }}>
                        <span style={{ fontSize: 11, fontWeight: 700, color: T.textSec, fontFamily: "ui-monospace, monospace", width: 40, flexShrink: 0 }}>{i+1}</span>
                        <div style={{ flex: 1, minWidth: 0 }}>
                          <div style={{ fontSize: 13, fontWeight: 600 }}>{r.customer_name}</div>
                          <div style={{ fontSize: 11, color: T.textSec, fontFamily: "ui-monospace, monospace" }}>{r.product_code} · ₦{r.premium_naira.toLocaleString()}/mo · ₦{r.coverage_naira.toLocaleString()}</div>
                        </div>
                        {r.status === "valid" && <Check size={15} color={T.success} strokeWidth={2.6}/>}
                        {r.status === "warning" && <AlertTriangle size={15} color={T.amber} strokeWidth={2.4}/>}
                        {r.status === "error" && <AlertCircle size={15} color={T.danger} strokeWidth={2.4}/>}
                        {r.note && <span style={{ fontSize: 11, color: T.textSec, maxWidth: 180, textAlign: "right" }}>{r.note}</span>}
                      </div>
                    ))}
                  </div>
                </>
              )}

              {uploadModal === "confirming" && (
                <div style={{ textAlign: "center", padding: "40px 0" }}>
                  <CasaAvatar size={56} state="thinking"/>
                  <Display size="sm" style={{ marginTop: 16 }}>Casa is issuing policies...</Display>
                  <div style={{ fontSize: 13, color: T.textSec, marginTop: 8 }}>This usually takes a few seconds.</div>
                </div>
              )}

              {uploadModal === "done" && (
                <div style={{ textAlign: "center", padding: "20px 0" }}>
                  <CasaAvatar size={64} state="celebrating"/>
                  <Display size="md" style={{ marginTop: 16 }}>Upload complete</Display>
                  <div style={{ fontSize: 13, color: T.textSec, marginTop: 8, lineHeight: 1.55 }}>
                    {previewRows.filter(r => r.status === "valid").length} policies issued.{" "}
                    {previewRows.filter(r => r.status !== "valid").length > 0 && `${previewRows.filter(r => r.status !== "valid").length} rows skipped due to errors.`}
                  </div>
                </div>
              )}
            </div>
            {(uploadModal === "preview" || uploadModal === "done") && (
              <div style={{ padding: "14px 20px", borderTop: `1px solid ${T.border}`, display: "flex", gap: 8, justifyContent: "flex-end" }}>
                {uploadModal === "preview" && (
                  <>
                    <Btn variant="ghost" onClick={closeModal}>Cancel</Btn>
                    <Btn variant="brand" icon={Check} onClick={confirmIssue}>Issue {previewRows.filter(r => r.status === "valid").length} policies</Btn>
                  </>
                )}
                {uploadModal === "done" && <Btn variant="primary" onClick={closeModal}>Done</Btn>}
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
}

function generateMockRows() {
  // Simulate a 12-row CSV with mostly valid, a few warnings, and some errors
  const samples = [
    { customer_name: "Ade Olamide",       product_code: "CL",  premium_naira: 4500,  coverage_naira: 5000000,   status: "valid" },
    { customer_name: "Chi-Chi Eze",        product_code: "DEV", premium_naira: 1850,  coverage_naira: 250000,    status: "valid" },
    { customer_name: "Obinna Adekunle",    product_code: "CL",  premium_naira: 2200,  coverage_naira: 1500000,   status: "valid" },
    { customer_name: "Salam Mohammed",     product_code: "FFP", premium_naira: 350,   coverage_naira: 500000,    status: "valid" },
    { customer_name: "Ifeoma Onyeka",      product_code: "DEV", premium_naira: 2100,  coverage_naira: 320000,    status: "warning", note: "BVN not in NIBSS yet — Casa will retry" },
    { customer_name: "Kunle Makinwa",      product_code: "CL",  premium_naira: 3100,  coverage_naira: 2500000,   status: "valid" },
    { customer_name: "Bose Ogundimu",      product_code: "BLD", premium_naira: 18500, coverage_naira: 25000000,  status: "valid" },
    { customer_name: "Aminu Tijani",       product_code: "DEV", premium_naira: 0,     coverage_naira: 200000,    status: "error",   note: "Premium below ₦150 floor" },
    { customer_name: "Yewande Akinfenwa",  product_code: "CL",  premium_naira: 1850,  coverage_naira: 1000000,   status: "valid" },
    { customer_name: "Tope Ogunbiyi",      product_code: "XXX", premium_naira: 1200,  coverage_naira: 500000,    status: "error",   note: "Unknown product code XXX" },
    { customer_name: "Abdulrahman Bello",  product_code: "FFP", premium_naira: 350,   coverage_naira: 500000,    status: "valid" },
    { customer_name: "Folake Adeyinka",    product_code: "CL",  premium_naira: 4500,  coverage_naira: 5000000,   status: "valid" },
  ];
  return samples;
}

// ═══════════════════════════════════════════════════════════════════════════════════
// FUNNEL STRIP — conversion funnel visualization for Quotes
// ═══════════════════════════════════════════════════════════════════════════════════
function FunnelStrip({ stages }) {
  // stages: [{ label, value, color? }]
  return (
    <div style={{ display: "grid", gridTemplateColumns: `repeat(${stages.length}, 1fr)`, gap: 8, alignItems: "stretch" }}>
      {stages.map((s, i) => {
        const prev = i > 0 ? stages[i - 1].value : null;
        const conv = prev ? Math.round((s.value / prev) * 100) : null;
        const isLast = i === stages.length - 1;
        return (
          <div key={s.label} style={{ position: "relative", display: "flex", flexDirection: "column" }}>
            <div style={{
              flex: 1, padding: "16px 14px", background: T.surface, border: `1px solid ${T.border}`, borderRadius: 14,
              display: "flex", flexDirection: "column", justifyContent: "space-between",
            }}>
              <Eyebrow color={s.color || T.textSec}>{s.label}</Eyebrow>
              <div style={{ fontFamily: FONT_DISPLAY, fontSize: 28, fontWeight: 400, letterSpacing: "-0.02em", color: s.color || T.text, marginTop: 6, fontVariantNumeric: "tabular-nums" }}>
                {s.value.toLocaleString()}
              </div>
              {conv != null && (
                <div style={{ marginTop: 8, fontSize: 11, color: T.textSec, fontWeight: 600 }}>
                  {conv}% of previous
                </div>
              )}
            </div>
            {!isLast && (
              <div style={{ position: "absolute", top: "50%", right: -8, transform: "translateY(-50%) translateX(50%)", zIndex: 1, display: "flex", alignItems: "center", justifyContent: "center", width: 16, height: 16, background: T.bg, borderRadius: "50%" }}>
                <ChevronRight size={12} color={T.textSec} strokeWidth={2.6}/>
              </div>
            )}
          </div>
        );
      })}
    </div>
  );
}

// ═══════════════════════════════════════════════════════════════════════════════════
// QUOTES — the conversion funnel between Get Insurance and Policies
// ═══════════════════════════════════════════════════════════════════════════════════
function QuotesPage({ navigate }) {
  const [q, setQ] = useState("");
  const [status, setStatus] = useState("all");
  const [stubOpen, setStubOpen] = useState(false);

  const filtered = QUOTES.filter(qt => {
    const matchQ = qt.customer.toLowerCase().includes(q.toLowerCase()) || qt.quoteRef.toLowerCase().includes(q.toLowerCase());
    const matchStatus = status === "all" || qt.status.toLowerCase() === status.toLowerCase();
    return matchQ && matchStatus;
  });

  // Derived funnel data — drawn from rolling 30-day mock totals
  const funnelStages = [
    { label: "Generated", value: 1247, color: T.brown },
    { label: "Sent", value: 982, color: T.text },
    { label: "Viewed", value: 743, color: T.text },
    { label: "Paid", value: 487, color: T.brand },
    { label: "Bound", value: 487, color: T.success },
  ];

  const statusPillColor = (s) => ({ Bound: "green", Paid: "brand", Viewed: "amber", Sent: "gray", Expired: "red" })[s] || "gray";

  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 20 }}>
      <div style={{ display: "flex", alignItems: "flex-start", justifyContent: "space-between", flexWrap: "wrap", gap: 12 }}>
        <div>
          <Display size="lg">Quotes</Display>
          <div style={{ fontSize: 14, color: T.textSec, marginTop: 6 }}>Quote-to-bind funnel and conversion analytics across all channels.</div>
        </div>
        <div style={{ display: "flex", gap: 8 }}>
          <Btn variant="outline" icon={Download}>Export</Btn>
          <Btn variant="primary" icon={Plus} onClick={() => setStubOpen(true)}>New quote</Btn>
        </div>
      </div>

      <div className="grid-2col-1col" style={{ gap: 14 }}>
        <Card title="30-day conversion funnel">
          <FunnelStrip stages={funnelStages}/>
          <div style={{ marginTop: 16, display: "flex", gap: 24, flexWrap: "wrap", fontSize: 12 }}>
            <div><span style={{ color: T.textSec }}>End-to-end conversion:</span> <strong>39.1%</strong></div>
            <div><span style={{ color: T.textSec }}>Avg time to bind:</span> <strong>2.8 days</strong></div>
            <div><span style={{ color: T.textSec }}>Biggest drop-off:</span> <strong>Sent → Viewed</strong></div>
          </div>
        </Card>
        <Card title="Status mix" right={<Pill color="gray" size="sm">All quotes</Pill>}>
          {(() => {
            const counts = QUOTES.reduce((acc, q) => {
              acc[q.status] = (acc[q.status] || 0) + 1;
              return acc;
            }, {});
            const orderedStatuses = ["Bound", "Paid", "Viewed", "Sent", "Expired"];
            const colorMap = {
              Bound:   T.success,
              Paid:    T.brand,
              Viewed:  T.amber,
              Sent:    T.brown,
              Expired: T.danger,
            };
            const data = orderedStatuses
              .filter(s => counts[s])
              .map(s => ({ label: s, value: counts[s], color: colorMap[s] }));
            return (
              <DonutChart
                size={160}
                thickness={26}
                centerValue={QUOTES.length}
                centerLabel="Quotes"
                data={data}
              />
            );
          })()}
        </Card>
      </div>

      <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(200px, 1fr))", gap: 14 }}>
        <PartnerKPICard label="Quotes this month" value="1,247" delta="142 this week" deltaDirection="up"/>
        <PartnerKPICard label="Conversion rate" value="39.1%" delta="2.4pp vs last mo" deltaDirection="up" accent={T.success}/>
        <PartnerKPICard label="Avg quote value" value="₦4,820" delta="8% vs last mo" deltaDirection="up"/>
        <PartnerKPICard label="Open quotes" value="173" helpText="awaiting customer action"/>
      </div>

      <FilterBar rightSlot={<SearchInput value={q} onChange={setQ} placeholder="Search by name or quote ref..."/>}>
        <ChipMenu label="Status" value={status === "all" ? "All" : status} options={[
          { id: "all", label: "All" },
          { id: "Sent", label: "Sent" },
          { id: "Viewed", label: "Viewed" },
          { id: "Paid", label: "Paid" },
          { id: "Bound", label: "Bound" },
          { id: "Expired", label: "Expired" },
        ]} selected={status} onSelect={setStatus}/>
        <FilterChip label="Product" value="All"/>
        <FilterChip label="Channel" value="All"/>
        {(status !== "all" || q) && (
          <button onClick={() => { setStatus("all"); setQ(""); }} style={{
            background: "transparent", border: "none", color: T.textSec,
            fontSize: 12, fontWeight: 600, cursor: "pointer", fontFamily: FONT, marginLeft: 4,
          }}>Clear all</button>
        )}
      </FilterBar>

      <DataTable
        emptyMessage={q || status !== "all" ? "No quotes match your filters." : "No quotes yet."}
        columns={[
          { key: "customer", label: "Customer", render: (v, r) => (
            <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
              <CustomerAvatar name={v} customerId={r.customerId} size={32}/>
              <div>
                <div style={{ fontWeight: 600 }}>{v}</div>
                <div style={{ fontSize: 11, color: T.textSec, fontFamily: "ui-monospace, monospace" }}>{r.quoteRef}</div>
              </div>
            </div>
          )},
          { key: "product", label: "Product", hideMobile: true, render: (v, r) => (
            <span style={{ display: "inline-flex", alignItems: "center", gap: 8 }}>
              <ProductIcon productId={PRODUCTS.find(p => p.name === v)?.id || ""} product={PRODUCTS.find(p => p.name === v)} size={18}/>
              {v}
            </span>
          )},
          { key: "channel", label: "Channel", hideMobile: true, render: v => <code style={{ fontSize: 11, color: T.textSec }}>{v}</code> },
          { key: "premium", label: "Premium", numeric: true, align: "right", render: v => <Money amount={v} size="sm"/> },
          { key: "status", label: "Status", render: v => <Pill color={statusPillColor(v)} size="sm">{v}</Pill> },
          { key: "expires", label: "Expires", hideMobile: true },
        ]}
        rows={filtered}
        onRowClick={r => navigate("quote-detail", { id: r.id })}
      />

      {stubOpen && <ComingSoonModal title="Generate a new quote" description="Select a customer (or capture a new one), pick a product, set cover amount and term. Casa runs underwriting checks and surfaces a premium offer instantly. Production wires this to the rating engine." onClose={() => setStubOpen(false)}/>}
    </div>
  );
}

function QuoteDetailPage({ navigate, params }) {
  const qt = QUOTES.find(x => x.id === params.id) || QUOTES[0];
  const [stubOpen, setStubOpen] = useState(null);

  // Build status timeline
  const statusOrder = ["Sent", "Viewed", "Paid", "Bound"];
  const idx = qt.status === "Expired" ? -1 : statusOrder.indexOf(qt.status);
  const stages = statusOrder.map((s, i) => ({
    label: s,
    status: qt.status === "Expired" ? "pending" : i < idx ? "complete" : i === idx ? "current" : "pending",
    date: i <= idx && qt.status !== "Expired" ? ["27 Apr 2026", "27 Apr 2026", "28 Apr 2026", "28 Apr 2026"][i] : null,
  }));

  const isExpired = qt.status === "Expired";
  const isBound = qt.status === "Bound";

  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 20 }}>
      <Btn variant="ghost" size="sm" icon={ChevronLeft} onClick={() => navigate("quotes")}>Back to quotes</Btn>

      <div style={{ display: "flex", alignItems: "flex-start", gap: 20, flexWrap: "wrap" }}>
        <div style={{ fontSize: 48, lineHeight: 1 }}>{qt.icon}</div>
        <div style={{ flex: 1, minWidth: 200 }}>
          <Eyebrow>{isExpired ? "EXPIRED QUOTE" : "QUOTE"} · {qt.product}</Eyebrow>
          <Display size="lg">{qt.quoteRef}</Display>
          <div style={{ fontSize: 13, color: T.textSec, marginTop: 4 }}>For {qt.customer} · via {qt.channel}</div>
        </div>
        <Pill color={({ Bound: "green", Paid: "brand", Viewed: "amber", Sent: "gray", Expired: "red" })[qt.status]}>{qt.status}</Pill>
      </div>

      {!isBound && !isExpired && (
        <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
          <Btn variant="brand" icon={Check} onClick={() => setStubOpen("bind")}>Bind policy</Btn>
          <Btn variant="outline" icon={Share2} onClick={() => setStubOpen("resend")}>Resend to customer</Btn>
          <Btn variant="outline" icon={Download} onClick={() => setStubOpen("download")}>Download PDF</Btn>
          <Btn variant="ghost" icon={X} onClick={() => setStubOpen("cancel")} style={{ color: T.dangerText, marginLeft: "auto" }}>Cancel quote</Btn>
        </div>
      )}
      {isExpired && (
        <Banner variant="amber" title="This quote has expired" description="The 14-day validity window passed without payment. Generate a new quote to re-engage this customer." action={<Btn size="sm" variant="primary" icon={Plus}>New quote for {qt.customer}</Btn>}/>
      )}
      {isBound && (
        <Banner variant="success" title="Policy bound" description={`This quote was converted into an active policy on ${qt.created}.`}/>
      )}

      <div className="grid-2col-1col">
        <div style={{ display: "flex", flexDirection: "column", gap: 16 }}>
          <Card title="Quote summary">
            <div style={{ display: "flex", alignItems: "baseline", gap: 24, marginBottom: 16, flexWrap: "wrap" }}>
              <div>
                <Eyebrow>Premium</Eyebrow>
                <div style={{ display: "flex", alignItems: "baseline", gap: 4 }}>
                  <Money amount={qt.premium} size="xl"/>
                  <span style={{ fontSize: 13, color: T.textSec }}>/month</span>
                </div>
              </div>
              <div>
                <Eyebrow>Sum assured</Eyebrow>
                <Money amount={qt.coverage} size="lg"/>
              </div>
            </div>
            <KeyValueGrid items={[
              { label: "Created", value: qt.created },
              { label: "Expires", value: qt.expires },
              { label: "Channel", value: qt.channel },
              { label: "Product", value: qt.product },
            ]}/>
          </Card>

          <Card title="Premium breakdown">
            <DisclosureRow label="Base premium" value={`₦${(qt.premium * 0.85).toLocaleString(undefined, { maximumFractionDigits: 0 })}`}/>
            <DisclosureRow label="Stamp duty" value={`₦${(qt.premium * 0.005).toLocaleString(undefined, { maximumFractionDigits: 0 })}`}/>
            <DisclosureRow label="Insurance levy" value={`₦${(qt.premium * 0.01).toLocaleString(undefined, { maximumFractionDigits: 0 })}`}/>
            <DisclosureRow label="Service charge" value={`₦${(qt.premium * 0.135).toLocaleString(undefined, { maximumFractionDigits: 0 })}`}/>
            <DisclosureRow label="Total premium" value={<strong>₦{qt.premium.toLocaleString()}/mo</strong>} ruled={false}/>
          </Card>
        </div>

        <div style={{ display: "flex", flexDirection: "column", gap: 16 }}>
          <Card title="Quote timeline">
            <ClaimTimeline stages={stages}/>
          </Card>
          <Card title="Customer">
            <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
              <Avatar name={qt.customer} size={44} gradient="pink"/>
              <div style={{ minWidth: 0, flex: 1 }}>
                <div style={{ fontSize: 14, fontWeight: 700 }}>{qt.customer}</div>
                <div style={{ fontSize: 12, color: T.textSec, overflow: "hidden", textOverflow: "ellipsis" }}>{qt.customerEmail}</div>
              </div>
            </div>
          </Card>
        </div>
      </div>

      {stubOpen && (
        <ComingSoonModal
          title={{ bind: "Bind this quote into a policy", resend: "Resend quote to customer", download: "Download quote PDF", cancel: "Cancel this quote" }[stubOpen]}
          description={{
            bind: `Convert ${qt.quoteRef} into an active policy for ${qt.customer}. Production runs final underwriting checks, captures payment, and issues the certificate.`,
            resend: `Re-deliver the quote to ${qt.customer} via email and WhatsApp.`,
            download: `Generate a NAICOM-compliant PDF of the quote, including the premium breakdown.`,
            cancel: `Cancel ${qt.quoteRef}. The customer is notified and the funnel records the cancellation reason.`,
          }[stubOpen]}
          onClose={() => setStubOpen(null)}
        />
      )}
    </div>
  );
}

// ═══════════════════════════════════════════════════════════════════════════════════
// TEAM & ACCESS
// ═══════════════════════════════════════════════════════════════════════════════════
function TeamPage() {
  const [inviteOpen, setInviteOpen] = useState(false);

  const roleColor = (r) => ({ Admin: "brand", Underwriter: "green", "Claims handler": "amber", Finance: "dark", Viewer: "gray" })[r] || "gray";

  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 20 }}>
      <div style={{ display: "flex", alignItems: "flex-start", justifyContent: "space-between", flexWrap: "wrap", gap: 12 }}>
        <div>
          <Display size="lg">Team & access</Display>
          <div style={{ fontSize: 14, color: T.textSec, marginTop: 6 }}>Manage who has access to your Casava partner account and what they can do.</div>
        </div>
        <Btn variant="primary" icon={Plus} onClick={() => setInviteOpen(true)}>Invite member</Btn>
      </div>

      <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(180px, 1fr))", gap: 14 }}>
        <StatBlock label="Team members" value={6} delay={0}/>
        <StatBlock label="Active" value={5} delay={60} accent={T.success}/>
        <StatBlock label="Pending invites" value={1} delay={120} accent={T.amber}/>
      </div>

      <Card title="Members">
        <DataTable
          columns={[
            { key: "name", label: "Member", render: (v, r) => (
              <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
                <Avatar name={r.status === "Pending" ? "?" : v} size={32} gradient={r.status === "Pending" ? "yellow" : "brown"}/>
                <div>
                  <div style={{ fontWeight: 600, color: r.status === "Pending" ? T.textSec : T.text }}>{r.status === "Pending" ? r.email : v}</div>
                  <div style={{ fontSize: 11, color: T.textSec }}>{r.status === "Pending" ? r.joined : r.email}</div>
                </div>
              </div>
            )},
            { key: "role", label: "Role", render: v => <Pill color={roleColor(v)} size="sm">{v}</Pill> },
            { key: "lastActive", label: "Last active", hideMobile: true, render: (v, r) => (
              <span style={{ color: r.lastActive === "Active now" ? T.success : T.textSec, fontWeight: r.lastActive === "Active now" ? 600 : 500 }}>{v}</span>
            )},
            { key: "status", label: "Status", render: v => <Pill color={v === "Active" ? "green" : "amber"} size="sm">{v}</Pill> },
            { key: "actions", label: "", sortable: false, align: "right", render: (_, r) => (
              <button style={{ background: "transparent", border: "none", padding: 6, cursor: "pointer", color: T.textSec, borderRadius: 6 }}>
                <MoreHorizontal size={16}/>
              </button>
            )},
          ]}
          rows={TEAM_MEMBERS}
        />
      </Card>

      <Card title="Roles & permissions">
        <div style={{ overflowX: "auto" }}>
          <table style={{ width: "100%", borderCollapse: "collapse", fontFamily: FONT, minWidth: 560 }}>
            <thead>
              <tr style={{ borderBottom: `1px solid ${T.border}` }}>
                <th style={{ padding: "10px 12px", textAlign: "left", fontSize: 11, fontWeight: 700, color: T.textSec, letterSpacing: "0.06em", textTransform: "uppercase" }}>Capability</th>
                {["Admin", "Underwriter", "Claims handler", "Finance", "Viewer"].map(r => (
                  <th key={r} style={{ padding: "10px 8px", textAlign: "center", fontSize: 10, fontWeight: 700, color: T.textSec, letterSpacing: "0.04em", textTransform: "uppercase" }}>{r}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {[
                { cap: "View dashboard & reports",    perms: [true, true, true, true, true] },
                { cap: "Create & edit customers",     perms: [true, true, false, false, false] },
                { cap: "Issue policies",              perms: [true, true, false, false, false] },
                { cap: "Approve/reject claims",       perms: [true, false, true, false, false] },
                { cap: "Access settlement & wallet",  perms: [true, false, false, true, false] },
                { cap: "Manage API credentials",      perms: [true, false, false, false, false] },
                { cap: "Invite & remove members",     perms: [true, false, false, false, false] },
              ].map((row, i) => (
                <tr key={i} style={{ borderBottom: `1px solid ${T.border}` }}>
                  <td style={{ padding: "10px 12px", fontSize: 13, color: T.text, fontWeight: 500 }}>{row.cap}</td>
                  {row.perms.map((p, j) => (
                    <td key={j} style={{ padding: "10px 8px", textAlign: "center" }}>
                      {p ? <Check size={16} color={T.success} strokeWidth={2.6}/> : <span style={{ color: T.textTer }}>—</span>}
                    </td>
                  ))}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </Card>

      {inviteOpen && <ComingSoonModal title="Invite a team member" description="Enter their email, pick a role, and Casa sends a magic-link invite. Production captures the invite acceptance, runs SSO if your domain is enrolled, and logs the addition to the audit trail." onClose={() => setInviteOpen(false)}/>}
    </div>
  );
}

// ═══════════════════════════════════════════════════════════════════════════════════
// ACCOUNT
// ═══════════════════════════════════════════════════════════════════════════════════
function AccountPage({ partner, setPartner }) {
  const [stubOpen, setStubOpen] = useState(null);
  const [editing, setEditing] = useState(null); // null | "org" | "contact" | "brand"
  const [draft, setDraft] = useState(null);
  const fileInputRef = useRef(null);

  const startEdit = (section) => {
    setDraft({ ...partner });
    setEditing(section);
  };
  const saveEdit = () => {
    setPartner(draft);
    setEditing(null);
    setDraft(null);
  };
  const cancelEdit = () => {
    setEditing(null);
    setDraft(null);
  };

  const handleLogoUpload = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    const reader = new FileReader();
    reader.onload = (ev) => {
      setPartner({ ...partner, logoUrl: ev.target.result });
    };
    reader.readAsDataURL(file);
  };

  const removeLogo = () => setPartner({ ...partner, logoUrl: null });

  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 20 }}>
      <div>
        <Display size="lg">Account</Display>
        <div style={{ fontSize: 14, color: T.textSec, marginTop: 6 }}>Your organization details, logo, brand, and commercial terms with Casava.</div>
      </div>

      <div className="grid-2col-1col">
        <div style={{ display: "flex", flexDirection: "column", gap: 16 }}>

          {/* Organization identity */}
          <Card title="Organization" right={editing === "org"
            ? <div style={{ display: "flex", gap: 6 }}><Btn variant="ghost" size="sm" onClick={cancelEdit}>Cancel</Btn><Btn variant="primary" size="sm" icon={Check} onClick={saveEdit}>Save</Btn></div>
            : <Btn variant="ghost" size="sm" onClick={() => startEdit("org")}>Edit</Btn>}>
            <div style={{ display: "flex", alignItems: "center", gap: 16, marginBottom: 18, paddingBottom: 18, borderBottom: `1px solid ${T.border}` }}>
              <PartnerLogo name={partner.name} color={partner.color} logoUrl={partner.logoUrl} size={56}/>
              <div style={{ flex: 1, minWidth: 0 }}>
                <div style={{ fontSize: 18, fontWeight: 700 }}>{partner.legalName}</div>
                <div style={{ fontSize: 13, color: T.textSec, marginTop: 2 }}>{partner.partnerType} · {partner.industry}</div>
              </div>
            </div>
            {editing === "org" ? (
              <div style={{ display: "flex", flexDirection: "column", gap: 12 }}>
                <Field label="Display name"><input type="text" value={draft.name} onChange={e => setDraft({ ...draft, name: e.target.value })} style={inputStyle}/></Field>
                <Field label="Legal name"><input type="text" value={draft.legalName} onChange={e => setDraft({ ...draft, legalName: e.target.value })} style={inputStyle}/></Field>
                <div className="grid-2col-fields">
                  <Field label="RC number"><input type="text" value={draft.rcNumber} onChange={e => setDraft({ ...draft, rcNumber: e.target.value })} style={inputStyle}/></Field>
                  <Field label="TIN"><input type="text" value={draft.tin} onChange={e => setDraft({ ...draft, tin: e.target.value })} style={inputStyle}/></Field>
                </div>
                <div className="grid-2col-fields">
                  <Field label="Partner type"><input type="text" value={draft.partnerType} onChange={e => setDraft({ ...draft, partnerType: e.target.value })} style={inputStyle}/></Field>
                  <Field label="Industry"><input type="text" value={draft.industry} onChange={e => setDraft({ ...draft, industry: e.target.value })} style={inputStyle}/></Field>
                </div>
              </div>
            ) : (
              <KeyValueGrid items={[
                { label: "Display name", value: partner.name },
                { label: "Legal name", value: partner.legalName },
                { label: "Partner type", value: partner.partnerType },
                { label: "Industry", value: partner.industry },
                { label: "RC number", value: partner.rcNumber, mono: true },
                { label: "TIN", value: partner.tin, mono: true },
                { label: "Established", value: partner.established },
                { label: "Time zone", value: "Africa/Lagos" },
              ]}/>
            )}
          </Card>

          {/* Brand & logo — editable */}
          <Card title="Brand & logo" right={<Btn variant="ghost" size="sm" onClick={() => startEdit("brand")}>{editing === "brand" ? null : "Edit"}</Btn>}>
            <div style={{ display: "flex", alignItems: "center", gap: 16, marginBottom: 16 }}>
              <PartnerLogo name={partner.name} color={partner.color} logoUrl={partner.logoUrl} size={64}/>
              <div style={{ flex: 1, minWidth: 0 }}>
                <Eyebrow mb={4}>Logo</Eyebrow>
                <div style={{ fontSize: 12, color: T.textSec, marginBottom: 10 }}>{partner.logoUrl ? "Custom logo uploaded" : "Using monogram fallback"} · appears in the top-left of the menu</div>
                <div style={{ display: "flex", gap: 6, flexWrap: "wrap" }}>
                  <input ref={fileInputRef} type="file" accept="image/*" onChange={handleLogoUpload} style={{ display: "none" }}/>
                  <Btn variant="outline" size="sm" icon={Upload} onClick={() => fileInputRef.current?.click()}>{partner.logoUrl ? "Replace" : "Upload"} logo</Btn>
                  {partner.logoUrl && <Btn variant="ghost" size="sm" onClick={removeLogo} style={{ color: T.dangerText }}>Remove</Btn>}
                </div>
                <div style={{ fontSize: 11, color: T.textSec, marginTop: 6 }}>Recommended: square PNG, transparent, ≥ 256×256.</div>
              </div>
            </div>
            <div style={{ paddingTop: 14, borderTop: `1px solid ${T.border}` }}>
              <Eyebrow mb={6}>Brand color</Eyebrow>
              <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
                <input type="color" value={partner.color} onChange={e => setPartner({ ...partner, color: e.target.value })} style={{ width: 36, height: 36, padding: 0, border: `1px solid ${T.border}`, borderRadius: 8, cursor: "pointer", background: "transparent" }}/>
                <code style={{ fontSize: 12, color: T.text, fontFamily: "ui-monospace, monospace", textTransform: "uppercase" }}>{partner.color}</code>
                <span style={{ fontSize: 11, color: T.textSec }}>Used as fallback monogram when no logo is uploaded</span>
              </div>
            </div>
          </Card>

          {/* Contact */}
          <Card title="Contact" right={editing === "contact"
            ? <div style={{ display: "flex", gap: 6 }}><Btn variant="ghost" size="sm" onClick={cancelEdit}>Cancel</Btn><Btn variant="primary" size="sm" icon={Check} onClick={saveEdit}>Save</Btn></div>
            : <Btn variant="ghost" size="sm" onClick={() => startEdit("contact")}>Edit</Btn>}>
            {editing === "contact" ? (
              <div style={{ display: "flex", flexDirection: "column", gap: 12 }}>
                <Field label="Primary contact"><input type="text" value={draft.primaryContact} onChange={e => setDraft({ ...draft, primaryContact: e.target.value })} style={inputStyle}/></Field>
                <div className="grid-2col-fields">
                  <Field label="Email"><input type="email" value={draft.primaryEmail} onChange={e => setDraft({ ...draft, primaryEmail: e.target.value })} style={inputStyle}/></Field>
                  <Field label="Phone"><input type="tel" value={draft.primaryPhone} onChange={e => setDraft({ ...draft, primaryPhone: e.target.value })} style={inputStyle}/></Field>
                </div>
                <Field label="Registered address"><input type="text" value={draft.registeredAddress} onChange={e => setDraft({ ...draft, registeredAddress: e.target.value })} style={inputStyle}/></Field>
              </div>
            ) : (
              <KeyValueGrid items={[
                { label: "Primary contact", value: partner.primaryContact },
                { label: "Email", value: partner.primaryEmail },
                { label: "Phone", value: partner.primaryPhone },
                { label: "Registered address", value: partner.registeredAddress },
              ]}/>
            )}
          </Card>
        </div>

        <div style={{ display: "flex", flexDirection: "column", gap: 16 }}>
          {/* Commercial terms with Casava */}
          <Card title="Commercial terms" right={<Pill color="green" size="sm">Active</Pill>}>
            <KeyValueGrid columns={1} items={[
              { label: "Contract start", value: partner.contractStart },
              { label: "Renewal", value: partner.contractRenewal },
              { label: "Commission rate", value: `${partner.commissionRate}% on premium` },
              { label: "Monthly volume cap", value: `${partner.monthlyVolumeCap.toLocaleString()} policies` },
            ]}/>
            <div style={{ marginTop: 14, paddingTop: 14, borderTop: `1px solid ${T.border}` }}>
              <Eyebrow mb={8}>Products you can sell</Eyebrow>
              <div style={{ display: "flex", flexWrap: "wrap", gap: 6 }}>
                {partner.productsEnabled.map(p => (
                  <Pill key={p} color="brand" size="sm">{p}</Pill>
                ))}
              </div>
              <Btn variant="ghost" size="sm" onClick={() => setStubOpen("addproduct")} style={{ marginTop: 10 }}>+ Request another product</Btn>
            </div>
          </Card>

          {/* Settlement bank — partner's bank, not Casava's */}
          <Card title="Settlement bank" right={<Btn variant="ghost" size="sm" onClick={() => setStubOpen("bank")}>Change</Btn>}>
            <div style={{ padding: 16, background: T.surfaceTint, borderRadius: 14, display: "flex", alignItems: "center", gap: 14 }}>
              <div style={{ width: 44, height: 44, borderRadius: 10, background: T.brown, color: "#fff", display: "inline-flex", alignItems: "center", justifyContent: "center", flexShrink: 0 }}>
                <Building2 size={20}/>
              </div>
              <div style={{ flex: 1, minWidth: 0 }}>
                <div style={{ fontSize: 14, fontWeight: 700 }}>Moniepoint MFB</div>
                <div style={{ fontSize: 12, color: T.textSec, fontFamily: "ui-monospace, monospace", marginTop: 2 }}>•••• •••• •••• 8412 · {partner.legalName}</div>
              </div>
              <Pill color="green" size="sm">Verified</Pill>
            </div>
            <div style={{ fontSize: 11, color: T.textSec, marginTop: 10, lineHeight: 1.55 }}>Casava settles your share (commission + premium float) into this account on the 2nd of each month.</div>
          </Card>

          {/* Underwriter — Casava */}
          <Card title="Underwritten by">
            <div style={{ display: "flex", alignItems: "center", gap: 14 }}>
              <CMarkBadge surface="brown" size={48}/>
              <div style={{ flex: 1, minWidth: 0 }}>
                <div style={{ fontSize: 14, fontWeight: 700 }}>Casava Microinsurance Ltd.</div>
                <div style={{ fontSize: 11, color: T.textSec, marginTop: 2 }}>NAICOM-licensed microinsurer · NIIRA 2025</div>
              </div>
            </div>
            <div style={{ marginTop: 14 }}>
              <AuthorityLockup authorities={["naicom", "cac", "ndpr"]} label={null} layout="horizontal"/>
            </div>
            <div style={{ fontSize: 11, color: T.textSec, marginTop: 12, lineHeight: 1.55 }}>All policies sold through Bloom are underwritten by Casava under NAICOM license MIC/2024/018.</div>
          </Card>

          {/* Plan */}
          <Card title="Plan">
            <Eyebrow>Current plan</Eyebrow>
            <div style={{ fontFamily: FONT_DISPLAY, fontSize: 24, marginTop: 4 }}>Embedded · Tier 1</div>
            <div style={{ fontSize: 12, color: T.textSec, marginTop: 4 }}>Volume-based commission</div>
            <Btn variant="outline" size="sm" onClick={() => setStubOpen("plan")} style={{ marginTop: 14 }} full>Talk to your account manager</Btn>
          </Card>
        </div>
      </div>

      {stubOpen && (
        <ComingSoonModal
          title={{ bank: "Change settlement bank", plan: "Talk to account manager", addproduct: "Request a new product" }[stubOpen]}
          description={{
            bank: "Add a new bank account for settlements. Production runs micro-deposit verification and pulls account name from NIBSS.",
            plan: "Your Casava account manager Bunmi Adeyemi will reach out within one business day to discuss volume tiers and commission terms.",
            addproduct: "Tell Casava which product you'd like to sell. Each new product requires regulatory review and a contract addendum (typically 5-10 business days).",
          }[stubOpen]}
          onClose={() => setStubOpen(null)}
        />
      )}
    </div>
  );
}

// ═══════════════════════════════════════════════════════════════════════════════════
// STATEMENTS
// ═══════════════════════════════════════════════════════════════════════════════════
function StatementsPage() {
  const [year, setYear] = useState(2026);
  const [stubOpen, setStubOpen] = useState(null);

  const filtered = STATEMENTS.filter(s => s.year === year);
  const ytdGross = filtered.reduce((sum, s) => sum + s.gross, 0);
  const ytdNet = filtered.reduce((sum, s) => sum + s.net, 0);
  const ytdPolicies = filtered.reduce((sum, s) => sum + s.policies, 0);

  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 20 }}>
      <div style={{ display: "flex", alignItems: "flex-start", justifyContent: "space-between", flexWrap: "wrap", gap: 12 }}>
        <div>
          <Display size="lg">Statements</Display>
          <div style={{ fontSize: 14, color: T.textSec, marginTop: 6 }}>Monthly statements with gross volume, fees, and net settlements. Audit-grade PDFs.</div>
        </div>
        <ChipMenu label="Year" value={String(year)} options={[
          { id: 2026, label: "2026" },
          { id: 2025, label: "2025" },
        ]} selected={year} onSelect={setYear}/>
      </div>

      <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(200px, 1fr))", gap: 14 }}>
        <PartnerKPICard label={`${year} gross`} value={`₦${(ytdGross/1_000_000).toFixed(2)}M`} helpText={`across ${filtered.length} months`}/>
        <PartnerKPICard label={`${year} net`} value={`₦${(ytdNet/1_000_000).toFixed(2)}M`} accent={T.success}/>
        <PartnerKPICard label={`${year} policies`} value={ytdPolicies.toLocaleString()}/>
        <PartnerKPICard label="Avg net per month" value={`₦${(ytdNet/filtered.length/1_000_000).toFixed(2)}M`}/>
      </div>

      <Card title="Monthly statements">
        <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fill, minmax(280px, 1fr))", gap: 12 }}>
          {filtered.length === 0 ? (
            <div style={{ gridColumn: "1 / -1", padding: 40, textAlign: "center", color: T.textSec }}>No statements for {year}.</div>
          ) : filtered.map(s => (
            <div key={s.id} onClick={() => setStubOpen(s)} style={{
              padding: 18, background: T.surface, border: `1px solid ${T.border}`, borderRadius: 14,
              cursor: "pointer", transition: "transform 0.15s, box-shadow 0.15s",
            }}
              onMouseEnter={e => (e.currentTarget.style.transform = "translateY(-2px)", e.currentTarget.style.boxShadow = "0 8px 20px rgba(0,0,0,0.05)")}
              onMouseLeave={e => (e.currentTarget.style.transform = "none", e.currentTarget.style.boxShadow = "none")}>
              <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 12 }}>
                <Eyebrow color={T.brand}>{s.month} {s.year}</Eyebrow>
                <FileText size={16} color={T.textSec}/>
              </div>
              <Money amount={s.net} size="lg"/>
              <div style={{ fontSize: 11, color: T.textSec, marginTop: 6 }}>Net to you · {s.policies} policies</div>
              <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginTop: 14, paddingTop: 12, borderTop: `1px solid ${T.border}`, fontSize: 11 }}>
                <span style={{ color: T.textSec }}>Generated {s.generated}</span>
                <span style={{ color: T.brand, fontWeight: 600, display: "inline-flex", alignItems: "center", gap: 3 }}>
                  Download <Download size={11} strokeWidth={2.4}/>
                </span>
              </div>
            </div>
          ))}
        </div>
      </Card>

      {stubOpen && (
        <ComingSoonModal
          title={`${stubOpen.month} ${stubOpen.year} statement`}
          description={`Download a NAICOM-compliant PDF showing all transactions, fees, and the net ₦${stubOpen.net.toLocaleString()} settlement. Includes a CSV companion file for your accounting system.`}
          onClose={() => setStubOpen(null)}
        />
      )}
    </div>
  );
}

// ═══════════════════════════════════════════════════════════════════════════════════
// NOTIFICATIONS
// ═══════════════════════════════════════════════════════════════════════════════════
function NotificationsPage() {
  const channels = ["In-app", "Email", "WhatsApp", "SMS"];
  const events = [
    { id: "policy_created", label: "New policy purchased", desc: "When a customer binds a quote into an active policy", defaults: [true, true, false, false] },
    { id: "claim_submitted", label: "New claim submitted", desc: "When a customer files a claim against any of your policies", defaults: [true, true, true, false] },
    { id: "claim_approved", label: "Claim approved", desc: "When underwriting clears a claim for payout", defaults: [true, true, true, true] },
    { id: "webhook_failed", label: "Webhook delivery failed", desc: "When a partner endpoint returns an error after retries", defaults: [true, true, false, false] },
    { id: "settlement_ready", label: "Settlement ready", desc: "When a monthly settlement is calculated and queued", defaults: [true, true, false, false] },
    { id: "premium_received", label: "Premium received", desc: "Each time a premium payment lands in the wallet", defaults: [true, false, false, false] },
    { id: "cover_expiring", label: "Cover expiring", desc: "30 days before a policy is due to expire", defaults: [true, true, false, false] },
    { id: "casa_alert", label: "Casa flagged opportunity", desc: "When the AI surfaces an upsell or anomaly", defaults: [true, false, false, false] },
  ];
  const [prefs, setPrefs] = useState(() => Object.fromEntries(events.map(e => [e.id, [...e.defaults]])));

  const toggle = (eventId, channelIdx) => {
    setPrefs(p => ({ ...p, [eventId]: p[eventId].map((v, i) => i === channelIdx ? !v : v) }));
  };

  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 20 }}>
      <div>
        <Display size="lg">Notifications</Display>
        <div style={{ fontSize: 14, color: T.textSec, marginTop: 6 }}>Pick how you want to be notified for each platform event. Changes save automatically.</div>
      </div>

      <Banner variant="info" title="WhatsApp Business is your default for customer-facing alerts" description="In Lagos, WhatsApp delivery has 3.2× higher open rate than SMS. Casava uses your verified WhatsApp Business number for customer notifications by default."/>

      <Card title="Preferences">
        <div style={{ overflowX: "auto" }}>
          <table style={{ width: "100%", borderCollapse: "collapse", fontFamily: FONT, minWidth: 560 }}>
            <thead>
              <tr style={{ borderBottom: `1px solid ${T.border}` }}>
                <th style={{ padding: "12px", textAlign: "left", fontSize: 11, fontWeight: 700, color: T.textSec, letterSpacing: "0.06em", textTransform: "uppercase" }}>Event</th>
                {channels.map(c => (
                  <th key={c} style={{ padding: "12px 8px", textAlign: "center", fontSize: 10, fontWeight: 700, color: T.textSec, letterSpacing: "0.04em", textTransform: "uppercase", width: 80 }}>{c}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {events.map((e, i) => (
                <tr key={e.id} style={{ borderBottom: i === events.length - 1 ? "none" : `1px solid ${T.border}` }}>
                  <td style={{ padding: "14px 12px" }}>
                    <div style={{ fontSize: 13, fontWeight: 600, color: T.text }}>{e.label}</div>
                    <div style={{ fontSize: 11, color: T.textSec, marginTop: 2 }}>{e.desc}</div>
                  </td>
                  {channels.map((c, ci) => (
                    <td key={ci} style={{ padding: "14px 8px", textAlign: "center" }}>
                      <Toggle on={prefs[e.id][ci]} onChange={() => toggle(e.id, ci)}/>
                    </td>
                  ))}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </Card>
    </div>
  );
}

function Toggle({ on, onChange }) {
  return (
    <button onClick={onChange} role="switch" aria-checked={on} style={{
      width: 36, height: 20, borderRadius: 999, padding: 2,
      background: on ? T.brand : T.borderStrong, border: "none", cursor: "pointer",
      display: "inline-flex", alignItems: "center", transition: "background 0.18s",
    }}>
      <span style={{
        width: 16, height: 16, borderRadius: "50%", background: "#fff",
        transform: on ? "translateX(16px)" : "translateX(0)",
        transition: "transform 0.18s",
        boxShadow: "0 1px 3px rgba(0,0,0,0.18)",
      }}/>
    </button>
  );
}

// ═══════════════════════════════════════════════════════════════════════════════════
// SECURITY
// ═══════════════════════════════════════════════════════════════════════════════════
function SecurityPage({ navigate }) {
  const [twoFA, setTwoFA] = useState(true);
  const [stubOpen, setStubOpen] = useState(null);

  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 20 }}>
      <div>
        <Display size="lg">Security</Display>
        <div style={{ fontSize: 14, color: T.textSec, marginTop: 6 }}>Two-factor authentication, session management, and login activity.</div>
      </div>

      <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(280px, 1fr))", gap: 14 }}>
        <Card>
          <div style={{ display: "flex", alignItems: "center", gap: 14, marginBottom: 14 }}>
            <div style={{ width: 44, height: 44, borderRadius: 10, background: twoFA ? T.successBg : T.surfaceTint, color: twoFA ? T.success : T.textSec, display: "inline-flex", alignItems: "center", justifyContent: "center" }}>
              <ShieldCheck size={22} strokeWidth={2.2}/>
            </div>
            <div style={{ flex: 1 }}>
              <div style={{ fontSize: 14, fontWeight: 700 }}>Two-factor authentication</div>
              <div style={{ fontSize: 12, color: T.textSec, marginTop: 2 }}>{twoFA ? "Enabled · Authenticator app" : "Disabled · we strongly recommend enabling"}</div>
            </div>
            <Toggle on={twoFA} onChange={() => setTwoFA(!twoFA)}/>
          </div>
          {twoFA && (
            <Btn variant="outline" size="sm" onClick={() => setStubOpen("regen")} full>Regenerate recovery codes</Btn>
          )}
        </Card>

        <Card>
          <div style={{ display: "flex", alignItems: "center", gap: 14, marginBottom: 14 }}>
            <div style={{ width: 44, height: 44, borderRadius: 10, background: T.surfaceTint, color: T.text, display: "inline-flex", alignItems: "center", justifyContent: "center" }}>
              <Lock size={22} strokeWidth={2.2}/>
            </div>
            <div style={{ flex: 1 }}>
              <div style={{ fontSize: 14, fontWeight: 700 }}>Password</div>
              <div style={{ fontSize: 12, color: T.textSec, marginTop: 2 }}>Last changed 26 Apr 2026</div>
            </div>
          </div>
          <Btn variant="outline" size="sm" onClick={() => setStubOpen("password")} full>Change password</Btn>
        </Card>

        <Card>
          <div style={{ display: "flex", alignItems: "center", gap: 14, marginBottom: 14 }}>
            <div style={{ width: 44, height: 44, borderRadius: 10, background: T.brandBg, color: T.brand, display: "inline-flex", alignItems: "center", justifyContent: "center" }}>
              <FileText size={22} strokeWidth={2.2}/>
            </div>
            <div style={{ flex: 1 }}>
              <div style={{ fontSize: 14, fontWeight: 700 }}>Audit log</div>
              <div style={{ fontSize: 12, color: T.textSec, marginTop: 2 }}>NDPR-compliant action history</div>
            </div>
          </div>
          <Btn variant="outline" size="sm" onClick={() => navigate("audit-log")} full>Open audit log</Btn>
        </Card>
      </div>

      <Card title="Active sessions" right={<Btn variant="ghost" size="sm" onClick={() => setStubOpen("revoke")}>Revoke all others</Btn>}>
        <DataTable
          columns={[
            { key: "device", label: "Device", render: (v, r) => (
              <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
                <Smartphone size={16} color={T.textSec} strokeWidth={2.2}/>
                <div>
                  <div style={{ fontWeight: 600 }}>{v}</div>
                  <div style={{ fontSize: 11, color: T.textSec, fontFamily: "ui-monospace, monospace" }}>{r.ip}</div>
                </div>
              </div>
            )},
            { key: "location", label: "Location", hideMobile: true },
            { key: "lastActive", label: "Last active", render: (v, r) => (
              <span style={{ color: r.current ? T.success : T.text, fontWeight: r.current ? 600 : 500 }}>{v}</span>
            )},
            { key: "current", label: "", sortable: false, align: "right", render: (v) => v ? <Pill color="green" size="sm">This device</Pill> : (
              <Btn variant="ghost" size="sm" onClick={() => setStubOpen("revoke-one")} style={{ color: T.dangerText }}>Revoke</Btn>
            )},
          ]}
          rows={SESSIONS}
        />
      </Card>

      <Card title="Recent login activity">
        <DataTable
          columns={[
            { key: "event", label: "Event", render: (v, r) => (
              <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
                {r.outcome === "success"
                  ? <Check size={14} color={T.success} strokeWidth={2.6}/>
                  : <AlertTriangle size={14} color={T.danger} strokeWidth={2.4}/>}
                <span style={{ fontWeight: 600 }}>{v}</span>
              </div>
            )},
            { key: "device", label: "Device", hideMobile: true },
            { key: "location", label: "Location", hideMobile: true },
            { key: "ip", label: "IP", hideMobile: true, render: v => <code style={{ fontSize: 11, color: T.textSec }}>{v}</code> },
            { key: "time", label: "Time" },
          ]}
          rows={LOGIN_ACTIVITY}
        />
      </Card>

      {stubOpen && (
        <ComingSoonModal
          title={{ regen: "Regenerate recovery codes", password: "Change password", audit: "Open audit log", revoke: "Revoke all other sessions", "revoke-one": "Revoke this session" }[stubOpen]}
          description={{
            regen: "Issue 10 new one-time recovery codes. Old codes are invalidated. Save them somewhere safe — they're shown only once.",
            password: "Update your password. Production enforces minimum complexity, prevents password reuse, and re-verifies your existing password.",
            audit: "View the full NDPR-compliant audit trail: who did what, when, and from which device. Exportable as CSV for compliance reviews.",
            revoke: "Sign out every device except this one. Affected users will need to sign in again.",
            "revoke-one": "End this specific session. The user is signed out immediately.",
          }[stubOpen]}
          onClose={() => setStubOpen(null)}
        />
      )}
    </div>
  );
}

// ═══════════════════════════════════════════════════════════════════════════════════
// LOGIN HERO CAROUSEL — auto-rotating brand/marketing/proof-point slides on the
// login right pane. Replaces the static brown panel. Slides intentionally ordered:
// trust → performance → urgency → news → proof. Respects prefers-reduced-motion.
// ═══════════════════════════════════════════════════════════════════════════════════
const LOGIN_SLIDES = [
  {
    id: "trust",
    eyebrow: "TRUSTED PARTNER",
    headline: "Underwritten by Casava — NAICOM-licensed and NIIRA 2025 compliant.",
    body: "Issue policies, file claims, manage settlements, and grow your portfolio from a single workspace.",
    photo: "https://images.unsplash.com/photo-1573497019940-1c28c88b4f3e?w=1400&auto=format&fit=crop&q=80",
    photoAlt: "Lagos professional",
    casaState: "winking",
    stats: [
      { value: "1,284",   label: "Active policies" },
      { value: "₦42.1M",  label: "Cover in force" },
      { value: "14%",     label: "Claims ratio" },
    ],
  },
  {
    id: "claims",
    eyebrow: "CLAIMS PERFORMANCE",
    headline: "₦184M paid out across 312 approved claims last quarter.",
    body: "94% approval rate. Average 5-day settlement on credit life. We pay fast — your customers stay loyal.",
    photo: "https://images.unsplash.com/photo-1556742502-ec7c0e9f34b1?w=1400&auto=format&fit=crop&q=80",
    photoAlt: "SME owner in Lagos market",
    casaState: "happy",
    stats: [
      { value: "94%",     label: "Approval rate" },
      { value: "5 days",  label: "Avg settle time" },
      { value: "312",     label: "Claims paid Q1" },
    ],
  },
  {
    id: "lasbca",
    eyebrow: "REGULATORY UPDATE",
    headline: "Lagos State LASBCA mandate goes live July 2026.",
    body: "Help your property customers stay compliant before ₦5M fines start. Building Insurance is live and pre-approved by LASBCA.",
    photo: "https://images.unsplash.com/photo-1531973576160-7125cd663d86?w=1400&auto=format&fit=crop&q=80",
    photoAlt: "Lagos high-rise property",
    casaState: "thinking",
    stats: null,
  },
  {
    id: "funds",
    eyebrow: "PRODUCT NEWS",
    headline: "Funds Protection — now live for embedded partners.",
    body: "₦150/month wallet protection at account opening. 34% attach rate. The fastest-growing micro-cover in Nigeria.",
    photo: "https://images.unsplash.com/photo-1563013544-824ae1b704d3?w=1400&auto=format&fit=crop&q=80",
    photoAlt: "Mobile banking and digital payments",
    casaState: "celebrating",
    stats: [
      { value: "34%",     label: "Attach rate" },
      { value: "47K+",    label: "Subscribers" },
      { value: "20%",     label: "Commission" },
    ],
  },
  {
    id: "proof",
    eyebrow: "PARTNER PROOF",
    headline: "Embedded distribution delivered ₦4.2B in covered loans this year.",
    body: "Banking partners drive 70% of our book. Auto-attach via API at disbursement. One integration, one bundle, recurring premium.",
    photo: "https://images.unsplash.com/photo-1551836022-deb4988cc6c0?w=1400&auto=format&fit=crop&q=80",
    photoAlt: "Banking partner workspace",
    casaState: "happy",
    stats: [
      { value: "₦4.2B",   label: "Loans covered" },
      { value: "70%",     label: "From banking" },
      { value: "78%",     label: "Avg attach" },
    ],
  },
];

function LoginHeroCarousel({ partner }) {
  const [active, setActive] = useState(0);
  const [paused, setPaused] = useState(false);
  const { eyeOffset, blinking, ref: casaRef } = useCasaAnimation({ trackCursor: true, autoBlink: true });

  // Detect prefers-reduced-motion
  const reduceMotion = typeof window !== "undefined" && typeof window.matchMedia === "function"
    ? window.matchMedia("(prefers-reduced-motion: reduce)").matches
    : false;

  // Auto-rotate. Pauses when hovered, when tab hidden, or when reduce-motion is on.
  useEffect(() => {
    if (paused || reduceMotion) return;
    const id = setInterval(() => {
      if (typeof document !== "undefined" && document.hidden) return;
      setActive(i => (i + 1) % LOGIN_SLIDES.length);
    }, 6500);
    return () => clearInterval(id);
  }, [paused, reduceMotion]);

  const slide = LOGIN_SLIDES[active];

  return (
    <div
      className="login-hero"
      onMouseEnter={() => setPaused(true)}
      onMouseLeave={() => setPaused(false)}
      onFocus={() => setPaused(true)}
      onBlur={() => setPaused(false)}
      style={{
        color: "#fff", padding: 48, display: "flex", flexDirection: "column", justifyContent: "space-between",
        position: "relative", overflow: "hidden", minHeight: 520,
        backgroundColor: T.brown,
      }}
    >
      {/* Photo layers — cross-fade between slides. Always render all so transitions are seamless. */}
      {LOGIN_SLIDES.map((s, i) => (
        <div key={s.id} aria-hidden={i !== active} style={{
          position: "absolute", inset: 0,
          backgroundImage: `linear-gradient(180deg, rgba(31, 18, 14, 0.55) 0%, rgba(54, 41, 26, 0.82) 100%), url('${s.photo}')`,
          backgroundSize: "cover", backgroundPosition: "center",
          opacity: i === active ? 1 : 0,
          transition: reduceMotion ? "none" : "opacity 1.2s ease",
          zIndex: 0,
        }}/>
      ))}

      {/* Top: partner co-brand strip — stays constant across slides */}
      <div style={{ position: "relative", zIndex: 1, display: "flex", alignItems: "center", gap: 10 }}>
        <PartnerLogo name={partner.name} color={partner.color} logoUrl={partner.logoUrl} size={20}/>
        <span style={{ fontSize: 13, fontWeight: 600 }}>{partner.name} · embedded with Casava</span>
      </div>

      {/* Middle: rotating slide content — keyed on slide id so animations restart */}
      <div key={slide.id} style={{ position: "relative", zIndex: 1, display: "flex", flexDirection: "column", gap: 24 }}>
        <div style={{
          width: 56, height: 56, borderRadius: 999,
          background: "rgba(255,255,255,0.14)", backdropFilter: "blur(8px)",
          display: "inline-flex", alignItems: "center", justifyContent: "center",
          border: "1px solid rgba(255,255,255,0.22)",
        }}>
          <CasaAvatar size={40} state={slide.casaState} eyeOffset={eyeOffset} blinking={blinking} innerRef={casaRef} color="#fff"/>
        </div>
        <div style={{ animation: reduceMotion ? "none" : "loginSlideIn 0.6s ease" }}>
          <div style={{ fontSize: 11, fontWeight: 700, letterSpacing: "0.1em", textTransform: "uppercase", opacity: 0.85, marginBottom: 12 }}>
            {slide.eyebrow}
          </div>
          <div style={{ fontFamily: FONT_DISPLAY, fontSize: 36, fontWeight: 400, lineHeight: 1.15, letterSpacing: "-0.02em", maxWidth: 480, textShadow: "0 2px 12px rgba(0,0,0,0.25)" }}>
            {slide.headline}
          </div>
          <div style={{ fontSize: 14, opacity: 0.85, marginTop: 16, lineHeight: 1.6, maxWidth: 460 }}>
            {slide.body}
          </div>
        </div>
        {slide.stats && (
          <div style={{ display: "flex", gap: 24, paddingTop: 16, borderTop: "1px solid rgba(255,255,255,0.22)", flexWrap: "wrap", animation: reduceMotion ? "none" : "loginSlideIn 0.7s ease 0.1s both" }}>
            {slide.stats.map((stat, i) => (
              <div key={i}>
                <div style={{ fontFamily: FONT_DISPLAY, fontSize: 24, fontWeight: 400 }}>{stat.value}</div>
                <div style={{ fontSize: 11, opacity: 0.78 }}>{stat.label}</div>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Bottom: regulatory + carousel dots */}
      <div style={{ position: "relative", zIndex: 1, display: "flex", justifyContent: "space-between", alignItems: "flex-end", gap: 16, flexWrap: "wrap" }}>
        <div style={{ fontSize: 11, opacity: 0.78 }}>
          NAICOM-licensed · NIIRA 2025 compliant · ₦3B recapitalised
        </div>
        <div role="tablist" aria-label="Bloom highlights" style={{ display: "flex", gap: 6 }}>
          {LOGIN_SLIDES.map((s, i) => (
            <button
              key={s.id}
              role="tab"
              aria-selected={i === active}
              aria-label={`Go to slide ${i + 1}: ${s.eyebrow}`}
              onClick={() => setActive(i)}
              style={{
                width: i === active ? 28 : 8, height: 8, borderRadius: 999,
                background: i === active ? "#fff" : "rgba(255,255,255,0.4)",
                border: "none", cursor: "pointer", padding: 0,
                transition: reduceMotion ? "none" : "width 0.3s ease, background 0.3s ease",
              }}
            />
          ))}
        </div>
      </div>
    </div>
  );
}

// ═══════════════════════════════════════════════════════════════════════════════════
// LOGIN — partner-aware sign-in screen with 2FA and SSO. Gates the app shell.
// ═══════════════════════════════════════════════════════════════════════════════════
function LoginPage({ partner, onSignIn }) {
  const [step, setStep] = useState("credentials"); // credentials | otp
  const [email, setEmail] = useState("bunmi.ade@moniepoint.com");
  const [password, setPassword] = useState("••••••••••••");
  const [otp, setOtp] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  const submitCredentials = (e) => {
    e?.preventDefault();
    if (!email || !password) return;
    setSubmitting(true);
    setTimeout(() => { setSubmitting(false); setStep("otp"); }, 700);
  };
  const submitOtp = (e) => {
    e?.preventDefault();
    if (otp.length < 6) return;
    setSubmitting(true);
    setTimeout(() => { setSubmitting(false); onSignIn(); }, 800);
  };

  return (
    <div style={{
      minHeight: "100vh", display: "grid", gridTemplateColumns: "1fr",
      background: T.bg, fontFamily: FONT,
    }}>
      <div style={{ display: "grid", gridTemplateColumns: "minmax(0, 1fr) minmax(0, 1.2fr)", minHeight: "100vh" }} className="login-grid">
        {/* Left: form */}
        <div className="login-form-pane" style={{ display: "flex", flexDirection: "column", padding: "32px 40px", maxWidth: 520, width: "100%", margin: "0 auto", justifyContent: "space-between" }}>
          <div style={{ display: "flex", alignItems: "center" }}>
            <Wordmark height={22}/>
          </div>

          <div style={{ paddingTop: 40, paddingBottom: 40 }}>
            {step === "credentials" && (
              <form onSubmit={submitCredentials} style={{ display: "flex", flexDirection: "column", gap: 18 }}>
                <div>
                  <Eyebrow color={T.brand}>Bloom · Partner portal</Eyebrow>
                  <Display size="lg">Welcome back.</Display>
                  <div style={{ fontSize: 14, color: T.textSec, marginTop: 8, lineHeight: 1.55 }}>
                    Sign in to your <strong style={{ color: T.text }}>{partner.name}</strong> account to manage policies, claims, and settlements.
                  </div>
                </div>

                <Field label="Work email">
                  <input type="email" autoComplete="email" required value={email} onChange={e => setEmail(e.target.value)} placeholder="you@partner.com" style={inputStyle}/>
                </Field>
                <Field label="Password">
                  <div style={{ position: "relative" }}>
                    <input type={showPassword ? "text" : "password"} autoComplete="current-password" required value={password} onChange={e => setPassword(e.target.value)} placeholder="Enter your password" style={{ ...inputStyle, paddingRight: 44 }}/>
                    <button type="button" onClick={() => setShowPassword(s => !s)} style={{ position: "absolute", right: 8, top: "50%", transform: "translateY(-50%)", background: "transparent", border: "none", padding: 6, cursor: "pointer", color: T.textSec, borderRadius: 6 }}>
                      <Eye size={15}/>
                    </button>
                  </div>
                </Field>

                <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", fontSize: 12 }}>
                  <label style={{ display: "inline-flex", alignItems: "center", gap: 6, cursor: "pointer", color: T.textSec }}>
                    <input type="checkbox" defaultChecked/> Keep me signed in
                  </label>
                  <button type="button" style={{ background: "transparent", border: "none", color: T.brand, cursor: "pointer", fontWeight: 600, fontFamily: FONT, fontSize: 12 }}>Forgot password?</button>
                </div>

                <Btn variant="brand" full disabled={submitting || !email || !password} iconRight={submitting ? null : ArrowRight} onClick={submitCredentials}>
                  {submitting ? "Signing in..." : "Sign in"}
                </Btn>

                <div style={{ display: "flex", alignItems: "center", gap: 12, color: T.textSec, fontSize: 11, fontWeight: 600, letterSpacing: "0.06em", textTransform: "uppercase" }}>
                  <div style={{ flex: 1, height: 1, background: T.border }}/>
                  Or continue with
                  <div style={{ flex: 1, height: 1, background: T.border }}/>
                </div>
                <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 8 }}>
                  <Btn variant="outline" onClick={() => setStep("otp")}>Microsoft SSO</Btn>
                  <Btn variant="outline" onClick={() => setStep("otp")}>Google SSO</Btn>
                </div>
              </form>
            )}

            {step === "otp" && (
              <form onSubmit={submitOtp} style={{ display: "flex", flexDirection: "column", gap: 18 }}>
                <Btn variant="ghost" size="sm" icon={ChevronLeft} onClick={() => setStep("credentials")}>Back</Btn>
                <div>
                  <Eyebrow color={T.brand}>Two-factor authentication</Eyebrow>
                  <Display size="md">Enter your code.</Display>
                  <div style={{ fontSize: 13, color: T.textSec, marginTop: 8, lineHeight: 1.55 }}>
                    We sent a 6-digit code to <strong style={{ color: T.text }}>{email}</strong>. It expires in 10 minutes.
                  </div>
                </div>

                <Field label="Authentication code">
                  <input type="text" inputMode="numeric" autoComplete="one-time-code" pattern="[0-9]*" maxLength={6} value={otp} onChange={e => setOtp(e.target.value.replace(/\D/g, ""))} placeholder="• • • • • •" autoFocus style={{ ...inputStyle, fontFamily: "ui-monospace, monospace", fontSize: 22, letterSpacing: "0.4em", textAlign: "center" }}/>
                </Field>

                <Btn variant="brand" full disabled={submitting || otp.length < 6} iconRight={submitting ? null : ArrowRight} onClick={submitOtp}>
                  {submitting ? "Verifying..." : "Verify and sign in"}
                </Btn>

                <div style={{ fontSize: 12, color: T.textSec, textAlign: "center" }}>
                  Didn't get it? <button type="button" style={{ background: "transparent", border: "none", color: T.brand, cursor: "pointer", fontWeight: 600, fontFamily: FONT, fontSize: 12 }}>Resend</button> · <button type="button" style={{ background: "transparent", border: "none", color: WA_GREEN_DEEP, cursor: "pointer", fontWeight: 600, fontFamily: FONT, fontSize: 12 }}>Try WhatsApp</button>
                </div>
              </form>
            )}
          </div>

          <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", fontSize: 11, color: T.textSec, gap: 12, flexWrap: "wrap" }}>
            <div>© 2026 Casava Microinsurance Ltd. · NAICOM MIC/2024/018</div>
            <div style={{ display: "flex", gap: 12 }}>
              <button style={{ background: "transparent", border: "none", color: T.textSec, cursor: "pointer", fontFamily: FONT, fontSize: 11 }}>Terms</button>
              <button style={{ background: "transparent", border: "none", color: T.textSec, cursor: "pointer", fontFamily: FONT, fontSize: 11 }}>Privacy</button>
            </div>
          </div>
        </div>

        {/* Right: rotating photographic carousel — brand + marketing + proof */}
        <LoginHeroCarousel partner={partner}/>
      </div>
    </div>
  );
}

// ═══════════════════════════════════════════════════════════════════════════════════
// RENEWALS — policies expiring soon, auto-renewal, batch actions
// ═══════════════════════════════════════════════════════════════════════════════════
function RenewalsPage({ navigate }) {
  const [window, setWindow] = useState("60");
  const [autoRenewal, setAutoRenewal] = useState({ p1: true, p2: false, p3: true });
  const [stubOpen, setStubOpen] = useState(null);

  // Build mock renewals based on existing policies, add an "expiresIn" field
  const mockRenewals = [
    { policyId: "p1",  customer: "Olatunde Akinola",   product: "Credit Life",     premium: 4500,  coverage: 5000000,  expiresIn: 8,  expires: "08 May 2026", channel: "Moniepoint",     icon: "💳" },
    { policyId: "p2",  customer: "Chinwe Okonkwo",     product: "Device Insurance",premium: 1850,  coverage: 250000,   expiresIn: 14, expires: "14 May 2026", channel: "M-KOPA",         icon: "📱" },
    { policyId: "p3",  customer: "Ibrahim Yusuf",      product: "Credit Life",     premium: 2200,  coverage: 1500000,  expiresIn: 22, expires: "22 May 2026", channel: "Samsung",        icon: "💳" },
    { policyId: "p4",  customer: "Adaeze Foods Ltd",   product: "Group Health",    premium: 12500, coverage: 5000000,  expiresIn: 31, expires: "31 May 2026", channel: "Kuda",           icon: "❤️" },
    { policyId: "p5",  customer: "Femi Bakare",        product: "Funds Protection",premium: 350,   coverage: 500000,   expiresIn: 45, expires: "14 Jun 2026", channel: "PalmPay",        icon: "🛡️" },
    { policyId: "p6",  customer: "Hauwa Trading Co",   product: "Building",        premium: 18500, coverage: 25000000, expiresIn: 53, expires: "22 Jun 2026", channel: "Direct",         icon: "🏢" },
    { policyId: "p7",  customer: "Tunde Salami",       product: "Credit Life",     premium: 850,   coverage: 300000,   expiresIn: 78, expires: "17 Jul 2026", channel: "Bolt",           icon: "💳" },
  ];
  const cutoff = parseInt(window);
  const filtered = mockRenewals.filter(r => r.expiresIn <= cutoff);
  const totalPremiumAtRisk = filtered.reduce((s, r) => s + r.premium * 12, 0);
  const autoRenewedCount = filtered.filter(r => autoRenewal[r.policyId]).length;

  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 20 }}>
      <div style={{ display: "flex", alignItems: "flex-start", justifyContent: "space-between", flexWrap: "wrap", gap: 12 }}>
        <div>
          <Display size="lg">Renewals</Display>
          <div style={{ fontSize: 14, color: T.textSec, marginTop: 6 }}>Policies expiring soon. Auto-renewal keeps cover continuous; manual renewal re-runs underwriting.</div>
        </div>
        <ChipMenu label="Window" value={`Next ${window} days`} options={[
          { id: "30", label: "Next 30 days" },
          { id: "60", label: "Next 60 days" },
          { id: "90", label: "Next 90 days" },
        ]} selected={window} onSelect={setWindow}/>
      </div>

      <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(200px, 1fr))", gap: 14 }}>
        <PartnerKPICard label="Expiring soon" value={filtered.length} helpText={`within ${window} days`}/>
        <PartnerKPICard label="On auto-renewal" value={autoRenewedCount} accent={T.success}/>
        <PartnerKPICard label="Manual review needed" value={filtered.length - autoRenewedCount} accent={T.amber}/>
        <PartnerKPICard label="Premium at risk" value={`₦${(totalPremiumAtRisk/1_000_000).toFixed(2)}M`} helpText="annualised"/>
      </div>

      <Banner
        variant="amber"
        title={`${filtered.filter(r => r.expiresIn <= 14 && !autoRenewal[r.policyId]).length} policies expire in 14 days without auto-renewal`}
        description="Casa flagged these for follow-up. Sending a WhatsApp renewal nudge today catches ~70% of these before lapse."
        action={<WhatsAppButton variant="subtle" size="sm" label="Send WhatsApp nudges" onClick={() => setStubOpen("nudge")}/>}
      />

      <Card title="Premium at risk by week" right={<Pill color="gray" size="sm">Annualised</Pill>}>
        {(() => {
          // Bucket the filtered renewals into 2-week bins. Bars are colored by urgency.
          const bins = [
            { label: "0–14 days",  range: [0, 14],  color: T.danger },
            { label: "15–30 days", range: [15, 30], color: T.amber },
            { label: "31–45 days", range: [31, 45], color: T.brand },
            { label: "46–60 days", range: [46, 60], color: T.brand },
            { label: "61–75 days", range: [61, 75], color: T.brandLight },
            { label: "76–90 days", range: [76, 90], color: T.brandLight },
          ];
          const data = bins
            .map(b => ({
              label: b.label,
              range: b.range,
              value: filtered
                .filter(r => r.expiresIn >= b.range[0] && r.expiresIn <= b.range[1])
                .reduce((s, r) => s + r.premium * 12, 0),
              color: b.color,
            }))
            .filter(b => b.value > 0 || parseInt(window) >= b.range[0]);
          return (
            <BarChart
              height={220}
              data={data}
              valueFormat={(v) => v >= 1_000_000 ? `₦${(v / 1_000_000).toFixed(1)}M` : `₦${(v / 1000).toFixed(0)}K`}
            />
          );
        })()}
      </Card>

      <Card title={`Expiring in next ${window} days`} right={<Btn variant="ghost" size="sm" onClick={() => setStubOpen("export")}>Export CSV</Btn>}>
        <DataTable
          columns={[
            { key: "customer", label: "Customer", render: (v, r) => (
              <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
                <CustomerAvatar name={v} customerId={r.customerId} size={32}/>
                <div>
                  <div style={{ fontWeight: 600 }}>{v}</div>
                  <div style={{ fontSize: 11, color: T.textSec, fontFamily: "ui-monospace, monospace" }}>{r.policyId.toUpperCase()} · {r.product}</div>
                </div>
              </div>
            )},
            { key: "channel", label: "Channel", hideMobile: true },
            { key: "premium", label: "Premium", numeric: true, align: "right", render: v => <Money amount={v} size="sm"/> },
            { key: "expiresIn", label: "Expires", render: (v, r) => (
              <div>
                <div style={{ fontSize: 13, fontWeight: 700, color: v <= 14 ? T.dangerText : v <= 30 ? T.amberText : T.text }}>
                  {v} {v === 1 ? "day" : "days"}
                </div>
                <div style={{ fontSize: 11, color: T.textSec }}>{r.expires}</div>
              </div>
            )},
            { key: "auto", label: "Auto-renewal", sortable: false, align: "center", render: (_, r) => (
              <Toggle on={!!autoRenewal[r.policyId]} onChange={() => setAutoRenewal(s => ({ ...s, [r.policyId]: !s[r.policyId] }))}/>
            )},
            { key: "actions", label: "", sortable: false, align: "right", render: (_, r) => (
              <Btn variant="ghost" size="sm" onClick={() => navigate("policy-detail", { id: r.policyId })}>View</Btn>
            )},
          ]}
          rows={filtered}
          emptyMessage={`No renewals in the next ${window} days.`}
        />
      </Card>

      {stubOpen && (
        <ComingSoonModal
          title={{ nudge: "Send renewal nudges", export: "Export renewals" }[stubOpen]}
          description={{
            nudge: "Send a WhatsApp + SMS reminder to all customers whose policies expire in 14 days without auto-renewal. Production wires this to the messaging service.",
            export: "Download a CSV of all renewals in the current window with customer details, premium, and renewal status.",
          }[stubOpen]}
          onClose={() => setStubOpen(null)}
        />
      )}
    </div>
  );
}

// ═══════════════════════════════════════════════════════════════════════════════════
// AUDIT LOG — full page (replaces stub modal on Security)
// ═══════════════════════════════════════════════════════════════════════════════════
function AuditLogPage() {
  const [actor, setActor] = useState("all");
  const [action, setAction] = useState("all");
  const [q, setQ] = useState("");

  const events = [
    { id: "a1",  ts: "29 Apr 2026 · 14:22:18", actor: "Bunmi Adeyemi",  email: "bunmi@moniepoint.com",   action: "policy.bound",         resource: "CL-2026-0142", outcome: "success", ip: "102.89.32.14" },
    { id: "a2",  ts: "29 Apr 2026 · 14:22:14", actor: "Bunmi Adeyemi",  email: "bunmi@moniepoint.com",   action: "quote.generated",      resource: "QT-2026-0142", outcome: "success", ip: "102.89.32.14" },
    { id: "a3",  ts: "29 Apr 2026 · 13:48:02", actor: "Tola Akinsanya", email: "tola@moniepoint.com",    action: "claim.approved",       resource: "CLM-2026-0014", outcome: "success", ip: "102.89.32.14" },
    { id: "a4",  ts: "29 Apr 2026 · 11:14:55", actor: "Bunmi Adeyemi",  email: "bunmi@moniepoint.com",   action: "bvn.revealed",         resource: "Customer c4",   outcome: "success", ip: "102.89.32.14" },
    { id: "a5",  ts: "29 Apr 2026 · 09:32:11", actor: "Damilola Egwu",  email: "damilola@moniepoint.com",action: "claim.note.added",     resource: "CLM-2026-0023", outcome: "success", ip: "102.89.32.14" },
    { id: "a6",  ts: "28 Apr 2026 · 22:18:04", actor: "Unknown",        email: "—",                      action: "auth.signin.failed",   resource: "—",             outcome: "failed",  ip: "102.89.31.22" },
    { id: "a7",  ts: "28 Apr 2026 · 16:48:23", actor: "Bunmi Adeyemi",  email: "bunmi@moniepoint.com",   action: "api_key.regenerated",  resource: "Sandbox",       outcome: "success", ip: "102.89.32.14" },
    { id: "a8",  ts: "28 Apr 2026 · 14:22:10", actor: "Seyi Williams",  email: "seyi@moniepoint.com",    action: "settlement.exported",  resource: "Mar 2026",      outcome: "success", ip: "102.89.32.14" },
    { id: "a9",  ts: "27 Apr 2026 · 09:14:00", actor: "Bunmi Adeyemi",  email: "bunmi@moniepoint.com",   action: "bulk_upload.completed",resource: "moniepoint-april-week4.csv", outcome: "success", ip: "102.89.32.14" },
    { id: "a10", ts: "26 Apr 2026 · 11:40:33", actor: "Bunmi Adeyemi",  email: "bunmi@moniepoint.com",   action: "password.changed",     resource: "—",             outcome: "success", ip: "102.89.32.14" },
    { id: "a11", ts: "25 Apr 2026 · 16:48:18", actor: "Tola Akinsanya", email: "tola@moniepoint.com",    action: "policy.cancelled",     resource: "DV-2025-0892",  outcome: "success", ip: "102.89.32.14" },
    { id: "a12", ts: "25 Apr 2026 · 12:04:19", actor: "Bunmi Adeyemi",  email: "bunmi@moniepoint.com",   action: "team.invited",         resource: "tobi@moniepoint.com", outcome: "success", ip: "102.89.32.14" },
  ];

  const filtered = events.filter(e => {
    const matchActor = actor === "all" || e.email === actor;
    const matchAction = action === "all" || e.action === action;
    const matchQ = !q || e.actor.toLowerCase().includes(q.toLowerCase()) || e.action.toLowerCase().includes(q.toLowerCase()) || e.resource.toLowerCase().includes(q.toLowerCase());
    return matchActor && matchAction && matchQ;
  });

  const actionOptions = [{ id: "all", label: "All actions" }, ...Array.from(new Set(events.map(e => e.action))).map(a => ({ id: a, label: a }))];
  const actorOptions = [{ id: "all", label: "All actors" }, ...Array.from(new Set(events.map(e => e.email).filter(em => em !== "—"))).map(em => ({ id: em, label: em }))];

  const actionColor = (a) => a.includes("failed") ? T.danger : a.includes("revealed") || a.includes("regenerated") || a.includes("changed") ? T.amber : T.text;

  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 20 }}>
      <div style={{ display: "flex", alignItems: "flex-start", justifyContent: "space-between", flexWrap: "wrap", gap: 12 }}>
        <div>
          <Display size="lg">Audit log</Display>
          <div style={{ fontSize: 14, color: T.textSec, marginTop: 6 }}>NDPR-compliant action history. Every meaningful action is logged with actor, time, and IP.</div>
        </div>
        <Btn variant="outline" icon={Download} onClick={() => alert("Audit log exported (demo)")}>Export CSV</Btn>
      </div>

      <FilterBar rightSlot={<SearchInput value={q} onChange={setQ} placeholder="Search actor, action, resource..."/>}>
        <ChipMenu label="Actor" value={actor === "all" ? "All" : actor} options={actorOptions} selected={actor} onSelect={setActor}/>
        <ChipMenu label="Action" value={action === "all" ? "All" : action} options={actionOptions} selected={action} onSelect={setAction}/>
        <FilterChip label="Date range"/>
        {(actor !== "all" || action !== "all" || q) && (
          <button onClick={() => { setActor("all"); setAction("all"); setQ(""); }} style={{ background: "transparent", border: "none", color: T.textSec, fontSize: 12, fontWeight: 600, cursor: "pointer", fontFamily: FONT, marginLeft: 4 }}>Clear all</button>
        )}
      </FilterBar>

      <Card title={`${filtered.length} events`}>
        <DataTable
          columns={[
            { key: "ts", label: "Timestamp", render: v => <span style={{ fontSize: 11, color: T.textSec, fontFamily: "ui-monospace, monospace" }}>{v}</span> },
            { key: "actor", label: "Actor", render: (v, r) => (
              <div>
                <div style={{ fontWeight: 600 }}>{v}</div>
                <div style={{ fontSize: 11, color: T.textSec }}>{r.email}</div>
              </div>
            )},
            { key: "action", label: "Action", render: (v, r) => (
              <code style={{ fontSize: 12, color: actionColor(v), fontFamily: "ui-monospace, monospace", fontWeight: 600 }}>
                {r.outcome === "failed" && "⚠ "}{v}
              </code>
            )},
            { key: "resource", label: "Resource", hideMobile: true, render: v => <span style={{ fontSize: 12, color: T.text, fontFamily: v.startsWith("CL") || v.startsWith("DV") || v.startsWith("CLM") || v.startsWith("QT") ? "ui-monospace, monospace" : FONT }}>{v}</span> },
            { key: "ip", label: "IP", hideMobile: true, render: v => <code style={{ fontSize: 11, color: T.textSec }}>{v}</code> },
          ]}
          rows={filtered}
          emptyMessage="No events match your filters."
        />
      </Card>
    </div>
  );
}

function PlaceholderPage({ title, description }) {
  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 20 }}>
      <div>
        <Display size="lg">{title}</Display>
        <div style={{ fontSize: 14, color: T.textSec, marginTop: 6 }}>{description}</div>
      </div>
      <div style={{ padding: 60, background: T.surface, border: `1px dashed ${T.borderStrong}`, borderRadius: 14, textAlign: "center" }}>
        <CasaAvatar size={56} state="thinking"/>
        <div style={{ marginTop: 16, fontSize: 14, fontWeight: 600 }}>This page is in the v2 backlog.</div>
        <div style={{ marginTop: 6, fontSize: 13, color: T.textSec, maxWidth: 380, margin: "6px auto 0" }}>Casa is still working on the rebuild for this surface. The existing Bloom route still functions while this is in progress.</div>
      </div>
    </div>
  );
}

// ═══════════════════════════════════════════════════════════════════════════════════
// SHARED HELPERS
// ═══════════════════════════════════════════════════════════════════════════════════
function Card({ title, right, children }) {
  return (
    <div style={{ background: T.surface, border: `1px solid ${T.border}`, borderRadius: 14, padding: 22 }}>
      {(title || right) && (
        <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 16, gap: 8 }}>
          {title && <Display size="sm">{title}</Display>}
          {right}
        </div>
      )}
      {children}
    </div>
  );
}

function KVP({ label, value }) {
  return (
    <div>
      <div style={{ fontSize: 11, fontWeight: 600, color: T.textSec, letterSpacing: "0.06em", textTransform: "uppercase", marginBottom: 4 }}>{label}</div>
      <div style={{ fontSize: 14, fontWeight: 500 }}>{value}</div>
    </div>
  );
}

function Field({ label, hint, children }) {
  return (
    <div>
      {label && <Eyebrow>{label}</Eyebrow>}
      {children}
      {hint && <div style={{ marginTop: 6, fontSize: 11, color: T.textSec }}>{hint}</div>}
    </div>
  );
}

function EmptyState({ message, icon: Icon = HelpCircle }) {
  return (
    <div style={{ padding: 40, textAlign: "center", color: T.textSec }}>
      <Icon size={32} strokeWidth={1.5} style={{ opacity: 0.4 }}/>
      <div style={{ marginTop: 10, fontSize: 13, fontWeight: 500 }}>{message}</div>
    </div>
  );
}

const inputStyle = {
  width: "100%", padding: "12px 14px", borderRadius: 10,
  border: `1px solid ${T.border}`, background: T.surface, fontFamily: FONT,
  fontSize: 14, color: T.text, outline: "none", boxSizing: "border-box",
};

function perilsForProduct(product) {
  if (product.includes("Credit")) {
    return [
      { id: "death", label: "Death", desc: "Natural or accidental death of the policyholder.", icon: "✝️" },
      { id: "disability", label: "Permanent disability", desc: "Total and permanent disability from accident or illness.", icon: "♿" },
      { id: "critical", label: "Critical illness", desc: "Diagnosis of cancer, stroke, or heart attack.", icon: "🏥" },
    ];
  }
  if (product.includes("Device")) {
    return [
      { id: "theft", label: "Theft", desc: "Stolen device with police report.", icon: "🚨" },
      { id: "damage", label: "Accidental damage", desc: "Liquid damage, drops, screen breakage.", icon: "💧" },
      { id: "loss", label: "Loss", desc: "Lost device, with proof of last location.", icon: "❓" },
    ];
  }
  if (product.includes("Funds") || product.includes("Funds Protection")) {
    return [
      { id: "fraud", label: "Unauthorized transaction", desc: "Account compromised, fraudulent debit.", icon: "🛡️" },
      { id: "sim", label: "SIM swap", desc: "SIM swap fraud leading to financial loss.", icon: "📱" },
      { id: "phish", label: "Phishing", desc: "Loss from phishing or social engineering.", icon: "🎣" },
    ];
  }
  return [
    { id: "other", label: "Other peril", desc: "Speak to support to file a claim outside standard perils.", icon: "❓" },
  ];
}

function stagesForClaim(status) {
  const order = ["Submitted", "Processing", "Approved", "Disbursed"];
  const i = order.indexOf(status);
  return order.map((s, idx) => ({
    label: s,
    status: idx < i ? "complete" : idx === i ? "current" : "pending",
    date: idx <= i ? ["28 Apr 2026", "29 Apr 2026", "01 May 2026", "02 May 2026"][idx] : null,
    note: idx === i ? {
      Submitted: "Claim received and queued for review.",
      Processing: "Casa pre-checked the claim. Currently with underwriting.",
      Approved: "Claim approved. Awaiting disbursement.",
      Disbursed: "Funds sent to nominated bank account.",
    }[s] : null,
  }));
}

// ═══════════════════════════════════════════════════════════════════════════════════
// APP
// ═══════════════════════════════════════════════════════════════════════════════════
export default function App() {
  useFonts();
  const [signedIn, setSignedIn] = useState(false);
  const [page, setPage] = useState("dashboard");
  const [params, setParams] = useState({});
  const [collapsed, setCollapsed] = useState(false);
  const [mobileNav, setMobileNav] = useState(false);
  const [searchOpen, setSearchOpen] = useState(false);
  const [chatOpen, setChatOpen] = useState(false);
  const [logoutOpen, setLogoutOpen] = useState(false);
  const [sellState, setSellState] = useState({ open: false, product: null, customer: null, loan: null });
  const [partner, setPartner] = useState(DEFAULT_PARTNER);
  const [, forceRender] = useState(0); // bump to re-render after mutating mock data
  const [openGroups, setOpenGroups] = useState(["manage"]); // Manage open by default
  const [w, setW] = useState(typeof window !== "undefined" ? window.innerWidth : 1200);

  useEffect(() => {
    const r = () => setW(window.innerWidth);
    window.addEventListener("resize", r);
    return () => window.removeEventListener("resize", r);
  }, []);
  const isMobile = w < 768;

  // ⌘K / Ctrl+K listener
  useEffect(() => {
    const handler = (e) => {
      if ((e.metaKey || e.ctrlKey) && e.key.toLowerCase() === "k") {
        e.preventDefault();
        setSearchOpen(s => !s);
      }
    };
    window.addEventListener("keydown", handler);
    return () => window.removeEventListener("keydown", handler);
  }, []);

  // Auto-open the parent group of the active page
  useEffect(() => {
    const groupId = findGroupForPage(page);
    if (groupId && !openGroups.includes(groupId)) {
      setOpenGroups(prev => [...prev, groupId]);
    }
  }, [page]); // eslint-disable-line react-hooks/exhaustive-deps

  // Auto-close mobile drawer when viewport widens past breakpoint
  useEffect(() => {
    if (!isMobile && mobileNav) setMobileNav(false);
  }, [isMobile, mobileNav]);

  // Body scroll lock when an overlay is open (mobile drawer, command palette, chat sheet, modals)
  useEffect(() => {
    const anyOverlay = mobileNav || searchOpen || chatOpen || logoutOpen;
    if (anyOverlay) {
      const prev = document.body.style.overflow;
      document.body.style.overflow = "hidden";
      return () => { document.body.style.overflow = prev; };
    }
  }, [mobileNav, searchOpen, chatOpen, logoutOpen]);

  const navigate = (p, par = {}) => {
    setPage(p);
    setParams(par);
    setMobileNav(false);
    if (typeof window !== "undefined") window.scrollTo({ top: 0, behavior: "instant" });
  };

  const toggleGroup = (gid) => {
    setOpenGroups(prev => prev.includes(gid) ? prev.filter(g => g !== gid) : [...prev, gid]);
  };

  const breadcrumb = useMemo(() => {
    // Detail pages
    if (page === "policy-detail") {
      const p = POLICIES.find(x => x.id === params.id);
      return [{ label: "Manage", page: null }, { label: "Policies", page: "policies" }, { label: p?.policyNumber || "Detail", page: null }];
    }
    if (page === "customer-detail") {
      const c = CUSTOMERS.find(x => x.id === params.id);
      return [{ label: "Customers", page: "customers" }, { label: c?.name || "Detail", page: null }];
    }
    if (page === "claim-detail") {
      const cl = CLAIMS.find(x => x.id === params.id);
      return [{ label: "Manage", page: null }, { label: "Claims", page: "claims" }, { label: cl?.claimRef || "Detail", page: null }];
    }
    if (page === "quote-detail") {
      const qt = QUOTES.find(x => x.id === params.id);
      return [{ label: "Sell", page: null }, { label: "Quotes", page: "quotes" }, { label: qt?.quoteRef || "Detail", page: null }];
    }
    if (page === "new-claim") return [{ label: "Manage", page: null }, { label: "Claims", page: "claims" }, { label: "New claim", page: null }];

    // Find in nav structure
    for (const g of [...NAV_GROUPS, ...NAV_BOTTOM]) {
      if (g.type === "item" && g.id === page) return [{ label: g.label, page: null }];
      if (g.type === "group") {
        const item = g.items.find(i => i.id === page);
        if (item) {
          // For Developers with section anchor, append section name
          if (params.section && g.id === "developers") {
            const sectionItem = g.items.find(i => i.section === params.section);
            return [{ label: g.label, page: null }, { label: sectionItem?.label || item.label, page: null }];
          }
          return [{ label: g.label, page: null }, { label: item.label, page: null }];
        }
      }
    }
    return [{ label: "Home", page: null }];
  }, [page, params]);

  let content;
  switch (page) {
    case "dashboard": content = <DashboardPage navigate={navigate} openSell={(product, customer, loan) => setSellState({ open: true, product, customer: customer || null, loan: loan || null })}/>; break;
    case "customers": content = <CustomersPage navigate={navigate}/>; break;
    case "customer-detail": content = <CustomerDetailPage navigate={navigate} params={params}/>; break;
    case "policies": content = <PoliciesPage navigate={navigate}/>; break;
    case "policy-detail": content = <PolicyDetailPage navigate={navigate} params={params}/>; break;
    case "claims": content = <ClaimsPage navigate={navigate}/>; break;
    case "claim-detail": content = <ClaimDetailPage navigate={navigate} params={params}/>; break;
    case "new-claim": content = <NewClaimPage navigate={navigate} params={params}/>; break;
    case "loan-book": content = <LoanBookPage navigate={navigate} openSell={(product, customer, loan) => setSellState({ open: true, product, customer: customer || null, loan: loan || null })}/>; break;
    case "get-insurance": content = <GetInsurancePage navigate={navigate} openSell={(product, customer, loan) => setSellState({ open: true, product, customer: customer || null, loan: loan || null })}/>; break;
    case "wallet": content = <WalletPage navigate={navigate}/>; break;
    case "settlements": content = <SettlementsPage navigate={navigate}/>; break;
    case "statements": content = <StatementsPage/>; break;
    case "developers": content = <IntegrationsPage section={params.section}/>; break;
    case "quotes": content = <QuotesPage navigate={navigate}/>; break;
    case "quote-detail": content = <QuoteDetailPage navigate={navigate} params={params}/>; break;
    case "bulk-uploads": content = <BulkUploadsPage/>; break;
    case "roles": content = <TeamPage/>; break;
    case "settings": content = <AccountPage partner={partner} setPartner={setPartner}/>; break;
    case "notifications": content = <NotificationsPage/>; break;
    case "security": content = <SecurityPage navigate={navigate}/>; break;
    case "renewals": content = <RenewalsPage navigate={navigate}/>; break;
    case "audit-log": content = <AuditLogPage/>; break;
    default: content = <DashboardPage navigate={navigate} openSell={(product, customer, loan) => setSellState({ open: true, product, customer: customer || null, loan: loan || null })}/>;
  }

  if (!signedIn) {
    return <LoginPage partner={partner} onSignIn={() => setSignedIn(true)}/>;
  }

  return (
    <div style={{
      minHeight: "100vh", background: T.bg, color: T.text, fontFamily: FONT, fontSize: 14, lineHeight: 1.5,
      display: "flex",
    }}>
      {!isMobile && (
        <Sidebar
          current={page} sectionParam={params.section}
          onNavigate={navigate} collapsed={collapsed}
          onToggle={() => setCollapsed(c => !c)}
          onSearchOpen={() => setSearchOpen(true)}
          openGroups={openGroups} onToggleGroup={toggleGroup}
          onLogout={() => setLogoutOpen(true)}
          partner={partner}
        />
      )}
      {isMobile && mobileNav && (
        <div style={{ position: "fixed", inset: 0, background: "rgba(0,0,0,0.4)", zIndex: 998 }} onClick={() => setMobileNav(false)}>
          <div onClick={e => e.stopPropagation()} style={{ width: 280, height: "100vh", background: T.surface }}>
            <Sidebar
              current={page} sectionParam={params.section}
              onNavigate={navigate} collapsed={false}
              onToggle={() => setMobileNav(false)}
              onSearchOpen={() => { setSearchOpen(true); setMobileNav(false); }}
              openGroups={openGroups} onToggleGroup={toggleGroup}
              onLogout={() => { setLogoutOpen(true); setMobileNav(false); }}
              partner={partner}
            />
          </div>
        </div>
      )}
      <main style={{ flex: 1, minWidth: 0, display: "flex", flexDirection: "column" }}>
        <div style={{ display: "flex", alignItems: "center", borderBottom: `1px solid ${T.border}` }}>
          {isMobile && (
            <button onClick={() => setMobileNav(true)} style={{ background: "transparent", border: "none", padding: 16, cursor: "pointer" }}>
              <Menu size={20}/>
            </button>
          )}
          <div style={{ flex: 1 }}>
            <TopBar user={{ name: "Bunmi Adeyemi", role: "Casava admin · Internal" }} breadcrumb={breadcrumb} onNavigate={navigate} onSearchOpen={() => setSearchOpen(true)}/>
          </div>
        </div>
        <div style={{ padding: isMobile ? 16 : 32, flex: 1 }}>
          {content}
        </div>
      </main>
      {!chatOpen && !mobileNav && <AskCasa onClick={() => setChatOpen(true)} casaState="happy"/>}
      <CasaChatSheet open={chatOpen} onClose={() => setChatOpen(false)}/>
      <CommandPalette open={searchOpen} onClose={() => setSearchOpen(false)} onNavigate={navigate}/>
      <SellFlow
        open={sellState.open}
        product={sellState.product}
        prefilledCustomer={sellState.customer}
        prefilledLoan={sellState.loan}
        onClose={() => setSellState({ open: false, product: null, customer: null, loan: null })}
        onComplete={() => forceRender(n => n + 1)}
      />
      {logoutOpen && (
        <Modal title="Log out?" onClose={() => setLogoutOpen(false)} maxWidth={420}
          footer={
            <>
              <Btn variant="ghost" onClick={() => setLogoutOpen(false)}>Cancel</Btn>
              <Btn variant="primary" icon={LogOut} onClick={() => { setLogoutOpen(false); setSignedIn(false); setPage("dashboard"); setParams({}); }}>Log out</Btn>
            </>
          }>
          <div style={{ fontSize: 13, color: T.textSec, lineHeight: 1.6 }}>
            You'll be returned to the sign-in screen. Any unsaved drafts will be discarded.
          </div>
        </Modal>
      )}
    </div>
  );
}
